import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _typeof from '@babel/runtime/helpers/esm/typeof';
import fetch from '@zillow/fetchier';
import { isSessionStorageSupported, isLocalStorageSupported } from '@zillow/is-local-storage-supported';
import crypto from 'crypto';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { createSelector, defaultMemoize } from 'reselect';
import { constants, formatters, parsers, loanFile as loanFile$3, address, preQualification, conditions as conditions$2 } from '@zillow/zhl-yup';
import omit from 'lodash/omit';
import querystring from 'querystring';
import { REHYDRATE, persistStore, persistReducer } from 'redux-persist';
import jwtDecode from 'jwt-decode';
import { createStore, combineReducers } from 'redux';
import _merge2 from 'lodash/merge';
import _isNumber2 from 'lodash/isNumber';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import uniqBy from 'lodash/uniqBy';
import flatten from 'lodash/flatten';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import isPlainObject from 'lodash/isPlainObject';
import isEmpty from 'lodash/isEmpty';
import groupBy from 'lodash/groupBy';
import flatMap from 'lodash/flatMap';
import cond from 'lodash/cond';
import mapValues from 'lodash/mapValues';
import isFunction from 'lodash/isFunction';
import assign from 'lodash/assign';
import uniq from 'lodash/uniq';
import omitBy from 'lodash/omitBy';
import _toPropertyKey from '@babel/runtime/helpers/esm/toPropertyKey';
import storage from 'redux-persist/lib/storage';
import { captureException } from '@sentry/browser';
import clone from 'lodash/clone';
import setWith from 'lodash/setWith';

// Unique ID creation requires a high quality random # generator.  In node.js
// this is pretty straight-forward - we use the crypto API.



var rng = function nodeRNG() {
  return crypto.randomBytes(16);
};

/**
 * Convert array of 16 byte values to UUID string format of the form:
 * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
 */
var byteToHex = [];
for (var i = 0; i < 256; ++i) {
  byteToHex[i] = (i + 0x100).toString(16).substr(1);
}

function bytesToUuid(buf, offset) {
  var i = offset || 0;
  var bth = byteToHex;
  // join used to fix memory issue caused by concatenation: https://bugs.chromium.org/p/v8/issues/detail?id=3175#c4
  return ([
    bth[buf[i++]], bth[buf[i++]],
    bth[buf[i++]], bth[buf[i++]], '-',
    bth[buf[i++]], bth[buf[i++]], '-',
    bth[buf[i++]], bth[buf[i++]], '-',
    bth[buf[i++]], bth[buf[i++]], '-',
    bth[buf[i++]], bth[buf[i++]],
    bth[buf[i++]], bth[buf[i++]],
    bth[buf[i++]], bth[buf[i++]]
  ]).join('');
}

var bytesToUuid_1 = bytesToUuid;

function v4(options, buf, offset) {
  var i = buf && offset || 0;

  if (typeof(options) == 'string') {
    buf = options === 'binary' ? new Array(16) : null;
    options = null;
  }
  options = options || {};

  var rnds = options.random || (options.rng || rng)();

  // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`
  rnds[6] = (rnds[6] & 0x0f) | 0x40;
  rnds[8] = (rnds[8] & 0x3f) | 0x80;

  // Copy bytes to buffer, if provided
  if (buf) {
    for (var ii = 0; ii < 16; ++ii) {
      buf[i + ii] = rnds[ii];
    }
  }

  return buf || bytesToUuid_1(rnds);
}

var v4_1 = v4;

var queryParamsStateSelector = function queryParamsStateSelector(_ref) {
  var queryParams = _ref.queryParams;
  return queryParams || null;
};
var queryParamsSelector = createSelector([queryParamsStateSelector], function (queryParams) {
  return queryParams ? omit(queryParams, '_persist') : null;
});
var createQueryParamSelector = function createQueryParamSelector(queryParam) {
  return createSelector([queryParamsSelector], function (queryParams) {
    return (queryParams === null || queryParams === void 0 ? void 0 : queryParams[queryParam]) || null;
  });
};
var userSessionIDSelector = createQueryParamSelector('userSessionId');
var sourceSelector = createQueryParamSelector('source');
var channelSelector = createQueryParamSelector('channel');
var shouldShowBorrowerPairsManagementSelector = createSelector([queryParamsSelector], function (queryParams) {
  return !!(queryParams !== null && queryParams !== void 0 && queryParams.showBorrowerPairsManagement);
});
var showRefinanceDisclaimerSelector = createSelector([queryParamsSelector], function (queryParams) {
  return !!(queryParams !== null && queryParams !== void 0 && queryParams.refinanceDisclaimerState);
});
var isPassportSourceSelector = createSelector([queryParamsSelector], function (queryParams) {
  return (queryParams === null || queryParams === void 0 ? void 0 : queryParams.source) === 'passport';
});

var queryParams = /*#__PURE__*/Object.freeze({
  __proto__: null,
  queryParamsStateSelector: queryParamsStateSelector,
  queryParamsSelector: queryParamsSelector,
  createQueryParamSelector: createQueryParamSelector,
  userSessionIDSelector: userSessionIDSelector,
  sourceSelector: sourceSelector,
  channelSelector: channelSelector,
  shouldShowBorrowerPairsManagementSelector: shouldShowBorrowerPairsManagementSelector,
  showRefinanceDisclaimerSelector: showRefinanceDisclaimerSelector,
  isPassportSourceSelector: isPassportSourceSelector
});

var USER_ROLES_COMMON = constants.USER_ROLES_COMMON;
var authSelector = function authSelector(_ref) {
  var auth = _ref.auth;
  return auth || null;
};
var ONE_MINUTE_IN_MS = 60 * 1000;
var EMPLOYEE_ALLOWED_SESSION_IDLE_TIME = 60 * ONE_MINUTE_IN_MS;
var NON_EMPLOYEE_ALLOWED_SESSION_IDLE_TIME = 60 * ONE_MINUTE_IN_MS;
var TIME_BEFORE_SESSION_EXPIRY_TO_SHOW_NOTIFICATION = 5 * ONE_MINUTE_IN_MS;
var accessTokenSelector = createSelector([authSelector], function (auth) {
  return auth ? auth.access_token : null;
});
var refreshTokenSelector = createSelector([authSelector], function (auth) {
  return auth ? auth.refresh_token : null;
});
var decodedAccessTokenSelector = createSelector([authSelector], function (auth) {
  return auth ? auth.decodedAccessToken : null;
});
var accessTokenSubMaskedSelector = createSelector([decodedAccessTokenSelector], function (decodedAccessToken) {
  return decodedAccessToken ? decodedAccessToken.sub_masked : null;
});
var accessTokenExpirationSelector = createSelector([decodedAccessTokenSelector], function (decodedAccessToken) {
  return decodedAccessToken ? new Date(decodedAccessToken.exp * 1000) : null;
});
var contactInfoSelector = createSelector([decodedAccessTokenSelector], function (decodedAccessToken) {
  var _decodedAccessToken$n, _decodedAccessToken$n2;

  return decodedAccessToken ? {
    email: decodedAccessToken.email,
    name: {
      first: ((_decodedAccessToken$n = decodedAccessToken.names) === null || _decodedAccessToken$n === void 0 ? void 0 : _decodedAccessToken$n.first) || null,
      last: ((_decodedAccessToken$n2 = decodedAccessToken.names) === null || _decodedAccessToken$n2 === void 0 ? void 0 : _decodedAccessToken$n2.last) || null
    }
  } : null;
});
var userRolesSelector = createSelector([decodedAccessTokenSelector], function (decodedAccessToken) {
  return (decodedAccessToken === null || decodedAccessToken === void 0 ? void 0 : decodedAccessToken.roles) || [];
});
var hasUnauthenticatedRoleSelector = createSelector([userRolesSelector], function (userRoles) {
  return userRoles.includes(USER_ROLES_COMMON.UNAUTHENTICATED_USER);
});
var isUserEmployeeFromAccessTokenSelector = createSelector([userRolesSelector], function (userRoles) {
  return userRoles.includes(USER_ROLES_COMMON.ZHL_EMPLOYEE);
});
var timeToSessionExpirySelector = createSelector([decodedAccessTokenSelector, isUserEmployeeFromAccessTokenSelector], function (decodedAccessToken, isUserEmployeeFromAccessToken) {
  if (!decodedAccessToken) {
    return null;
  }

  return isUserEmployeeFromAccessToken ? EMPLOYEE_ALLOWED_SESSION_IDLE_TIME : NON_EMPLOYEE_ALLOWED_SESSION_IDLE_TIME;
});
var timeToSessionNearingExpirySelector = createSelector([timeToSessionExpirySelector], function (timeToSessionExpiry) {
  if (!timeToSessionExpiry) {
    return null;
  }

  return timeToSessionExpiry - TIME_BEFORE_SESSION_EXPIRY_TO_SHOW_NOTIFICATION;
});
var isAuthenticatedSelector = createSelector([accessTokenSelector], function (accessToken) {
  return !!accessToken;
});
var didUserInvokeLogoutSelector = createSelector([authSelector], function (auth) {
  return auth ? auth.didUserInvokeLogout : false;
});
var didUserSessionExpireSelector = createSelector([authSelector], function (auth) {
  return auth ? auth.didUserSessionExpire : false;
});
var showSessionNearingExpiryNotificationSelector = createSelector([authSelector], function (auth) {
  return auth ? auth.showSessionNearingExpiryNotification : false;
});
var gaTrackingGUIDSelector = createSelector([authSelector], function (auth) {
  return (auth === null || auth === void 0 ? void 0 : auth.gaTrackingGUID) || null;
});

var getAbsoluteURL = function getAbsoluteURL(path, hash, queryParams) {
  var workingURL = new URL(path, window.location.origin);
  Object.entries(queryParams !== null && queryParams !== void 0 ? queryParams : {}).forEach(function (_ref2) {
    var _ref3 = _slicedToArray(_ref2, 2),
        key = _ref3[0],
        value = _ref3[1];

    workingURL.searchParams.append(key, value);
  });
  workingURL.hash = hash;
  return workingURL;
};

var redirectURLSelector = createSelector([queryParamsSelector], function (queryParameters) {
  return typeof window === 'undefined' ? null : getAbsoluteURL(window.location.pathname, window.location.hash, queryParameters).href;
});
var redirectURLForTabLoginSelector = createSelector([queryParamsSelector], function (queryParameters) {
  return typeof window === 'undefined' ? null : getAbsoluteURL('/handleTabLogin/', '', queryParameters).href;
});

var auth = /*#__PURE__*/Object.freeze({
  __proto__: null,
  authSelector: authSelector,
  EMPLOYEE_ALLOWED_SESSION_IDLE_TIME: EMPLOYEE_ALLOWED_SESSION_IDLE_TIME,
  NON_EMPLOYEE_ALLOWED_SESSION_IDLE_TIME: NON_EMPLOYEE_ALLOWED_SESSION_IDLE_TIME,
  TIME_BEFORE_SESSION_EXPIRY_TO_SHOW_NOTIFICATION: TIME_BEFORE_SESSION_EXPIRY_TO_SHOW_NOTIFICATION,
  accessTokenSelector: accessTokenSelector,
  refreshTokenSelector: refreshTokenSelector,
  decodedAccessTokenSelector: decodedAccessTokenSelector,
  accessTokenSubMaskedSelector: accessTokenSubMaskedSelector,
  accessTokenExpirationSelector: accessTokenExpirationSelector,
  contactInfoSelector: contactInfoSelector,
  userRolesSelector: userRolesSelector,
  hasUnauthenticatedRoleSelector: hasUnauthenticatedRoleSelector,
  isUserEmployeeFromAccessTokenSelector: isUserEmployeeFromAccessTokenSelector,
  timeToSessionExpirySelector: timeToSessionExpirySelector,
  timeToSessionNearingExpirySelector: timeToSessionNearingExpirySelector,
  isAuthenticatedSelector: isAuthenticatedSelector,
  didUserInvokeLogoutSelector: didUserInvokeLogoutSelector,
  didUserSessionExpireSelector: didUserSessionExpireSelector,
  showSessionNearingExpiryNotificationSelector: showSessionNearingExpiryNotificationSelector,
  gaTrackingGUIDSelector: gaTrackingGUIDSelector,
  redirectURLSelector: redirectURLSelector,
  redirectURLForTabLoginSelector: redirectURLForTabLoginSelector
});

function getConfig() {
  var config = typeof window === 'undefined' ? null : window.zmAPIConfig; // If no config is specified on the page, we are not in a test environment context, so use a prod config

  if (!config && process.env.NODE_ENV === 'production') {
    config = {
      serverURLs: {
        MAX: 'https://mortgageapi.zillow.com/'
      },
      partnerId: 'RD-XZQQPCN',
      clientId: 'zillow'
    };
  }

  return config;
}

var createActionObject = function createActionObject(base) {
  return {
    BEGIN: base + "_BEGIN",
    SUCCESS: base + "_SUCCESS",
    FAILURE: base + "_FAILURE"
  };
};

var _excluded = ["access_token", "refresh_token", "decodedAccessToken"],
    _excluded2 = ["access_token", "refresh_token", "decodedAccessToken"];
var sessionTimeouts = {
  expiry: null,
  nearingExpiry: null
};
var USER_LOGOUT = 'USER_LOGOUT';
var RESET_DID_USER_INVOKE_LOGOUT = 'RESET_DID_USER_INVOKE_LOGOUT';
var SET_DID_USER_SESSION_EXPIRE = 'SET_DID_USER_SESSION_EXPIRE';
var SET_SHOW_SESSION_NEARING_EXPIRY_NOTIFICATION = 'SET_SHOW_SESSION_NEARING_EXPIRY_NOTIFICATION';
var USE_REFRESH_TOKEN = createActionObject('USE_REFRESH_TOKEN');
var SESSION_LENGTH_MINUTES = 30;
var REFRESH_FAILURE_HTTP_STATUS = 401;

var clearSessionTimeouts = function clearSessionTimeouts() {
  clearTimeout(sessionTimeouts.expiry);
  clearTimeout(sessionTimeouts.nearingExpiry);
};

var logoutHelper = function logoutHelper(didUserSessionExpire) {
  return {
    type: USER_LOGOUT,
    payload: {
      didUserSessionExpire: didUserSessionExpire
    }
  };
};

var logout = function logout() {
  return logoutHelper(false);
};
var forceLogout = function forceLogout() {
  return logoutHelper(true);
};
var setShowSessionNearingExpiryNotification = function setShowSessionNearingExpiryNotification(showSessionNearingExpiryNotification) {
  return {
    type: SET_SHOW_SESSION_NEARING_EXPIRY_NOTIFICATION,
    payload: {
      showSessionNearingExpiryNotification: showSessionNearingExpiryNotification
    }
  };
};
var resetDidUserInvokeLogout = function resetDidUserInvokeLogout() {
  return {
    type: RESET_DID_USER_INVOKE_LOGOUT
  };
};
var setDidUserSessionExpire = function setDidUserSessionExpire(didUserSessionExpire) {
  return {
    type: SET_DID_USER_SESSION_EXPIRE,
    payload: {
      didUserSessionExpire: didUserSessionExpire
    }
  };
};
var resetDidUserSessionExpire = function resetDidUserSessionExpire() {
  return setDidUserSessionExpire(false);
};

var setSessionTimeouts = function setSessionTimeouts() {
  return function (dispatch, getState) {
    var currState = getState();
    var isAuthenticated = isAuthenticatedSelector(currState); //eslint-disable-next-line no-use-before-define

    sessionTimeoutsHandler.clearSessionTimeouts();

    if (isAuthenticated) {
      var timeToSessionExpiry = timeToSessionExpirySelector(currState);
      var timeToSessionNearingExpiry = timeToSessionNearingExpirySelector(currState);

      if (timeToSessionExpiry) {
        sessionTimeouts.expiry = setTimeout(function () {
          dispatch(forceLogout());
        }, timeToSessionExpiry);
      }

      if (timeToSessionNearingExpiry) {
        sessionTimeouts.nearingExpiry = setTimeout(function () {
          dispatch(setShowSessionNearingExpiryNotification(true));
        }, timeToSessionNearingExpiry);
      }
    }
  };
};

var sessionTimeoutsHandler = {
  setSessionTimeouts: setSessionTimeouts,
  clearSessionTimeouts: clearSessionTimeouts
};

var generateAuthRedirectUrl = function generateAuthRedirectUrl(params) {
  var _zhlConfig = getConfig(),
      clientId = _zhlConfig.clientId,
      auth = _zhlConfig.serverURLs.auth;

  var queryParams = querystring.stringify(Object.assign({
    identityProvider: clientId
  }, params));
  return auth + "/v1/authorization/redirect?" + queryParams;
};

var initiateLogin = function initiateLogin(redirectURL, params) {
  if (params === void 0) {
    params = {};
  }

  window.location.assign(generateAuthRedirectUrl(Object.assign({
    redirectURL: redirectURL
  }, params)));
};
var initiateLoginThunk = function initiateLoginThunk() {
  return function (_, getState) {
    var state = getState();
    var redirectURL = redirectURLSelector(state);
    var subMasked = accessTokenSubMaskedSelector(state);
    var additionalQueryParams = subMasked ? {
      subMasked: subMasked
    } : {};
    return initiateLogin(redirectURL, additionalQueryParams);
  };
};
var INITIATE_LOGIN_IN_NEW_TAB = 'INITIATE_LOGIN_IN_NEW_TAB';
var initiateLoginInNewTab = function initiateLoginInNewTab() {
  return function (dispatch, getState) {
    var storageListener = function storageListener(_ref) {
      var newValue = _ref.newValue,
          key = _ref.key;

      if (key === 'auth') {
        dispatch({
          type: INITIATE_LOGIN_IN_NEW_TAB,
          payload: JSON.parse(newValue)
        });
        dispatch(sessionTimeoutsHandler.setSessionTimeouts());
        window.removeEventListener('storage', storageListener);
      }
    };

    var state = getState();
    var redirectURL = redirectURLForTabLoginSelector(state);
    var subMasked = accessTokenSubMaskedSelector(state);
    var additionalQueryParams = subMasked ? {
      subMasked: subMasked
    } : {};
    window.open(generateAuthRedirectUrl(Object.assign({
      redirectURL: redirectURL
    }, additionalQueryParams)), '_blank');
    window.addEventListener('storage', storageListener);
  };
};
var useRefreshToken = function useRefreshToken(handleLogin) {
  return function (dispatch, getState) {
    dispatch({
      type: USE_REFRESH_TOKEN.BEGIN
    });
    var refreshToken = refreshTokenSelector(getState());
    return fetch(getConfig().serverURLs.auth + "/v1/token/refresh", {
      method: 'POST',
      body: {
        token: refreshToken
      }
    }).then(function (resp) {
      return resp.json().then(function (json) {
        return Object.assign({
          ok: resp.ok,
          statusCode: resp.status
        }, json);
      });
    }).catch(function () {
      return {
        ok: false,
        statusCode: REFRESH_FAILURE_HTTP_STATUS
      };
    }).then(function (authRefreshResp) {
      var ok = authRefreshResp.ok,
          token = authRefreshResp.token,
          statusCode = authRefreshResp.statusCode;

      if (ok) {
        dispatch({
          type: USE_REFRESH_TOKEN.SUCCESS,
          payload: token
        });
        dispatch(sessionTimeoutsHandler.setSessionTimeouts());
        return {
          token: token
        };
      } else {
        dispatch({
          type: USE_REFRESH_TOKEN.FAILURE
        });
        sessionTimeoutsHandler.clearSessionTimeouts();

        if (!handleLogin && statusCode === REFRESH_FAILURE_HTTP_STATUS) {
          dispatch(setDidUserSessionExpire(true));
        }

        return Promise.reject();
      }
    });
  };
};
var FETCH_UNAUTHENTICATED_TOKEN = createActionObject('FETCH_UNAUTHENTICATED_TOKEN');
var acquireUnauthenticatedToken = function acquireUnauthenticatedToken() {
  return function (dispatch) {
    return new Promise(function ($return, $error) {
      var authServiceURLBase, response, json;
      dispatch({
        type: FETCH_UNAUTHENTICATED_TOKEN.BEGIN
      });

      var $Try_1_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_1_Catch = function (err) {
        try {
          dispatch({
            type: FETCH_UNAUTHENTICATED_TOKEN.FAILURE
          });
          throw err;
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        authServiceURLBase = getConfig().serverURLs.auth;
        return Promise.resolve(fetch(authServiceURLBase + "/v1/token/unauthenticated-session", {
          method: 'POST'
        })).then(function ($await_2) {
          try {
            response = $await_2;
            return Promise.resolve(response.json()).then(function ($await_3) {
              try {
                json = $await_3;
                dispatch({
                  type: FETCH_UNAUTHENTICATED_TOKEN.SUCCESS,
                  payload: json
                });
                return $Try_1_Post();
              } catch ($boundEx) {
                return $Try_1_Catch($boundEx);
              }
            }, $Try_1_Catch);
          } catch ($boundEx) {
            return $Try_1_Catch($boundEx);
          }
        }, $Try_1_Catch);
      } catch (err) {
        $Try_1_Catch(err);
      }
    });
  };
};
var getHashString = function getHashString() {
  return typeof window !== 'undefined' && window.location.hash ? window.location.hash.substr(1) : null;
};
var APPLICABLE_HASH_PARAMS = ['access_token', 'refresh_token', 'access_token_expires_in'];
var getHashApplicableParams = function getHashApplicableParams() {
  var hash = getHashString();
  var hashParams = hash ? querystring.parse(hash) : null;

  if (hashParams) {
    Object.keys(hashParams).forEach(function (key) {
      if (APPLICABLE_HASH_PARAMS.indexOf(key) === -1) {
        delete hashParams[key];
      } else if (key === 'access_token') {
        hashParams.decodedAccessToken = jwtDecode(hashParams[key]);
        hashParams.gaTrackingGUID = v4_1();
      }
    });
  }

  return hashParams;
};
var authParamsOnLoad = getHashApplicableParams();
var DEFAULT_STATE = Object.assign({
  rehydrated: false,
  didUserInvokeLogout: false,
  didUserSessionExpire: false,
  showSessionNearingExpiryNotification: false
}, authParamsOnLoad);
var auth$1 = (function (state, _ref2) {
  if (state === void 0) {
    state = DEFAULT_STATE;
  }

  var type = _ref2.type,
      _ref2$payload = _ref2.payload,
      payload = _ref2$payload === void 0 ? {} : _ref2$payload,
      key = _ref2.key;

  if (type === REHYDRATE && key === 'auth') {
    var didUserInvokeLogout = payload.didUserInvokeLogout,
        didUserSessionExpire = payload.didUserSessionExpire;
    return Object.assign({}, payload, state, {
      rehydrated: true,
      didUserInvokeLogout: didUserInvokeLogout,
      didUserSessionExpire: didUserSessionExpire
    });
  }

  switch (type) {
    case INITIATE_LOGIN_IN_NEW_TAB:
      return Object.assign({}, state, payload);

    case USE_REFRESH_TOKEN.SUCCESS:
      return Object.assign({}, state, {
        access_token: payload
      });

    case FETCH_UNAUTHENTICATED_TOKEN.SUCCESS:
      return Object.assign({}, state, {
        access_token: payload.token,
        access_token_expires_in: payload.expires_in,
        decodedAccessToken: jwtDecode(payload.token)
      });

    case USER_LOGOUT:
      var access_token = DEFAULT_STATE.access_token,
          refresh_token = DEFAULT_STATE.refresh_token,
          decodedAccessToken = DEFAULT_STATE.decodedAccessToken,
          rest = _objectWithoutProperties(DEFAULT_STATE, _excluded);

      sessionTimeoutsHandler.clearSessionTimeouts();
      return Object.assign({}, rest, {
        didUserInvokeLogout: !payload.didUserSessionExpire,
        didUserSessionExpire: payload.didUserSessionExpire,
        rehydrated: true
      });

    case USE_REFRESH_TOKEN.FAILURE:
      var _access_token = DEFAULT_STATE.access_token,
          _refresh_token = DEFAULT_STATE.refresh_token,
          _decodedAccessToken = DEFAULT_STATE.decodedAccessToken,
          _rest = _objectWithoutProperties(DEFAULT_STATE, _excluded2);

      return Object.assign({}, _rest, {
        rehydrated: true
      });

    case RESET_DID_USER_INVOKE_LOGOUT:
      return Object.assign({}, state, {
        didUserInvokeLogout: false
      });

    case SET_DID_USER_SESSION_EXPIRE:
      return Object.assign({}, state, {
        didUserSessionExpire: !!payload.didUserSessionExpire
      });

    case SET_SHOW_SESSION_NEARING_EXPIRY_NOTIFICATION:
      return Object.assign({}, state, {
        showSessionNearingExpiryNotification: payload.showSessionNearingExpiryNotification
      });

    default:
      return state;
  }
});

var auth$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  sessionTimeouts: sessionTimeouts,
  USER_LOGOUT: USER_LOGOUT,
  RESET_DID_USER_INVOKE_LOGOUT: RESET_DID_USER_INVOKE_LOGOUT,
  SET_DID_USER_SESSION_EXPIRE: SET_DID_USER_SESSION_EXPIRE,
  SET_SHOW_SESSION_NEARING_EXPIRY_NOTIFICATION: SET_SHOW_SESSION_NEARING_EXPIRY_NOTIFICATION,
  USE_REFRESH_TOKEN: USE_REFRESH_TOKEN,
  SESSION_LENGTH_MINUTES: SESSION_LENGTH_MINUTES,
  logout: logout,
  forceLogout: forceLogout,
  setShowSessionNearingExpiryNotification: setShowSessionNearingExpiryNotification,
  resetDidUserInvokeLogout: resetDidUserInvokeLogout,
  setDidUserSessionExpire: setDidUserSessionExpire,
  resetDidUserSessionExpire: resetDidUserSessionExpire,
  sessionTimeoutsHandler: sessionTimeoutsHandler,
  initiateLogin: initiateLogin,
  initiateLoginThunk: initiateLoginThunk,
  INITIATE_LOGIN_IN_NEW_TAB: INITIATE_LOGIN_IN_NEW_TAB,
  initiateLoginInNewTab: initiateLoginInNewTab,
  useRefreshToken: useRefreshToken,
  acquireUnauthenticatedToken: acquireUnauthenticatedToken,
  getHashString: getHashString,
  APPLICABLE_HASH_PARAMS: APPLICABLE_HASH_PARAMS,
  getHashApplicableParams: getHashApplicableParams,
  DEFAULT_STATE: DEFAULT_STATE,
  'default': auth$1
});

var _excluded$1 = ["handleLogin", "metadata"];
var API = {};
var config = getConfig();
var NETWORK_ERROR_TYPE = 'NetworkError';
var DEFAULT_TIMEOUT = 10000;
var MAX_URL_LENGTH = 2047; // see http://stackoverflow.com/questions/417142/what-is-the-maximum-length-of-a-url-in-different-browsers

/*
 * Construct query parameters for an API server.
 *
 * ###### Numbers, Strings, Booleans
 *
 *     apiServerQueryParams({ myNumber: 1, myString: 'abc', myBoolean: true }); // "?myNumber=1&myString=abc&myBoolean=true"
 *
 * ###### Arrays
 *
 *     apiServerQueryParams({ myArray: [ 'a', 'b', 'c' ] }); // "?myArray=a&myArray=b&myArray=c"
 * ###### Empty
 *
 *     apiServerQueryParams({ myObject: {}, myArray: [], myString: '' }); // "?myObject=&myArray=&myString="
 */

function apiServerQueryParams(params) {
  if (params === void 0) {
    params = {};
  }

  var arr = [];
  Object.keys(params).forEach(function (key) {
    var value = params[key];

    var type = _typeof(value);

    if (type === 'number' || type === 'string' || type === 'boolean') {
      arr.push(encodeURIComponent(key) + "=" + encodeURIComponent(value));
    } else if (type === 'object' && value && value.length > 0) {
      value.forEach(function (item) {
        return arr.push(encodeURIComponent(key) + "=" + encodeURIComponent(item));
      });
    } else if (type !== 'undefined') {
      arr.push(encodeURIComponent(key) + "=");
    }
  });
  return arr.length ? "?" + arr.join('&') : '';
}

var SUPPORTED_HTTP_METHODS = ['GET', 'POST', 'DELETE', 'PUT'];

var APIService = function APIService(baseURL) {
  var _this = this;

  this.url = function (action, params) {
    return _this.baseURL + "/" + action + apiServerQueryParams(params);
  };

  this.promise = function (givenParams) {
    return new Promise(function (resolve, reject) {
      var timeout = givenParams.timeout || DEFAULT_TIMEOUT;

      function handleResponse(response, definitelyFailed) {
        // error on a valid response is legacy MAX behavior and will not exist when we no longer call MAX
        if (response && !response.error && !definitelyFailed) {
          resolve(response);
        } else {
          reject(response);
        }
      }

      var go = function go(params) {
        var url;
        var convertToPost = false; // Build the URL and, if doing a POST, the headers and payload as well.

        if (givenParams.method === 'GET') {
          url = _this.url(params.action, params.input); // If the GET URL is really long, switch to POST to avoid issues
          // with browser or server URL length limits.

          convertToPost = url.length > MAX_URL_LENGTH;
        }

        var fetchOpts = {
          method: convertToPost ? 'POST' : givenParams.method,
          timeout: timeout,
          headers: givenParams.headers || {}
        };

        if (givenParams.method !== 'GET') {
          // "Simple" CORS requires POST Content-Type be one of:
          //   - application/x-www-form-urlencoded
          //   - multipart/form-data
          //   - text/plain
          // We use text/plain as it is the "least wrong" of the three.
          // Our API servers ignore the Content-Type header, so this
          // doesn't cause any issues. When our request body is FormData
          // we need to let the browser set this header with the
          // appropriate boundary directive
          if (!(params.input instanceof FormData) && !fetchOpts.headers['Content-Type']) {
            fetchOpts.headers['Content-Type'] = 'text/plain';
          }

          var jsonEncoder = params.jsonEncoder || function (obj) {
            return obj;
          };

          fetchOpts.body = jsonEncoder(params.input || {});
          url = _this.url(params.action, Object.assign({}, params.queryParams, {
            partnerId: params.requiresPartnerId ? config.partnerId : undefined
          }));
        }

        if (params.accessToken) {
          fetchOpts.headers = Object.assign({}, fetchOpts.headers, {
            Authorization: "Bearer " + params.accessToken
          });
        }

        return fetch(url, fetchOpts).then(function (res) {
          return new Promise(function ($return, $error) {
            var text, json;
            return Promise.resolve(res.text()).then(function ($await_1) {
              try {
                text = $await_1;
                json = text === '' ? {} : JSON.parse(text);
                // Sometimes an API doesn't return a response at all (null value), but still responds with a success code
                return $return(handleResponse(Object.assign(json !== null && json !== void 0 ? json : {}, {
                  statusCode: res.status
                }), !res.ok));
              } catch ($boundEx) {
                return $error($boundEx);
              }
            }, $error);
          });
        }).catch(function (error) {
          return handleResponse(error instanceof TypeError ? {
            type: NETWORK_ERROR_TYPE
          } : null, true);
        });
      };

      return go(givenParams);
    });
  };

  this.baseURL = baseURL;
  SUPPORTED_HTTP_METHODS.forEach(function (method) {
    _this[method] = function (params) {
      return _this.promise(Object.assign({}, params, {
        method: method
      }));
    };

    _this[method].thunk = function (request, actionObject) {
      return function (dispatch, getState) {
        var _request$handleLogin = request.handleLogin,
            handleLogin = _request$handleLogin === void 0 ? true : _request$handleLogin,
            metadata = request.metadata,
            requestConfig = _objectWithoutProperties(request, _excluded$1);

        dispatch({
          type: actionObject.BEGIN,
          metadata: metadata
        });

        if (requestConfig.authenticated) {
          var _requestConfig$isUser;

          var state = getState();
          var accessToken = accessTokenSelector(state);

          if (!accessToken && handleLogin) {
            dispatch(initiateLoginThunk());
          }

          requestConfig.accessToken = accessToken;
          requestConfig.refreshToken = refreshTokenSelector(state);
          var isUserInitiatedAction = (_requestConfig$isUser = requestConfig.isUserInitiatedAction) !== null && _requestConfig$isUser !== void 0 ? _requestConfig$isUser : true;

          if (isUserInitiatedAction) {
            dispatch(sessionTimeoutsHandler.setSessionTimeouts());
          }
        }

        return _this[method](requestConfig).then(function (response) {
          return response;
        }, function (failure) {
          if (failure && failure.statusCode === 401 && requestConfig.authenticated && requestConfig.refreshToken) {
            // Attempt to get a new access token from the refresh token, then try the request again.
            return dispatch(useRefreshToken(handleLogin)).then(function (refreshResponse) {
              return _this[method](Object.assign({}, requestConfig, {
                accessToken: refreshResponse.token
              }));
            }, function () {
              return Promise.reject(failure);
            });
          }

          return Promise.reject(failure);
        }).then(function (response) {
          delete response.statusCode; // clean up any 200s

          dispatch({
            type: actionObject.SUCCESS,
            payload: response,
            request: requestConfig.input,
            metadata: metadata
          });
          return response;
        }, function (refreshError) {
          // We failed to refresh the access token or the second MAX call failed. Perhaps the user logged out on Zillow.
          dispatch({
            type: actionObject.FAILURE,
            payload: refreshError,
            metadata: metadata
          }); // Note, we'll want to ensure that the API failures are consistent between MAX and ZHL

          if (handleLogin && (refreshError === null || refreshError === void 0 ? void 0 : refreshError.statusCode) === 401) {
            dispatch(initiateLoginThunk());
          }

          return Promise.reject(refreshError);
        });
      };
    };
  });
};

if (config) {
  var KEY = 'zm-userSessionId';
  var userSessionId;

  if (isSessionStorageSupported()) {
    userSessionId = window.sessionStorage.getItem(KEY) || v4_1();
    window.sessionStorage.setItem(KEY, userSessionId);
  } else {
    userSessionId = v4_1();
  }

  API.userSessionId = userSessionId;
  Object.keys(config.serverURLs).forEach(function (key) {
    API[key] = new APIService(config.serverURLs[key]);
  });
  API.config = config;
}

var DEFAULT_LOP_API_FAILURE_CTA = 'Please try again later. If this continues to persist please contact Application Support.';
var DEFAULT_LOP_API_FAILURE_ERROR_MESSAGE = "Something went wrong. " + DEFAULT_LOP_API_FAILURE_CTA;

var createStubPersistor = function createStubPersistor() {
  var reducer = function reducer(state) {
    if (state === void 0) {
      state = {
        bootstrapped: true
      };
    }

    return state;
  };

  return Object.assign({}, createStore(reducer), {
    purge: function purge() {
      return Promise.resolve([]);
    },
    flush: function flush() {
      return Promise.resolve([]);
    },
    pause: function pause() {},
    persist: function persist() {}
  });
};

var standardReduxPersistStore = (function (store, options, callback) {
  if (!isLocalStorageSupported()) {
    callback === null || callback === void 0 ? void 0 : callback();
    return createStubPersistor();
  }

  return persistStore(store, options, callback);
});

var STATUS = {
  LOADING: 'loading',
  ERROR: 'error',
  SUCCESS: 'success'
};
var SORT_TYPE = {
  ASCENDING: 'asc',
  DESCENDING: 'desc'
};
var CREDIT_BUREAUS = ['Equifax', 'TransUnion', 'Experian'];
var CLEAR_STATUS = 'CLEAR_STATUS';

var createPropsSelector = function createPropsSelector(callback) {
  return defaultMemoize(function (_, props) {
    return callback(props);
  });
};
var getAccessibleRoutesSelector = function getAccessibleRoutesSelector(routes) {
  return createSelector([isAuthenticatedSelector, userRolesSelector], function (isAuthenticated, userRoles) {
    return isAuthenticated && userRoles ? Object.keys(routes).reduce(function (prev, curr) {
      var hasPermissionToCurrent = routes[curr].reduce(function (hasAccess, currentRole) {
        return hasAccess || userRoles.indexOf(currentRole) !== -1;
      }, false);
      return hasPermissionToCurrent ? prev.concat(curr) : prev;
    }, []) : [];
  });
};
/*
    Curry a selector so that evaluation can be deferred until `context` is available.
    Useful when `context` is derived from the value returned by another selector.
    Usage:
        const getValueSelector = bindSelectorToState(valueSelector);
        const mySelector = createSelector(
            [getValueSelector],
            getValue => getValue(context)
        );
*/

var bindSelectorToState = function bindSelectorToState(selector) {
  return function (state) {
    return function (context) {
      return selector(state, context);
    };
  };
};
/*
    Wrap a selector that returns an array as a selector that returns
    an object, keyed by the `id` property of the values in that array.
    Usage:
        const employmentsByIDSelector = byID(employmentsItemsSelector);
*/

var byID = function byID(itemsSelector) {
  return createSelector([itemsSelector], function (items) {
    return (items || []).reduce(function (acc, next) {
      var _Object$assign;

      return Object.assign({}, acc, (_Object$assign = {}, _Object$assign[next.id] = next, _Object$assign));
    }, {});
  });
};

var calculatorsSelector = function calculatorsSelector(_ref) {
  var calculators = _ref.calculators;
  return calculators || null;
};
var refinanceSelector = createSelector([calculatorsSelector], function (calculators) {
  return calculators ? calculators.refinance : null;
});
var refinanceResultsSelector = createSelector([refinanceSelector], function (refinance) {
  return refinance ? refinance.calculatedRefinanceValues : null;
});
var refinanceCalculatorFieldsSelector = createSelector([refinanceSelector], function (refinance) {
  return refinance ? refinance.fields : null;
});

var calculators = /*#__PURE__*/Object.freeze({
  __proto__: null,
  calculatorsSelector: calculatorsSelector,
  refinanceSelector: refinanceSelector,
  refinanceResultsSelector: refinanceResultsSelector,
  refinanceCalculatorFieldsSelector: refinanceCalculatorFieldsSelector
});

var _PMI;

/**
 * An enumeration of loan types. Available loan types are `FIXED` and `ARM`.
 *
 * @property LOAN_TYPE
 * @static
 */
var LOAN_TYPE = {
  FIXED: 'fixed',
  ARM: 'arm'
};
/**
 * A table of LTV ratios to annual PMI rates by loan type. For example, the
 * PMI rate for a fixed loan with an 90% LTV would be:
 *
 *     var pmiRate = PMI[LOAN_TYPE.FIXED][90];
 *
 * @property PMI
 * @static
 */

var PMI = (_PMI = {}, _PMI[LOAN_TYPE.FIXED] = {
  80: 0,
  85: 0.0044,
  90: 0.0059,
  95: 0.0076,
  100: 0.0098
}, _PMI[LOAN_TYPE.ARM] = {
  80: 0,
  85: 0.0068,
  90: 0.0081,
  95: 0.0101,
  100: 0.0117
}, _PMI);
/**
 * Get the PMI bucket for the `ZMM.PMI` table.
 *
 * @method getPMIBucket
 * @static
 * @param ltv The loan-to-value ratio as a percentage, i.e. 95
 */

function getPMIBucket(ltv) {
  if (ltv > 95) {
    return 100;
  } else if (ltv > 90) {
    return 95;
  } else if (ltv > 85) {
    return 90;
  } else if (ltv > 80) {
    return 85;
  } else {
    return 80;
  }
}
/**
 * Calculates and returns the estimated monthly loan payment for the given
 * loan amount, percentage interest rate, and loan term in months.  The
 * equation used to calculate the monthly payment is:
 *
 *                  (LOAN_AMOUNT) * MONTHLY_RATE * (1 + MONTHLY_RATE)^TERM
 *     MONTHLY_PI = ------------------------------------------------------
 *                              (1 + MONTHLY_RATE)^TERM - 1
 *
 * @method monthlyPI
 * @static
 * @param {Number} amount The total loan amount
 * @param {Number} rate The interest rate of the loan as a percent
 * @param {Number} term The loan term in months
 * @param {boolean} [noRounding=false] Do not round the result (the default
 *     behavior is to round to a dollar value with two decimal places)
 * @return {Number} the monthly principal and interest payment
 */

function monthlyPI(amount, rate, term, noRounding) {
  var monthlyRate, exponentiationTerm, monthlyPIAmount; // No interest, split amount by the number of terms

  if (rate === 0) {
    return $$(amount / term);
  } // Over 12 months converted to decimal


  monthlyRate = rate / 1200;
  exponentiationTerm = Math.pow(1 + monthlyRate, term);
  monthlyPIAmount = amount * monthlyRate * exponentiationTerm / (exponentiationTerm - 1); // Return rounded dollar value

  if (!noRounding) {
    return $$(monthlyPIAmount);
  }

  return monthlyPIAmount;
}
function loanAmount(monthlyPIAmount, rate, term) {
  if (rate === 0) {
    return 0; // This is impossible
  }

  var monthlyRate = rate / 1200;
  var exponentiationTerm = Math.pow(1 + monthlyRate, term);
  return monthlyPIAmount / (monthlyRate * exponentiationTerm / (exponentiationTerm - 1));
}
/**
 * Calculates and returns the estimated monthly property taxes given
 * the property value and tax rate.
 * The equation used to calculate the monthly property tax is:
 *
 *                             (PROPERTY_VALUE) * TAX_RATE
 *    MONTHLY_PROPERTY_TAXES = ----------------------------
 *                                          12
 *
 * @method monthlyPropertyTaxes
 * @static
 * @param {Number} property value
 * @param {Number} taxRate
 * @return {Number} the monthly property tax
 */

function monthlyPropertyTaxes(propertyValue, taxRate) {
  return propertyValue * taxRate / 12;
}
/**
 * Calculates and returns the estimated monthly PMI given the
 * loan amount, property value, and whether the loan ARM.
 * The equation used to calculate the monthly PMI is:
 *
 *                   (PMI) * LOAN_AMOUNT
 *     MONTHLY_PMI = --------------------
 *                           12
 *
 * @method monthlyPMI
 * @static
 * @param {Number} loanAmount
 * @param {Number} propertyValue
 * @param {boolean} isARM
 * @return {Number} the monthly PMI
 */

function monthlyPMI(loanAmountValue, propertyValue, isARM) {
  var ltv = loanAmountValue / propertyValue * 100,
      pmiRate = PMI[isARM ? LOAN_TYPE.ARM : LOAN_TYPE.FIXED][getPMIBucket(ltv)];
  return pmiRate * loanAmountValue / 12;
}
/**
 * Calculate the mortgage amount based on monthly principal and interest
 * payment.
 *
 * - `n` Number of Payments
 * - `i` Monthly Interest Rate
 * - `pi` Monthly Principal and Interest
 *
 * `mortgageAmount = pi * [(1 + i)^n – 1] / [i * (1 + i)^n]`
 *
 * @method amountByPayment
 * @static
 * @param {number} monthlyPi The monthly principal and interest payment
 * @param {number} rate The interest rate of the loan as a percent
 * @param {number} term The loan term in months
 * @param {boolean} [noRounding=false] Do not round the result (the default
 *     behavior is to round to a dollar value with two decimal places)
 * @return {number} The total loan amount
 */

function amountByPayment(monthlyPIAmount, rate, term, noRounding) {
  var monthlyRate, exponentiationTerm, amount;

  if (rate > 0) {
    // Over 12 months converted to decimal
    monthlyRate = rate / 1200;
    exponentiationTerm = Math.pow(1 + monthlyRate, term);
    amount = monthlyPIAmount * (exponentiationTerm - 1) / (monthlyRate * exponentiationTerm);
  } else {
    // No interest, full payment goes towards amount
    amount = term * monthlyPIAmount;
  } // Return rounded dollar value


  if (!noRounding) {
    return $$(amount);
  }

  return amount;
}
/**
 * @method amountByLTV
 * @static
 * @param {number} downPayment The down payment amount.
 * @param {number} ltv Loan to value as a percent, i.e. 80
 */

function amountByLTV(downPayment, ltv) {
  return $$(downPayment * (1 / (1 - ltv / 100)));
}
/**
 * Get the amortization schedule for the given loan parameters.
 *
 * @method amortization
 * @static
 * @param {Number} amount The total loan amount
 * @param {Number} rate The interest rate of the loan as a percent
 * @param {Number} term The loan term in months
 * @return {Object} The amortization schedule
 */

function amortization(amount, rate, term) {
  var monthlyRate = rate / 1200,
      // Over 12 months converted to decimal
  monthlyPIAmount = monthlyPI(amount, rate, term),
      remainingBalance = amount,
      totalInterest = 0,
      amortizationTable = [],
      i,
      monthlyInterest,
      monthlyPrincipal,
      twelveMonthsInterest = 0,
      twelveMonthsPrincipal = 0;

  for (i = 0; i < term; i += 1) {
    // Interest
    monthlyInterest = $$(remainingBalance * monthlyRate);
    totalInterest = $$(totalInterest + monthlyInterest); // Principal

    if (i === term - 1) {
      // The last payment makes up for any rouding differences
      monthlyPIAmount = $$(remainingBalance + monthlyInterest);
      monthlyPrincipal = remainingBalance;
      remainingBalance = 0;
    } else {
      monthlyPrincipal = $$(monthlyPIAmount - monthlyInterest);
      remainingBalance = $$(remainingBalance - monthlyPrincipal);
    }

    twelveMonthsInterest += monthlyInterest;
    twelveMonthsPrincipal += monthlyPrincipal;

    if (i - 12 >= 0) {
      twelveMonthsInterest -= amortizationTable[i - 12].monthlyInterest;
      twelveMonthsPrincipal -= amortizationTable[i - 12].monthlyPrincipal;
    }

    amortizationTable.push({
      month: i + 1,
      monthlyInterest: monthlyInterest,
      totalInterest: totalInterest,
      monthlyPrincipal: monthlyPrincipal,
      totalPrincipal: $$(amount - remainingBalance),
      remainingBalance: remainingBalance,
      twelveMonthsInterest: twelveMonthsInterest,
      twelveMonthsPrincipal: twelveMonthsPrincipal
    });
  }

  return amortizationTable;
}
/**
 * Get the number of payments remaining in the loan.
 *
 * @method paymentsRemaining
 * @static
 * @param {Number} originationYear The year the loan originated
 * @param {Number} term The loan term in months
 * @return {Number} The number of payments remaining
 */

function paymentsRemaining(originationYear, term) {
  var today = new Date();
  var yearsPaid = today.getFullYear() - originationYear;
  var monthsPaid = today.getMonth() + 1; // Assume July 1st origination month (unless originating in the same year).

  var paymentsMade = Math.max(yearsPaid * 12 - 6, 0) + monthsPaid;
  var remaining = term - paymentsMade; // Future origination date should have all payments still

  if (yearsPaid < 0) {
    remaining = term;
  } // Can't have negative payments remaining


  if (remaining < 0) {
    remaining = 0;
  }

  return remaining;
}
/**
 * Round to two decimal points. This is useful for fixing floating point
 * precision issues.
 *
 * @method $$
 * @private
 * @param {Number} amount The amount to round
 * @return {Number} The amount rounded to two decimal places
 */

function $$(amount) {
  return +amount.toFixed(2);
}
/**
 * A monthly payment calculator.
 *
 * @method payment
 */

function payment(_ref) {
  var term = _ref.term,
      homePrice = _ref.homePrice,
      rate = _ref.rate,
      downPayment = _ref.downPayment,
      includeHOA = _ref.includeHOA,
      monthlyHOA = _ref.monthlyHOA,
      includeHomeownersInsurance = _ref.includeHomeownersInsurance,
      annualHomeownersInsurance = _ref.annualHomeownersInsurance,
      includePropertyTax = _ref.includePropertyTax,
      propertyTaxAmount = _ref.propertyTaxAmount,
      propertyTaxRate = _ref.propertyTaxRate,
      includePMI = _ref.includePMI,
      includeTaxesInsurance = _ref.includeTaxesInsurance,
      isARM = _ref.isARM,
      dti = _ref.dti;
  var loanAmountValue = homePrice - downPayment;
  var piPayment = monthlyPI(loanAmountValue, rate, term);
  var hoa = includeHOA ? monthlyHOA : 0;
  var homeIns = includeHomeownersInsurance ? annualHomeownersInsurance / 12 : 0;
  var propTax = includePropertyTax ? propertyTaxAmount ? propertyTaxAmount / 12 : monthlyPropertyTaxes(homePrice, propertyTaxRate / 100) : 0;
  var pmi = includePMI && includeTaxesInsurance ? monthlyPMI(loanAmountValue, homePrice, isARM) : 0;
  var monthlyTaxesAndInsurance = propTax + homeIns + pmi + hoa;
  var monthlyPaymentAmount = piPayment + (monthlyTaxesAndInsurance || 0);
  var income = monthlyPaymentAmount / dti / 100 * 12;
  return {
    monthlyPayment: monthlyPaymentAmount || 0,
    monthlyPI: piPayment || 0,
    monthlyPropertyTax: propTax || 0,
    monthlyHomeownersInsurance: homeIns || 0,
    monthlyPMI: homePrice === 0 ? 0 : pmi || 0,
    requiredIncome: income || 0,
    monthlyHOA: hoa || 0,
    amortization: amortization(loanAmountValue, rate, term)
  };
}

function calculateLoanAmount(_ref) {
  var originationYear = _ref.originationYear,
      rate = _ref.rate,
      term = _ref.term,
      currentBalance = _ref.currentBalance;
  var today = new Date();
  var yearsPaid = today.getFullYear() - originationYear;
  var monthsPaid = today.getMonth() + 1;
  var paymentsMade = Math.max(yearsPaid * 12 - 6, 0) + monthsPaid;
  var remainingTerm = term - paymentsMade;
  var monthlyPI$1 = monthlyPI(currentBalance, rate, remainingTerm);
  return loanAmount(monthlyPI$1, rate, term);
}
/**
 * David Cantrell's interest rate estimation formula
 * https://brownmath.com/bsci/loan.htm#Cantrell2
 *
 * @param loanAmount
 * @param term (in months)
 * @param monthlyPayment
 * @param paymentPerYear
 * @returns {Number} estimated interest rate as a percent (0 - 100)
 */

function estimateRate(_ref2) {
  var loanAmount = _ref2.loanAmount,
      term = _ref2.term,
      monthlyPayment = _ref2.monthlyPayment;
  var totalPayments = term;
  var q = Math.log(1 + 1 / totalPayments) / Math.log(2);
  return 100 * 12 * (Math.pow(Math.pow(1 + monthlyPayment / loanAmount, 1 / q) - 1, q) - 1);
}
function estimateRateForDesiredSavings(_ref3) {
  var loanAmount = _ref3.loanAmount,
      term = _ref3.term,
      currentRate = _ref3.currentRate,
      currentBalance = _ref3.currentBalance,
      desiredSavings = _ref3.desiredSavings,
      _ref3$newTerm = _ref3.newTerm,
      newTerm = _ref3$newTerm === void 0 ? 360 : _ref3$newTerm;
  var currentPayment = monthlyPI(loanAmount, currentRate, term);
  var desiredNewPayment = currentPayment - desiredSavings;
  return estimateRate({
    loanAmount: currentBalance,
    term: newTerm,
    monthlyPayment: desiredNewPayment
  });
}
function calculateSavings(_ref4) {
  var loanAmount = _ref4.loanAmount,
      term = _ref4.term,
      currentRate = _ref4.currentRate,
      currentBalance = _ref4.currentBalance,
      _ref4$newTerm = _ref4.newTerm,
      newTerm = _ref4$newTerm === void 0 ? 360 : _ref4$newTerm,
      newRate = _ref4.newRate;
  var currentPayment = monthlyPI(loanAmount, currentRate, term);
  var newPayment = monthlyPI(currentBalance, newRate, newTerm);
  return currentPayment - newPayment;
}
/**
 *  Get the length of the term for a loan based on the monthlyPayment.
 *
 *   FORMULA:
 *              (        MONTHLY_PI                  )
 LOG(      ---------------               )
 *              (MONTHLY_PI - LOAN_AMT * MONTHLY_RATE)
 *   TERM = -------------------------------------------
 *                   LOG(1 + MONTHLY_RATE)
 */

function calculateNewTermFromPayment(loanAmount, rate, monthlyPI) {
  var monthlyRate = rate / 1200;
  var top = Math.log(monthlyPI / (monthlyPI - loanAmount * monthlyRate));
  var bottom = Math.log(1 + monthlyRate);
  var result = top / bottom;
  return Math.round(result);
}
/**
 * Number of payments remaining on a current balance
 *
 * N = −log(1−iA/P) / log(1+i)
 * where i = rate, A = currentBalance, P = monthlyPI
 */

function paymentsRemaining$1(_ref5) {
  var loanAmount = _ref5.loanAmount,
      currentBalance = _ref5.currentBalance,
      rate = _ref5.rate,
      term = _ref5.term;
  var monthlyPI$1 = monthlyPI(loanAmount, rate, term);
  return calculateNewTermFromPayment(currentBalance, rate, monthlyPI$1);
}
var calculateNewTermFromAdditionalPayment = function calculateNewTermFromAdditionalPayment(_ref6) {
  var loanAmount = _ref6.loanAmount,
      currentBalance = _ref6.currentBalance,
      rate = _ref6.rate,
      term = _ref6.term,
      additionalPayment = _ref6.additionalPayment;
  var monthlyPI$1 = monthlyPI(loanAmount, rate, term);
  return calculateNewTermFromPayment(currentBalance, rate, monthlyPI$1 + additionalPayment);
};
var calculateAdditionalPaymentFromNewTerm = function calculateAdditionalPaymentFromNewTerm(_ref7) {
  var loanAmount = _ref7.loanAmount,
      currentBalance = _ref7.currentBalance,
      rate = _ref7.rate,
      term = _ref7.term,
      newTerm = _ref7.newTerm;
  var monthlyPI$1 = monthlyPI(loanAmount, rate, term);
  var newMonthlyPI = monthlyPI(currentBalance, rate, newTerm);
  return newMonthlyPI - monthlyPI$1;
};

function monthDiff(d1, d2) {
  var months;
  months = (d2.getFullYear() - d1.getFullYear()) * 12;
  months -= d1.getMonth() + 1;
  months += d2.getMonth();
  return months <= 0 ? 0 : months;
}

function calculateRemainingBalance(initialLoanAmount, rate, loanTermInMonths, originationYear) {
  var originationYearDate = new Date(originationYear, 7, 1); //middle of the year since we don't know the exact date

  var currentDate = new Date();
  var amortization$1 = amortization(initialLoanAmount, rate, loanTermInMonths);
  var monthsDifference = monthDiff(originationYearDate, currentDate);

  if (monthsDifference <= 0) {
    return initialLoanAmount;
  }

  return amortization$1[monthsDifference - 1].remainingBalance;
}
function monthsUntilTargetRemainingBalance(_ref8) {
  var targetRemainingBalance = _ref8.targetRemainingBalance,
      loanAmount = _ref8.loanAmount,
      rate = _ref8.rate,
      term = _ref8.term;
  var amortization$1 = amortization(loanAmount, rate, term);

  for (var i = 0; i < amortization$1.length; i++) {
    if (amortization$1[i].remainingBalance <= targetRemainingBalance) {
      return amortization$1[i].month;
    }
  }

  return null;
}
/**
 * Calculate the results of refinancing
 *
 * @method affordability
 * inputs
 *  - loanAmount
 *  - rate
 *  - term (in months)
 *  - originationYear OR currentBalance
 *  - rollFees
 *  - fees
 *  - cashOut
 *  - newRate
 *  - newTerm
 *  - newLoanAmount
 */

function refinance (inputs) {
  var currentPayment = monthlyPI(inputs.loanAmount, inputs.rate, inputs.term),
      currentRemaining = inputs.originationYear ? paymentsRemaining(inputs.originationYear, inputs.term) : paymentsRemaining$1({
    loanAmount: inputs.loanAmount,
    currentBalance: inputs.currentBalance,
    rate: inputs.rate,
    term: inputs.term
  }),
      newTotal,
      newPayment,
      newRemaining,
      monthlySavings,
      schedule,
      fees,
      totalSavings,
      i,
      breakeven,
      previousTotal,
      months; // Are fees rolled into the new loan amount?

  if (inputs.rollFees) {
    newTotal = inputs.newLoanAmount + inputs.fees + inputs.cashOut;
    fees = 0;
  } else {
    newTotal = inputs.newLoanAmount + inputs.cashOut;
    fees = inputs.fees;
  } // Calculate new payment based on new calculated loan amount


  newPayment = monthlyPI(newTotal, inputs.newRate, inputs.newTerm); // Calculate the new monthly savings amount

  if (currentRemaining === 0) {
    monthlySavings = $$(-newPayment);
  } else {
    monthlySavings = $$(currentPayment - newPayment);
  } // Calculate the new amortization schedule


  schedule = amortization(newTotal, inputs.newRate, inputs.newTerm); // Get the number of payments for the new loan

  newRemaining = schedule.length; // Savings initialized at the price to refinance

  totalSavings = -fees;
  previousTotal = totalSavings; // The number of months in the savings schedule may be larger than
  // the amortization table if you reduce the term of your loan

  months = Math.max(newRemaining, currentRemaining); // Update amortization schedule with savings information

  for (i = 0; i < months; i += 1) {
    // Make sure there is an object for this month (there won't be
    // an object for months greater than the amortization schedule).
    if (!schedule[i]) {
      schedule[i] = {
        month: i + 1
      };
    } // Caculate total savings


    if (i < currentRemaining && i < newRemaining) {
      // While both your previous and current loan are active,
      // your savings are the difference of the monthly payments
      totalSavings = $$(totalSavings + monthlySavings);
    } else if (i < currentRemaining && i >= newRemaining) {
      // Your new loan is paid off, you save the loan payment you
      // would have been paying on your own loan
      totalSavings = $$(totalSavings + currentPayment);
    } else {
      // Your old loan is paid off so your losing money with each
      // new payment
      totalSavings = $$(totalSavings - newPayment);
    }

    schedule[i].totalSavings = totalSavings; // Did you breakeven this term?

    if (previousTotal <= 0 && totalSavings >= 0) {
      schedule[i].breakEven = true;
      breakeven = i + 1;
    } // Did we go negative this term?


    if (previousTotal >= 0 && totalSavings < 0) {
      schedule[i].breakNegative = true;
    } // Would this have been the last payment on previous loan


    if (i === currentRemaining - 1) {
      schedule[i].currentLoanComplete = true;
    } // You would have still been making payments on previous loan


    if (i === newRemaining - 1) {
      schedule[i].newLoanComplete = true;
    } // Update previous


    previousTotal = totalSavings;
  }

  return {
    lifetimeSavings: totalSavings,
    breakEven: breakeven,
    currentPayment: currentPayment,
    newPayment: newPayment,
    newTotal: newTotal,
    monthlySavings: monthlySavings,
    savingsSchedule: schedule
  };
}

var _refinance = /*#__PURE__*/Object.freeze({
    calculateLoanAmount: calculateLoanAmount,
    estimateRate: estimateRate,
    estimateRateForDesiredSavings: estimateRateForDesiredSavings,
    calculateSavings: calculateSavings,
    calculateNewTermFromPayment: calculateNewTermFromPayment,
    paymentsRemaining: paymentsRemaining$1,
    calculateNewTermFromAdditionalPayment: calculateNewTermFromAdditionalPayment,
    calculateAdditionalPaymentFromNewTerm: calculateAdditionalPaymentFromNewTerm,
    calculateRemainingBalance: calculateRemainingBalance,
    monthsUntilTargetRemainingBalance: monthsUntilTargetRemainingBalance,
    default: refinance
});

/**
 * Get the loan to value (LTV) of the total.
 *
 * @method _ltv
 * @private
 * @param {number} total Total loan amount
 * @param {number} downPayment
 * @return The LTV as a percent, i.e. 90 for 90%
 */

function _ltv(total, downPayment) {
  return (total - downPayment) / total * 100;
}
/**
 * Helper for getting the annual property tax. Annual taxes can be supplied by
 * rate or by amount.
 *
 * @method _annualPropertyTax
 * @private
 * @param {object} inputs Affordability inputs
 * @param {number} total Total loan amount
 * @return {number} The annual property tax amount.
 */


function _annualPropertyTax(inputs, total) {
  if (!inputs.includePropertyTax) {
    return 0;
  } else if (_isNumber2(inputs.propertyTaxAmount)) {
    return inputs.propertyTaxAmount;
  } else {
    return total * inputs.propertyTaxRate / 100;
  }
}
/**
 * Calculate the affordability including an additional percentage rate.
 * The additional rate could be for taxes and/or pmi.
 *
 * @method _affordabilityWith
 * @private
 * @param {number} affordability The initial affordability calculated
 *     without PMI or taxes
 * @param {number} withRate The monthly percentage rate as a decimal,
 *     i.e. 0.02 for 2%
 */


function _affordabilityWith(affordability, withRate, rate, term) {
  var monthlyRate = rate / 1200,
      exponentiationTerm = Math.pow(1 + monthlyRate, term);
  return affordability / (1 + withRate * (exponentiationTerm - 1) / (monthlyRate * exponentiationTerm));
}
/**
 * The inverse of _affordabilityWith. Used to calculate an initial total
 * without taxes or pmi included.
 *
 * @method _affordabilityWithout
 * @private
 * @param {number} affordabilityWith Affordability calculated by
 *     _affordabilityWith
 * @param {number} withRate The monthly percentage rate to back out,
 *     i.e. 0.02 for 2%
 */


function _affordabilityWithout(affordabilityWith, withRate, rate, term) {
  var monthlyRate = rate / 1200,
      exponentiationTerm = Math.pow(1 + monthlyRate, term);
  return affordabilityWith * (1 + withRate * (exponentiationTerm - 1) / (monthlyRate * exponentiationTerm));
}
/**
 * Get default inputs for affordability calculations. Optionally pass in an
 * object of default overrides.
 *
 * @method affordabilityDefaults
 * @param {Object} overrides
 */


function affordabilityDefaults(overrides) {
  return _merge2({
    // Gross annual income.
    annualIncome: 70000,
    // If `customPayment` is true, then use this monthly payment when
    // calculating affordability by payment.
    monthlyPayment: 1850,
    // When calculating affordability by payment, use the specified
    // `monthlyPayment` value rather than calculating a monthly payment
    // from `annualIncome` and `dti`.
    customPayment: false,
    // The total monthly debts. Include credit card payments, car loan
    // payments, student loan payments, child support payments, alimony
    // payments, and legal judgments. Don't include PITI payments.
    monthlyDebts: 0,
    // The down payment for the loan.
    downPayment: 20000,
    // The interest rate for the loan as a percentage, i.e. 3.5.
    rate: 3.875,
    // The term of the loan in months. For example, the term for a 30
    // year fixed would be 360.
    term: 360,
    // The annual property taxes as a dollar amount. If set, this takes
    // precedence over propertyTaxRate.
    propertyTaxAmount: undefined,
    // The annual property tax rate as a percentage, i.e. 1.5.
    propertyTaxRate: 0,
    // Include property taxes in the calculation.
    includePropertyTax: false,
    // Include private mortgage insurance (PMI) in the calculation.
    includePMI: true,
    // The total annual homeowners insurance premium. The second "I" in
    // "PITI". Don't include hazard insurance or mortgage insurance.
    annualHomeownersInsurance: 0,
    // Include homeowners insurance in the calculation?
    includeHomeownersInsurance: true,
    // The monthly homeowners association dues. This value should be
    // included with the other PITI values.
    monthlyHOA: 0,
    // Include homeowners association dues in calculation?
    includeHOA: true,
    // The loan-to-value ratio to use when calculating the affordability
    // by LTV.
    ltv: 80,
    // The loan-to-value ratio to use when calculating the affordability
    // by LTV for jumbo loans.
    ltvJumbo: 80,
    // The dollar amount to start using the jumbo ltv when calculating
    // affordability by ltv.
    jumboLimit: 453100,
    // The backend debt-to-income ratio (includes both PITI and other
    // debts) as a percentage, i.e. 36.
    dti: 36,
    // The loan type. This is used to get the PMI rate.
    loanType: LOAN_TYPE.FIXED
  }, overrides);
}
/**
 * Calculate the affordability by DTI.
 *
 * @method affordabilityByDTI
 */

function affordabilityByDTI(inputs) {
  var taxByAmount = _isNumber2(inputs.propertyTaxAmount),
      monthlyIncome,
      monthlyInsurance = 0,
      monthlyTaxes = 0,
      monthlyHOA = 0,
      monthlyAvailable,
      initialTotal,
      total,
      taxRate = 0,
      pmiRate = 0,
      pmiTotal,
      pmiTotals = [],
      pmiBucket,
      ltv,
      iBucket,
      outputs = {};

  if (inputs.includeHomeownersInsurance) {
    monthlyInsurance = inputs.annualHomeownersInsurance / 12;
  }

  if (inputs.includeHOA) {
    monthlyHOA = inputs.monthlyHOA;
  }

  if (inputs.includePropertyTax && taxByAmount) {
    monthlyTaxes = inputs.propertyTaxAmount / 12;
  } // Get the monthly available amount, or calculate it from income if
  // not given.


  if (inputs.customPayment) {
    monthlyAvailable = inputs.monthlyPayment - monthlyInsurance - monthlyHOA - monthlyTaxes;
  } else {
    monthlyIncome = inputs.annualIncome / 12; // Monthly available is the monthly income multiplied by the
    // dti ratio minus any other monthly debts (not including PMI).

    monthlyAvailable = monthlyIncome * inputs.dti / 100 - monthlyInsurance - monthlyHOA - monthlyTaxes - inputs.monthlyDebts;
  } // No money for monthly payments


  if (monthlyAvailable <= 0) {
    total = inputs.downPayment;
    outputs.ltv = 0;
    outputs.affordabilityByDTI = total;
    outputs.annualPropertyTax = $$(_annualPropertyTax(inputs, total));
    return outputs;
  } // The initial total is calculated without taxes or PMI. The
  // total is the sum of the loan amount calculated from the
  // payment, plus the down payment.


  initialTotal = amountByPayment(monthlyAvailable, inputs.rate, inputs.term, true) + inputs.downPayment;
  total = initialTotal; // Update the amount with property taxes included

  if (inputs.includePropertyTax && !taxByAmount) {
    taxRate = inputs.propertyTaxRate / 1200;
    total = _affordabilityWith(initialTotal, taxRate, inputs.rate, inputs.term);
  } // Update the amount with PMI included


  if (inputs.includePMI) {
    // Calculate ltv for PMI bucket
    ltv = _ltv(total, inputs.downPayment);
    pmiBucket = getPMIBucket(ltv);
    pmiRate = PMI[inputs.loanType][pmiBucket] / 12;
    pmiTotals.push(_affordabilityWith(initialTotal, pmiRate + taxRate, inputs.rate, inputs.term)); // Test other PMI buckets as they will sometimes result in an
    // overally higher affordability.

    for (iBucket = 80; iBucket < pmiBucket; iBucket += 5) {
      // Calculate affordability needed for a this PMI bucket
      pmiTotal = amountByLTV(inputs.downPayment, iBucket); // Update total to not include tax rate

      pmiTotal = _affordabilityWithout(pmiTotal, taxRate, inputs.rate, inputs.term); // Calculate affordability with the new PMI rate

      pmiRate = PMI[inputs.loanType][iBucket] / 12;
      pmiTotals.push(_affordabilityWith(pmiTotal, pmiRate + taxRate, inputs.rate, inputs.term));
    }

    total = Math.max.apply(Math, pmiTotals);
  } // Affordability with taxes/insurance can be less than down payment
  // for a really large down payment, take the max of the two


  total = Math.max(total, inputs.downPayment); // Update read only attributes

  outputs.ltv = _ltv(total, inputs.downPayment);
  outputs.affordabilityByDTI = $$(total);
  outputs.annualPropertyTax = $$(_annualPropertyTax(inputs, total));
  return outputs;
}
/**
 * Calculate the affordability by LTV
 *
 * @method affordabilityByLTV
 */

function affordabilityByLTV(inputs) {
  var affordability = amountByLTV(inputs.downPayment, inputs.ltv),
      loanAmount = affordability - inputs.downPayment;

  if (loanAmount > inputs.jumboLimit) {
    affordability = Math.max(inputs.jumboLimit + inputs.downPayment, amountByLTV(inputs.downPayment, inputs.ltvJumbo));
    loanAmount = affordability - inputs.downPayment;
  }

  return {
    affordabilityByLTV: affordability,
    loanAmountByLTV: loanAmount
  };
}
/**
 * Calculate the overall affordability, comparing affordability by LTV
 * and affordability by DTI.
 *
 * @method affordability
 */

function calculateOverallAffordability(inputs) {
  var byDTI = affordabilityByDTI(inputs),
      byLTV = affordabilityByLTV(inputs),
      affordability = Math.min(byDTI.affordabilityByDTI, byLTV.affordabilityByLTV),
      loanAmount = affordability - inputs.downPayment;
  return _merge2(byDTI, byLTV, {
    affordability: affordability,
    loanAmount: loanAmount
  });
}

var _affordability = /*#__PURE__*/Object.freeze({
    affordabilityDefaults: affordabilityDefaults,
    affordabilityByDTI: affordabilityByDTI,
    affordabilityByLTV: affordabilityByLTV,
    default: calculateOverallAffordability
});

var refinance$1 = _refinance;
var affordability = _affordability;
var mortgageCalculations = payment;

var AFFORDABILITY_DTI_MAX = constants.AFFORDABILITY_DTI_MAX,
    AFFORDABILITY_IMG_MAX = constants.AFFORDABILITY_IMG_MAX,
    AFFORDABILITY_IMG_MIN = constants.AFFORDABILITY_IMG_MIN,
    AFFORDABILITY_SIZE = constants.AFFORDABILITY_SIZE;
var affordabilityCalculatorFieldsSelector = createSelector([calculatorsSelector], function (calculators) {
  return calculators && calculators.affordability ? calculators.affordability.fields : null;
});
var calculatedAffordabilityValuesSelector = createSelector([calculatorsSelector], function (calculators) {
  return calculators && calculators.affordability ? calculators.affordability.calculatedAffordabilityValues : null;
});
var affordabilityInsuranceSelector = createSelector([affordabilityCalculatorFieldsSelector], function (fields) {
  return fields ? fields.annualHomeownersInsurance / 12 : null;
});
var affordabilityPMISelector = createSelector([affordabilityCalculatorFieldsSelector, calculatedAffordabilityValuesSelector], function (fields, calculated) {
  return fields && calculated ? monthlyPMI(calculated.affordabilityByDTI - fields.downPayment, calculated.affordabilityByDTI, false) : null;
});
var affordabilityPropertyTaxesSelector = createSelector([affordabilityCalculatorFieldsSelector, calculatedAffordabilityValuesSelector], function (fields, calculated) {
  return fields && calculated ? calculated.affordabilityByDTI * (fields.propertyTaxRate / 100) / 12 : null;
});
var affordabilityPISelector = createSelector([affordabilityCalculatorFieldsSelector, calculatedAffordabilityValuesSelector], function (fields, calculated) {
  return fields && calculated !== null ? monthlyPI(calculated.affordabilityByDTI - fields.downPayment, fields.rate, fields.term) : null;
});
var affordabilityPaymentSelector = createSelector([calculatedAffordabilityValuesSelector], function (calculated) {
  return calculated ? calculated.monthlyPayment : null;
});
var affordabilityAggressiveSelector = createSelector([affordabilityCalculatorFieldsSelector], function (fields) {
  return fields ? fields.dti > 36 : false;
});
var affordabilityImageRatioSelector = createSelector([affordabilityCalculatorFieldsSelector], function (fields) {
  var ratio = null;

  if (fields) {
    if (fields.customPayment) {
      ratio = fields.monthlyPayment / fields.maximumPayment;
    } else {
      ratio = fields.dti / AFFORDABILITY_DTI_MAX;
    }
  }

  return ratio;
});
var affordabilityImageLargeSelector = createSelector([affordabilityImageRatioSelector], function (ratio) {
  return ratio !== null ? AFFORDABILITY_IMG_MIN + AFFORDABILITY_SIZE * ratio : null;
});
var affordabilityImageSmallSelector = createSelector([affordabilityImageRatioSelector], function (ratio) {
  return ratio !== null ? AFFORDABILITY_IMG_MAX - AFFORDABILITY_SIZE * ratio : null;
});

var affordability$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  affordabilityCalculatorFieldsSelector: affordabilityCalculatorFieldsSelector,
  calculatedAffordabilityValuesSelector: calculatedAffordabilityValuesSelector,
  affordabilityInsuranceSelector: affordabilityInsuranceSelector,
  affordabilityPMISelector: affordabilityPMISelector,
  affordabilityPropertyTaxesSelector: affordabilityPropertyTaxesSelector,
  affordabilityPISelector: affordabilityPISelector,
  affordabilityPaymentSelector: affordabilityPaymentSelector,
  affordabilityAggressiveSelector: affordabilityAggressiveSelector,
  affordabilityImageLargeSelector: affordabilityImageLargeSelector,
  affordabilityImageSmallSelector: affordabilityImageSmallSelector
});

var LOAN_TERM_IN_MONTHS = constants.LOAN_TERM_IN_MONTHS,
    LOAN_PROGRAMS = constants.LOAN_PROGRAMS;
var mortgageCalculatorFieldsSelector = createSelector([calculatorsSelector], function (calculators) {
  var _calculators$mortgage;

  return (calculators === null || calculators === void 0 ? void 0 : (_calculators$mortgage = calculators.mortgage) === null || _calculators$mortgage === void 0 ? void 0 : _calculators$mortgage.fields) || null;
});
var loanAmountSelector = createSelector([mortgageCalculatorFieldsSelector], function (fields) {
  return fields ? fields.homePrice - fields.downPayment : null;
});
var ARM_PROGRAMS = [LOAN_PROGRAMS.arm3, LOAN_PROGRAMS.arm5, LOAN_PROGRAMS.arm7];
var isARMSelector = createSelector([mortgageCalculatorFieldsSelector], function (fields) {
  return fields ? ARM_PROGRAMS.indexOf(fields.loanProgram) !== -1 : false;
});
var monthlyPISelector = createSelector([mortgageCalculatorFieldsSelector, loanAmountSelector], function (fields, loanAmount) {
  return fields && loanAmount !== null ? monthlyPI(loanAmount, fields.interestRate, LOAN_TERM_IN_MONTHS[fields.loanProgram]) : null;
});
var monthlyPMISelector = createSelector([mortgageCalculatorFieldsSelector, loanAmountSelector, isARMSelector], function (fields, loanAmount, isARM) {
  return fields && loanAmount !== null ? monthlyPMI(loanAmount, fields.homePrice, isARM) : null;
});
var monthlyPropertyTaxSelector = createSelector([mortgageCalculatorFieldsSelector], function (fields) {
  return fields ? fields.homePrice * (fields.taxRate / 100) / 12 : null;
});
var monthlyInsuranceSelector = createSelector([mortgageCalculatorFieldsSelector], function (fields) {
  return fields ? fields.homeInsurance / 12 : null;
});
var monthlyPaymentSelector = createSelector([mortgageCalculatorFieldsSelector, monthlyPISelector, monthlyPMISelector, monthlyPropertyTaxSelector, monthlyInsuranceSelector], function (fields, monthlyPI, monthlyPMI, monthlyTaxes, monthlyInsurance) {
  return fields ? monthlyPI + (fields.includePMI ? monthlyPMI : 0) + (fields.includeTaxesInsurance ? monthlyInsurance + monthlyTaxes : 0) + fields.hoa : null;
});

var mortgage = /*#__PURE__*/Object.freeze({
  __proto__: null,
  mortgageCalculatorFieldsSelector: mortgageCalculatorFieldsSelector,
  loanAmountSelector: loanAmountSelector,
  isARMSelector: isARMSelector,
  monthlyPISelector: monthlyPISelector,
  monthlyPMISelector: monthlyPMISelector,
  monthlyPropertyTaxSelector: monthlyPropertyTaxSelector,
  monthlyInsuranceSelector: monthlyInsuranceSelector,
  monthlyPaymentSelector: monthlyPaymentSelector
});

var _excluded$2 = ["downPayment", "firstTimeBuyer", "hasAgent", "currentBalance", "wantsCashOut", "name", "budget", "homeValue", "bankruptcy", "hasSecondMortgage", "creditScoreRange", "loanType"];
var LOAN_TYPES = constants.LOAN_TYPES,
    LONG_FLOW_CREDIT_SCORES = constants.longFlow.LONG_FLOW_CREDIT_SCORES,
    PROPERTY_TYPES_LOOKUP = constants.leads.PROPERTY_TYPES_LOOKUP;
var longFlowSelector = function longFlowSelector(_ref) {
  var longFlow = _ref.longFlow;
  return longFlow || null;
};
var longFlowResponsesSelector = createSelector([longFlowSelector], function (longFlow) {
  return longFlow && longFlow.responses || null;
});

var responseByKeyInterior = function responseByKeyInterior(key, state) {
  var responses = longFlowResponsesSelector(state);
  return responses ? responses[key] : null;
};

var responseByKeySelector = function responseByKeySelector(key, state) {
  return state ? responseByKeyInterior(key, state) : function (stateArg) {
    return responseByKeyInterior(key, stateArg);
  };
};
var longFlowErrorSelector = createSelector([longFlowSelector], function (longFlow) {
  return longFlow && longFlow.longFlowError || null;
});
var userFriendlyLongFlowErrorSelector = createSelector([longFlowErrorSelector], function (str) {
  var userFriendly = null;

  switch (str) {
    case 'UnknownLender':
      userFriendly = 'The lender you attempted to contact is invalid.';
      break;

    case 'InvalidFirstName':
    case 'InvalidLastName':
      userFriendly = 'The name you entered is invalid.';
      break;

    case 'InvalidEmailAddress':
      userFriendly = 'The email you entered is invalid.';
      break;

    case 'InvalidPhoneNumber':
      userFriendly = 'The phone number you entered is invalid.';
      break;

    default:
      userFriendly = str;
      break;
  }

  return userFriendly;
});
var isRefinanceSelector = createSelector([responseByKeySelector('loanType')], function (response) {
  return !!(response && response === LOAN_TYPES.REFINANCE);
});
var isVASelector = createSelector([responseByKeySelector('vaEligible')], function (response) {
  return !!response;
});

var createRequestFromUserResponses = function createRequestFromUserResponses(isRefinance, _ref2, _ref3) {
  var _LONG_FLOW_CREDIT_SCO, _LONG_FLOW_CREDIT_SCO2;

  var downPayment = _ref2.downPayment,
      firstTimeBuyer = _ref2.firstTimeBuyer,
      hasAgent = _ref2.hasAgent,
      currentBalance = _ref2.currentBalance,
      wantsCashOut = _ref2.wantsCashOut,
      name = _ref2.name,
      budget = _ref2.budget,
      homeValue = _ref2.homeValue,
      bankruptcy = _ref2.bankruptcy,
      hasSecondMortgage = _ref2.hasSecondMortgage,
      creditScoreRange = _ref2.creditScoreRange,
      loanType = _ref2.loanType,
      rest = _objectWithoutProperties(_ref2, _excluded$2);

  var source = _ref3.source,
      channel = _ref3.channel;
  return Object.assign({}, name, {
    propertyValue: isRefinance ? homeValue : budget,
    hasBankruptcy: bankruptcy,
    hasForeclosure: bankruptcy,
    creditScore: creditScoreRange ? {
      creditScoreHigh: (_LONG_FLOW_CREDIT_SCO = LONG_FLOW_CREDIT_SCORES[creditScoreRange]) === null || _LONG_FLOW_CREDIT_SCO === void 0 ? void 0 : _LONG_FLOW_CREDIT_SCO.creditScoreHigh,
      creditScoreLow: (_LONG_FLOW_CREDIT_SCO2 = LONG_FLOW_CREDIT_SCORES[creditScoreRange]) === null || _LONG_FLOW_CREDIT_SCO2 === void 0 ? void 0 : _LONG_FLOW_CREDIT_SCO2.creditScoreLow
    } : undefined,
    details: isRefinance ? {
      refinance: {
        hasSecondMortgage: hasSecondMortgage,
        currentBalance: currentBalance,
        wantsCashOut: wantsCashOut
      }
    } : {
      purchase: {
        downPayment: downPayment,
        firstTimeBuyer: firstTimeBuyer,
        hasAgent: hasAgent
      }
    }
  }, rest, {
    source: source || undefined,
    channel: channel || rest.channel
  });
};

var longFlowRequestSelector = createSelector([isRefinanceSelector, longFlowResponsesSelector, queryParamsSelector], function (isRefinance, responses, queryParams) {
  return responses ? createRequestFromUserResponses(isRefinance, responses, queryParams || {}) : null;
});
var facebookEventSelector = createSelector([isRefinanceSelector, isVASelector], function (isRefinance, isVA) {
  return "ZHL" + (isVA ? 'VA' : '') + (isRefinance ? 'Refinance' : 'Purchase') + "Lead";
});
var buyBoxEventSelector = createSelector([isRefinanceSelector, isVASelector, longFlowRequestSelector], function (isRefinance, isVA, longFlowRequest) {
  var _details$refinance$cu, _details$refinance, _details$purchase$dow, _details$purchase, _details$refinance2;

  var _ref4 = longFlowRequest !== null && longFlowRequest !== void 0 ? longFlowRequest : {},
      details = _ref4.details,
      propertyValue = _ref4.propertyValue,
      annualIncome = _ref4.annualIncome,
      creditScore = _ref4.creditScore,
      hasBankruptcy = _ref4.hasBankruptcy,
      hasForeclosure = _ref4.hasForeclosure,
      propertyType = _ref4.propertyType,
      propertyUse = _ref4.propertyUse,
      stateAbbreviation = _ref4.stateAbbreviation;

  var loanAmount = isRefinance ? (_details$refinance$cu = details === null || details === void 0 ? void 0 : (_details$refinance = details.refinance) === null || _details$refinance === void 0 ? void 0 : _details$refinance.currentBalance) !== null && _details$refinance$cu !== void 0 ? _details$refinance$cu : 0 : propertyValue - ((_details$purchase$dow = details === null || details === void 0 ? void 0 : (_details$purchase = details.purchase) === null || _details$purchase === void 0 ? void 0 : _details$purchase.downPayment) !== null && _details$purchase$dow !== void 0 ? _details$purchase$dow : 0);
  var ltv = propertyValue ? loanAmount / propertyValue : 0;
  var event = (isRefinance ? 'Refinance' : 'Purchase') + "BuyBoxSubmit";
  return {
    event: event,
    income: annualIncome,
    ltv: ltv,
    creditScoreLowerBound: creditScore === null || creditScore === void 0 ? void 0 : creditScore.creditScoreLow,
    loanAmount: loanAmount,
    isVA: isVA,
    hasBankruptcy: hasBankruptcy,
    hasForeclosure: hasForeclosure,
    isMobileOrManufactured: propertyType === PROPERTY_TYPES_LOOKUP.MOBILE_OR_MANUFACTURED,
    propertyType: propertyType,
    propertyUse: propertyUse,
    stateAbbreviation: stateAbbreviation,
    wantsCashOut: (details === null || details === void 0 ? void 0 : (_details$refinance2 = details.refinance) === null || _details$refinance2 === void 0 ? void 0 : _details$refinance2.wantsCashOut) || false
  };
});
var isSubmittedSelector = createSelector([longFlowSelector], function (longFlow) {
  return longFlow ? !!longFlow.leadSubmitted : false;
});
var applicationURLSelector = createSelector([longFlowSelector], function (longFlow) {
  var _longFlow$application;

  return (_longFlow$application = longFlow === null || longFlow === void 0 ? void 0 : longFlow.applicationURL) !== null && _longFlow$application !== void 0 ? _longFlow$application : null;
});
var leadSubmissionInProgressSelector = createSelector([longFlowSelector], function (longFlow) {
  var _longFlow$leadSubmiss;

  return (_longFlow$leadSubmiss = longFlow === null || longFlow === void 0 ? void 0 : longFlow.leadSubmissionInProgress) !== null && _longFlow$leadSubmiss !== void 0 ? _longFlow$leadSubmiss : false;
});

var longFlow = /*#__PURE__*/Object.freeze({
  __proto__: null,
  longFlowSelector: longFlowSelector,
  longFlowResponsesSelector: longFlowResponsesSelector,
  responseByKeySelector: responseByKeySelector,
  longFlowErrorSelector: longFlowErrorSelector,
  userFriendlyLongFlowErrorSelector: userFriendlyLongFlowErrorSelector,
  isRefinanceSelector: isRefinanceSelector,
  isVASelector: isVASelector,
  longFlowRequestSelector: longFlowRequestSelector,
  facebookEventSelector: facebookEventSelector,
  buyBoxEventSelector: buyBoxEventSelector,
  isSubmittedSelector: isSubmittedSelector,
  applicationURLSelector: applicationURLSelector,
  leadSubmissionInProgressSelector: leadSubmissionInProgressSelector
});

var DELETE_PARAM = 'DELETE_PARAM';
var deleteParam = function deleteParam(param) {
  return {
    type: DELETE_PARAM,
    payload: param
  };
};

var getSearchString = function getSearchString() {
  return typeof window !== 'undefined' && window.location.search ? window.location.search.substr(1) : null;
};

var clearParameters = function clearParameters(onCompletion) {
  if (onCompletion === void 0) {
    onCompletion = function onCompletion() {};
  }

  var query = getSearchString();
  var hash = getHashString();
  var hashParamsToRemove = getHashApplicableParams();
  var targetedHREF = window.location.href;

  if (query) {
    targetedHREF = targetedHREF.replace("?" + query, '');
  }

  if (hash) {
    APPLICABLE_HASH_PARAMS.forEach(function (key) {
      var encoded = window.escape(hashParamsToRemove[key]);
      targetedHREF = targetedHREF.replace(key + "=" + encoded, '');
    }); // Remove excess ampersands

    targetedHREF = targetedHREF.replace(/&(?!\w)/g, '');
  } // Remove unneeded hash if necessary


  if (targetedHREF.substr(-1) === '#') {
    targetedHREF = targetedHREF.substr(0, targetedHREF.length - 1);
  }

  if (targetedHREF !== window.location.href) {
    window.history.replaceState({}, null, targetedHREF);
    onCompletion();
  }
};

var getQueryParams = function getQueryParams() {
  var query = getSearchString();
  return query ? querystring.parse(query) : null;
};

var INITIAL_STATE = Object.assign({}, getQueryParams());
var queryParams$1 = (function (state, _ref) {
  if (state === void 0) {
    state = INITIAL_STATE;
  }

  var type = _ref.type,
      payload = _ref.payload,
      key = _ref.key;

  if (type === REHYDRATE && key === 'queryParams') {
    return Object.assign({}, payload, state);
  }

  switch (type) {
    case DELETE_PARAM:
      {
        var temp = Object.assign({}, state);
        delete temp[payload];
        return temp;
      }

    default:
      return state;
  }
});

var queryParams$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  deleteParam: deleteParam,
  clearParameters: clearParameters,
  INITIAL_STATE: INITIAL_STATE,
  'default': queryParams$1
});

var _CMS_DOCUMENT_IDS, _CMS_DOCUMENT_TITLES, _CMS_DOCUMENT_CANONIC;
var DISMISS_ABAD = 'DISMISS_ABAD';
var dismissAbad = function dismissAbad() {
  return function (dispatch) {
    dispatch({
      type: DISMISS_ABAD
    });
    dispatch(deleteParam('showAbad'));
  };
};
var CMS_DOCUMENT_LOOKUP = {
  ABAD: 'abad',
  LICENSING: 'licensing',
  PRIVACY: 'privacy',
  ARCHIVED_PRIVACY: 'archivedPrivacy',
  TERMS: 'terms',
  CONVENTIONAL_SAMPLES: 'conventionalSamples',
  VA_SAMPLES: 'vaSamples',
  FHA_SAMPLES: 'fhaSamples',
  PASSPORT_DISCLAIMER: 'passportDisclaimer'
};
var CMS_DOCUMENT_IDS = (_CMS_DOCUMENT_IDS = {}, _CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.ABAD] = 'zhl-affiliate-business-arrangement-disclosure', _CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.LICENSING] = 'zhl-licensing-info', _CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.PRIVACY] = 'zhl-privacy-policy', _CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.ARCHIVED_PRIVACY] = 'zhl-privacy-policy', _CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.TERMS] = 'zhl-tos', _CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.CONVENTIONAL_SAMPLES] = 'zhl-conventional-samples', _CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.VA_SAMPLES] = 'zhl-va-samples', _CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.FHA_SAMPLES] = 'zhl-fha-samples', _CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.PASSPORT_DISCLAIMER] = 'zhl-disclaimer-passport', _CMS_DOCUMENT_IDS);
var CMS_DOCUMENT_TITLES = (_CMS_DOCUMENT_TITLES = {}, _CMS_DOCUMENT_TITLES[CMS_DOCUMENT_LOOKUP.LICENSING] = 'Licensing info', _CMS_DOCUMENT_TITLES[CMS_DOCUMENT_LOOKUP.PRIVACY] = 'Privacy policy', _CMS_DOCUMENT_TITLES[CMS_DOCUMENT_LOOKUP.ARCHIVED_PRIVACY] = 'Archived privacy policies', _CMS_DOCUMENT_TITLES[CMS_DOCUMENT_LOOKUP.TERMS] = 'Terms of use', _CMS_DOCUMENT_TITLES);
var CMS_DOCUMENT_CANONICALS = (_CMS_DOCUMENT_CANONIC = {}, _CMS_DOCUMENT_CANONIC[CMS_DOCUMENT_LOOKUP.LICENSING] = '/licensing', _CMS_DOCUMENT_CANONIC[CMS_DOCUMENT_LOOKUP.PRIVACY] = '/privacy', _CMS_DOCUMENT_CANONIC[CMS_DOCUMENT_LOOKUP.ARCHIVED_PRIVACY] = '/archived-privacy', _CMS_DOCUMENT_CANONIC[CMS_DOCUMENT_LOOKUP.TERMS] = '/terms', _CMS_DOCUMENT_CANONIC); // The CMS API for pulling the latest version of a CMS document does not yield back its version so we store something
// representing that it is the latest version.

var PSUEDO_LATEST_VERSION = 'LATEST';
var SET_CMS_CONTENT = 'SET_CMS_CONTENT';

var setContent = function setContent(_ref) {
  var id = _ref.id,
      content = _ref.content,
      _ref$version = _ref.version,
      version = _ref$version === void 0 ? PSUEDO_LATEST_VERSION : _ref$version,
      lastModified = _ref.lastModified;
  return {
    type: SET_CMS_CONTENT,
    payload: {
      id: id,
      content: content,
      version: version,
      lastModified: lastModified
    }
  };
};

var fetchMarkdownDocumentContent = function fetchMarkdownDocumentContent(id) {
  return new Promise(function ($return, $error) {
    var _await$API$MAX$GET, content;

    return Promise.resolve(API.MAX.GET({
      action: 'getMarkdownCMSDocument',
      input: {
        id: id
      }
    })).then(function ($await_1) {
      try {
        _await$API$MAX$GET = $await_1, content = _await$API$MAX$GET.content;
        return $return(content);
      } catch ($boundEx) {
        return $error($boundEx);
      }
    }, $error);
  });
};
var fetchMarkdownDocument = function fetchMarkdownDocument(id) {
  return function (dispatch) {
    return new Promise(function ($return, $error) {
      var content;
      return Promise.resolve(fetchMarkdownDocumentContent(id)).then(function ($await_2) {
        try {
          content = $await_2;
          return $return(dispatch(setContent({
            id: id,
            content: content
          })));
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  };
};
var FETCH_MARKDOWN_DOCUMENT_HISTORY = createActionObject('FETCH_MARKDOWN_DOCUMENT_HISTORY');
var fetchMarkdownDocumentHistory = function fetchMarkdownDocumentHistory(id) {
  return API.MAX.GET.thunk({
    action: 'getMarkdownCMSDocumentForAllVersions',
    input: {
      id: id
    }
  }, FETCH_MARKDOWN_DOCUMENT_HISTORY);
};
var fetchAbadContent = function fetchAbadContent() {
  return fetchMarkdownDocument(CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.ABAD]);
};
var fetchLicensingContent = function fetchLicensingContent() {
  return fetchMarkdownDocument(CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.LICENSING]);
};
var fetchPrivacyContent = function fetchPrivacyContent() {
  return fetchMarkdownDocument(CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.PRIVACY]);
};
var fetchTermsOfUseContent = function fetchTermsOfUseContent() {
  return fetchMarkdownDocument(CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.TERMS]);
};
var fetchPassportDisclaimer = function fetchPassportDisclaimer() {
  return fetchMarkdownDocument(CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.PASSPORT_DISCLAIMER]);
};
var INITIAL_STATE$1 = {
  abadDismissed: false,
  documents: {}
};
var cms = (function (state, _ref2) {
  if (state === void 0) {
    state = INITIAL_STATE$1;
  }

  var type = _ref2.type,
      payload = _ref2.payload;

  switch (type) {
    case DISMISS_ABAD:
      return Object.assign({}, state, {
        abadDismissed: true
      });

    case SET_CMS_CONTENT:
      var _id, _Object$assign;

      var id = payload.id,
          content = payload.content,
          lastModified = payload.lastModified,
          version = payload.version;
      return Object.assign({}, state, {
        documents: Object.assign({}, state.documents, (_Object$assign = {}, _Object$assign[id] = (_id = {}, _id[version] = {
          content: content,
          lastModified: lastModified,
          version: version
        }, _id), _Object$assign))
      });

    case FETCH_MARKDOWN_DOCUMENT_HISTORY.SUCCESS:
      var _Object$assign3;

      var _id2 = payload.id,
          versions = payload.versions;
      return Object.assign({}, state, {
        documents: Object.assign({}, state.documents, (_Object$assign3 = {}, _Object$assign3[_id2] = versions.reduce(function (memo, _ref3) {
          var _Object$assign2;

          var content = _ref3.content,
              lastModified = _ref3.lastModified,
              version = _ref3.version;
          return Object.assign({}, memo, (_Object$assign2 = {}, _Object$assign2[version] = {
            content: content,
            lastModified: lastModified,
            version: version
          }, _Object$assign2));
        }, {}), _Object$assign3))
      });

    default:
      return state;
  }
});

var cms$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  DISMISS_ABAD: DISMISS_ABAD,
  dismissAbad: dismissAbad,
  CMS_DOCUMENT_LOOKUP: CMS_DOCUMENT_LOOKUP,
  CMS_DOCUMENT_IDS: CMS_DOCUMENT_IDS,
  CMS_DOCUMENT_TITLES: CMS_DOCUMENT_TITLES,
  CMS_DOCUMENT_CANONICALS: CMS_DOCUMENT_CANONICALS,
  PSUEDO_LATEST_VERSION: PSUEDO_LATEST_VERSION,
  SET_CMS_CONTENT: SET_CMS_CONTENT,
  fetchMarkdownDocumentContent: fetchMarkdownDocumentContent,
  fetchMarkdownDocument: fetchMarkdownDocument,
  FETCH_MARKDOWN_DOCUMENT_HISTORY: FETCH_MARKDOWN_DOCUMENT_HISTORY,
  fetchMarkdownDocumentHistory: fetchMarkdownDocumentHistory,
  fetchAbadContent: fetchAbadContent,
  fetchLicensingContent: fetchLicensingContent,
  fetchPrivacyContent: fetchPrivacyContent,
  fetchTermsOfUseContent: fetchTermsOfUseContent,
  fetchPassportDisclaimer: fetchPassportDisclaimer,
  'default': cms
});

var cmsSelector = function cmsSelector(_ref) {
  var cms = _ref.cms;
  return cms;
};

var shouldShowAbadSelector = createSelector([queryParamsSelector, cmsSelector], function (queryParams, cms) {
  return !!(queryParams && queryParams.showAbad && cms && !cms.abadDismissed);
});
var cmsDocumentsSelector = createSelector([cmsSelector], function (cms) {
  var _cms$documents;

  return (_cms$documents = cms === null || cms === void 0 ? void 0 : cms.documents) !== null && _cms$documents !== void 0 ? _cms$documents : {};
});
var cmsLatestDocumentsContentsSelector = createSelector([cmsDocumentsSelector], function (cmsDocuments) {
  return Object.entries(cmsDocuments).reduce(function (memo, _ref2) {
    var _Object$assign;

    var _ref3 = _slicedToArray(_ref2, 2),
        documentID = _ref3[0],
        documentHistory = _ref3[1];

    var latestVersion = documentHistory === null || documentHistory === void 0 ? void 0 : documentHistory[PSUEDO_LATEST_VERSION];
    return Object.assign({}, memo, (_Object$assign = {}, _Object$assign[documentID] = latestVersion === null || latestVersion === void 0 ? void 0 : latestVersion.content, _Object$assign));
  }, {});
});
var privacyPolicyHistorySelector = createSelector([cmsDocumentsSelector], function (cmsDocuments) {
  var documentID = CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.PRIVACY];
  var privacyPolicyVersions = cmsDocuments[documentID];

  if (!privacyPolicyVersions) {
    return null;
  }

  var unsortedPrivacyPolicyVersionsList = Object.values(privacyPolicyVersions);
  var sortedPrivacyPolicyVersionsList = unsortedPrivacyPolicyVersionsList.sort(function (_ref4, _ref5) {
    var lastModifiedA = _ref4.lastModified;
    var lastModifiedB = _ref5.lastModified;
    return new Date(lastModifiedB).getTime() - new Date(lastModifiedA).getTime();
  });
  return sortedPrivacyPolicyVersionsList;
});
var abadContentSelector = createSelector([cmsLatestDocumentsContentsSelector], function (cmsLatestDocumentsContents) {
  var documentID = CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.ABAD];
  return cmsLatestDocumentsContents[documentID];
});
var termsOfUseContentSelector = createSelector([cmsLatestDocumentsContentsSelector], function (cmsLatestDocumentsContents) {
  var documentID = CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.TERMS];
  return cmsLatestDocumentsContents[documentID];
});
var privacyContentSelector = createSelector([cmsLatestDocumentsContentsSelector], function (cmsLatestDocumentsContents) {
  var documentID = CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.PRIVACY];
  return cmsLatestDocumentsContents[documentID];
});
var licensingContentSelector = createSelector([cmsLatestDocumentsContentsSelector], function (cmsLatestDocumentsContents) {
  var documentID = CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.LICENSING];
  return cmsLatestDocumentsContents[documentID];
});
var passportDisclaimerSelector = createSelector([cmsLatestDocumentsContentsSelector], function (cmsLatestDocumentsContents) {
  var documentID = CMS_DOCUMENT_IDS[CMS_DOCUMENT_LOOKUP.PASSPORT_DISCLAIMER];
  return cmsLatestDocumentsContents[documentID];
});

var cms$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  shouldShowAbadSelector: shouldShowAbadSelector,
  cmsLatestDocumentsContentsSelector: cmsLatestDocumentsContentsSelector,
  privacyPolicyHistorySelector: privacyPolicyHistorySelector,
  abadContentSelector: abadContentSelector,
  termsOfUseContentSelector: termsOfUseContentSelector,
  privacyContentSelector: privacyContentSelector,
  licensingContentSelector: licensingContentSelector,
  passportDisclaimerSelector: passportDisclaimerSelector
});

var _ADMIN_ROUTE_PERMISSI;
var ROUTE_MAP = {
  SETTINGS: 'SETTINGS'
};
var ADMIN_ROUTE_PERMISSIONS = (_ADMIN_ROUTE_PERMISSI = {}, _ADMIN_ROUTE_PERMISSI[ROUTE_MAP.SETTINGS] = ['zhl-settings-reader'], _ADMIN_ROUTE_PERMISSI);
var adminSelector = function adminSelector(_ref) {
  var admin = _ref.admin;
  return admin || null;
};
var accessibleRoutesSelector = getAccessibleRoutesSelector(ADMIN_ROUTE_PERMISSIONS);

var admin = /*#__PURE__*/Object.freeze({
  __proto__: null,
  ROUTE_MAP: ROUTE_MAP,
  ADMIN_ROUTE_PERMISSIONS: ADMIN_ROUTE_PERMISSIONS,
  adminSelector: adminSelector,
  accessibleRoutesSelector: accessibleRoutesSelector
});

var _excluded$3 = ["isPublic", "readRoles", "writeRoles", "value"];
var USER_ROLES_COMMON$1 = constants.USER_ROLES_COMMON;
var settingsSelector = function settingsSelector(_ref) {
  var settings = _ref.settings;
  return settings || null;
};
var editingSettingFieldSelector = createSelector([settingsSelector], function (settings) {
  return settings !== null && settings !== void 0 && settings.fields ? Object.assign({}, settings.fields, {
    isPublic: settings.fields.readRoles === false,
    readRoles: Array.isArray(settings.fields.readRoles) ? settings.fields.readRoles : ''
  }) : null;
});
var definitionsSelectors = createSelector([settingsSelector], function (settings) {
  return (settings === null || settings === void 0 ? void 0 : settings.definitions) || null;
});
var settingsValuesSelector = createSelector([settingsSelector], function (settings) {
  return (settings === null || settings === void 0 ? void 0 : settings.values) || null;
});
var createSettingsValueSelector = function createSettingsValueSelector(key) {
  return createSelector([settingsValuesSelector], function (values) {
    return (values === null || values === void 0 ? void 0 : values[key]) || null;
  });
};
var settingsFetchValueErrorsSelector = createSelector([settingsSelector], function (settings) {
  var _settings$fetchValueE;

  return (_settings$fetchValueE = settings === null || settings === void 0 ? void 0 : settings.fetchValueErrors) !== null && _settings$fetchValueE !== void 0 ? _settings$fetchValueE : {};
});
var settingsFilterStringSelector = createSelector([settingsSelector], function (settings) {
  return (settings === null || settings === void 0 ? void 0 : settings.filter) || null;
});
var categorizedSettingsSelector = createSelector([settingsSelector, definitionsSelectors, settingsValuesSelector, userRolesSelector], function (settings, definitions, values, userRoles) {
  return definitions && values && userRoles.length ? Object.keys(settings.categories).reduce(function (categories, current) {
    var _Object$assign;

    return Object.assign({}, categories, (_Object$assign = {}, _Object$assign[current] = settings.categories[current].map(function (key) {
      return {
        definition: definitions[key],
        hasAccessToWrite: userRoles.indexOf(USER_ROLES_COMMON$1.SETTINGS_ADMIN) !== -1 || userRoles.indexOf(USER_ROLES_COMMON$1.SETTINGS_WRITER) !== -1 && (definitions[key].writeRoles.length === 0 || definitions[key].writeRoles.some(function (currentRole) {
          return userRoles.indexOf(currentRole) !== -1;
        })),
        canModifyDefinition: userRoles.indexOf(USER_ROLES_COMMON$1.SETTINGS_ADMIN) !== -1,
        value: values[key],
        key: key
      };
    }), _Object$assign));
  }, {}) : null;
});
var filteredCategorizedSettingsSelector = createSelector([categorizedSettingsSelector, settingsFilterStringSelector], function (settings, filter) {
  if (!settings) {
    return null;
  } else if (!filter) {
    return settings;
  }

  var filteredResult = {};
  var lowerCasedFilter = filter.toLowerCase();
  Object.keys(settings).forEach(function (category) {
    var setting = settings[category];
    var filteredSettings = setting.filter(function (_ref2) {
      var _ref2$definition = _ref2.definition,
          description = _ref2$definition.description,
          friendlyName = _ref2$definition.friendlyName;
      return description.toLowerCase().includes(lowerCasedFilter) || friendlyName.toLowerCase().includes(lowerCasedFilter) || category.toLowerCase().includes(lowerCasedFilter);
    });

    if (filteredSettings.length) {
      filteredResult[category] = filteredSettings;
    }
  });
  return Object.keys(filteredResult).length === 0 ? null : filteredResult;
});

var getArrayFromString = function getArrayFromString(input) {
  return typeof input === 'string' && input.trim() !== '' ? input.split(',').map(function (s) {
    return s.trim();
  }) : input || [];
};

var mutateForRequest = function mutateForRequest(_ref3) {
  var isPublic = _ref3.isPublic,
      readRoles = _ref3.readRoles,
      writeRoles = _ref3.writeRoles,
      value = _ref3.value,
      rest = _objectWithoutProperties(_ref3, _excluded$3);

  var parseValue = function parseValue(val) {
    try {
      return JSON.parse(val);
    } catch (err) {
      return val;
    }
  };

  return Object.assign({}, rest, {
    readRoles: isPublic === false ? getArrayFromString(readRoles) : false,
    writeRoles: getArrayFromString(writeRoles),
    value: parseValue(value)
  });
};

var settingsRequestSelector = createSelector([settingsSelector], function (settings) {
  return settings !== null && settings !== void 0 && settings.fields ? mutateForRequest(settings.fields) : null;
});

var settings = /*#__PURE__*/Object.freeze({
  __proto__: null,
  settingsSelector: settingsSelector,
  editingSettingFieldSelector: editingSettingFieldSelector,
  definitionsSelectors: definitionsSelectors,
  settingsValuesSelector: settingsValuesSelector,
  createSettingsValueSelector: createSettingsValueSelector,
  settingsFetchValueErrorsSelector: settingsFetchValueErrorsSelector,
  settingsFilterStringSelector: settingsFilterStringSelector,
  categorizedSettingsSelector: categorizedSettingsSelector,
  filteredCategorizedSettingsSelector: filteredCategorizedSettingsSelector,
  settingsRequestSelector: settingsRequestSelector
});

var getSharedWithFromOwners = function getSharedWithFromOwners(owners, borrowerID) {
  if (owners === void 0) {
    owners = [];
  }

  if (borrowerID === void 0) {
    borrowerID = null;
  }

  return owners.filter(function (id) {
    return id !== borrowerID;
  }).reduce(function (sharedWith, id) {
    var _Object$assign;

    return Object.assign({}, sharedWith, (_Object$assign = {}, _Object$assign[id] = true, _Object$assign));
  }, {});
};

var _constants$loanFile = constants.loanFile,
    LOAN_STATUS_LOOKUP = _constants$loanFile.LOAN_STATUS_LOOKUP,
    READ_ONLY_LOAN_STATUSES = _constants$loanFile.READ_ONLY_LOAN_STATUSES,
    LOAN_EDITOR_LOOKUP = _constants$loanFile.LOAN_EDITOR_LOOKUP;
var loanFileSelector = function loanFileSelector(_ref) {
  var loanFile = _ref.loanFile;
  return loanFile !== null && loanFile !== void 0 ? loanFile : null;
};
var loanNumberSelector = createSelector([loanFileSelector], function (loanFile) {
  var _loanFile$loanNumber;

  return (_loanFile$loanNumber = loanFile === null || loanFile === void 0 ? void 0 : loanFile.loanNumber) !== null && _loanFile$loanNumber !== void 0 ? _loanFile$loanNumber : null;
});
var loanFileStatusSelector = createSelector([loanFileSelector], function (loanFile) {
  var _loanFile$status;

  return (_loanFile$status = loanFile === null || loanFile === void 0 ? void 0 : loanFile.status) !== null && _loanFile$status !== void 0 ? _loanFile$status : null;
});
var loanFileHasLoadedSelector = createSelector([loanFileStatusSelector], function (status) {
  return status !== null;
});
var loanFileStatusIsReadOnlySelector = createSelector([loanFileStatusSelector], function (status) {
  return READ_ONLY_LOAN_STATUSES.has(status);
});
var loanFileCanBeWithdrawnOrDeniedSelector = createSelector([loanFileStatusIsReadOnlySelector], function (statusIsReadOnly) {
  return !statusIsReadOnly;
});
var loanFileEditorSelector = createSelector([loanFileSelector], function (loanFile) {
  var _loanFile$editor;

  return (_loanFile$editor = loanFile === null || loanFile === void 0 ? void 0 : loanFile.editor) !== null && _loanFile$editor !== void 0 ? _loanFile$editor : null;
});
var loanFileHasBeenTakenOverSelector = createSelector([loanFileEditorSelector], function (editor) {
  return editor === LOAN_EDITOR_LOOKUP.LENDER;
});
var loanFileCanBeTakenOverSelector = createSelector([loanFileStatusIsReadOnlySelector, loanFileHasBeenTakenOverSelector], function (statusIsReadOnly, loanFileHasBeenTakenOver) {
  return !statusIsReadOnly && !loanFileHasBeenTakenOver;
});
var loanFileIsReadOnlySelector = createSelector([loanFileStatusIsReadOnlySelector, loanFileHasBeenTakenOverSelector], function (statusIsReadOnly, loanFileHasBeenTakenOver) {
  return statusIsReadOnly || !loanFileHasBeenTakenOver;
});
var loanIsActiveSelector = createSelector([loanFileStatusSelector], function (status) {
  return status === LOAN_STATUS_LOOKUP.ACTIVE;
});
var loanHasEncompassLoanSelector = createSelector([loanNumberSelector], function (loanNumber) {
  return loanNumber !== null;
});
var loanIsSubmittableSelector = createSelector([loanIsActiveSelector, loanHasEncompassLoanSelector], function (loanIsActive, loanHasEncompassLoan) {
  return loanIsActive && loanHasEncompassLoan;
});
var loanSubmissionModalAlertTextSelector = createSelector([loanIsActiveSelector, loanIsSubmittableSelector], function (loanIsActive, loanIsSubmittable) {
  return loanIsSubmittable ? null : loanIsActive ? 'Application cannot be submitted. Check to make sure credit has been run, or trigger TRID to create an Encompass file.' : 'Application cannot be submitted. This loan file is no longer active.';
});
var tridTriggeredUTCSelector = createSelector([loanFileSelector], function (loanFile) {
  var _loanFile$tridTrigger;

  return (_loanFile$tridTrigger = loanFile === null || loanFile === void 0 ? void 0 : loanFile.tridTriggeredUTC) !== null && _loanFile$tridTrigger !== void 0 ? _loanFile$tridTrigger : null;
});
var showTRIDTriggeredNotificationSelector = createSelector([loanFileStatusSelector, tridTriggeredUTCSelector], function (loanFileStatus, tridTriggeredUTC) {
  return !!(tridTriggeredUTC && loanFileStatus === LOAN_STATUS_LOOKUP.ACTIVE);
});

var loanFile = /*#__PURE__*/Object.freeze({
  __proto__: null,
  loanFileSelector: loanFileSelector,
  loanNumberSelector: loanNumberSelector,
  loanFileStatusSelector: loanFileStatusSelector,
  loanFileHasLoadedSelector: loanFileHasLoadedSelector,
  loanFileCanBeWithdrawnOrDeniedSelector: loanFileCanBeWithdrawnOrDeniedSelector,
  loanFileEditorSelector: loanFileEditorSelector,
  loanFileCanBeTakenOverSelector: loanFileCanBeTakenOverSelector,
  loanFileIsReadOnlySelector: loanFileIsReadOnlySelector,
  loanIsActiveSelector: loanIsActiveSelector,
  loanHasEncompassLoanSelector: loanHasEncompassLoanSelector,
  loanIsSubmittableSelector: loanIsSubmittableSelector,
  loanSubmissionModalAlertTextSelector: loanSubmissionModalAlertTextSelector,
  tridTriggeredUTCSelector: tridTriggeredUTCSelector,
  showTRIDTriggeredNotificationSelector: showTRIDTriggeredNotificationSelector
});

var loanIDPropSelector = createPropsSelector(function (_ref) {
  var loanID = _ref.loanID;
  return loanID !== null && loanID !== void 0 ? loanID : null;
});
var loanIDRouteParamSelector = createPropsSelector(function (_ref2) {
  var _match$params$loanID;

  var match = _ref2.match;
  return (_match$params$loanID = match === null || match === void 0 ? void 0 : match.params.loanID) !== null && _match$params$loanID !== void 0 ? _match$params$loanID : null;
});
var borrowerIDPropSelector = createPropsSelector(function (_ref3) {
  var borrowerID = _ref3.borrowerID;
  return borrowerID !== null && borrowerID !== void 0 ? borrowerID : null;
});
var underwriterPropSelector = createPropsSelector(function (_ref4) {
  var underwriter = _ref4.underwriter;
  return underwriter !== null && underwriter !== void 0 ? underwriter : null;
});
var scenarioLabelPropSelector = createPropsSelector(function (_ref5) {
  var _scenario$scenario$sc, _scenario$scenario;

  var scenario = _ref5.scenario;
  return (_scenario$scenario$sc = scenario === null || scenario === void 0 ? void 0 : (_scenario$scenario = scenario.scenario) === null || _scenario$scenario === void 0 ? void 0 : _scenario$scenario.scenarioLabel) !== null && _scenario$scenario$sc !== void 0 ? _scenario$scenario$sc : null;
});
var scenarioLabelFromSelectedScenarioPropSelector = createPropsSelector(function (_ref6) {
  var _selectedScenario$sce, _selectedScenario$sce2;

  var selectedScenario = _ref6.selectedScenario;
  return (_selectedScenario$sce = selectedScenario === null || selectedScenario === void 0 ? void 0 : (_selectedScenario$sce2 = selectedScenario.scenario) === null || _selectedScenario$sce2 === void 0 ? void 0 : _selectedScenario$sce2.scenarioLabel) !== null && _selectedScenario$sce !== void 0 ? _selectedScenario$sce : null;
});
var scenarioIDPropSelector = createPropsSelector(function (_ref7) {
  var _scenario$id;

  var scenario = _ref7.scenario;
  return (_scenario$id = scenario === null || scenario === void 0 ? void 0 : scenario.id) !== null && _scenario$id !== void 0 ? _scenario$id : null;
});

var propsSelectors = /*#__PURE__*/Object.freeze({
  __proto__: null,
  loanIDPropSelector: loanIDPropSelector,
  loanIDRouteParamSelector: loanIDRouteParamSelector,
  borrowerIDPropSelector: borrowerIDPropSelector,
  underwriterPropSelector: underwriterPropSelector,
  scenarioLabelPropSelector: scenarioLabelPropSelector,
  scenarioLabelFromSelectedScenarioPropSelector: scenarioLabelFromSelectedScenarioPropSelector,
  scenarioIDPropSelector: scenarioIDPropSelector
});

var _excluded$4 = ["owners"];
var ASSET_TYPES_LOOKUP = constants.loanFile.ASSET_TYPES_LOOKUP;
var UNKNOWN_SOURCING = 'Unknown';
var DIGITALLY_SOURCED = 'DigitallySourced';
var assetsSelector = createSelector([loanFileSelector], function (loanFile) {
  return (loanFile === null || loanFile === void 0 ? void 0 : loanFile.assets) || null;
});
var assetFormFieldsSelector = createSelector([assetsSelector], function (assets) {
  return (assets === null || assets === void 0 ? void 0 : assets.fields) || null;
});
var isOtherAssetTypeFormFieldSelector = createSelector([assetFormFieldsSelector], function (fields) {
  var _fields$attributes;

  return (fields === null || fields === void 0 ? void 0 : (_fields$attributes = fields.attributes) === null || _fields$attributes === void 0 ? void 0 : _fields$attributes.type) === ASSET_TYPES_LOOKUP.OTHER;
});
var assetFormErrorsSelector = createSelector([assetsSelector], function (assets) {
  return (assets === null || assets === void 0 ? void 0 : assets.errors) || null;
});
var assetsItemsSelector = createSelector([assetsSelector], function (assets) {
  return (assets === null || assets === void 0 ? void 0 : assets.items) || [];
});
var assetDigitallySourcedStatusByIDSelector = createSelector([assetsItemsSelector], function (items) {
  return items.reduce(function (acc, cur) {
    var _cur$attributes, _Object$assign;

    return Object.assign({}, acc, (_Object$assign = {}, _Object$assign[cur.id] = (cur === null || cur === void 0 ? void 0 : (_cur$attributes = cur.attributes) === null || _cur$attributes === void 0 ? void 0 : _cur$attributes.verificationStatus) === DIGITALLY_SOURCED, _Object$assign));
  }, {});
});
var requestAssetFormFieldsSelector = createSelector([assetFormFieldsSelector, isOtherAssetTypeFormFieldSelector], function (fields, isOtherAssetType) {
  return Object.assign({}, fields, {
    sharedWith: fields !== null && fields !== void 0 && fields.sharedWith ? Object.keys(fields.sharedWith).filter(function (key) {
      return fields.sharedWith[key];
    }) : [],
    attributes: fields !== null && fields !== void 0 && fields.attributes ? omit(fields.attributes, isOtherAssetType ? [] : ['otherTypeSpecified', 'otherTypeLiquid']) : undefined
  });
});
var borrowersAssetsSelector = createSelector([assetsItemsSelector, borrowerIDPropSelector], function (assetsItems, borrowerID) {
  return assetsItems.filter(function (_ref) {
    var owners = _ref.owners;
    return owners.indexOf(borrowerID) !== -1;
  }).map(function (_ref2) {
    var owners = _ref2.owners,
        rest = _objectWithoutProperties(_ref2, _excluded$4);

    return Object.assign({}, rest, {
      sharedWith: getSharedWithFromOwners(owners, borrowerID)
    });
  });
});

var assets = /*#__PURE__*/Object.freeze({
  __proto__: null,
  UNKNOWN_SOURCING: UNKNOWN_SOURCING,
  DIGITALLY_SOURCED: DIGITALLY_SOURCED,
  assetsSelector: assetsSelector,
  assetFormFieldsSelector: assetFormFieldsSelector,
  isOtherAssetTypeFormFieldSelector: isOtherAssetTypeFormFieldSelector,
  assetFormErrorsSelector: assetFormErrorsSelector,
  assetsItemsSelector: assetsItemsSelector,
  assetDigitallySourcedStatusByIDSelector: assetDigitallySourcedStatusByIDSelector,
  requestAssetFormFieldsSelector: requestAssetFormFieldsSelector,
  borrowersAssetsSelector: borrowersAssetsSelector
});

var toJSON = function toJSON(body) {
  return JSON.stringify(body, function (key, value) {
    return value === undefined ? null : value;
  });
};
var toDateString = function toDateString(dateString) {
  if (!dateString) {
    return null;
  }

  try {
    var date = new Date(dateString);
    return date.toISOString().substr(0, 10);
  } catch (_unused) {
    return dateString;
  }
};
var COUNTRY_CODE_PREFIX = '+';
var US_COUNTRY_CODE = '1';
var toPhoneString = function toPhoneString(phoneString) {
  var digits = phoneString === null || phoneString === void 0 ? void 0 : phoneString.replace(/\D/g, '');

  if (!digits) {
    return null;
  }

  var hasCountryCode = digits[0] === US_COUNTRY_CODE && digits.length === 11;
  return "" + COUNTRY_CODE_PREFIX + (hasCountryCode ? '' : US_COUNTRY_CODE) + digits;
};
var removeNullAndUndefined = function removeNullAndUndefined(obj) {
  // used so we don't send empty nested objects to the loan-file-service,
  // which prefers `null` to be received instead of an empty object
  // Remove null values from the array
  if (Array.isArray(obj)) {
    return obj.map(removeNullAndUndefined).filter(function (item) {
      return item !== null;
    });
  }

  if (obj === null || obj === undefined) {
    return null;
  }

  if (!isPlainObject(obj)) {
    return obj;
  } // Remove null values from the object


  var reducedObject = Object.keys(obj).reduce(function (acc, curr) {
    var _Object$assign;

    var emptiedValue = removeNullAndUndefined(obj[curr]);
    var isEmptyArray = Array.isArray(emptiedValue) && emptiedValue.length === 0;
    return !isEmptyArray && emptiedValue !== null && emptiedValue !== '' ? Object.assign({}, acc, (_Object$assign = {}, _Object$assign[curr] = emptiedValue, _Object$assign)) : acc;
  }, {}); // Replace empty object objects with null

  return Object.keys(reducedObject).length === 0 ? null : reducedObject;
};

var _excluded$5 = ["militaryService"],
    _excluded2$1 = ["attributes"],
    _excluded3 = ["na", "onlyNonActivatedReserve"],
    _excluded4 = ["activeDutyExpiration", "militaryStatusType"],
    _excluded5 = ["displayName"],
    _excluded6 = ["addressType", "id", "isResidenceAlsoMailingAddress", "startDate", "endDate"],
    _excluded7 = ["creditConsent", "mailingIsCurrentAddress"],
    _excluded8 = ["attributes"];
var _constants$loanFile$1 = constants.loanFile,
    MILITARY_STATUS_TYPE_LOOKUP = _constants$loanFile$1.MILITARY_STATUS_TYPE_LOOKUP,
    ADDRESS_TYPES_LOOKUP = _constants$loanFile$1.ADDRESS_TYPES_LOOKUP;
var deeplyFormat = formatters.deeplyFormat; // Military service is a union of MilitaryService, NA, and `null`
// NA: { na: true } (only possible NA shape) implies no military service at all
// MilitaryService (exists only if na is not explicitly true) describes the military service of the borrower
// `null` describes the military service not being set either way

var getMilitaryStatusFromMilitaryServiceOrNA = function getMilitaryStatusFromMilitaryServiceOrNA(militaryService) {
  // null represents the borrowers not having any military status set, and is set as EMPTY
  if (!militaryService) {
    return MILITARY_STATUS_TYPE_LOOKUP.EMPTY;
  } // NA shape maps to explicitly non-military


  if (militaryService.na) {
    return MILITARY_STATUS_TYPE_LOOKUP.NONE;
  } // if military service is described, map the status type appropriately


  return militaryService.onlyNonActivatedReserve ? MILITARY_STATUS_TYPE_LOOKUP.NAT_GUARD_OR_RESERVES : MILITARY_STATUS_TYPE_LOOKUP.REGULAR;
};
var fromAPI = function fromAPI(borrower) {
  var _borrower$eConsent;

  var _borrower$attributes = borrower.attributes;
  _borrower$attributes = _borrower$attributes === void 0 ? {} : _borrower$attributes;

  var militaryService = _borrower$attributes.militaryService,
      attributes = _objectWithoutProperties(_borrower$attributes, _excluded$5),
      restOfBorrower = _objectWithoutProperties(borrower, _excluded2$1);

  if (attributes.mailingAddress) {
    var _attributes$residence, _attributes$residence2;

    var _ref = (_attributes$residence = (_attributes$residence2 = attributes.residences) === null || _attributes$residence2 === void 0 ? void 0 : _attributes$residence2.find(function (history) {
      return history.current;
    })) !== null && _attributes$residence !== void 0 ? _attributes$residence : {},
        currentAddress = _ref.address;

    attributes.mailingIsCurrentAddress = isEqual(currentAddress, attributes.mailingAddress);
  }

  if (militaryService) {
    var na = militaryService.na,
        onlyNonActivatedReserve = militaryService.onlyNonActivatedReserve,
        restOfMilitaryService = _objectWithoutProperties(militaryService, _excluded3);

    attributes.militaryService = Object.assign({
      militaryStatusType: getMilitaryStatusFromMilitaryServiceOrNA(militaryService)
    }, restOfMilitaryService);
  }

  return Object.assign({}, restOfBorrower, {
    eConsent: {
      granted: !!((_borrower$eConsent = borrower.eConsent) !== null && _borrower$eConsent !== void 0 && _borrower$eConsent.grantedUTC)
    },
    attributes: attributes
  });
};
var borrowerAttributesFormatters = {
  birthDate: toDateString,
  militaryService: function militaryService(_militaryService) {
    var _ref2 = _militaryService || {},
        activeDutyExpiration = _ref2.activeDutyExpiration,
        militaryStatusType = _ref2.militaryStatusType,
        restOfMilitaryService = _objectWithoutProperties(_ref2, _excluded4);

    if (!militaryStatusType || militaryStatusType === MILITARY_STATUS_TYPE_LOOKUP.EMPTY) {
      return null;
    }

    return militaryStatusType === MILITARY_STATUS_TYPE_LOOKUP.NONE ? {
      na: true
    } : Object.assign({}, restOfMilitaryService, {
      activeDutyExpiration: toDateString(activeDutyExpiration),
      onlyNonActivatedReserve: militaryStatusType === MILITARY_STATUS_TYPE_LOOKUP.NAT_GUARD_OR_RESERVES
    });
  },
  names: function names(_names) {
    return (removeNullAndUndefined(_names) || []).map(function (_ref3) {
      var displayName = _ref3.displayName,
          rest = _objectWithoutProperties(_ref3, _excluded5);

      return rest;
    });
  },
  cellPhone: toPhoneString,
  homePhone: toPhoneString,
  workPhoneAndExtension: {
    phone: toPhoneString
  },
  email: function email(_email) {
    return _email || null;
  },
  dependents: function dependents(_dependents) {
    return _dependents !== null && _dependents !== void 0 ? _dependents : [];
  },
  taxpayerID: function taxpayerID(_taxpayerID) {
    return (_taxpayerID === null || _taxpayerID === void 0 ? void 0 : _taxpayerID.replace(/\D/g, '')) || null;
  },
  residences: function residences(_residences) {
    return (removeNullAndUndefined(_residences) || []).map(function (_ref4) {
      var addressType = _ref4.addressType,
          id = _ref4.id,
          isResidenceAlsoMailingAddress = _ref4.isResidenceAlsoMailingAddress,
          startDate = _ref4.startDate,
          endDate = _ref4.endDate,
          rest = _objectWithoutProperties(_ref4, _excluded6);

      return Object.assign({}, rest, {
        startDate: toDateString(startDate),
        endDate: toDateString(endDate)
      });
    });
  },
  mailingAddress: removeNullAndUndefined
};
var toAPI = function toAPI(_ref5) {
  var _ref5$attributes = _ref5.attributes;
  _ref5$attributes = _ref5$attributes === void 0 ? {} : _ref5$attributes;

  var creditConsent = _ref5$attributes.creditConsent,
      mailingIsCurrentAddress = _ref5$attributes.mailingIsCurrentAddress,
      attributes = _objectWithoutProperties(_ref5$attributes, _excluded7),
      topLevelBorrowerProperties = _objectWithoutProperties(_ref5, _excluded8);

  var formattedAttributes = deeplyFormat(attributes, pick(borrowerAttributesFormatters, Object.keys(attributes)), false);
  return Object.assign({}, topLevelBorrowerProperties, {
    attributes: Object.keys(formattedAttributes).reduce(function (acc, curr) {
      var _Object$assign;

      return Object.assign({}, acc, (_Object$assign = {}, _Object$assign[curr] = removeNullAndUndefined(formattedAttributes[curr]), _Object$assign));
    }, {})
  });
};

var getBorrowerAddressAttributes = function getBorrowerAddressAttributes(addressItems) {
  var _addressItems$find;

  var mailingAddress = addressItems === null || addressItems === void 0 ? void 0 : (_addressItems$find = addressItems.find(function (_ref6) {
    var addressType = _ref6.addressType,
        isResidenceAlsoMailingAddress = _ref6.isResidenceAlsoMailingAddress;
    return addressType === ADDRESS_TYPES_LOOKUP.MAILING_ADDRESS || isResidenceAlsoMailingAddress;
  })) === null || _addressItems$find === void 0 ? void 0 : _addressItems$find.address;
  var residences = addressItems === null || addressItems === void 0 ? void 0 : addressItems.filter(function (_ref7) {
    var addressType = _ref7.addressType;
    return addressType !== ADDRESS_TYPES_LOOKUP.MAILING_ADDRESS;
  }).map(function (residence) {
    return Object.assign({}, residence, {
      current: residence.addressType === ADDRESS_TYPES_LOOKUP.CURRENT_ADDRESS
    });
  });
  return {
    residences: residences,
    mailingAddress: mailingAddress
  };
};

var addressOneLineSummary = function addressOneLineSummary(address, index) {
  var _address$address$stre, _address$address;

  return ((_address$address$stre = address === null || address === void 0 ? void 0 : (_address$address = address.address) === null || _address$address === void 0 ? void 0 : _address$address.street) !== null && _address$address$stre !== void 0 ? _address$address$stre : '') + "-" + index;
};

var addressItemsToAPI = function addressItemsToAPI(items) {
  return toAPI({
    attributes: getBorrowerAddressAttributes(items)
  });
};

var getAddressesFromBorrower = function getAddressesFromBorrower(_ref8) {
  var _ref8$attributes = _ref8.attributes;
  _ref8$attributes = _ref8$attributes === void 0 ? {} : _ref8$attributes;
  var mailingAddress = _ref8$attributes.mailingAddress,
      _ref8$attributes$resi = _ref8$attributes.residences,
      residences = _ref8$attributes$resi === void 0 ? [] : _ref8$attributes$resi;
  var addressesFromResidences = residences.map(function (residence, index) {
    return Object.assign({}, residence, {
      id: addressOneLineSummary(residence, index),
      addressType: residence !== null && residence !== void 0 && residence.current ? ADDRESS_TYPES_LOOKUP.CURRENT_ADDRESS : ADDRESS_TYPES_LOOKUP.PREVIOUS_ADDRESS,
      isResidenceAlsoMailingAddress: isEqual(residence === null || residence === void 0 ? void 0 : residence.address, mailingAddress)
    });
  });
  var residencesHaveMailingAddress = addressesFromResidences === null || addressesFromResidences === void 0 ? void 0 : addressesFromResidences.some(function (_ref9) {
    var isResidenceAlsoMailingAddress = _ref9.isResidenceAlsoMailingAddress;
    return isResidenceAlsoMailingAddress;
  });

  if (!residencesHaveMailingAddress && mailingAddress) {
    return addressesFromResidences.concat({
      id: addressOneLineSummary(mailingAddress, addressesFromResidences.length),
      address: mailingAddress,
      addressType: ADDRESS_TYPES_LOOKUP.MAILING_ADDRESS
    });
  }

  return addressesFromResidences;
};

var addressItemsFromAPI = function addressItemsFromAPI(borrowers) {
  return borrowers.reduce(function (acc, borrower) {
    var _Object$assign2;

    return Object.assign({}, acc, (_Object$assign2 = {}, _Object$assign2[borrower.id] = getAddressesFromBorrower(borrower), _Object$assign2));
  }, {});
};
var fromPreQualifyFieldsToBorrowerAttributes = function fromPreQualifyFieldsToBorrowerAttributes(_ref10) {
  var socialSecurityNumber = _ref10.socialSecurityNumber,
      address = _ref10.address;
  return {
    taxpayerID: socialSecurityNumber,
    residences: address && [{
      current: true,
      address: address
    }]
  };
};

var borrowerPairsSelector = createSelector([loanFileSelector], function (loanFile) {
  var _loanFile$borrowerPai, _loanFile$borrowerPai2;

  return (_loanFile$borrowerPai = loanFile === null || loanFile === void 0 ? void 0 : (_loanFile$borrowerPai2 = loanFile.borrowerPairs) === null || _loanFile$borrowerPai2 === void 0 ? void 0 : _loanFile$borrowerPai2.pairs) !== null && _loanFile$borrowerPai !== void 0 ? _loanFile$borrowerPai : [];
});

var borrowerPairs = /*#__PURE__*/Object.freeze({
  __proto__: null,
  borrowerPairsSelector: borrowerPairsSelector
});

var _excluded$6 = ["attributes"];
var DEFAULT_ERROR_MESSAGES = constants.DEFAULT_ERROR_MESSAGES;
var date = formatters.date,
    phoneNumber = formatters.phoneNumber,
    socialSecurityNumber = formatters.socialSecurityNumber;
var dateString = function dateString(val) {
  var result = val && date(val);
  return result === null ? val : result;
};
var blendEmailsByIDSelector = createSelector([loanFileSelector], function (loanFile) {
  var _loanFile$borrowers$b, _loanFile$borrowers;

  return (_loanFile$borrowers$b = loanFile === null || loanFile === void 0 ? void 0 : (_loanFile$borrowers = loanFile.borrowers) === null || _loanFile$borrowers === void 0 ? void 0 : _loanFile$borrowers.blendEmails) !== null && _loanFile$borrowers$b !== void 0 ? _loanFile$borrowers$b : {};
});
var borrowerIDsSelector = createSelector([loanFileSelector], function (loanFile) {
  var _loanFile$borrowers$i, _loanFile$borrowers2;

  return Object.keys((_loanFile$borrowers$i = loanFile === null || loanFile === void 0 ? void 0 : (_loanFile$borrowers2 = loanFile.borrowers) === null || _loanFile$borrowers2 === void 0 ? void 0 : _loanFile$borrowers2.items) !== null && _loanFile$borrowers$i !== void 0 ? _loanFile$borrowers$i : {});
});
var borrowerOrderSelector = createSelector([borrowerPairsSelector], function (pairs) {
  return pairs.flat();
});
var borrowerAPIErrorsSelector = createSelector([loanFileSelector], function (loanFile) {
  var _loanFile$borrowers$a;

  return (_loanFile$borrowers$a = loanFile === null || loanFile === void 0 ? void 0 : loanFile.borrowers.apiErrors) !== null && _loanFile$borrowers$a !== void 0 ? _loanFile$borrowers$a : {};
});
var apiPathToValidatableLocalPath = {
  'body.attributes.homePhone': 'attributes.homePhone',
  'body.attributes.cellPhone': 'attributes.cellPhone'
};
var validationErrorTypeToMessage = {
  'value_error.phone': DEFAULT_ERROR_MESSAGES.PHONE
};
var borrowerAPIFieldValidationErrorsSelector = createSelector([borrowerIDPropSelector, borrowerAPIErrorsSelector], function (borrowerID, apiErrors) {
  var _borrowerError$error;

  var borrowerError = apiErrors[borrowerID];

  if ((borrowerError === null || borrowerError === void 0 ? void 0 : borrowerError.type) !== 'ValidationError') {
    return {};
  }

  return (((_borrowerError$error = borrowerError.error) === null || _borrowerError$error === void 0 ? void 0 : _borrowerError$error.detail) || []).reduce(function (memo, errorDetail) {
    var _validationErrorTypeT;

    var _errorDetail$loc = errorDetail.loc,
        loc = _errorDetail$loc === void 0 ? [] : _errorDetail$loc,
        type = errorDetail.type;
    var apiPath = loc.join('.');
    var validatableLocalPath = apiPathToValidatableLocalPath[apiPath];
    var message = (_validationErrorTypeT = validationErrorTypeToMessage[type]) !== null && _validationErrorTypeT !== void 0 ? _validationErrorTypeT : DEFAULT_ERROR_MESSAGES.VALUE;

    if (validatableLocalPath && message) {
      var _Object$assign;

      return Object.assign({}, memo, (_Object$assign = {}, _Object$assign[validatableLocalPath] = message, _Object$assign));
    }

    return memo;
  }, {});
});

var getDisplayName = function getDisplayName(_ref, index) {
  var first = _ref.first,
      last = _ref.last;
  return first && last ? first + " " + last : "Borrower " + (index + 1);
};

var borrowersByIDSelector = createSelector([loanFileSelector, borrowerOrderSelector], function (loanFile, borrowerOrder) {
  var _loanFile$borrowers$i2, _loanFile$borrowers3;

  var borrowers = (_loanFile$borrowers$i2 = loanFile === null || loanFile === void 0 ? void 0 : (_loanFile$borrowers3 = loanFile.borrowers) === null || _loanFile$borrowers3 === void 0 ? void 0 : _loanFile$borrowers3.items) !== null && _loanFile$borrowers$i2 !== void 0 ? _loanFile$borrowers$i2 : {};
  return Object.entries(borrowers).reduce(function (acc, _ref2) {
    var _attributes$workPhone, _Object$assign2;

    var _ref3 = _slicedToArray(_ref2, 2),
        borrowerID = _ref3[0],
        _ref3$ = _ref3[1],
        _ref3$$attributes = _ref3$.attributes,
        attributes = _ref3$$attributes === void 0 ? {} : _ref3$$attributes,
        borrower = _objectWithoutProperties(_ref3$, _excluded$6);

    return Object.assign({}, acc, (_Object$assign2 = {}, _Object$assign2[borrowerID] = Object.assign({}, borrower, {
      attributes: Object.assign({}, attributes, {
        birthDate: dateString(attributes.birthDate),
        cellPhone: phoneNumber(attributes.cellPhone),
        homePhone: phoneNumber(attributes.homePhone),
        workPhoneAndExtension: Object.assign({}, attributes.workPhoneAndExtension, {
          phone: phoneNumber((_attributes$workPhone = attributes.workPhoneAndExtension) === null || _attributes$workPhone === void 0 ? void 0 : _attributes$workPhone.phone)
        }),
        taxpayerID: socialSecurityNumber(attributes.taxpayerID),
        dependents: attributes.dependents || [{}],
        names: (attributes.names || [{}]).map(function (name) {
          return Object.assign({}, name, {
            displayName: getDisplayName(name, borrowerOrder.indexOf(borrowerID))
          });
        })
      })
    }), _Object$assign2));
  }, {});
});
var primaryBorrowerIDSelector = createSelector([borrowersByIDSelector], function (borrowersByID) {
  var _primaryBorrower$id;

  var allBorrowers = Object.values(borrowersByID);
  var allBorrowersSortedCreatedUTCAscending = allBorrowers.sort(function (_ref4, _ref5) {
    var aBorrowerCreatedUTC = _ref4.createdUTC;
    var bBorrowerCreatedUTC = _ref5.createdUTC;
    return new Date(aBorrowerCreatedUTC).getTime() - new Date(bBorrowerCreatedUTC).getTime();
  });
  var primaryBorrower = allBorrowersSortedCreatedUTCAscending[0];
  return (_primaryBorrower$id = primaryBorrower === null || primaryBorrower === void 0 ? void 0 : primaryBorrower.id) !== null && _primaryBorrower$id !== void 0 ? _primaryBorrower$id : null;
});
var primaryBorrowerSelector = createSelector([borrowersByIDSelector, primaryBorrowerIDSelector], function (borrowersByID, primaryBorrowerID) {
  return primaryBorrowerID ? borrowersByID[primaryBorrowerID] : null;
});
var primaryBorrowerNameSelector = createSelector([primaryBorrowerSelector], function (borrower) {
  var _borrower$attributes$, _borrower$attributes, _borrower$attributes$2;

  var _ref6 = (_borrower$attributes$ = borrower === null || borrower === void 0 ? void 0 : (_borrower$attributes = borrower.attributes) === null || _borrower$attributes === void 0 ? void 0 : (_borrower$attributes$2 = _borrower$attributes.names) === null || _borrower$attributes$2 === void 0 ? void 0 : _borrower$attributes$2[0]) !== null && _borrower$attributes$ !== void 0 ? _borrower$attributes$ : {},
      first = _ref6.first,
      last = _ref6.last;

  return first && last ? last + ", " + first : null;
});
var coBorrowerIDsSelector = createSelector([borrowerOrderSelector, primaryBorrowerIDSelector], function (borrowerOrder, primaryBorrowerID) {
  return borrowerOrder.filter(function (borrowerID) {
    return borrowerID !== primaryBorrowerID;
  });
});
var borrowerListSelector = createSelector([borrowersByIDSelector, borrowerOrderSelector], function (borrowersByID, borrowerOrder) {
  return borrowerOrder.map(function (borrowerID) {
    return borrowersByID[borrowerID];
  }).filter(Boolean);
});
var borrowerDisplayNamesSelector = createSelector([borrowerListSelector], function (borrowers) {
  return borrowers.reduce(function (memo, borrower) {
    var _borrower$attributes2, _borrower$attributes3, _borrower$attributes4, _Object$assign3;

    return Object.assign({}, memo, (_Object$assign3 = {}, _Object$assign3[borrower === null || borrower === void 0 ? void 0 : borrower.id] = borrower === null || borrower === void 0 ? void 0 : (_borrower$attributes2 = borrower.attributes) === null || _borrower$attributes2 === void 0 ? void 0 : (_borrower$attributes3 = _borrower$attributes2.names) === null || _borrower$attributes3 === void 0 ? void 0 : (_borrower$attributes4 = _borrower$attributes3[0]) === null || _borrower$attributes4 === void 0 ? void 0 : _borrower$attributes4.displayName, _Object$assign3));
  }, {});
});
var loanFileMilitaryServiceSelector = createSelector([borrowerListSelector], function (borrowers) {
  var _borrowers$find$attri, _borrowers$find, _borrowers$find$attri2;

  return (_borrowers$find$attri = (_borrowers$find = borrowers.find(function (borrower) {
    var _borrower$attributes$3;

    return !((_borrower$attributes$3 = borrower.attributes.militaryService) !== null && _borrower$attributes$3 !== void 0 && _borrower$attributes$3.na);
  })) === null || _borrowers$find === void 0 ? void 0 : (_borrowers$find$attri2 = _borrowers$find.attributes) === null || _borrowers$find$attri2 === void 0 ? void 0 : _borrowers$find$attri2.militaryService) !== null && _borrowers$find$attri !== void 0 ? _borrowers$find$attri : null;
});
var loanFileFirstTimeHomeBuyerSelector = createSelector([borrowerListSelector], function (borrowers) {
  return borrowers.some(function (borrower) {
    return borrower.attributes.firstTimeBuyer;
  });
});
var otherBorrowersSelector = createSelector([borrowerListSelector, borrowerIDPropSelector], function (borrowerList, selectedBorrowerID) {
  return borrowerList.filter(function (_ref7) {
    var id = _ref7.id;
    return id !== selectedBorrowerID;
  });
});
var wereBorrowersFetchedSelector = createSelector([loanFileSelector], function (loanFile) {
  return !!(loanFile !== null && loanFile !== void 0 && loanFile.borrowers.fetched);
});
var borrowerSelector = createSelector([borrowerIDPropSelector, borrowersByIDSelector], function (borrowerID, borrowersByID) {
  return borrowersByID[borrowerID] || null;
});
var addressItemsSelector = createSelector([borrowerListSelector], function (borrowers) {
  return addressItemsFromAPI(borrowers);
});

var sortByDescendingStartDate = function sortByDescendingStartDate(_ref8, _ref9) {
  var _ref8$startDate = _ref8.startDate,
      firstStartDate = _ref8$startDate === void 0 ? null : _ref8$startDate;
  var _ref9$startDate = _ref9.startDate,
      secondStartDate = _ref9$startDate === void 0 ? null : _ref9$startDate;
  return new Date(secondStartDate) - new Date(firstStartDate);
};

var borrowersAddressesSelector = createSelector([addressItemsSelector, borrowerIDPropSelector], function (addresses, borrowerID) {
  var _addresses$borrowerID;

  return ((_addresses$borrowerID = addresses[borrowerID]) !== null && _addresses$borrowerID !== void 0 ? _addresses$borrowerID : []).sort(sortByDescendingStartDate);
});

var borrowers = /*#__PURE__*/Object.freeze({
  __proto__: null,
  dateString: dateString,
  blendEmailsByIDSelector: blendEmailsByIDSelector,
  borrowerIDsSelector: borrowerIDsSelector,
  borrowerOrderSelector: borrowerOrderSelector,
  borrowerAPIErrorsSelector: borrowerAPIErrorsSelector,
  borrowerAPIFieldValidationErrorsSelector: borrowerAPIFieldValidationErrorsSelector,
  borrowersByIDSelector: borrowersByIDSelector,
  primaryBorrowerIDSelector: primaryBorrowerIDSelector,
  primaryBorrowerSelector: primaryBorrowerSelector,
  primaryBorrowerNameSelector: primaryBorrowerNameSelector,
  coBorrowerIDsSelector: coBorrowerIDsSelector,
  borrowerListSelector: borrowerListSelector,
  borrowerDisplayNamesSelector: borrowerDisplayNamesSelector,
  loanFileMilitaryServiceSelector: loanFileMilitaryServiceSelector,
  loanFileFirstTimeHomeBuyerSelector: loanFileFirstTimeHomeBuyerSelector,
  otherBorrowersSelector: otherBorrowersSelector,
  wereBorrowersFetchedSelector: wereBorrowersFetchedSelector,
  borrowerSelector: borrowerSelector,
  addressItemsSelector: addressItemsSelector,
  borrowersAddressesSelector: borrowersAddressesSelector
});

var LOAN_TYPES_LOOKUP = constants.loanFile.LOAN_TYPES_LOOKUP;
var loanAttributesBaseSelector = createSelector([loanFileSelector], function (loanFile) {
  var _loanFile$loanAttribu;

  return (_loanFile$loanAttribu = loanFile === null || loanFile === void 0 ? void 0 : loanFile.loanAttributes) !== null && _loanFile$loanAttribu !== void 0 ? _loanFile$loanAttribu : null;
});
var loanAttributesSelector = createSelector([loanAttributesBaseSelector], function (loanAttributes) {
  var _loanAttributes$attri;

  return (_loanAttributes$attri = loanAttributes === null || loanAttributes === void 0 ? void 0 : loanAttributes.attributes) !== null && _loanAttributes$attri !== void 0 ? _loanAttributes$attri : null;
});
var computedLoanAttributesSelector = createSelector([loanAttributesBaseSelector], function (loanFileAttributes) {
  var _loanFileAttributes$c;

  return (_loanFileAttributes$c = loanFileAttributes === null || loanFileAttributes === void 0 ? void 0 : loanFileAttributes.computed) !== null && _loanFileAttributes$c !== void 0 ? _loanFileAttributes$c : null;
});
var loanFileCountyNameSelector = createSelector([computedLoanAttributesSelector], function (computed) {
  var _computed$addressCoun, _computed$addressCoun2;

  return (_computed$addressCoun = computed === null || computed === void 0 ? void 0 : (_computed$addressCoun2 = computed.addressCountyDetails) === null || _computed$addressCoun2 === void 0 ? void 0 : _computed$addressCoun2.name) !== null && _computed$addressCoun !== void 0 ? _computed$addressCoun : null;
});
var loanPurposeSelector = createSelector([loanAttributesSelector], function (attributes) {
  var _attributes$loanPurpo;

  return (_attributes$loanPurpo = attributes === null || attributes === void 0 ? void 0 : attributes.loanPurpose) !== null && _attributes$loanPurpo !== void 0 ? _attributes$loanPurpo : null;
});
var isRefinanceLoanSelector = createSelector([loanPurposeSelector], function (loanPurpose) {
  return loanPurpose === LOAN_TYPES_LOOKUP.REFINANCE;
});
var propertyCountyFIPSSelector = createSelector([loanAttributesBaseSelector], function (loanAttributes) {
  var _loanAttributes$prope;

  return (_loanAttributes$prope = loanAttributes === null || loanAttributes === void 0 ? void 0 : loanAttributes.propertyCountyFIPS) !== null && _loanAttributes$prope !== void 0 ? _loanAttributes$prope : null;
});

var loanAttributes = /*#__PURE__*/Object.freeze({
  __proto__: null,
  loanAttributesBaseSelector: loanAttributesBaseSelector,
  loanAttributesSelector: loanAttributesSelector,
  computedLoanAttributesSelector: computedLoanAttributesSelector,
  loanFileCountyNameSelector: loanFileCountyNameSelector,
  loanPurposeSelector: loanPurposeSelector,
  isRefinanceLoanSelector: isRefinanceLoanSelector,
  propertyCountyFIPSSelector: propertyCountyFIPSSelector
});

var ownedRealEstatesSelector = createSelector([loanFileSelector], function (loanFile) {
  return (loanFile === null || loanFile === void 0 ? void 0 : loanFile.ownedRealEstates) || null;
});
var ownedRealEstatesFormFieldsSelector = createSelector([ownedRealEstatesSelector], function (ownedRealEstates) {
  return (ownedRealEstates === null || ownedRealEstates === void 0 ? void 0 : ownedRealEstates.fields) || null;
});
var requestOwnedRealEstatesFieldsSelector = createSelector([ownedRealEstatesFormFieldsSelector], function (fields) {
  return fields ? Object.assign({}, fields, {
    sharedWith: fields !== null && fields !== void 0 && fields.sharedWith ? Object.keys(fields.sharedWith).filter(function (key) {
      return fields.sharedWith[key];
    }) : []
  }) : null;
});
var ownedRealEstatesItemsSelector = createSelector([ownedRealEstatesSelector], function (ownedRealEstates) {
  var _ownedRealEstates$ite;

  return (_ownedRealEstates$ite = ownedRealEstates === null || ownedRealEstates === void 0 ? void 0 : ownedRealEstates.items) !== null && _ownedRealEstates$ite !== void 0 ? _ownedRealEstates$ite : [];
});
var ownedRealEstatesItemsByIDSelector = byID(ownedRealEstatesItemsSelector);

var ownedRealEstatesBase = /*#__PURE__*/Object.freeze({
  __proto__: null,
  ownedRealEstatesSelector: ownedRealEstatesSelector,
  ownedRealEstatesFormFieldsSelector: ownedRealEstatesFormFieldsSelector,
  requestOwnedRealEstatesFieldsSelector: requestOwnedRealEstatesFieldsSelector,
  ownedRealEstatesItemsSelector: ownedRealEstatesItemsSelector,
  ownedRealEstatesItemsByIDSelector: ownedRealEstatesItemsByIDSelector
});

var COUNTRIES_LOOKUP = constants.COUNTRIES_LOOKUP;
var displayAddress = formatters.displayAddress;
var isRestrictedToUSPropSelector = createPropsSelector(function (_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      isRestrictedToUS = _ref.isRestrictedToUS;

  return isRestrictedToUS !== null && isRestrictedToUS !== void 0 ? isRestrictedToUS : false;
});
var allBorrowersAddressesSelector = createSelector([addressItemsSelector], function (addressItems) {
  return flatten(Object.values(addressItems)).map(function (_ref2) {
    var address = _ref2.address;
    return address;
  });
});
var allOwnedRealEstatesAddressesSelector = createSelector([ownedRealEstatesItemsSelector], function (ownedRealEstatesItems) {
  return ownedRealEstatesItems.map(function (_ref3) {
    var address = _ref3.attributes.address;
    return address;
  });
});

var mapLoanAttributesToAddress = function mapLoanAttributesToAddress(_ref4) {
  var addressStreet = _ref4.addressStreet,
      addressUnit = _ref4.addressUnit,
      addressCity = _ref4.addressCity,
      addressState = _ref4.addressState,
      addressZIP = _ref4.addressZIP;
  return {
    street: addressStreet,
    unit: addressUnit,
    city: addressCity,
    subdivision: addressState,
    code: addressZIP,
    country: COUNTRIES_LOOKUP.US // subject property is strictly in the US

  };
};

var hasPropertyAddress = function hasPropertyAddress(_ref5) {
  var addressStreet = _ref5.addressStreet,
      addressUnit = _ref5.addressUnit,
      addressCity = _ref5.addressCity,
      addressState = _ref5.addressState,
      addressZIP = _ref5.addressZIP;
  return Boolean(addressStreet || addressUnit || addressCity || addressState || addressZIP);
};

var allLoanAttributesAddressesSelector = createSelector([loanAttributesSelector], function (loanAttributes) {
  return hasPropertyAddress(loanAttributes !== null && loanAttributes !== void 0 ? loanAttributes : {}) ? [mapLoanAttributesToAddress(loanAttributes)] : [];
});
var uniqueLoanFileAddressesSelector = createSelector([allBorrowersAddressesSelector, allOwnedRealEstatesAddressesSelector, allLoanAttributesAddressesSelector, isRestrictedToUSPropSelector], function (allBorrowersAddresses, allOwnedRealEstatesAddresses, allLoanAttributesAddresses, isRestrictedToUS) {
  return uniqBy([].concat(_toConsumableArray(allBorrowersAddresses), _toConsumableArray(allOwnedRealEstatesAddresses), _toConsumableArray(allLoanAttributesAddresses)).filter(function (_ref6) {
    var country = _ref6.country;
    return isRestrictedToUS ? country === COUNTRIES_LOOKUP.US : true;
  }), displayAddress);
});

var addresses = /*#__PURE__*/Object.freeze({
  __proto__: null,
  isRestrictedToUSPropSelector: isRestrictedToUSPropSelector,
  allBorrowersAddressesSelector: allBorrowersAddressesSelector,
  allOwnedRealEstatesAddressesSelector: allOwnedRealEstatesAddressesSelector,
  allLoanAttributesAddressesSelector: allLoanAttributesAddressesSelector,
  uniqueLoanFileAddressesSelector: uniqueLoanFileAddressesSelector
});

var giftsSelector = createSelector([loanFileSelector], function (loanFile) {
  return (loanFile === null || loanFile === void 0 ? void 0 : loanFile.gifts) || null;
});
var giftsItemsSelector = createSelector([giftsSelector], function (gifts) {
  return (gifts === null || gifts === void 0 ? void 0 : gifts.items) || [];
});
var borrowersGiftsSelector = createSelector([giftsItemsSelector, borrowerIDPropSelector], function (giftsItems, borrowerID) {
  return giftsItems.filter(function (item) {
    return item.borrowerID === borrowerID;
  });
});
var giftFormFieldsSelector = createSelector([giftsSelector], function (gifts) {
  return (gifts === null || gifts === void 0 ? void 0 : gifts.fields) || null;
});
var isGiftFormFieldsSet = createSelector([giftsSelector], function (gifts) {
  return gifts === null || gifts === void 0 ? void 0 : gifts.fieldsSet;
});

var gifts = /*#__PURE__*/Object.freeze({
  __proto__: null,
  giftsSelector: giftsSelector,
  giftsItemsSelector: giftsItemsSelector,
  borrowersGiftsSelector: borrowersGiftsSelector,
  giftFormFieldsSelector: giftFormFieldsSelector,
  isGiftFormFieldsSet: isGiftFormFieldsSet
});

var borrowersCombinedAssetsAndGiftsSelector = createSelector([borrowersAssetsSelector, borrowersGiftsSelector], function (assetItems, giftsItems) {
  return [].concat(_toConsumableArray(assetItems), _toConsumableArray(giftsItems));
});
var assetBalanceSelector = createSelector([assetsItemsSelector, giftsItemsSelector], function (assetsItems, giftItems) {
  return [].concat(_toConsumableArray(assetsItems), _toConsumableArray(giftItems)).reduce(function (prev, _ref) {
    var marketValue = _ref.attributes.marketValue;
    return prev + marketValue;
  }, 0);
});

var assetsAndGifts = /*#__PURE__*/Object.freeze({
  __proto__: null,
  borrowersCombinedAssetsAndGiftsSelector: borrowersCombinedAssetsAndGiftsSelector,
  assetBalanceSelector: assetBalanceSelector
});

var CONNECTION_ERROR_MESSAGE = "We're sorry, an error has occurred. If the problem continues, please try again later.";

var _excluded$7 = ["income", "phone", "startDate", "endDate", "source", "employmentStatus", "noAddress"];
var _constants$loanFile$2 = constants.loanFile,
    _constants$loanFile$E = _constants$loanFile$2.EMPLOYMENT_STATUS_LOOKUP,
    CURRENT = _constants$loanFile$E.CURRENT,
    PREVIOUS = _constants$loanFile$E.PREVIOUS,
    EMPLOYMENT = _constants$loanFile$2.EMPLOYMENT_TYPE_LOOKUP.EMPLOYMENT;
var employmentsSelector = createSelector([loanFileSelector], function (loanFile) {
  return (loanFile === null || loanFile === void 0 ? void 0 : loanFile.employments) || null;
});
var employmentsFormFieldsSelector = createSelector([employmentsSelector], function (employments) {
  return (employments === null || employments === void 0 ? void 0 : employments.fields) || null;
});
var employmentsFormFieldsErrorsSelector = createSelector([employmentsSelector], function (employments) {
  return (employments === null || employments === void 0 ? void 0 : employments.errors) || [];
});
var baseEmploymentsItemsSelector = createSelector([employmentsSelector], function (employments) {
  var _employments$items;

  return (_employments$items = employments === null || employments === void 0 ? void 0 : employments.items) !== null && _employments$items !== void 0 ? _employments$items : [];
});

var extractIncomeFromEmployment = function extractIncomeFromEmployment(employment) {
  var income = employment.attributes.income;

  if (employment.attributes.selfEmployed || employment.attributes.endDate) {
    return income;
  } else {
    var _income$base;

    return Object.assign({}, income, {
      frequency: (_income$base = income.base) === null || _income$base === void 0 ? void 0 : _income$base.frequency,
      amount: Object.keys(income).reduce(function (acc, curr) {
        var _income$curr$amount, _income$curr;

        return acc + ((_income$curr$amount = (_income$curr = income[curr]) === null || _income$curr === void 0 ? void 0 : _income$curr.amount) !== null && _income$curr$amount !== void 0 ? _income$curr$amount : 0);
      }, 0)
    });
  }
};

var employmentsItemsSelector = createSelector([baseEmploymentsItemsSelector], function (items) {
  return items.map(function (employment) {
    var _employment$attribute, _employment$attribute2, _employment$attribute3;

    return Object.assign({}, employment, {
      attributes: Object.assign({}, employment === null || employment === void 0 ? void 0 : employment.attributes, {
        noAddress: !(employment !== null && employment !== void 0 && (_employment$attribute = employment.attributes) !== null && _employment$attribute !== void 0 && _employment$attribute.address),
        employmentStatus: employment !== null && employment !== void 0 && (_employment$attribute2 = employment.attributes) !== null && _employment$attribute2 !== void 0 && _employment$attribute2.endDate ? PREVIOUS : CURRENT,
        source: EMPLOYMENT,
        income: employment !== null && employment !== void 0 && (_employment$attribute3 = employment.attributes) !== null && _employment$attribute3 !== void 0 && _employment$attribute3.income ? extractIncomeFromEmployment(employment) : undefined
      })
    });
  });
});
var employmentDigitallySourcedStatusByIDSelector = createSelector([employmentsItemsSelector], function (items) {
  return items.reduce(function (acc, cur) {
    var _cur$attributes, _Object$assign;

    return Object.assign({}, acc, (_Object$assign = {}, _Object$assign[cur.id] = (cur === null || cur === void 0 ? void 0 : (_cur$attributes = cur.attributes) === null || _cur$attributes === void 0 ? void 0 : _cur$attributes.verificationStatus) === DIGITALLY_SOURCED, _Object$assign));
  }, {});
});
var borrowersEmploymentsSelector = createSelector([employmentsItemsSelector, borrowerIDPropSelector], function (employmentsItems, borrowerID) {
  return employmentsItems.filter(function (item) {
    return item.borrowerID === borrowerID;
  });
});
var isEmploymentFormFieldsSetSelector = createSelector([employmentsSelector], function (employments) {
  return employments === null || employments === void 0 ? void 0 : employments.fieldsSet;
});

var getEmploymentIncomeBreakdown = function getEmploymentIncomeBreakdown(income) {
  if (income === void 0) {
    income = {};
  }

  var breakdown = Object.keys(income).filter(function (type) {
    var _income$type;

    return (_income$type = income[type]) === null || _income$type === void 0 ? void 0 : _income$type.amount;
  }).reduce(function (acc, curr) {
    var _Object$assign2;

    return Object.assign({}, acc, (_Object$assign2 = {}, _Object$assign2[curr] = Object.assign({}, income[curr], {
      frequency: typeof income[curr].frequency !== 'undefined' ? income[curr].frequency : income.frequency
    }), _Object$assign2));
  }, {});
  return isEmpty(breakdown) ? undefined : breakdown;
};

var isSimpleIncomeStream = function isSimpleIncomeStream(_ref) {
  var source = _ref.source,
      selfEmployed = _ref.selfEmployed,
      employmentStatus = _ref.employmentStatus;
  return source !== EMPLOYMENT || selfEmployed || employmentStatus === PREVIOUS;
};

var getEmploymentAttributesForRequest = function getEmploymentAttributesForRequest(_ref2) {
  var income = _ref2.income,
      phone = _ref2.phone,
      startDate = _ref2.startDate,
      endDate = _ref2.endDate,
      source = _ref2.source,
      employmentStatus = _ref2.employmentStatus,
      noAddress = _ref2.noAddress,
      otherAttributes = _objectWithoutProperties(_ref2, _excluded$7);

  return Object.assign({}, otherAttributes, {
    income: isSimpleIncomeStream({
      source: source,
      employmentStatus: employmentStatus,
      selfEmployed: otherAttributes.selfEmployed
    }) && typeof (income === null || income === void 0 ? void 0 : income.amount) === 'number' ? income : getEmploymentIncomeBreakdown(income),
    phone: toPhoneString(phone),
    startDate: toDateString(startDate),
    endDate: endDate ? toDateString(endDate) : undefined
  });
};
var requestEmploymentsFormFieldsSelector = createSelector([employmentsFormFieldsSelector], function (fields) {
  return fields ? Object.assign({}, fields, {
    attributes: getEmploymentAttributesForRequest(fields.attributes)
  }) : null;
});

var employments = /*#__PURE__*/Object.freeze({
  __proto__: null,
  employmentsSelector: employmentsSelector,
  employmentsFormFieldsSelector: employmentsFormFieldsSelector,
  employmentsFormFieldsErrorsSelector: employmentsFormFieldsErrorsSelector,
  employmentsItemsSelector: employmentsItemsSelector,
  employmentDigitallySourcedStatusByIDSelector: employmentDigitallySourcedStatusByIDSelector,
  borrowersEmploymentsSelector: borrowersEmploymentsSelector,
  isEmploymentFormFieldsSetSelector: isEmploymentFormFieldsSetSelector,
  getEmploymentAttributesForRequest: getEmploymentAttributesForRequest,
  requestEmploymentsFormFieldsSelector: requestEmploymentsFormFieldsSelector
});

var incomeSelector = createSelector([loanFileSelector], function (loanFile) {
  var _loanFile$income;

  return (_loanFile$income = loanFile === null || loanFile === void 0 ? void 0 : loanFile.income) !== null && _loanFile$income !== void 0 ? _loanFile$income : null;
});
var incomeFormFieldsSelector = createSelector([incomeSelector], function (income) {
  var _income$fields;

  return (_income$fields = income === null || income === void 0 ? void 0 : income.fields) !== null && _income$fields !== void 0 ? _income$fields : null;
});
var requestIncomeFormFieldsSelector = createSelector([incomeFormFieldsSelector], function (fields) {
  var _fields$attributes, _fields$attributes$in, _fields$attributes2, _toDateString, _fields$attributes3;

  return Object.assign({}, fields, {
    attributes: Object.assign({}, fields === null || fields === void 0 ? void 0 : fields.attributes, {
      income: typeof (fields === null || fields === void 0 ? void 0 : (_fields$attributes = fields.attributes) === null || _fields$attributes === void 0 ? void 0 : (_fields$attributes$in = _fields$attributes.income) === null || _fields$attributes$in === void 0 ? void 0 : _fields$attributes$in.amount) === 'number' ? fields === null || fields === void 0 ? void 0 : (_fields$attributes2 = fields.attributes) === null || _fields$attributes2 === void 0 ? void 0 : _fields$attributes2.income : undefined,
      endDate: (_toDateString = toDateString(fields === null || fields === void 0 ? void 0 : (_fields$attributes3 = fields.attributes) === null || _fields$attributes3 === void 0 ? void 0 : _fields$attributes3.endDate)) !== null && _toDateString !== void 0 ? _toDateString : undefined
    })
  });
});
var incomeFormErrorsSelector = createSelector([incomeSelector], function (income) {
  var _income$errors;

  return (_income$errors = income === null || income === void 0 ? void 0 : income.errors) !== null && _income$errors !== void 0 ? _income$errors : null;
});
var incomeItemsSelector = createSelector([incomeSelector], function (income) {
  var _income$items;

  return (_income$items = income === null || income === void 0 ? void 0 : income.items) !== null && _income$items !== void 0 ? _income$items : [];
});
var borrowersIncomeSelector = createSelector([incomeItemsSelector, borrowerIDPropSelector], function (incomeItems, borrowerID) {
  return incomeItems.filter(function (item) {
    return item.borrowerID === borrowerID;
  });
});

var income = /*#__PURE__*/Object.freeze({
  __proto__: null,
  incomeSelector: incomeSelector,
  incomeFormFieldsSelector: incomeFormFieldsSelector,
  requestIncomeFormFieldsSelector: requestIncomeFormFieldsSelector,
  incomeFormErrorsSelector: incomeFormErrorsSelector,
  incomeItemsSelector: incomeItemsSelector,
  borrowersIncomeSelector: borrowersIncomeSelector
});

var isDeeplyEmpty = function isDeeplyEmpty(obj) {
  return Object.values(obj).every(function (value) {
    return value === undefined || value === null || _typeof(value) === 'object' && isDeeplyEmpty(value);
  });
};

var API_GET_GIFTS = createActionObject('API_GET_GIFTS');
var apiGetGifts = function apiGetGifts(loanID) {
  return API['loan-file'].GET.thunk({
    action: 'v1/gifts',
    input: {
      loanID: loanID
    },
    authenticated: true
  }, API_GET_GIFTS);
};
var API_CREATE_GIFT = createActionObject('API_CREATE_GIFT');
var apiCreateGift = function apiCreateGift(attributes, borrowerID) {
  return API['loan-file'].POST.thunk({
    action: 'v1/gifts',
    authenticated: true,
    input: {
      borrowerID: borrowerID,
      attributes: attributes
    }
  }, API_CREATE_GIFT);
};
var API_UPDATE_GIFT_ATTRIBUTES = createActionObject('API_UPDATE_GIFT_ATTRIBUTES');
var apiUpdateGiftAttributes = function apiUpdateGiftAttributes(giftID, attributes) {
  return API['loan-file'].PUT.thunk({
    action: "v1/gifts/" + giftID + "/attributes",
    authenticated: true,
    input: {
      attributes: attributes
    },
    metadata: giftID
  }, API_UPDATE_GIFT_ATTRIBUTES);
};
var API_DELETE_GIFT = createActionObject('API_DELETE_GIFT');
var apiDeleteGift = function apiDeleteGift(giftID) {
  return API['loan-file'].DELETE.thunk({
    action: "v1/gifts/" + giftID,
    authenticated: true,
    metadata: giftID
  }, API_DELETE_GIFT);
};
var SET_GIFT_FORM_VALUES = 'SET_GIFT_FORM_VALUES';
var setGiftFormValues = function setGiftFormValues(item) {
  return {
    type: SET_GIFT_FORM_VALUES,
    payload: item
  };
};
var RESET_GIFT_FIELDS = 'RESET_GIFT_FIELDS';
var resetGiftFields = function resetGiftFields() {
  return {
    type: RESET_GIFT_FIELDS
  };
};
var UPDATE_FIELDS = 'gifts/UPDATE_FIELDS';
var updateFields = function updateFields(fields, errors) {
  return {
    type: UPDATE_FIELDS,
    payload: {
      fields: fields,
      errors: errors
    }
  };
};
var fields = {
  attributes: {},
  id: null
};
var DEFAULT_STATE$1 = {
  fields: fields,
  fieldsSet: false,
  errors: {},
  items: []
};
var gifts$1 = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$1;
  }

  var type = _ref.type,
      payload = _ref.payload,
      metadata = _ref.metadata,
      request = _ref.request;

  switch (type) {
    case API_GET_GIFTS.SUCCESS:
      return Object.assign({}, state, {
        items: payload.gifts
      });

    case API_CREATE_GIFT.SUCCESS:
      return Object.assign({}, state, {
        items: state.items.concat(Object.assign({}, request, {
          id: payload.giftID,
          attributes: request.attributes
        }))
      });

    case API_UPDATE_GIFT_ATTRIBUTES.SUCCESS:
      return Object.assign({}, state, {
        items: state.items.map(function (item) {
          return item.id === metadata ? Object.assign({}, item, {
            attributes: request.attributes
          }) : item;
        })
      });

    case API_DELETE_GIFT.SUCCESS:
      return Object.assign({}, state, {
        items: state.items.filter(function (_ref2) {
          var id = _ref2.id;
          return id !== metadata;
        })
      });

    case UPDATE_FIELDS:
      return isDeeplyEmpty(payload.errors) ? Object.assign({}, state, {
        fields: payload.fields,
        errors: {}
      }) : Object.assign({}, state, {
        errors: payload.errors
      });

    case SET_GIFT_FORM_VALUES:
      return Object.assign({}, state, {
        fields: payload,
        fieldsSet: true,
        errors: {}
      });

    case RESET_GIFT_FIELDS:
      return Object.assign({}, state, {
        fields: fields,
        fieldsSet: false,
        errors: {}
      });

    default:
      return state;
  }
});

var gifts$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  API_GET_GIFTS: API_GET_GIFTS,
  apiGetGifts: apiGetGifts,
  API_CREATE_GIFT: API_CREATE_GIFT,
  apiCreateGift: apiCreateGift,
  API_UPDATE_GIFT_ATTRIBUTES: API_UPDATE_GIFT_ATTRIBUTES,
  apiUpdateGiftAttributes: apiUpdateGiftAttributes,
  API_DELETE_GIFT: API_DELETE_GIFT,
  apiDeleteGift: apiDeleteGift,
  SET_GIFT_FORM_VALUES: SET_GIFT_FORM_VALUES,
  setGiftFormValues: setGiftFormValues,
  RESET_GIFT_FIELDS: RESET_GIFT_FIELDS,
  resetGiftFields: resetGiftFields,
  UPDATE_FIELDS: UPDATE_FIELDS,
  updateFields: updateFields,
  DEFAULT_STATE: DEFAULT_STATE$1,
  'default': gifts$1
});

var API_GET_ASSETS = createActionObject('API_GET_ASSETS');
var apiGetAssets = function apiGetAssets(loanID) {
  return API['loan-file'].GET.thunk({
    action: 'v1/assets',
    input: {
      loanID: loanID
    },
    authenticated: true
  }, API_GET_ASSETS);
};
var API_CREATE_ASSET = createActionObject('API_CREATE_ASSET');
var apiCreateAsset = function apiCreateAsset(attributes, owners) {
  return API['loan-file'].POST.thunk({
    action: 'v1/assets',
    authenticated: true,
    input: {
      owners: owners,
      attributes: attributes
    }
  }, API_CREATE_ASSET);
};
var API_UPDATE_ASSET = createActionObject('API_UPDATE_ASSET');
var apiUpdateAsset = function apiUpdateAsset(assetID, attributes, owners) {
  return API['loan-file'].PUT.thunk({
    action: "v1/assets/" + assetID,
    authenticated: true,
    input: {
      attributes: attributes,
      owners: owners
    },
    metadata: assetID
  }, API_UPDATE_ASSET);
};
var API_UPDATE_ASSET_ATTRIBUTES = createActionObject('API_UPDATE_ASSET_ATTRIBUTES');
var apiUpdateAssetAttributes = function apiUpdateAssetAttributes(assetID, attributes) {
  return API['loan-file'].PUT.thunk({
    action: "v1/assets/" + assetID + "/attributes",
    authenticated: true,
    input: {
      attributes: attributes
    },
    metadata: assetID
  }, API_UPDATE_ASSET_ATTRIBUTES);
};
var API_DELETE_ASSET = createActionObject('API_DELETE_ASSET');
var apiDeleteAsset = function apiDeleteAsset(assetID) {
  return API['loan-file'].DELETE.thunk({
    action: "v1/assets/" + assetID,
    authenticated: true,
    metadata: assetID
  }, API_DELETE_ASSET);
};
var SET_ASSETS_FORM_VALUES = 'SET_ASSETS_FORM_VALUES';
var setAssetFormValues = function setAssetFormValues(item) {
  return {
    type: SET_ASSETS_FORM_VALUES,
    payload: item
  };
};
var RESET_ASSETS_FIELDS = 'RESET_ASSETS_FIELDS';
var resetAssetFields = function resetAssetFields() {
  return {
    type: RESET_ASSETS_FIELDS
  };
};
var UPDATE_FIELDS$1 = 'assets/UPDATE_FIELDS';
var updateFields$1 = function updateFields(fields, errors) {
  return {
    type: UPDATE_FIELDS$1,
    payload: {
      fields: fields,
      errors: errors
    }
  };
};
var fields$1 = {
  attributes: {},
  sharedWith: {},
  id: null
};
var DEFAULT_STATE$2 = {
  fields: fields$1,
  errors: {},
  items: []
};
var assets$1 = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$2;
  }

  var type = _ref.type,
      payload = _ref.payload,
      metadata = _ref.metadata,
      request = _ref.request;

  switch (type) {
    case API_GET_ASSETS.SUCCESS:
      return Object.assign({}, state, {
        items: payload.assets
      });

    case API_CREATE_ASSET.SUCCESS:
      return Object.assign({}, state, {
        items: state.items.concat(Object.assign({}, request, {
          id: payload.assetID
        })),
        fields: fields$1,
        errors: {}
      });

    case API_UPDATE_ASSET.SUCCESS:
      return Object.assign({}, state, {
        items: state.items.map(function (item) {
          return item.id === metadata ? Object.assign({}, item, request) : item;
        }),
        errors: {}
      });

    case API_DELETE_ASSET.SUCCESS:
      return Object.assign({}, state, {
        items: state.items.filter(function (_ref2) {
          var id = _ref2.id;
          return id !== metadata;
        })
      });

    case UPDATE_FIELDS$1:
      return isDeeplyEmpty(payload.errors) ? Object.assign({}, state, {
        fields: payload.fields,
        errors: {}
      }) : Object.assign({}, state, {
        errors: payload.errors
      });

    case SET_ASSETS_FORM_VALUES:
      return Object.assign({}, state, {
        fields: payload,
        errors: {}
      });

    case RESET_ASSETS_FIELDS:
      return Object.assign({}, state, {
        fields: fields$1,
        errors: {}
      });

    default:
      return state;
  }
});

var assets$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  API_GET_ASSETS: API_GET_ASSETS,
  apiGetAssets: apiGetAssets,
  API_CREATE_ASSET: API_CREATE_ASSET,
  apiCreateAsset: apiCreateAsset,
  API_UPDATE_ASSET: API_UPDATE_ASSET,
  apiUpdateAsset: apiUpdateAsset,
  API_UPDATE_ASSET_ATTRIBUTES: API_UPDATE_ASSET_ATTRIBUTES,
  apiUpdateAssetAttributes: apiUpdateAssetAttributes,
  API_DELETE_ASSET: API_DELETE_ASSET,
  apiDeleteAsset: apiDeleteAsset,
  SET_ASSETS_FORM_VALUES: SET_ASSETS_FORM_VALUES,
  setAssetFormValues: setAssetFormValues,
  RESET_ASSETS_FIELDS: RESET_ASSETS_FIELDS,
  resetAssetFields: resetAssetFields,
  UPDATE_FIELDS: UPDATE_FIELDS$1,
  updateFields: updateFields$1,
  DEFAULT_STATE: DEFAULT_STATE$2,
  'default': assets$1
});

var API_GET_EMPLOYMENTS = createActionObject('API_GET_EMPLOYMENTS');
var apiGetEmployments = function apiGetEmployments(loanID) {
  return API['loan-file'].GET.thunk({
    action: 'v1/employments',
    input: {
      loanID: loanID
    },
    authenticated: true
  }, API_GET_EMPLOYMENTS);
};
var API_CREATE_EMPLOYMENT = createActionObject('API_CREATE_EMPLOYMENT');
var apiCreateEmployment = function apiCreateEmployment(attributes, borrowerID) {
  return API['loan-file'].POST.thunk({
    action: 'v1/employments',
    authenticated: true,
    input: {
      borrowerID: borrowerID,
      attributes: attributes
    }
  }, API_CREATE_EMPLOYMENT);
};
var API_UPDATE_EMPLOYMENT_ATTRIBUTES = createActionObject('API_UPDATE_EMPLOYMENT_ATTRIBUTES');
var apiUpdateEmploymentAttributes = function apiUpdateEmploymentAttributes(employmentID, attributes) {
  return API['loan-file'].PUT.thunk({
    action: "v1/employments/" + employmentID + "/attributes",
    authenticated: true,
    input: {
      attributes: attributes
    },
    metadata: employmentID
  }, API_UPDATE_EMPLOYMENT_ATTRIBUTES);
};
var API_DELETE_EMPLOYMENT = createActionObject('API_DELETE_EMPLOYMENT');
var apiDeleteEmployment = function apiDeleteEmployment(employmentID) {
  return API['loan-file'].DELETE.thunk({
    action: "v1/employments/" + employmentID,
    authenticated: true,
    metadata: employmentID
  }, API_DELETE_EMPLOYMENT);
};
var SET_EMPLOYMENT_FORM_VALUES = 'SET_EMPLOYMENT_FORM_VALUES';
var setEmploymentFormValues = function setEmploymentFormValues(item) {
  return {
    type: SET_EMPLOYMENT_FORM_VALUES,
    payload: item
  };
};
var RESET_EMPLOYMENT_FIELDS = 'RESET_EMPLOYMENT_FIELDS';
var resetEmploymentFields = function resetEmploymentFields() {
  return {
    type: RESET_EMPLOYMENT_FIELDS
  };
};
var UPDATE_FIELDS$2 = 'employments/UPDATE_FIELDS';
var updateFields$2 = function updateFields(fields, errors) {
  return {
    type: UPDATE_FIELDS$2,
    payload: {
      fields: fields,
      errors: errors
    }
  };
};
var fields$2 = {
  attributes: {}
};
var DEFAULT_STATE$3 = {
  fields: fields$2,
  fieldsSet: false,
  items: [],
  errors: null
};
var employments$1 = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$3;
  }

  var type = _ref.type,
      payload = _ref.payload,
      metadata = _ref.metadata,
      request = _ref.request;

  switch (type) {
    case API_GET_EMPLOYMENTS.SUCCESS:
      return Object.assign({}, state, {
        items: payload.employments
      });

    case API_CREATE_EMPLOYMENT.BEGIN:
    case API_UPDATE_EMPLOYMENT_ATTRIBUTES.BEGIN:
      return Object.assign({}, state, {
        errors: null
      });

    case API_CREATE_EMPLOYMENT.SUCCESS:
      return Object.assign({}, state, {
        items: state.items.concat(Object.assign({}, request, {
          id: payload.employmentID
        }))
      });

    case API_CREATE_EMPLOYMENT.FAILURE:
    case API_UPDATE_EMPLOYMENT_ATTRIBUTES.FAILURE:
      return Object.assign({}, state, {
        errors: payload.detail
      });

    case API_UPDATE_EMPLOYMENT_ATTRIBUTES.SUCCESS:
      return Object.assign({}, state, {
        items: state.items.map(function (item) {
          return item.id === metadata ? Object.assign({}, item, request) : item;
        })
      });

    case API_DELETE_EMPLOYMENT.SUCCESS:
      return Object.assign({}, state, {
        items: state.items.filter(function (_ref2) {
          var id = _ref2.id;
          return id !== metadata;
        })
      });

    case SET_EMPLOYMENT_FORM_VALUES:
      return Object.assign({}, state, {
        fields: payload,
        fieldsSet: true
      });

    case RESET_EMPLOYMENT_FIELDS:
      return Object.assign({}, state, {
        fields: fields$2,
        fieldsSet: false
      });

    case UPDATE_FIELDS$2:
      return Object.assign({}, state, {
        fields: payload.fields
      });

    default:
      return state;
  }
});

var employments$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  API_GET_EMPLOYMENTS: API_GET_EMPLOYMENTS,
  apiGetEmployments: apiGetEmployments,
  API_CREATE_EMPLOYMENT: API_CREATE_EMPLOYMENT,
  apiCreateEmployment: apiCreateEmployment,
  API_UPDATE_EMPLOYMENT_ATTRIBUTES: API_UPDATE_EMPLOYMENT_ATTRIBUTES,
  apiUpdateEmploymentAttributes: apiUpdateEmploymentAttributes,
  API_DELETE_EMPLOYMENT: API_DELETE_EMPLOYMENT,
  apiDeleteEmployment: apiDeleteEmployment,
  SET_EMPLOYMENT_FORM_VALUES: SET_EMPLOYMENT_FORM_VALUES,
  setEmploymentFormValues: setEmploymentFormValues,
  RESET_EMPLOYMENT_FIELDS: RESET_EMPLOYMENT_FIELDS,
  resetEmploymentFields: resetEmploymentFields,
  UPDATE_FIELDS: UPDATE_FIELDS$2,
  updateFields: updateFields$2,
  DEFAULT_STATE: DEFAULT_STATE$3,
  'default': employments$1
});

var API_GET_OTHER_INCOMES = createActionObject('API_GET_OTHER_INCOMES');
var apiGetOtherIncomes = function apiGetOtherIncomes(loanID) {
  return API['loan-file'].GET.thunk({
    action: 'v1/other-incomes',
    input: {
      loanID: loanID
    },
    authenticated: true
  }, API_GET_OTHER_INCOMES);
};
var API_CREATE_OTHER_INCOME = createActionObject('API_CREATE_OTHER_INCOME');
var apiCreateOtherIncome = function apiCreateOtherIncome(attributes, borrowerID) {
  return API['loan-file'].POST.thunk({
    action: 'v1/other-incomes',
    authenticated: true,
    input: {
      borrowerID: borrowerID,
      attributes: attributes
    }
  }, API_CREATE_OTHER_INCOME);
};
var API_UPDATE_OTHER_INCOME_ATTRIBUTES = createActionObject('API_UPDATE_OTHER_INCOME_ATTRIBUTES');
var apiUpdateOtherIncomeAttributes = function apiUpdateOtherIncomeAttributes(otherIncomeID, attributes) {
  return API['loan-file'].PUT.thunk({
    action: "v1/other-incomes/" + otherIncomeID + "/attributes",
    authenticated: true,
    input: {
      attributes: attributes
    },
    metadata: otherIncomeID
  }, API_UPDATE_OTHER_INCOME_ATTRIBUTES);
};
var API_DELETE_OTHER_INCOME = createActionObject('API_DELETE_OTHER_INCOME');
var apiDeleteOtherIncome = function apiDeleteOtherIncome(otherIncomeID) {
  return API['loan-file'].DELETE.thunk({
    action: "v1/other-incomes/" + otherIncomeID,
    authenticated: true,
    metadata: otherIncomeID
  }, API_DELETE_OTHER_INCOME);
};
var SET_INCOME_FORM_VALUES = 'SET_INCOME_FORM_VALUES';
var setIncomeFormValues = function setIncomeFormValues(item) {
  return {
    type: SET_INCOME_FORM_VALUES,
    payload: item
  };
};
var RESET_INCOME_FIELDS = 'RESET_INCOME_FIELDS';
var resetIncomeFields = function resetIncomeFields() {
  return {
    type: RESET_INCOME_FIELDS
  };
};
var UPDATE_FIELDS$3 = 'income/UPDATE_FIELDS';
var updateFields$3 = function updateFields(fields, errors) {
  return {
    type: UPDATE_FIELDS$3,
    payload: {
      fields: fields,
      errors: errors
    }
  };
};
var fields$3 = {
  attributes: {}
};
var DEFAULT_STATE$4 = {
  fields: fields$3,
  errors: {},
  items: []
};
var income$1 = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$4;
  }

  var type = _ref.type,
      payload = _ref.payload,
      metadata = _ref.metadata,
      request = _ref.request;

  switch (type) {
    case API_GET_OTHER_INCOMES.SUCCESS:
      return Object.assign({}, state, {
        items: payload.otherIncomes
      });

    case API_CREATE_OTHER_INCOME.SUCCESS:
      return Object.assign({}, state, {
        items: state.items.concat(Object.assign({}, request, {
          id: payload.otherIncomeID
        }))
      });

    case API_UPDATE_OTHER_INCOME_ATTRIBUTES.SUCCESS:
      return Object.assign({}, state, {
        items: state.items.map(function (item) {
          return item.id === metadata ? Object.assign({}, item, request) : item;
        })
      });

    case API_DELETE_OTHER_INCOME.SUCCESS:
      return Object.assign({}, state, {
        items: state.items.filter(function (_ref2) {
          var id = _ref2.id;
          return id !== metadata;
        })
      });

    case UPDATE_FIELDS$3:
      return isDeeplyEmpty(payload.errors) ? Object.assign({}, state, {
        fields: payload.fields,
        errors: {}
      }) : Object.assign({}, state, {
        errors: payload.errors
      });

    case SET_INCOME_FORM_VALUES:
      return Object.assign({}, state, {
        fields: payload,
        errors: {}
      });

    case RESET_INCOME_FIELDS:
      return Object.assign({}, state, {
        fields: fields$3,
        errors: {}
      });

    default:
      return state;
  }
});

var income$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  API_GET_OTHER_INCOMES: API_GET_OTHER_INCOMES,
  apiGetOtherIncomes: apiGetOtherIncomes,
  API_CREATE_OTHER_INCOME: API_CREATE_OTHER_INCOME,
  apiCreateOtherIncome: apiCreateOtherIncome,
  API_UPDATE_OTHER_INCOME_ATTRIBUTES: API_UPDATE_OTHER_INCOME_ATTRIBUTES,
  apiUpdateOtherIncomeAttributes: apiUpdateOtherIncomeAttributes,
  API_DELETE_OTHER_INCOME: API_DELETE_OTHER_INCOME,
  apiDeleteOtherIncome: apiDeleteOtherIncome,
  SET_INCOME_FORM_VALUES: SET_INCOME_FORM_VALUES,
  setIncomeFormValues: setIncomeFormValues,
  RESET_INCOME_FIELDS: RESET_INCOME_FIELDS,
  resetIncomeFields: resetIncomeFields,
  UPDATE_FIELDS: UPDATE_FIELDS$3,
  updateFields: updateFields$3,
  DEFAULT_STATE: DEFAULT_STATE$4,
  'default': income$1
});

var _excluded$8 = ["totalUnpaidBalance"];
var API_GET_OWNED_REAL_ESTATES = createActionObject('API_GET_OWNED_REAL_ESTATES');
var apiGetOwnedRealEstates = function apiGetOwnedRealEstates(loanID) {
  return API['loan-file'].GET.thunk({
    action: 'v1/real-estates',
    input: {
      loanID: loanID
    },
    authenticated: true
  }, API_GET_OWNED_REAL_ESTATES);
};
var API_CREATE_OWNED_REAL_ESTATE = createActionObject('API_CREATE_OWNED_REAL_ESTATE');
var apiCreateOwnedRealEstate = function apiCreateOwnedRealEstate(attributes, owners) {
  return API['loan-file'].POST.thunk({
    action: 'v1/real-estates',
    authenticated: true,
    input: {
      owners: owners,
      attributes: attributes
    }
  }, API_CREATE_OWNED_REAL_ESTATE);
};
var API_UPDATE_OWNED_REAL_ESTATE = createActionObject('API_UPDATE_OWNED_REAL_ESTATE');
var apiUpdateOwnedRealEstate = function apiUpdateOwnedRealEstate(ownedRealEstateID, attributes, owners) {
  return API['loan-file'].PUT.thunk({
    action: "v1/real-estates/" + ownedRealEstateID,
    authenticated: true,
    input: {
      attributes: attributes,
      owners: owners
    },
    metadata: ownedRealEstateID
  }, API_UPDATE_OWNED_REAL_ESTATE);
};
var API_DELETE_OWNED_REAL_ESTATE = createActionObject('API_DELETE_OWNED_REAL_ESTATE');
var apiDeleteOwnedRealEstate = function apiDeleteOwnedRealEstate(ownedRealEstateID) {
  return API['loan-file'].DELETE.thunk({
    action: "v1/real-estates/" + ownedRealEstateID,
    authenticated: true,
    metadata: ownedRealEstateID
  }, API_DELETE_OWNED_REAL_ESTATE);
};
var getOwnedRealEstates = function getOwnedRealEstates(props) {
  return function (dispatch, getState) {
    var loanID = loanIDPropSelector(getState(), props);
    return dispatch(apiGetOwnedRealEstates(loanID));
  };
};
var createOwnedRealEstate = function createOwnedRealEstate(props) {
  return function (dispatch, getState) {
    var borrowerID = borrowerIDPropSelector(getState(), props);

    var _requestOwnedRealEsta = requestOwnedRealEstatesFieldsSelector(getState()),
        attributes = _requestOwnedRealEsta.attributes,
        sharedWith = _requestOwnedRealEsta.sharedWith;

    var owners = sharedWith.concat(borrowerID);
    return dispatch(apiCreateOwnedRealEstate(attributes, owners));
  };
};
var updateOwnedRealEstate = function updateOwnedRealEstate(props) {
  return function (dispatch, getState) {
    var borrowerID = borrowerIDPropSelector(getState(), props);

    var _requestOwnedRealEsta2 = requestOwnedRealEstatesFieldsSelector(getState()),
        id = _requestOwnedRealEsta2.id,
        _requestOwnedRealEsta3 = _requestOwnedRealEsta2.attributes,
        totalUnpaidBalance = _requestOwnedRealEsta3.totalUnpaidBalance,
        attributes = _objectWithoutProperties(_requestOwnedRealEsta3, _excluded$8),
        sharedWith = _requestOwnedRealEsta2.sharedWith;

    var owners = sharedWith.concat(borrowerID);
    return dispatch(apiUpdateOwnedRealEstate(id, attributes, owners));
  };
};
var deleteOwnedRealEstate = function deleteOwnedRealEstate() {
  return function (dispatch, getState) {
    var _requestOwnedRealEsta4 = requestOwnedRealEstatesFieldsSelector(getState()),
        id = _requestOwnedRealEsta4.id;

    return dispatch(apiDeleteOwnedRealEstate(id));
  };
};
var SET_OWNED_REAL_ESTATE_FORM_VALUES = 'SET_OWNED_REAL_ESTATE_FORM_VALUES';
var setOwnedRealEstateFormValues = function setOwnedRealEstateFormValues(item) {
  return {
    type: SET_OWNED_REAL_ESTATE_FORM_VALUES,
    payload: item
  };
};
var RESET_OWNED_REAL_ESTATE_FIELDS = 'RESET_OWNED_REAL_ESTATE_FIELDS';
var resetOwnedRealEstateFields = function resetOwnedRealEstateFields() {
  return {
    type: RESET_OWNED_REAL_ESTATE_FIELDS
  };
};
var UPDATE_FIELDS$4 = 'ownedRealEstates/UPDATE_FIELDS';
var updateFields$4 = function updateFields(fields, errors) {
  return {
    type: UPDATE_FIELDS$4,
    payload: {
      fields: fields,
      errors: errors
    }
  };
};
var fields$4 = {
  attributes: {}
};
var DEFAULT_STATE$5 = {
  fields: fields$4,
  errors: {},
  items: []
};
var ownedRealEstates = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$5;
  }

  var type = _ref.type,
      payload = _ref.payload,
      metadata = _ref.metadata,
      request = _ref.request;

  switch (type) {
    case API_GET_OWNED_REAL_ESTATES.SUCCESS:
      return Object.assign({}, state, {
        items: payload.realEstates
      });

    case API_CREATE_OWNED_REAL_ESTATE.SUCCESS:
      return Object.assign({}, state, {
        items: state.items.concat(Object.assign({}, request, {
          id: payload.realEstateID
        }))
      });

    case API_UPDATE_OWNED_REAL_ESTATE.SUCCESS:
      return Object.assign({}, state, {
        items: state.items.map(function (item) {
          return item.id === metadata ? Object.assign({}, item, request) : item;
        })
      });

    case API_DELETE_OWNED_REAL_ESTATE.SUCCESS:
      return Object.assign({}, state, {
        items: state.items.filter(function (_ref2) {
          var id = _ref2.id;
          return id !== metadata;
        })
      });

    case UPDATE_FIELDS$4:
      return isDeeplyEmpty(payload.errors) ? Object.assign({}, state, {
        fields: payload.fields,
        errors: {}
      }) : Object.assign({}, state, {
        errors: payload.errors
      });

    case SET_OWNED_REAL_ESTATE_FORM_VALUES:
      return Object.assign({}, state, {
        fields: payload,
        errors: {}
      });

    case RESET_OWNED_REAL_ESTATE_FIELDS:
      return Object.assign({}, state, {
        fields: fields$4,
        errors: {}
      });

    default:
      return state;
  }
});

var ownedRealEstates$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  API_GET_OWNED_REAL_ESTATES: API_GET_OWNED_REAL_ESTATES,
  apiGetOwnedRealEstates: apiGetOwnedRealEstates,
  API_CREATE_OWNED_REAL_ESTATE: API_CREATE_OWNED_REAL_ESTATE,
  apiCreateOwnedRealEstate: apiCreateOwnedRealEstate,
  API_UPDATE_OWNED_REAL_ESTATE: API_UPDATE_OWNED_REAL_ESTATE,
  apiUpdateOwnedRealEstate: apiUpdateOwnedRealEstate,
  API_DELETE_OWNED_REAL_ESTATE: API_DELETE_OWNED_REAL_ESTATE,
  apiDeleteOwnedRealEstate: apiDeleteOwnedRealEstate,
  getOwnedRealEstates: getOwnedRealEstates,
  createOwnedRealEstate: createOwnedRealEstate,
  updateOwnedRealEstate: updateOwnedRealEstate,
  deleteOwnedRealEstate: deleteOwnedRealEstate,
  SET_OWNED_REAL_ESTATE_FORM_VALUES: SET_OWNED_REAL_ESTATE_FORM_VALUES,
  setOwnedRealEstateFormValues: setOwnedRealEstateFormValues,
  RESET_OWNED_REAL_ESTATE_FIELDS: RESET_OWNED_REAL_ESTATE_FIELDS,
  resetOwnedRealEstateFields: resetOwnedRealEstateFields,
  UPDATE_FIELDS: UPDATE_FIELDS$4,
  updateFields: updateFields$4,
  DEFAULT_STATE: DEFAULT_STATE$5,
  'default': ownedRealEstates
});

var _constants$loanFile$3 = constants.loanFile,
    GIFT_TYPES = _constants$loanFile$3.GIFT_TYPES,
    EMPLOYMENT$1 = _constants$loanFile$3.EMPLOYMENT_TYPE_LOOKUP.EMPLOYMENT,
    CREDIT_PULL_TYPE_LOOKUP = _constants$loanFile$3.CREDIT_PULL_TYPE_LOOKUP,
    LFS_ERROR_TYPES = constants.LFS_ERROR_TYPES;
var RESET_LOAN = 'RESET_LOAN';
var resetLoan = function resetLoan() {
  return {
    type: RESET_LOAN
  };
};
var FETCH_LOAN = createActionObject('FETCH_LOAN');
var fetchLoan = function fetchLoan(loanID) {
  return API['loan-file'].GET.thunk({
    action: "v1/loans/" + loanID,
    authenticated: true
  }, FETCH_LOAN);
};
var FETCH_LOAN_STATUS = createActionObject('FETCH_LOAN_STATUS');
var fetchLoanStatusThunk = function fetchLoanStatusThunk(loanID, isUserInitiatedAction) {
  return API['loan-file'].GET.thunk({
    action: "v1/loans/" + loanID + "/status",
    authenticated: true,
    isUserInitiatedAction: isUserInitiatedAction
  }, FETCH_LOAN_STATUS);
};
var fetchLoanStatus = function fetchLoanStatus(props, isUserInitiatedAction) {
  return function (dispatch) {
    var loanID = loanIDPropSelector(null, props);
    return dispatch(fetchLoanStatusThunk(loanID, isUserInitiatedAction));
  };
};
var SEARCH_LOANS = createActionObject('SEARCH_LOANS');
var searchLoans = function searchLoans(params) {
  return API['loan-file'].GET.thunk({
    action: 'v1/loans',
    authenticated: true,
    input: Object.assign({}, params)
  }, SEARCH_LOANS);
};
var API_UPDATE_LOAN_EDITOR = createActionObject('API_UPDATE_LOAN_EDITOR');
var apiUpdateLoanEditor = function apiUpdateLoanEditor(loanID, editor) {
  return API['loan-file'].PUT.thunk({
    action: "v1/loans/" + loanID + "/editor",
    input: {
      editor: editor
    },
    authenticated: true
  }, API_UPDATE_LOAN_EDITOR);
};
var FETCH_CURRENT_REPORTS = createActionObject('FETCH_CURRENT_REPORTS');
var apiFetchReports = function apiFetchReports(loanID, creditPullType) {
  if (creditPullType === void 0) {
    creditPullType = CREDIT_PULL_TYPE_LOOKUP.HARD;
  }

  return API.credit.GET.thunk({
    action: "v1/loan-reports/" + loanID,
    authenticated: true,
    input: {
      current: true,
      compute: 'report.documentURL',
      creditPullType: creditPullType
    }
  }, FETCH_CURRENT_REPORTS);
};
var API_REMOVE_BORROWER = createActionObject('API_REMOVE_BORROWER');
var apiRemoveBorrower = function apiRemoveBorrower(borrowerID) {
  return API['loan-file'].DELETE.thunk({
    action: "v1/borrowers/" + borrowerID,
    authenticated: true,
    metadata: {
      borrowerID: borrowerID
    }
  }, API_REMOVE_BORROWER);
};
var API_CREATE_BORROWER = createActionObject('API_CREATE_BORROWER');
var apiCreateBorrower = function apiCreateBorrower(loanID, attributes) {
  return API['loan-file'].POST.thunk({
    action: 'v1/borrowers',
    input: {
      loanID: loanID,
      attributes: attributes
    },
    authenticated: true,
    jsonEncoder: toJSON
  }, API_CREATE_BORROWER);
};
var fetchLoanIDFromLead = function fetchLoanIDFromLead(leadID) {
  return function (dispatch) {
    return new Promise(function ($return, $error) {
      var _await$dispatch, _await$dispatch$loans, loan;

      return Promise.resolve(dispatch(searchLoans({
        leadID: leadID
      }))).then(function ($await_1) {
        try {
          _await$dispatch = $await_1, _await$dispatch$loans = _slicedToArray(_await$dispatch.loans, 1), loan = _await$dispatch$loans[0];
          return $return(loan === null || loan === void 0 ? void 0 : loan.id);
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  };
};
var fetchAssetsAndGifts = function fetchAssetsAndGifts(loanID) {
  return function (dispatch) {
    return Promise.all([dispatch(apiGetAssets(loanID)), dispatch(apiGetGifts(loanID))]);
  };
};
var createAssetOrGift = function createAssetOrGift(borrowerID) {
  return function (dispatch, getState) {
    var state = getState();
    var isGift = isGiftFormFieldsSet(state);

    if (isGift) {
      var _giftFormFieldsSelect = giftFormFieldsSelector(state),
          _attributes = _giftFormFieldsSelect.attributes;

      return dispatch(apiCreateGift(_attributes, borrowerID));
    }

    var _requestAssetFormFiel = requestAssetFormFieldsSelector(state),
        sharedWith = _requestAssetFormFiel.sharedWith,
        attributes = _requestAssetFormFiel.attributes;

    var owners = sharedWith.concat(borrowerID);
    return dispatch(apiCreateAsset(attributes, owners));
  };
};
var updateGift = function updateGift() {
  return function (dispatch, getState) {
    var _giftFormFieldsSelect2 = giftFormFieldsSelector(getState()),
        id = _giftFormFieldsSelect2.id,
        attributes = _giftFormFieldsSelect2.attributes;

    return dispatch(apiUpdateGiftAttributes(id, attributes));
  };
};
var updateAsset = function updateAsset(borrowerID) {
  return function (dispatch, getState) {
    var _requestAssetFormFiel2 = requestAssetFormFieldsSelector(getState()),
        id = _requestAssetFormFiel2.id,
        attributes = _requestAssetFormFiel2.attributes,
        sharedWith = _requestAssetFormFiel2.sharedWith;

    var owners = sharedWith.concat(borrowerID);
    return dispatch(apiUpdateAsset(id, attributes, owners));
  };
};
var updateAssetOrGift = function updateAssetOrGift(borrowerID) {
  return function (dispatch, getState) {
    var isGift = isGiftFormFieldsSet(getState());
    return dispatch(isGift ? updateGift() : updateAsset(borrowerID));
  };
};
var deleteAssetOrGift = function deleteAssetOrGift() {
  return function (dispatch, getState) {
    var state = getState();
    var isGift = isGiftFormFieldsSet(state);

    if (isGift) {
      var _giftFormFieldsSelect3 = giftFormFieldsSelector(state),
          _id = _giftFormFieldsSelect3.id;

      return dispatch(apiDeleteGift(_id));
    }

    var _requestAssetFormFiel3 = requestAssetFormFieldsSelector(state),
        id = _requestAssetFormFiel3.id;

    return dispatch(apiDeleteAsset(id));
  };
};
var resetCombinedAssetAndGiftFormFields = function resetCombinedAssetAndGiftFormFields() {
  return function (dispatch, getState) {
    var isGift = isGiftFormFieldsSet(getState());
    return dispatch(isGift ? resetGiftFields() : resetAssetFields());
  };
};

var isGiftItem = function isGiftItem(item) {
  var _item$attributes;

  return Object.keys(GIFT_TYPES).indexOf(item === null || item === void 0 ? void 0 : (_item$attributes = item.attributes) === null || _item$attributes === void 0 ? void 0 : _item$attributes.type) !== -1;
};

var setCombinedAssetAndGiftFormFields = function setCombinedAssetAndGiftFormFields(item) {
  return function (dispatch) {
    return dispatch((isGiftItem(item) ? setGiftFormValues : setAssetFormValues)(item));
  };
};
var changeFromGiftToAsset = function changeFromGiftToAsset(type) {
  return function (dispatch) {
    dispatch(resetGiftFields());
    dispatch(setAssetFormValues({
      attributes: {
        type: type
      }
    }));
  };
};
var changeFromAssetToGift = function changeFromAssetToGift(type) {
  return function (dispatch) {
    dispatch(resetAssetFields());
    dispatch(setGiftFormValues({
      attributes: {
        type: type
      }
    }));
  };
};
var fetchEmploymentsAndIncome = function fetchEmploymentsAndIncome(props) {
  return function (dispatch) {
    var loanID = loanIDPropSelector(null, props);
    return Promise.all([dispatch(apiGetEmployments(loanID)), dispatch(apiGetOtherIncomes(loanID))]);
  };
};
var resetCombinedEmploymentAndIncomeFields = function resetCombinedEmploymentAndIncomeFields() {
  return function (dispatch, getState) {
    var isEmployment = isEmploymentFormFieldsSetSelector(getState());
    return dispatch(isEmployment ? resetEmploymentFields() : resetIncomeFields());
  };
};
var changeFromEmploymentToIncome = function changeFromEmploymentToIncome(source) {
  return function (dispatch) {
    dispatch(resetEmploymentFields());
    dispatch(setIncomeFormValues({
      attributes: {
        source: source
      }
    }));
  };
};
var changeFromIncomeToEmployment = function changeFromIncomeToEmployment(source) {
  return function (dispatch) {
    dispatch(resetIncomeFields());
    dispatch(setEmploymentFormValues({
      attributes: {
        source: source
      }
    }));
  };
};

var isEmploymentItem = function isEmploymentItem(item) {
  var _item$attributes2;

  return (item === null || item === void 0 ? void 0 : (_item$attributes2 = item.attributes) === null || _item$attributes2 === void 0 ? void 0 : _item$attributes2.source) === EMPLOYMENT$1;
};

var setCombinedEmploymentAndIncomeFormValues = function setCombinedEmploymentAndIncomeFormValues(item) {
  return function (dispatch) {
    return dispatch((isEmploymentItem(item) ? setEmploymentFormValues : setIncomeFormValues)(item));
  };
};
var createEmploymentOrIncome = function createEmploymentOrIncome(borrowerID, loanID) {
  return function (dispatch, getState) {
    var state = getState();
    var isEmployment = isEmploymentFormFieldsSetSelector(state);

    if (isEmployment) {
      var _requestEmploymentsFo = requestEmploymentsFormFieldsSelector(state),
          _attributes2 = _requestEmploymentsFo.attributes;

      return dispatch(apiCreateEmployment(_attributes2, borrowerID)).then(function () {
        return dispatch(fetchLoan(loanID));
      });
    }

    var _requestIncomeFormFie = requestIncomeFormFieldsSelector(state),
        attributes = _requestIncomeFormFie.attributes;

    return dispatch(apiCreateOtherIncome(attributes, borrowerID)).then(function () {
      return dispatch(fetchLoan(loanID));
    });
  };
};
var updateEmployment = function updateEmployment() {
  return function (dispatch, getState) {
    var _requestEmploymentsFo2 = requestEmploymentsFormFieldsSelector(getState()),
        attributes = _requestEmploymentsFo2.attributes,
        id = _requestEmploymentsFo2.id;

    return dispatch(apiUpdateEmploymentAttributes(id, attributes));
  };
};
var updateIncome = function updateIncome() {
  return function (dispatch, getState) {
    var _requestIncomeFormFie2 = requestIncomeFormFieldsSelector(getState()),
        attributes = _requestIncomeFormFie2.attributes,
        id = _requestIncomeFormFie2.id;

    return dispatch(apiUpdateOtherIncomeAttributes(id, attributes));
  };
};
var updateEmploymentOrIncome = function updateEmploymentOrIncome(loanID) {
  return function (dispatch, getState) {
    var isEmployment = isEmploymentFormFieldsSetSelector(getState());
    return dispatch(isEmployment ? updateEmployment() : updateIncome()).then(function () {
      return dispatch(fetchLoan(loanID));
    });
  };
};
var deleteEmploymentOrIncome = function deleteEmploymentOrIncome(loanID) {
  return function (dispatch, getState) {
    var state = getState();
    var isEmployment = isEmploymentFormFieldsSetSelector(state);

    if (isEmployment) {
      var _requestEmploymentsFo3 = requestEmploymentsFormFieldsSelector(state),
          _id2 = _requestEmploymentsFo3.id;

      return dispatch(apiDeleteEmployment(_id2)).then(function () {
        return dispatch(fetchLoan(loanID));
      });
    }

    var _requestIncomeFormFie3 = requestIncomeFormFieldsSelector(state),
        id = _requestIncomeFormFie3.id;

    return dispatch(apiDeleteOtherIncome(id)).then(function () {
      return dispatch(fetchLoan(loanID));
    });
  };
};
var fetchIncomeAndAssets = function fetchIncomeAndAssets(loanID) {
  return function (dispatch) {
    return Promise.all([apiGetEmployments(loanID), apiGetOtherIncomes(loanID), apiGetAssets(loanID), apiGetGifts(loanID), apiGetOwnedRealEstates(loanID), apiFetchReports(loanID)].map(dispatch));
  };
};
var DEFAULT_STATE$6 = {
  loanNumber: null,
  tridTriggeredUTC: null,
  computed: {}
};
var loanNumber = function loanNumber(state, _ref) {
  var _payload$loan$loanNum;

  if (state === void 0) {
    state = DEFAULT_STATE$6.loanNumber;
  }

  var payload = _ref.payload,
      type = _ref.type;
  return type === FETCH_LOAN.SUCCESS ? (_payload$loan$loanNum = payload.loan.loanNumber) !== null && _payload$loan$loanNum !== void 0 ? _payload$loan$loanNum : null : state;
};
var tridTriggeredUTC = function tridTriggeredUTC(state, _ref2) {
  if (state === void 0) {
    state = DEFAULT_STATE$6.tridTriggeredUTC;
  }

  var type = _ref2.type,
      payload = _ref2.payload;

  switch (type) {
    case FETCH_LOAN_STATUS.SUCCESS:
      var _payload$tridTriggere;

      return (_payload$tridTriggere = payload.tridTriggeredUTC) !== null && _payload$tridTriggere !== void 0 ? _payload$tridTriggere : null;

    case FETCH_LOAN.SUCCESS:
      var _payload$loan$tridTri;

      return (_payload$loan$tridTri = payload.loan.tridTriggeredUTC) !== null && _payload$loan$tridTri !== void 0 ? _payload$loan$tridTri : null;

    default:
      return state;
  }
};
var computed = function computed(state, _ref3) {
  var _payload$loan$compute;

  if (state === void 0) {
    state = DEFAULT_STATE$6.computed;
  }

  var payload = _ref3.payload,
      type = _ref3.type;
  return type === FETCH_LOAN.SUCCESS ? (_payload$loan$compute = payload.loan.computed) !== null && _payload$loan$compute !== void 0 ? _payload$loan$compute : {} : state;
};
/*
Its worth noting that this is not the exact error we got back from the wire, but rather it is
the error after client-side processing. For example error.statusCode is not present in the API
response itself but we put it there client-side based on the observed HTTP status code.
*/

var errorTypeFromError = function errorTypeFromError(error) {
  if ((error === null || error === void 0 ? void 0 : error.type) === LFS_ERROR_TYPES.NETWORK_ERROR) {
    return error.type;
  } else if ((error === null || error === void 0 ? void 0 : error.statusCode) === 422) {
    return LFS_ERROR_TYPES.VALIDATION_ERROR;
  } else if ((error === null || error === void 0 ? void 0 : error.statusCode) >= 500) {
    return LFS_ERROR_TYPES.SERVER_ERROR;
  }
};

var _excluded$9 = ["accountStatus"],
    _excluded2$2 = ["sharedWith", "attributes"];
var _constants$loanFile$4 = constants.loanFile,
    LIABILITY_ACCOUNT_STATUS_TYPES_LOOKUP = _constants$loanFile$4.LIABILITY_ACCOUNT_STATUS_TYPES_LOOKUP,
    LIABILITY_CREATION_TYPES_LOOKUP = _constants$loanFile$4.LIABILITY_CREATION_TYPES_LOOKUP,
    LIABILITY_PAY_OFF_TYPES_LOOKUP = _constants$loanFile$4.LIABILITY_PAY_OFF_TYPES_LOOKUP;
var CLOSING_COSTS_LIABILITIES_PAY_OFF_TYPES = [LIABILITY_PAY_OFF_TYPES_LOOKUP.PAID_AT_CLOSING];
var PAID_THROUGH_LOAN_LIABILITIES_PAY_OFF_TYPES = [LIABILITY_PAY_OFF_TYPES_LOOKUP.PAID_OFF_THROUGH_LOAN];
var liabilitiesSelector = createSelector([loanFileSelector], function (loanFile) {
  var _loanFile$liabilities;

  return (_loanFile$liabilities = loanFile === null || loanFile === void 0 ? void 0 : loanFile.liabilities) !== null && _loanFile$liabilities !== void 0 ? _loanFile$liabilities : null;
});
var liabilitiesItemsSelector = createSelector([liabilitiesSelector], function (liabilities) {
  var _liabilities$items;

  return (_liabilities$items = liabilities === null || liabilities === void 0 ? void 0 : liabilities.items) !== null && _liabilities$items !== void 0 ? _liabilities$items : {};
});
var liabilityFieldsSelector = createSelector([liabilitiesSelector], function (liabilities) {
  return (liabilities === null || liabilities === void 0 ? void 0 : liabilities.fields) || {};
});
var customLiabilitiesSelector = createSelector([liabilitiesItemsSelector], function (liabilitiesItems) {
  return Object.values(liabilitiesItems).filter(function (liability) {
    return (liability === null || liability === void 0 ? void 0 : liability.creationType) === LIABILITY_CREATION_TYPES_LOOKUP.CUSTOM;
  });
});
var openLiabilitiesSelector = createSelector([liabilitiesItemsSelector], function (liabilitiesItems) {
  return Object.values(liabilitiesItems).filter(function (liability) {
    var _liability$attributes;

    return (liability === null || liability === void 0 ? void 0 : (_liability$attributes = liability.attributes) === null || _liability$attributes === void 0 ? void 0 : _liability$attributes.accountStatus) === LIABILITY_ACCOUNT_STATUS_TYPES_LOOKUP.OPEN;
  });
});
var frozenLiabilitiesSelector = createSelector([liabilitiesItemsSelector], function (liabilitiesItems) {
  return Object.values(liabilitiesItems).filter(function (liability) {
    var _liability$attributes2;

    return (liability === null || liability === void 0 ? void 0 : (_liability$attributes2 = liability.attributes) === null || _liability$attributes2 === void 0 ? void 0 : _liability$attributes2.accountStatus) === LIABILITY_ACCOUNT_STATUS_TYPES_LOOKUP.FROZEN;
  });
});
var totalMonthlyLiabilitiesSelector = createSelector([liabilitiesSelector], function (liabilities) {
  var _liabilities$totalMon;

  return (_liabilities$totalMon = liabilities === null || liabilities === void 0 ? void 0 : liabilities.totalMonthlyLiabilities) !== null && _liabilities$totalMon !== void 0 ? _liabilities$totalMon : 0;
});
var requestLiabilityFormFieldsSelector = createSelector([liabilityFieldsSelector, borrowerIDPropSelector], function (fields, borrowerID) {
  var _fields$sharedWith = fields.sharedWith,
      sharedWith = _fields$sharedWith === void 0 ? {} : _fields$sharedWith,
      _fields$attributes = fields.attributes;
  _fields$attributes = _fields$attributes === void 0 ? {} : _fields$attributes;

  var accountStatus = _fields$attributes.accountStatus,
      attributesForRequest = _objectWithoutProperties(_fields$attributes, _excluded$9),
      rest = _objectWithoutProperties(fields, _excluded2$2);

  return Object.assign({
    attributes: attributesForRequest
  }, rest, {
    borrowers: [borrowerID].concat(_toConsumableArray(Object.keys(sharedWith).filter(function (key) {
      return sharedWith[key];
    })))
  });
});
var creationTypeSelector = createSelector([requestLiabilityFormFieldsSelector], function (_ref) {
  var creationType = _ref.creationType;
  return creationType;
});
var customLiabilityIDFormFieldsSelector = createSelector([requestLiabilityFormFieldsSelector], function (_ref2) {
  var id = _ref2.id;
  return id;
});
var customLiabilityFormFieldsSelector = createSelector([requestLiabilityFormFieldsSelector], function (_ref3) {
  var attributes = _ref3.attributes,
      borrowers = _ref3.borrowers,
      realEstateID = _ref3.realEstateID;
  return {
    attributes: attributes,
    borrowers: borrowers,
    realEstateID: realEstateID
  };
});
var overrideLiabilityIDFormFieldsSelector = createSelector([requestLiabilityFormFieldsSelector], function (_ref4) {
  var overrideLiabilityID = _ref4.overrideLiabilityID;
  return overrideLiabilityID;
});
var overrideLiabilityFormFieldsSelector = createSelector([requestLiabilityFormFieldsSelector], function (_ref5) {
  var id = _ref5.id,
      attributes = _ref5.attributes,
      overrideLiabilityID = _ref5.overrideLiabilityID,
      realEstateID = _ref5.realEstateID;
  return {
    creditLiabilityID: id,
    attributes: attributes,
    overrideLiabilityID: overrideLiabilityID,
    realEstateID: realEstateID
  };
});
var paidOpenAndCustomLiabilitiesSelector = createSelector([openLiabilitiesSelector, customLiabilitiesSelector], function (openLiabilities, customLiabilities) {
  return [].concat(_toConsumableArray(openLiabilities), _toConsumableArray(customLiabilities)).filter(function (liability) {
    var _liability$attributes3;

    return liability === null || liability === void 0 ? void 0 : (_liability$attributes3 = liability.attributes) === null || _liability$attributes3 === void 0 ? void 0 : _liability$attributes3.payOffType;
  });
});
var closingCostsLiabilitiesSelector = createSelector([paidOpenAndCustomLiabilitiesSelector], function (paidOpenAndCustomLiabilities) {
  return paidOpenAndCustomLiabilities.filter(function (liability) {
    return CLOSING_COSTS_LIABILITIES_PAY_OFF_TYPES.includes(liability.attributes.payOffType);
  });
});
var paidOffThroughLoanLiabilitiesSelector = createSelector([paidOpenAndCustomLiabilitiesSelector], function (paidOpenAndCustomLiabilities) {
  return paidOpenAndCustomLiabilities.filter(function (liability) {
    return PAID_THROUGH_LOAN_LIABILITIES_PAY_OFF_TYPES.includes(liability.attributes.payOffType);
  });
});
var totalLiabilitiesPaidAtClosingSelector = createSelector([closingCostsLiabilitiesSelector], function (liabilities) {
  return liabilities.reduce(function (acc, liability) {
    var _liability$attributes4;

    return acc + ((_liability$attributes4 = liability.attributes.unpaidBalance) !== null && _liability$attributes4 !== void 0 ? _liability$attributes4 : 0);
  }, 0);
});
var totalLiabilitiesPaidOffThroughLoanSelector = createSelector([paidOffThroughLoanLiabilitiesSelector], function (liabilities) {
  return liabilities.reduce(function (acc, liability) {
    var _liability$attributes5;

    return acc + ((_liability$attributes5 = liability.attributes.unpaidBalance) !== null && _liability$attributes5 !== void 0 ? _liability$attributes5 : 0);
  }, 0);
});

var liabilitiesBase = /*#__PURE__*/Object.freeze({
  __proto__: null,
  liabilitiesSelector: liabilitiesSelector,
  liabilitiesItemsSelector: liabilitiesItemsSelector,
  liabilityFieldsSelector: liabilityFieldsSelector,
  customLiabilitiesSelector: customLiabilitiesSelector,
  openLiabilitiesSelector: openLiabilitiesSelector,
  frozenLiabilitiesSelector: frozenLiabilitiesSelector,
  totalMonthlyLiabilitiesSelector: totalMonthlyLiabilitiesSelector,
  requestLiabilityFormFieldsSelector: requestLiabilityFormFieldsSelector,
  creationTypeSelector: creationTypeSelector,
  customLiabilityIDFormFieldsSelector: customLiabilityIDFormFieldsSelector,
  customLiabilityFormFieldsSelector: customLiabilityFormFieldsSelector,
  overrideLiabilityIDFormFieldsSelector: overrideLiabilityIDFormFieldsSelector,
  overrideLiabilityFormFieldsSelector: overrideLiabilityFormFieldsSelector,
  paidOpenAndCustomLiabilitiesSelector: paidOpenAndCustomLiabilitiesSelector,
  closingCostsLiabilitiesSelector: closingCostsLiabilitiesSelector,
  paidOffThroughLoanLiabilitiesSelector: paidOffThroughLoanLiabilitiesSelector,
  totalLiabilitiesPaidAtClosingSelector: totalLiabilitiesPaidAtClosingSelector,
  totalLiabilitiesPaidOffThroughLoanSelector: totalLiabilitiesPaidOffThroughLoanSelector
});

var _constants$loanFile$5 = constants.loanFile,
    LIABILITY_CREATION_TYPES_LOOKUP$1 = _constants$loanFile$5.LIABILITY_CREATION_TYPES_LOOKUP,
    CREDIT_PULL_TYPE_LOOKUP$1 = _constants$loanFile$5.CREDIT_PULL_TYPE_LOOKUP;
var DELETE_ORIGINAL_LIABILITY_ERROR = 'Credit report liability cannot be deleted, mark unpaid balance and monthly payment as 0 instead';
var UPDATE_LIABILITY_FORM_FIELDS = 'UPDATE_LIABILITY_FORM_FIELDS';
var updateLiabilityFormFields = function updateLiabilityFormFields(fields, errors) {
  return {
    type: UPDATE_LIABILITY_FORM_FIELDS,
    payload: {
      fields: fields,
      errors: errors
    }
  };
};
var RESET_LIABILITY_FORM_FIELDS = 'RESET_LIABILITY_FORM_FIELDS';
var resetLiabilityFormFields = function resetLiabilityFormFields() {
  return {
    type: RESET_LIABILITY_FORM_FIELDS
  };
};
var SET_LIABILITY_FORM_FIELDS = 'SET_LIABILITY_FORM_FIELDS';
var setLiabilityFormFields = function setLiabilityFormFields(item) {
  return {
    type: SET_LIABILITY_FORM_FIELDS,
    payload: item
  };
};
var SET_LIABILITIES_FROM_CREDIT = 'SET_LIABILITIES_FROM_CREDIT';
var setLiabilitiesFromCredit = function setLiabilitiesFromCredit(creditReport) {
  return {
    type: SET_LIABILITIES_FROM_CREDIT,
    payload: creditReport
  };
};
var CREATE_CUSTOM_LIABILITY = createActionObject('CREATE_CUSTOM_LIABILITY');
var UPDATE_CUSTOM_LIABILITY = createActionObject('UPDATE_CUSTOM_LIABILITY');
var DELETE_CUSTOM_LIABILITY = createActionObject('DELETE_CUSTOM_LIABILITY');
var CREATE_OVERRIDE_LIABILITY = createActionObject('CREATE_OVERRIDE_LIABILITY');
var UPDATE_OVERRIDE_LIABILITY = createActionObject('UPDATE_OVERRIDE_LIABILITY');
var DELETE_OVERRIDE_LIABILITY = createActionObject('DELETE_OVERRIDE_LIABILITY');
var createCustomLiability = function createCustomLiability(loanID, customLiability) {
  return API.credit.POST.thunk({
    action: 'v1/custom-liabilities',
    input: Object.assign({
      loanID: loanID
    }, customLiability),
    authenticated: true
  }, CREATE_CUSTOM_LIABILITY);
};
var createOverrideLiability = function createOverrideLiability(loanID, overrideLiability) {
  return API.credit.POST.thunk({
    action: 'v1/override-liabilities',
    input: Object.assign({
      loanID: loanID
    }, overrideLiability),
    authenticated: true
  }, CREATE_OVERRIDE_LIABILITY);
};
var updateCustomLiability = function updateCustomLiability(customLiabilityID, customLiability) {
  return API.credit.PUT.thunk({
    action: "v1/custom-liabilities/" + customLiabilityID,
    input: customLiability,
    authenticated: true,
    metadata: customLiabilityID
  }, UPDATE_CUSTOM_LIABILITY);
};
var updateOverrideLiability = function updateOverrideLiability(_ref) {
  var overrideLiabilityID = _ref.overrideLiabilityID,
      creditLiabilityID = _ref.creditLiabilityID,
      attributes = _ref.attributes,
      realEstateID = _ref.realEstateID;
  return API.credit.PUT.thunk({
    action: "v1/override-liabilities/" + overrideLiabilityID,
    input: {
      attributes: attributes,
      realEstateID: realEstateID
    },
    authenticated: true,
    metadata: creditLiabilityID
  }, UPDATE_OVERRIDE_LIABILITY);
};
var deleteCustomLiability = function deleteCustomLiability(customLiabilityID) {
  return API.credit.DELETE.thunk({
    action: "v1/custom-liabilities/" + customLiabilityID,
    authenticated: true,
    metadata: customLiabilityID
  }, DELETE_CUSTOM_LIABILITY);
};
var deleteOverrideLiability = function deleteOverrideLiability(overrideLiabilityID) {
  return API.credit.DELETE.thunk({
    action: "v1/override-liabilities/" + overrideLiabilityID,
    authenticated: true
  }, DELETE_OVERRIDE_LIABILITY);
};
var createCustomLiabilityAndPopulateStore = function createCustomLiabilityAndPopulateStore(props) {
  return function (dispatch, getState) {
    var loanID = loanIDPropSelector(getState(), props);
    var customLiability = customLiabilityFormFieldsSelector(getState(), props);
    return dispatch(createCustomLiability(loanID, customLiability));
  };
};
var handleDeleteLiability = function handleDeleteLiability(props) {
  return function (dispatch, getState) {
    var creationType = creationTypeSelector(getState(), props);

    if (creationType === LIABILITY_CREATION_TYPES_LOOKUP$1.CUSTOM) {
      var id = customLiabilityIDFormFieldsSelector(getState(), props);
      return dispatch(deleteCustomLiability(id));
    } else if (creationType === LIABILITY_CREATION_TYPES_LOOKUP$1.ORIGINAL) {
      return Promise.reject({
        type: 'CustomError',
        msg: DELETE_ORIGINAL_LIABILITY_ERROR
      });
    } else {
      // creationType = Override
      var overrideLiabilityID = overrideLiabilityIDFormFieldsSelector(getState(), props);
      return dispatch(deleteOverrideLiability(overrideLiabilityID));
    }
  };
};
var handleUpdateLiability = function handleUpdateLiability(props) {
  return function (dispatch, getState) {
    var creationType = creationTypeSelector(getState(), props);

    if (creationType === LIABILITY_CREATION_TYPES_LOOKUP$1.CUSTOM) {
      var id = customLiabilityIDFormFieldsSelector(getState(), props);
      var customLiability = customLiabilityFormFieldsSelector(getState(), props);
      return dispatch(updateCustomLiability(id, customLiability));
    } else if (creationType === LIABILITY_CREATION_TYPES_LOOKUP$1.ORIGINAL) {
      // Original liability cannot be updated. So create an override instead.
      var loanID = loanIDPropSelector(getState(), props);
      var overrideLiability = overrideLiabilityFormFieldsSelector(getState(), props);
      return dispatch(createOverrideLiability(loanID, overrideLiability));
    } else {
      // This is an override and can be updated
      var _overrideLiability = overrideLiabilityFormFieldsSelector(getState(), props);

      return dispatch(updateOverrideLiability(_overrideLiability));
    }
  };
};

var extractLiabilitiesFromCreditResponse = function extractLiabilitiesFromCreditResponse(payload) {
  var _payload$included, _payload$included$cus, _payload$included2;

  var liabilities = (payload === null || payload === void 0 ? void 0 : payload.creditPullType) === CREDIT_PULL_TYPE_LOOKUP$1.HARD ? payload === null || payload === void 0 ? void 0 : (_payload$included = payload.included) === null || _payload$included === void 0 ? void 0 : _payload$included.liabilities : {};
  var customLiabilities = (_payload$included$cus = payload === null || payload === void 0 ? void 0 : (_payload$included2 = payload.included) === null || _payload$included2 === void 0 ? void 0 : _payload$included2.customLiabilities) !== null && _payload$included$cus !== void 0 ? _payload$included$cus : {};
  var items = {};
  Object.keys(liabilities).forEach(function (liabilityID) {
    return items[liabilityID] = Object.assign({}, liabilities[liabilityID], {
      creationType: liabilities[liabilityID].overrideLiabilityID ? LIABILITY_CREATION_TYPES_LOOKUP$1.OVERRIDE : LIABILITY_CREATION_TYPES_LOOKUP$1.ORIGINAL
    });
  });
  Object.keys(customLiabilities).forEach(function (customLiabilityID) {
    return items[customLiabilityID] = Object.assign({}, customLiabilities[customLiabilityID], {
      creationType: LIABILITY_CREATION_TYPES_LOOKUP$1.CUSTOM
    });
  });
  return items;
};

var DEFAULT_STATE$7 = {
  items: {},
  totalMonthlyLiabilities: 0,
  fields: {
    attributes: {},
    sharedWith: {},
    realEstateID: null,
    creationType: LIABILITY_CREATION_TYPES_LOOKUP$1.CUSTOM,
    id: null
  },
  errors: {}
};
var liabilities = (function (state, _ref2) {
  if (state === void 0) {
    state = DEFAULT_STATE$7;
  }

  var type = _ref2.type,
      payload = _ref2.payload;

  switch (type) {
    case SET_LIABILITIES_FROM_CREDIT:
      {
        var _payload$loanReport, _payload$loanReport$c;

        return Object.assign({}, state, {
          items: extractLiabilitiesFromCreditResponse(payload),
          totalMonthlyLiabilities: payload === null || payload === void 0 ? void 0 : (_payload$loanReport = payload.loanReport) === null || _payload$loanReport === void 0 ? void 0 : (_payload$loanReport$c = _payload$loanReport.computed) === null || _payload$loanReport$c === void 0 ? void 0 : _payload$loanReport$c.totalMonthlyLiabilities
        });
      }

    case UPDATE_LIABILITY_FORM_FIELDS:
      return isDeeplyEmpty(payload.errors) ? Object.assign({}, state, {
        fields: payload.fields,
        errors: {}
      }) : Object.assign({}, state, {
        errors: payload.errors
      });

    case RESET_LIABILITY_FORM_FIELDS:
      return Object.assign({}, state, {
        fields: DEFAULT_STATE$7.fields,
        errors: {}
      });

    case SET_LIABILITY_FORM_FIELDS:
      return Object.assign({}, state, {
        fields: payload,
        errors: {}
      });

    default:
      return state;
  }
});

var liabilities$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  DELETE_ORIGINAL_LIABILITY_ERROR: DELETE_ORIGINAL_LIABILITY_ERROR,
  UPDATE_LIABILITY_FORM_FIELDS: UPDATE_LIABILITY_FORM_FIELDS,
  updateLiabilityFormFields: updateLiabilityFormFields,
  RESET_LIABILITY_FORM_FIELDS: RESET_LIABILITY_FORM_FIELDS,
  resetLiabilityFormFields: resetLiabilityFormFields,
  SET_LIABILITY_FORM_FIELDS: SET_LIABILITY_FORM_FIELDS,
  setLiabilityFormFields: setLiabilityFormFields,
  SET_LIABILITIES_FROM_CREDIT: SET_LIABILITIES_FROM_CREDIT,
  setLiabilitiesFromCredit: setLiabilitiesFromCredit,
  CREATE_CUSTOM_LIABILITY: CREATE_CUSTOM_LIABILITY,
  UPDATE_CUSTOM_LIABILITY: UPDATE_CUSTOM_LIABILITY,
  DELETE_CUSTOM_LIABILITY: DELETE_CUSTOM_LIABILITY,
  CREATE_OVERRIDE_LIABILITY: CREATE_OVERRIDE_LIABILITY,
  UPDATE_OVERRIDE_LIABILITY: UPDATE_OVERRIDE_LIABILITY,
  DELETE_OVERRIDE_LIABILITY: DELETE_OVERRIDE_LIABILITY,
  createCustomLiability: createCustomLiability,
  createOverrideLiability: createOverrideLiability,
  updateCustomLiability: updateCustomLiability,
  updateOverrideLiability: updateOverrideLiability,
  deleteCustomLiability: deleteCustomLiability,
  deleteOverrideLiability: deleteOverrideLiability,
  createCustomLiabilityAndPopulateStore: createCustomLiabilityAndPopulateStore,
  handleDeleteLiability: handleDeleteLiability,
  handleUpdateLiability: handleUpdateLiability,
  DEFAULT_STATE: DEFAULT_STATE$7,
  'default': liabilities
});

var _excluded$a = ["primaryBorrowerID", "jointBorrowerID", "attributes"];
var CREDIT_PULL_TIMEOUT = 120000;
var CREDIT_PULL_TYPE_LOOKUP$2 = constants.loanFile.CREDIT_PULL_TYPE_LOOKUP;
var TOGGLE_JOINT_BORROWER = 'TOGGLE_JOINT_BORROWER';
var toggleJointBorrower = function toggleJointBorrower(borrowerID) {
  return {
    type: TOGGLE_JOINT_BORROWER,
    payload: borrowerID
  };
};
var SET_CREDIT_REPORT_RESULTS = 'SET_CREDIT_REPORT_RESULTS';

var setCreditReportResults = function setCreditReportResults(creditReports) {
  return {
    type: SET_CREDIT_REPORT_RESULTS,
    payload: creditReports
  };
};

var fetchReports = function fetchReports(loanID) {
  return function (dispatch) {
    return new Promise(function ($return, $error) {
      var _hard$included, _soft$included, _await$Promise$all, _await$Promise$all2, soft, hard, consolidatedCreditReport, creditPullType, creditReportForLiabilities;

      return Promise.resolve(Promise.all([dispatch(apiFetchReports(loanID, CREDIT_PULL_TYPE_LOOKUP$2.SOFT)), dispatch(apiFetchReports(loanID, CREDIT_PULL_TYPE_LOOKUP$2.HARD))])).then(function ($await_1) {
        try {
          _await$Promise$all = $await_1, _await$Promise$all2 = _slicedToArray(_await$Promise$all, 2), soft = _await$Promise$all2[0], hard = _await$Promise$all2[1];
          consolidatedCreditReport = {
            soft: soft,
            hard: hard
          };
          creditPullType = !isEmpty(hard === null || hard === void 0 ? void 0 : (_hard$included = hard.included) === null || _hard$included === void 0 ? void 0 : _hard$included.reports) ? CREDIT_PULL_TYPE_LOOKUP$2.HARD : !isEmpty(soft === null || soft === void 0 ? void 0 : (_soft$included = soft.included) === null || _soft$included === void 0 ? void 0 : _soft$included.reports) ? CREDIT_PULL_TYPE_LOOKUP$2.SOFT : null;
          creditReportForLiabilities = creditPullType === CREDIT_PULL_TYPE_LOOKUP$2.HARD ? Object.assign({}, hard) : Object.assign({}, soft);
          creditReportForLiabilities.creditPullType = creditPullType;
          dispatch(setCreditReportResults(consolidatedCreditReport));
          dispatch(setLiabilitiesFromCredit(creditReportForLiabilities));
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  };
};
var CREDIT_STATUS_FIELDS = {
  currentAddress: 'current address',
  taxpayerID: 'Social Security number'
};
var parseCreditErrors = function parseCreditErrors(errors) {
  return errors ? Object.entries(errors).reduce(function (acc, _ref) {
    var _validationError$desc, _Object$assign;

    var _ref2 = _slicedToArray(_ref, 2),
        borrowerID = _ref2[0],
        validationError = _ref2[1];

    return validationError !== null && validationError !== void 0 && validationError.description ? Object.assign({}, acc, (_Object$assign = {}, _Object$assign[borrowerID] = (_validationError$desc = validationError.description) === null || _validationError$desc === void 0 ? void 0 : _validationError$desc.map(function (errorField) {
      var _CREDIT_STATUS_FIELDS;

      return (_CREDIT_STATUS_FIELDS = CREDIT_STATUS_FIELDS[errorField === null || errorField === void 0 ? void 0 : errorField.loc[0]]) !== null && _CREDIT_STATUS_FIELDS !== void 0 ? _CREDIT_STATUS_FIELDS : errorField === null || errorField === void 0 ? void 0 : errorField.loc[0];
    }), _Object$assign)) : acc;
  }, {}) : null;
};
var PULL_CREDIT_REPORT = createActionObject('PULL_CREDIT_REPORT');

var apiPullCreditReport = function apiPullCreditReport(apiVersion, input) {
  return API.credit.POST.thunk({
    action: apiVersion + "/loan-reports",
    input: input,
    timeout: CREDIT_PULL_TIMEOUT,
    authenticated: true
  }, PULL_CREDIT_REPORT);
};

var pullCreditReport = function pullCreditReport(input) {
  return function (dispatch, getState) {
    return new Promise(function ($return, $error) {
      var state = getState();
      var showBorrowerPairs = shouldShowBorrowerPairsManagementSelector(state);
      var apiVersion = showBorrowerPairs ? 'v2' : 'v1';
      return $return(dispatch(apiPullCreditReport(apiVersion, input)));
    });
  };
};
var pullCreditReportAndFetchReports = function pullCreditReportAndFetchReports(_ref3) {
  var loanID = _ref3.loanID,
      borrowerID = _ref3.borrowerID,
      jointBorrowerID = _ref3.jointBorrowerID;
  return function (dispatch) {
    return new Promise(function ($return, $error) {
      return Promise.resolve(dispatch(pullCreditReport({
        loanID: loanID,
        borrowerID: borrowerID,
        jointBorrowerID: jointBorrowerID
      }))).then(function ($await_2) {
        try {
          return Promise.resolve(dispatch(fetchReports(loanID))).then($return, $error);
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  };
};

var transformLoanReportsResponse = function transformLoanReportsResponse(payload) {
  var _payload$loanReport, _payload$loanReport2;

  var reports = ((payload === null || payload === void 0 ? void 0 : (_payload$loanReport = payload.loanReport) === null || _payload$loanReport === void 0 ? void 0 : _payload$loanReport.reports) || []).reduce(function (resultsPerBorrower, reportID) {
    var _payload$included;

    var report = payload === null || payload === void 0 ? void 0 : (_payload$included = payload.included) === null || _payload$included === void 0 ? void 0 : _payload$included.reports[reportID];

    var primaryBorrowerID = report.primaryBorrowerID,
        jointBorrowerID = report.jointBorrowerID,
        attributes = report.attributes,
        metadata = _objectWithoutProperties(report, _excluded$a);

    var borrowers = [{
      borrowerID: primaryBorrowerID,
      scores: attributes === null || attributes === void 0 ? void 0 : attributes.primaryBorrowerScores
    }, {
      borrowerID: jointBorrowerID,
      scores: attributes === null || attributes === void 0 ? void 0 : attributes.jointBorrowerScores
    }].filter(function (borrower) {
      return borrower === null || borrower === void 0 ? void 0 : borrower.borrowerID;
    });
    borrowers.forEach(function (_ref4) {
      var borrowerID = _ref4.borrowerID,
          scores = _ref4.scores;

      if (!resultsPerBorrower[borrowerID]) {
        resultsPerBorrower[borrowerID] = Object.assign({
          scores: scores
        }, metadata);
      }
    });
    return resultsPerBorrower;
  }, {});
  var creditResults = {
    reports: reports,
    computed: (payload === null || payload === void 0 ? void 0 : (_payload$loanReport2 = payload.loanReport) === null || _payload$loanReport2 === void 0 ? void 0 : _payload$loanReport2.computed) || {}
  };
  return creditResults;
};

var DEFAULT_STATE$8 = {
  selectedJointBorrowers: null,
  status: null,
  softResults: null,
  hardResults: null
};
var credit = (function (state, _ref5) {
  var _Object$assign3;

  if (state === void 0) {
    state = DEFAULT_STATE$8;
  }

  var type = _ref5.type,
      payload = _ref5.payload,
      metadata = _ref5.metadata;

  switch (type) {
    case FETCH_CURRENT_REPORTS.BEGIN:
      return Object.assign({}, state, {
        selectedJointBorrowers: {},
        status: {
          type: STATUS.LOADING
        }
      });

    case FETCH_CURRENT_REPORTS.FAILURE:
      return Object.assign({}, state, {
        status: {
          type: STATUS.ERROR,
          action: FETCH_CURRENT_REPORTS.FAILURE
        }
      });

    case SET_CREDIT_REPORT_RESULTS:
      return Object.assign({}, state, {
        hardResults: transformLoanReportsResponse(payload.hard),
        softResults: transformLoanReportsResponse(payload.soft),
        status: {
          type: STATUS.SUCCESS
        }
      });

    case PULL_CREDIT_REPORT.BEGIN:
      return Object.assign({}, state, {
        status: {
          type: STATUS.LOADING
        }
      });

    case PULL_CREDIT_REPORT.SUCCESS:
      return Object.assign({}, state, {
        status: {
          type: STATUS.SUCCESS
        }
      });

    case PULL_CREDIT_REPORT.FAILURE:
      var _ref6, _payload$msg, _errors$;

      // For error schema, see
      // https://credit.dev.zillowhomeloans.net/docs#/report/create_loan_credit_report_api_v1_loans_reports_post
      var errors = Object.values((payload === null || payload === void 0 ? void 0 : payload.errors) || {});
      var errorType = payload === null || payload === void 0 ? void 0 : payload.type;
      return Object.assign({}, state, {
        status: {
          type: STATUS.ERROR,
          action: PULL_CREDIT_REPORT.FAILURE,
          message: (_ref6 = (_payload$msg = payload === null || payload === void 0 ? void 0 : payload.msg) !== null && _payload$msg !== void 0 ? _payload$msg : (_errors$ = errors[0]) === null || _errors$ === void 0 ? void 0 : _errors$.error) !== null && _ref6 !== void 0 ? _ref6 : errorType,
          errorFields: parseCreditErrors(payload === null || payload === void 0 ? void 0 : payload.errors)
        }
      });

    case TOGGLE_JOINT_BORROWER:
      var _Object$assign2;

      return Object.assign({}, state, {
        selectedJointBorrowers: Object.assign({}, state.selectedJointBorrowers, (_Object$assign2 = {}, _Object$assign2[payload] = !state.selectedJointBorrowers[payload], _Object$assign2))
      });

    case API_REMOVE_BORROWER.SUCCESS:
      return Object.assign({}, state, {
        selectedJointBorrowers: Object.assign({}, state.selectedJointBorrowers, (_Object$assign3 = {}, _Object$assign3[metadata.borrowerID] = undefined, _Object$assign3))
      });

    default:
      return state;
  }
});

var credit$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  apiFetchReports: apiFetchReports,
  FETCH_CURRENT_REPORTS: FETCH_CURRENT_REPORTS,
  CREDIT_PULL_TIMEOUT: CREDIT_PULL_TIMEOUT,
  TOGGLE_JOINT_BORROWER: TOGGLE_JOINT_BORROWER,
  toggleJointBorrower: toggleJointBorrower,
  SET_CREDIT_REPORT_RESULTS: SET_CREDIT_REPORT_RESULTS,
  fetchReports: fetchReports,
  CREDIT_STATUS_FIELDS: CREDIT_STATUS_FIELDS,
  parseCreditErrors: parseCreditErrors,
  PULL_CREDIT_REPORT: PULL_CREDIT_REPORT,
  pullCreditReport: pullCreditReport,
  pullCreditReportAndFetchReports: pullCreditReportAndFetchReports,
  DEFAULT_STATE: DEFAULT_STATE$8,
  'default': credit
});

var _CREDIT_STATUS_MESSAG;
var CREDIT_PULL_TYPE_LOOKUP$3 = constants.loanFile.CREDIT_PULL_TYPE_LOOKUP;
var CREDIT_STATUS_MESSAGES = (_CREDIT_STATUS_MESSAG = {}, _CREDIT_STATUS_MESSAG[FETCH_CURRENT_REPORTS.FAILURE] = CONNECTION_ERROR_MESSAGE, _CREDIT_STATUS_MESSAG[PULL_CREDIT_REPORT.FAILURE] = CONNECTION_ERROR_MESSAGE, _CREDIT_STATUS_MESSAG.IncompleteBorrower = 'Information missing that is needed to run credit', _CREDIT_STATUS_MESSAG.MissingCreditConsent = 'Incomplete credit consent for one or more borrowers', _CREDIT_STATUS_MESSAG.SSNMismatch = 'The SSN provided for one or more borrowers did not match the credit vendor database', _CREDIT_STATUS_MESSAG);
var LOAN_REPORT_STATUS_MESSAGES = {
  RepeatingReports: 'Duplicate credit reports received. Click run credit to update reports.',
  NonExistentBorrowerReport: 'One or more borrowers have been removed. Click run credit to update reports.',
  IncompleteCreditPull: 'Credit has not been pulled for all borrowers'
};
var creditSelector = createSelector([loanFileSelector], function (loanFile) {
  var _loanFile$credit;

  return (_loanFile$credit = loanFile === null || loanFile === void 0 ? void 0 : loanFile.credit) !== null && _loanFile$credit !== void 0 ? _loanFile$credit : null;
});
var softResultsSelector = createSelector([creditSelector], function (credit) {
  var _credit$softResults;

  return (_credit$softResults = credit === null || credit === void 0 ? void 0 : credit.softResults) !== null && _credit$softResults !== void 0 ? _credit$softResults : null;
});
var hardResultsSelector = createSelector([creditSelector], function (credit) {
  var _credit$hardResults;

  return (_credit$hardResults = credit === null || credit === void 0 ? void 0 : credit.hardResults) !== null && _credit$hardResults !== void 0 ? _credit$hardResults : null;
});
var creditPullTypeSelector = createSelector([softResultsSelector, hardResultsSelector], function (soft, hard) {
  return !isEmpty(hard === null || hard === void 0 ? void 0 : hard.reports) ? CREDIT_PULL_TYPE_LOOKUP$3.HARD : !isEmpty(soft === null || soft === void 0 ? void 0 : soft.reports) ? CREDIT_PULL_TYPE_LOOKUP$3.SOFT : null;
});
var primaryCreditResultsSelector = createSelector([softResultsSelector, hardResultsSelector, creditPullTypeSelector], function (soft, hard, type) {
  return type === CREDIT_PULL_TYPE_LOOKUP$3.HARD ? hard : type === CREDIT_PULL_TYPE_LOOKUP$3.SOFT ? soft : null;
});
var jointBorrowerIDsSetSelector = createSelector([creditSelector], function (credit) {
  var _credit$selectedJoint;

  return (_credit$selectedJoint = credit === null || credit === void 0 ? void 0 : credit.selectedJointBorrowers) !== null && _credit$selectedJoint !== void 0 ? _credit$selectedJoint : null;
});
var jointCreditPullDisabledSelector = createSelector([creditSelector], function (credit) {
  return credit !== null && credit !== void 0 && credit.selectedJointBorrowers ? Object.values(credit.selectedJointBorrowers).filter(function (selected) {
    return selected;
  }).length !== 2 : true;
});
var creditStatusSelector = createSelector([creditSelector], function (credit) {
  var _credit$status, _credit$status2, _ref, _credit$status3;

  var defaultActionMessage = CREDIT_STATUS_MESSAGES[credit === null || credit === void 0 ? void 0 : (_credit$status = credit.status) === null || _credit$status === void 0 ? void 0 : _credit$status.action];
  var friendlyMessage = CREDIT_STATUS_MESSAGES[credit === null || credit === void 0 ? void 0 : (_credit$status2 = credit.status) === null || _credit$status2 === void 0 ? void 0 : _credit$status2.message];
  return credit !== null && credit !== void 0 && credit.status ? Object.assign({}, credit.status, {
    message: (_ref = friendlyMessage !== null && friendlyMessage !== void 0 ? friendlyMessage : credit === null || credit === void 0 ? void 0 : (_credit$status3 = credit.status) === null || _credit$status3 === void 0 ? void 0 : _credit$status3.message) !== null && _ref !== void 0 ? _ref : defaultActionMessage
  }) : {};
}); // https://credit.dev.zillowhomeloans.net/docs#/loan-report-v1/get_loan_credit_report_api_v1_loan_reports__loanID__get

var COMPUTED_STATUS_INDICATOR_OF_SUCCESS = 'OK';
var creditHasBeenPulledSelector = createSelector([primaryCreditResultsSelector], function (results) {
  var _results$computed;

  return (results === null || results === void 0 ? void 0 : (_results$computed = results.computed) === null || _results$computed === void 0 ? void 0 : _results$computed.status) === COMPUTED_STATUS_INDICATOR_OF_SUCCESS;
});
var showInProgressSelector = createSelector([creditStatusSelector], function (status) {
  return status.type === STATUS.LOADING;
});
var errorMessageSelector = createSelector([creditStatusSelector], function (status) {
  var _status$message;

  return (_status$message = status === null || status === void 0 ? void 0 : status.message) !== null && _status$message !== void 0 ? _status$message : null;
});
var errorFieldsSelector = createSelector([creditStatusSelector], function (status) {
  var _status$errorFields;

  return (_status$errorFields = status === null || status === void 0 ? void 0 : status.errorFields) !== null && _status$errorFields !== void 0 ? _status$errorFields : null;
});
var creditComputedSelector = createSelector([primaryCreditResultsSelector], function (primaryCreditResults) {
  var _primaryCreditResults;

  return (_primaryCreditResults = primaryCreditResults === null || primaryCreditResults === void 0 ? void 0 : primaryCreditResults.computed) !== null && _primaryCreditResults !== void 0 ? _primaryCreditResults : null;
});
var computedLoanTotalScoreSelector = createSelector([creditComputedSelector], function (loanReportComputed) {
  var _loanReportComputed$t;

  return (_loanReportComputed$t = loanReportComputed === null || loanReportComputed === void 0 ? void 0 : loanReportComputed.totalScore) !== null && _loanReportComputed$t !== void 0 ? _loanReportComputed$t : null;
});
var computedLoanReportStatusMessageSelector = createSelector([creditComputedSelector], function (loanReportComputed) {
  var _LOAN_REPORT_STATUS_M;

  return (_LOAN_REPORT_STATUS_M = LOAN_REPORT_STATUS_MESSAGES[loanReportComputed === null || loanReportComputed === void 0 ? void 0 : loanReportComputed.status]) !== null && _LOAN_REPORT_STATUS_M !== void 0 ? _LOAN_REPORT_STATUS_M : null;
});

var creditBase = /*#__PURE__*/Object.freeze({
  __proto__: null,
  CREDIT_STATUS_MESSAGES: CREDIT_STATUS_MESSAGES,
  LOAN_REPORT_STATUS_MESSAGES: LOAN_REPORT_STATUS_MESSAGES,
  creditSelector: creditSelector,
  softResultsSelector: softResultsSelector,
  hardResultsSelector: hardResultsSelector,
  creditPullTypeSelector: creditPullTypeSelector,
  primaryCreditResultsSelector: primaryCreditResultsSelector,
  jointBorrowerIDsSetSelector: jointBorrowerIDsSetSelector,
  jointCreditPullDisabledSelector: jointCreditPullDisabledSelector,
  creditStatusSelector: creditStatusSelector,
  creditHasBeenPulledSelector: creditHasBeenPulledSelector,
  showInProgressSelector: showInProgressSelector,
  errorMessageSelector: errorMessageSelector,
  errorFieldsSelector: errorFieldsSelector,
  creditComputedSelector: creditComputedSelector,
  computedLoanTotalScoreSelector: computedLoanTotalScoreSelector,
  computedLoanReportStatusMessageSelector: computedLoanReportStatusMessageSelector
});

var _excluded$b = ["scores"];
var CREDIT_PULL_TYPE_LOOKUP$4 = constants.loanFile.CREDIT_PULL_TYPE_LOOKUP;
var showJointBorrowerModalSelector = createSelector([borrowerListSelector], function (borrowerList) {
  return borrowerList && borrowerList.length > 2;
});
var allowJointCreditPullOptionSelector = createSelector([borrowerListSelector], function (borrowerList) {
  return borrowerList.length > 1;
});

var getBureauScores = function getBureauScores(bureauNames, scores) {
  var bureauScores = bureauNames.map(function (bureauName) {
    var _scores$bureauName, _scores$bureauName2;

    var bureauScore = scores === null || scores === void 0 ? void 0 : (_scores$bureauName = scores[bureauName]) === null || _scores$bureauName === void 0 ? void 0 : _scores$bureauName.score;
    var bureauStatus = scores === null || scores === void 0 ? void 0 : (_scores$bureauName2 = scores[bureauName]) === null || _scores$bureauName2 === void 0 ? void 0 : _scores$bureauName2.status;
    return {
      bureauName: bureauName,
      score: bureauScore || bureauStatus
    };
  }); // We determine the "mid-point" score out of the list using only the valid integer score for a given bureau.

  var validScores = bureauScores.filter(function (_ref) {
    var score = _ref.score;
    return typeof score === 'number';
  }).sort(function (a, b) {
    return a.score - b.score;
  });
  var midScoreBureau = validScores && validScores.length > 0 ? validScores[Math.ceil(validScores.length / 2) - 1].bureauName : null;
  return bureauScores.map(function (score) {
    return Object.assign({}, score, {
      isMidScore: score.bureauName === midScoreBureau
    });
  });
};

var transformCreditScoreResults = function transformCreditScoreResults(results) {
  var scores = results.scores,
      rest = _objectWithoutProperties(results, _excluded$b);

  return Object.assign({}, rest, {
    scores: getBureauScores(CREDIT_BUREAUS, scores)
  });
};

var creditResultsBorrowerScoresSelector = createSelector([borrowerListSelector, borrowerDisplayNamesSelector, softResultsSelector, hardResultsSelector], function (borrowerList, borrowerDisplayNames, softResults, hardResults) {
  return borrowerList.map(function (borrower) {
    var borrowerID = borrower.id;
    var displayName = borrowerDisplayNames[borrowerID];
    var result = {
      borrowerID: borrowerID,
      displayName: displayName
    };

    if (hardResults !== null && hardResults !== void 0 && hardResults.reports[borrowerID]) {
      result.report = transformCreditScoreResults(hardResults.reports[borrowerID]);
      result.creditPullType = CREDIT_PULL_TYPE_LOOKUP$4.HARD;
    } else if (softResults !== null && softResults !== void 0 && softResults.reports[borrowerID]) {
      result.report = transformCreditScoreResults(softResults.reports[borrowerID]);
      result.creditPullType = CREDIT_PULL_TYPE_LOOKUP$4.SOFT;
    } else {
      result.report = transformCreditScoreResults({});
    }

    return result;
  });
});
var jointBorrowerIDsSelector = createSelector([jointBorrowerIDsSetSelector, borrowerListSelector], function (jointBorrowerIDsSet, borrowersList) {
  // The borrowerIDs need to be ordered in the same way as they are on the loan
  // when we pull a credit report for a borrower pair.
  if (borrowersList.length < 2) {
    // Not enough borrowers on the loan for a joint credit pull
    return {};
  } else if (borrowersList.length === 2) {
    // There are two borrowers, so we know exactly who is on the joint credit pull
    return {
      borrowerID: borrowersList[0].id,
      coBorrowerID: borrowersList[1].id
    };
  } else if (Object.values(jointBorrowerIDsSet || {}).filter(function (selected) {
    return selected;
  }).length === 2) {
    // There are more than two borrowers, so use the ones that are selected, but
    // in the order they are on the loan file.
    var borrowerIDs = borrowersList.filter(function (_ref2) {
      var id = _ref2.id;
      return jointBorrowerIDsSet[id];
    });
    return {
      borrowerID: borrowerIDs[0].id,
      coBorrowerID: borrowerIDs[1].id
    };
  } // 3 or more borrowers and there aren't 2 selected


  return {};
});

var borrowersCredit = /*#__PURE__*/Object.freeze({
  __proto__: null,
  showJointBorrowerModalSelector: showJointBorrowerModalSelector,
  allowJointCreditPullOptionSelector: allowJointCreditPullOptionSelector,
  creditResultsBorrowerScoresSelector: creditResultsBorrowerScoresSelector,
  jointBorrowerIDsSelector: jointBorrowerIDsSelector
});

var COMPUTED_STATUS_INDICATOR_OF_SUCCESS$1 = 'OK'; // https://credit.dev.zillowhomeloans.net/docs#/loan-report-v1/get_loan_credit_report_api_v1_loan_reports__loanID__get

var creditHasBeenPulledSelector$1 = createSelector([primaryCreditResultsSelector, borrowerListSelector], function (results, borrowers) {
  var _results$computed;

  var _ref = results !== null && results !== void 0 ? results : {},
      reports = _ref.reports;

  var borrowersWithReports = Object.keys(reports !== null && reports !== void 0 ? reports : {});
  return (results === null || results === void 0 ? void 0 : (_results$computed = results.computed) === null || _results$computed === void 0 ? void 0 : _results$computed.status) === COMPUTED_STATUS_INDICATOR_OF_SUCCESS$1 && borrowers.every(function (borrower) {
    return borrowersWithReports.includes(borrower.id);
  });
});

var compositeCredit = /*#__PURE__*/Object.freeze({
  __proto__: null,
  creditHasBeenPulledSelector: creditHasBeenPulledSelector$1
});

var detailSelector = createSelector([loanFileSelector], function (loanFile) {
  return (loanFile === null || loanFile === void 0 ? void 0 : loanFile.detail) || null;
});

var detailBase = /*#__PURE__*/Object.freeze({
  __proto__: null,
  detailSelector: detailSelector
});

var isHowCanWeHelpRouteValidSelector = createSelector([detailSelector, borrowerIDsSelector], // Ensure previous inputs exist and no co-borrowers exist
function (detail, borrowerIDs) {
  return !!(detail !== null && detail !== void 0 && detail.purpose && borrowerIDs.length <= 1);
});
var isCoBorrowersRouteValidSelector = createSelector([detailSelector, borrowerIDsSelector], // Ensure previous inputs exist, primary borrower exists, and no co-borrowers exist
function (detail, borrowerIDs) {
  return !!(detail !== null && detail !== void 0 && detail.purpose && detail !== null && detail !== void 0 && detail.uiFlow && borrowerIDs.length === 1);
});
var isBorrowerRouteValidSelector = createSelector([detailSelector, borrowerIDsSelector], // Ensure previous inputs exist
function (detail, borrowerIDs) {
  return !!(detail !== null && detail !== void 0 && detail.purpose && detail !== null && detail !== void 0 && detail.uiFlow && borrowerIDs.length >= 2);
});

var borrowersDetail = /*#__PURE__*/Object.freeze({
  __proto__: null,
  isHowCanWeHelpRouteValidSelector: isHowCanWeHelpRouteValidSelector,
  isCoBorrowersRouteValidSelector: isCoBorrowersRouteValidSelector,
  isBorrowerRouteValidSelector: isBorrowerRouteValidSelector
});

var borrowerLiabilitiesSelector = createSelector([openLiabilitiesSelector, frozenLiabilitiesSelector, customLiabilitiesSelector, borrowerIDPropSelector, ownedRealEstatesItemsByIDSelector], function (openLiabilities, frozenLiabilities, customLiabilities, borrowerID, ownedRealEstateByID) {
  return flatMap([].concat(_toConsumableArray(openLiabilities), _toConsumableArray(frozenLiabilities), _toConsumableArray(customLiabilities)), function (liability) {
    var _liability$borrowers;

    if (liability !== null && liability !== void 0 && (_liability$borrowers = liability.borrowers) !== null && _liability$borrowers !== void 0 && _liability$borrowers.includes(borrowerID)) {
      var _ownedRealEstateByID$;

      return [Object.assign({}, liability, {
        realEstate: (_ownedRealEstateByID$ = ownedRealEstateByID[liability === null || liability === void 0 ? void 0 : liability.realEstateID]) !== null && _ownedRealEstateByID$ !== void 0 ? _ownedRealEstateByID$ : null,
        sharedWith: getSharedWithFromOwners(liability === null || liability === void 0 ? void 0 : liability.borrowers, borrowerID)
      })];
    }

    return [];
  });
});

var borrowersLiabilities = /*#__PURE__*/Object.freeze({
  __proto__: null,
  borrowerLiabilitiesSelector: borrowerLiabilitiesSelector
});

var _excluded$c = ["owners"];
var REAL_ESTATE_RELATED_LIABILITY_TYPES = constants.loanFile.REAL_ESTATE_RELATED_LIABILITY_TYPES;
var realEstateRelatedLiabilitiesByRealEstateSelector = createSelector([borrowerLiabilitiesSelector], function (liabilities) {
  return groupBy(liabilities.filter(function (_ref) {
    var realEstateID = _ref.realEstateID,
        attributes = _ref.attributes;
    return REAL_ESTATE_RELATED_LIABILITY_TYPES.has(attributes.type) && !!realEstateID;
  }), function (mortgage) {
    return mortgage.realEstateID;
  });
});

var getTotalUnpaidBalance = function getTotalUnpaidBalance(mortgages) {
  return mortgages.reduce(function (total, mortgage) {
    var _mortgage$attributes$;

    return total + ((_mortgage$attributes$ = mortgage.attributes.unpaidBalance) !== null && _mortgage$attributes$ !== void 0 ? _mortgage$attributes$ : 0);
  }, 0);
};

var borrowersOwnedRealEstatesItemsSelector = createSelector([ownedRealEstatesItemsSelector, realEstateRelatedLiabilitiesByRealEstateSelector, borrowerIDPropSelector], function (items, liabilitiesByRealEstate, borrowerID) {
  return items.filter(function (_ref2) {
    var owners = _ref2.owners;
    return owners.indexOf(borrowerID) !== -1;
  }).map(function (_ref3) {
    var _liabilitiesByRealEst;

    var owners = _ref3.owners,
        ownedRealEstate = _objectWithoutProperties(_ref3, _excluded$c);

    var relatedLiabilities = (_liabilitiesByRealEst = liabilitiesByRealEstate[ownedRealEstate.id]) !== null && _liabilitiesByRealEst !== void 0 ? _liabilitiesByRealEst : [];
    return Object.assign({}, ownedRealEstate, {
      attributes: Object.assign({}, ownedRealEstate.attributes, {
        totalUnpaidBalance: getTotalUnpaidBalance(relatedLiabilities),
        relatedLiabilities: relatedLiabilities
      }),
      sharedWith: getSharedWithFromOwners(owners, borrowerID)
    });
  });
});
var borrowerOwnedRealEstateOptionsSelector = createSelector([borrowersOwnedRealEstatesItemsSelector], function (items) {
  return items.map(function (_ref4) {
    var id = _ref4.id,
        attributes = _ref4.attributes;
    return {
      value: id,
      label: formatters.displayAddress(attributes.address)
    };
  });
});

var borrowersOwnedRealEstates = /*#__PURE__*/Object.freeze({
  __proto__: null,
  borrowersOwnedRealEstatesItemsSelector: borrowersOwnedRealEstatesItemsSelector,
  borrowerOwnedRealEstateOptionsSelector: borrowerOwnedRealEstateOptionsSelector
});

var borrowersCombinedEmploymentAndIncomeSelector = createSelector([borrowersEmploymentsSelector, borrowersIncomeSelector], function (employments, income) {
  return [].concat(_toConsumableArray(employments), _toConsumableArray(income));
});

var employmentsAndIncome = /*#__PURE__*/Object.freeze({
  __proto__: null,
  borrowersCombinedEmploymentAndIncomeSelector: borrowersCombinedEmploymentAndIncomeSelector
});

function toInteger(dirtyNumber) {
  if (dirtyNumber === null || dirtyNumber === true || dirtyNumber === false) {
    return NaN;
  }

  var number = Number(dirtyNumber);

  if (isNaN(number)) {
    return number;
  }

  return number < 0 ? Math.ceil(number) : Math.floor(number);
}

function requiredArgs(required, args) {
  if (args.length < required) {
    throw new TypeError(required + ' argument' + (required > 1 ? 's' : '') + ' required, but only ' + args.length + ' present');
  }
}

/**
 * @name toDate
 * @category Common Helpers
 * @summary Convert the given argument to an instance of Date.
 *
 * @description
 * Convert the given argument to an instance of Date.
 *
 * If the argument is an instance of Date, the function returns its clone.
 *
 * If the argument is a number, it is treated as a timestamp.
 *
 * If the argument is none of the above, the function returns Invalid Date.
 *
 * **Note**: *all* Date arguments passed to any *date-fns* function is processed by `toDate`.
 *
 * @param {Date|Number} argument - the value to convert
 * @returns {Date} the parsed date in the local time zone
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // Clone the date:
 * const result = toDate(new Date(2014, 1, 11, 11, 30, 30))
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert the timestamp to date:
 * const result = toDate(1392098430000)
 * //=> Tue Feb 11 2014 11:30:30
 */

function toDate(argument) {
  requiredArgs(1, arguments);
  var argStr = Object.prototype.toString.call(argument); // Clone the date

  if (argument instanceof Date || typeof argument === 'object' && argStr === '[object Date]') {
    // Prevent the date to lose the milliseconds when passed to new Date() in IE10
    return new Date(argument.getTime());
  } else if (typeof argument === 'number' || argStr === '[object Number]') {
    return new Date(argument);
  } else {
    if ((typeof argument === 'string' || argStr === '[object String]') && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn("Starting with v2.0.0-beta.1 date-fns doesn't accept strings as date arguments. Please use `parseISO` to parse strings. See: https://git.io/fjule"); // eslint-disable-next-line no-console

      console.warn(new Error().stack);
    }

    return new Date(NaN);
  }
}

/**
 * @name addDays
 * @category Day Helpers
 * @summary Add the specified number of days to the given date.
 *
 * @description
 * Add the specified number of days to the given date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of days to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} - the new date with the days added
 * @throws {TypeError} - 2 arguments required
 *
 * @example
 * // Add 10 days to 1 September 2014:
 * const result = addDays(new Date(2014, 8, 1), 10)
 * //=> Thu Sep 11 2014 00:00:00
 */

function addDays(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var amount = toInteger(dirtyAmount);

  if (isNaN(amount)) {
    return new Date(NaN);
  }

  if (!amount) {
    // If 0 days, no-op to avoid changing times in the hour before end of DST
    return date;
  }

  date.setDate(date.getDate() + amount);
  return date;
}

/**
 * @name addMonths
 * @category Month Helpers
 * @summary Add the specified number of months to the given date.
 *
 * @description
 * Add the specified number of months to the given date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of months to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the months added
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Add 5 months to 1 September 2014:
 * const result = addMonths(new Date(2014, 8, 1), 5)
 * //=> Sun Feb 01 2015 00:00:00
 */

function addMonths(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var amount = toInteger(dirtyAmount);

  if (isNaN(amount)) {
    return new Date(NaN);
  }

  if (!amount) {
    // If 0 months, no-op to avoid changing times in the hour before end of DST
    return date;
  }

  var dayOfMonth = date.getDate(); // The JS Date object supports date math by accepting out-of-bounds values for
  // month, day, etc. For example, new Date(2020, 0, 0) returns 31 Dec 2019 and
  // new Date(2020, 13, 1) returns 1 Feb 2021.  This is *almost* the behavior we
  // want except that dates will wrap around the end of a month, meaning that
  // new Date(2020, 13, 31) will return 3 Mar 2021 not 28 Feb 2021 as desired. So
  // we'll default to the end of the desired month by adding 1 to the desired
  // month and using a date of 0 to back up one day to the end of the desired
  // month.

  var endOfDesiredMonth = new Date(date.getTime());
  endOfDesiredMonth.setMonth(date.getMonth() + amount + 1, 0);
  var daysInMonth = endOfDesiredMonth.getDate();

  if (dayOfMonth >= daysInMonth) {
    // If we're already at the end of the month, then this is the correct date
    // and we're done.
    return endOfDesiredMonth;
  } else {
    // Otherwise, we now know that setting the original day-of-month value won't
    // cause an overflow, so set the desired day-of-month. Note that we can't
    // just set the date of `endOfDesiredMonth` because that object may have had
    // its time changed in the unusual case where where a DST transition was on
    // the last day of the month and its local time was in the hour skipped or
    // repeated next to a DST transition.  So we use `date` instead which is
    // guaranteed to still have the original time.
    date.setFullYear(endOfDesiredMonth.getFullYear(), endOfDesiredMonth.getMonth(), dayOfMonth);
    return date;
  }
}

/**
 * @name add
 * @category Common Helpers
 * @summary Add the specified years, months, weeks, days, hours, minutes and seconds to the given date.
 *
 * @description
 * Add the specified years, months, weeks, days, hours, minutes and seconds to the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Duration} duration - the object with years, months, weeks, days, hours, minutes and seconds to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 *
 * | Key            | Description                        |
 * |----------------|------------------------------------|
 * | years          | Amount of years to be added        |
 * | months         | Amount of months to be added       |
 * | weeks          | Amount of weeks to be added        |
 * | days           | Amount of days to be added         |
 * | hours          | Amount of hours to be added        |
 * | minutes        | Amount of minutes to be added      |
 * | seconds        | Amount of seconds to be added      |
 *
 * All values default to 0
 *
 * @returns {Date} the new date with the seconds added
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Add the following duration to 1 September 2014, 10:19:50
 * const result = add(new Date(2014, 8, 1, 10, 19, 50), {
 *   years: 2,
 *   months: 9,
 *   weeks: 1,
 *   days: 7,
 *   hours: 5,
 *   minutes: 9,
 *   seconds: 30,
 * })
 * //=> Thu Jun 15 2017 15:29:20
 */
function add(dirtyDate, duration) {
  requiredArgs(2, arguments);
  if (!duration || typeof duration !== 'object') return new Date(NaN);
  var years = duration.years ? toInteger(duration.years) : 0;
  var months = duration.months ? toInteger(duration.months) : 0;
  var weeks = duration.weeks ? toInteger(duration.weeks) : 0;
  var days = duration.days ? toInteger(duration.days) : 0;
  var hours = duration.hours ? toInteger(duration.hours) : 0;
  var minutes = duration.minutes ? toInteger(duration.minutes) : 0;
  var seconds = duration.seconds ? toInteger(duration.seconds) : 0; // Add years and months

  var date = toDate(dirtyDate);
  var dateWithMonths = months || years ? addMonths(date, months + years * 12) : date; // Add weeks and days

  var dateWithDays = days || weeks ? addDays(dateWithMonths, days + weeks * 7) : dateWithMonths; // Add days, hours, minutes and seconds

  var minutesToAdd = minutes + hours * 60;
  var secondsToAdd = seconds + minutesToAdd * 60;
  var msToAdd = secondsToAdd * 1000;
  var finalDate = new Date(dateWithDays.getTime() + msToAdd);
  return finalDate;
}

/**
 * @name differenceInMilliseconds
 * @category Millisecond Helpers
 * @summary Get the number of milliseconds between the given dates.
 *
 * @description
 * Get the number of milliseconds between the given dates.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} dateLeft - the later date
 * @param {Date|Number} dateRight - the earlier date
 * @returns {Number} the number of milliseconds
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // How many milliseconds are between
 * // 2 July 2014 12:30:20.600 and 2 July 2014 12:30:21.700?
 * const result = differenceInMilliseconds(
 *   new Date(2014, 6, 2, 12, 30, 21, 700),
 *   new Date(2014, 6, 2, 12, 30, 20, 600)
 * )
 * //=> 1100
 */

function differenceInMilliseconds(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeft = toDate(dirtyDateLeft);
  var dateRight = toDate(dirtyDateRight);
  return dateLeft.getTime() - dateRight.getTime();
}

var MILLISECONDS_IN_HOUR = 3600000;
/**
 * @name differenceInHours
 * @category Hour Helpers
 * @summary Get the number of hours between the given dates.
 *
 * @description
 * Get the number of hours between the given dates.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} dateLeft - the later date
 * @param {Date|Number} dateRight - the earlier date
 * @returns {Number} the number of hours
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // How many hours are between 2 July 2014 06:50:00 and 2 July 2014 19:00:00?
 * var result = differenceInHours(
 *   new Date(2014, 6, 2, 19, 0),
 *   new Date(2014, 6, 2, 6, 50)
 * )
 * //=> 12
 */

function differenceInHours(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var diff = differenceInMilliseconds(dirtyDateLeft, dirtyDateRight) / MILLISECONDS_IN_HOUR;
  return diff > 0 ? Math.floor(diff) : Math.ceil(diff);
}

var MILLISECONDS_IN_HOUR$1 = 3600000;
var MILLISECONDS_IN_MINUTE = 60000;
var DEFAULT_ADDITIONAL_DIGITS = 2;
var patterns = {
  dateTimeDelimiter: /[T ]/,
  timeZoneDelimiter: /[Z ]/i,
  timezone: /([Z+-].*)$/
};
var dateRegex = /^-?(?:(\d{3})|(\d{2})(?:-?(\d{2}))?|W(\d{2})(?:-?(\d{1}))?|)$/;
var timeRegex = /^(\d{2}(?:[.,]\d*)?)(?::?(\d{2}(?:[.,]\d*)?))?(?::?(\d{2}(?:[.,]\d*)?))?$/;
var timezoneRegex = /^([+-])(\d{2})(?::?(\d{2}))?$/;
/**
 * @name parseISO
 * @category Common Helpers
 * @summary Parse ISO string
 *
 * @description
 * Parse the given string in ISO 8601 format and return an instance of Date.
 *
 * Function accepts complete ISO 8601 formats as well as partial implementations.
 * ISO 8601: http://en.wikipedia.org/wiki/ISO_8601
 *
 * If the argument isn't a string, the function cannot parse the string or
 * the values are invalid, it returns Invalid Date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * - The previous `parse` implementation was renamed to `parseISO`.
 *
 *   ```javascript
 *   // Before v2.0.0
 *   parse('2016-01-01')
 *
 *   // v2.0.0 onward
 *   parseISO('2016-01-01')
 *   ```
 *
 * - `parseISO` now validates separate date and time values in ISO-8601 strings
 *   and returns `Invalid Date` if the date is invalid.
 *
 *   ```javascript
 *   parseISO('2018-13-32')
 *   //=> Invalid Date
 *   ```
 *
 * - `parseISO` now doesn't fall back to `new Date` constructor
 *   if it fails to parse a string argument. Instead, it returns `Invalid Date`.
 *
 * @param {String} argument - the value to convert
 * @param {Object} [options] - an object with options.
 * @param {0|1|2} [options.additionalDigits=2] - the additional number of digits in the extended year format
 * @returns {Date} the parsed date in the local time zone
 * @throws {TypeError} 1 argument required
 * @throws {RangeError} `options.additionalDigits` must be 0, 1 or 2
 *
 * @example
 * // Convert string '2014-02-11T11:30:30' to date:
 * var result = parseISO('2014-02-11T11:30:30')
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert string '+02014101' to date,
 * // if the additional number of digits in the extended year format is 1:
 * var result = parseISO('+02014101', { additionalDigits: 1 })
 * //=> Fri Apr 11 2014 00:00:00
 */

function parseISO(argument, dirtyOptions) {
  requiredArgs(1, arguments);
  var options = dirtyOptions || {};
  var additionalDigits = options.additionalDigits == null ? DEFAULT_ADDITIONAL_DIGITS : toInteger(options.additionalDigits);

  if (additionalDigits !== 2 && additionalDigits !== 1 && additionalDigits !== 0) {
    throw new RangeError('additionalDigits must be 0, 1 or 2');
  }

  if (!(typeof argument === 'string' || Object.prototype.toString.call(argument) === '[object String]')) {
    return new Date(NaN);
  }

  var dateStrings = splitDateString(argument);
  var date;

  if (dateStrings.date) {
    var parseYearResult = parseYear(dateStrings.date, additionalDigits);
    date = parseDate(parseYearResult.restDateString, parseYearResult.year);
  }

  if (isNaN(date) || !date) {
    return new Date(NaN);
  }

  var timestamp = date.getTime();
  var time = 0;
  var offset;

  if (dateStrings.time) {
    time = parseTime(dateStrings.time);

    if (isNaN(time) || time === null) {
      return new Date(NaN);
    }
  }

  if (dateStrings.timezone) {
    offset = parseTimezone(dateStrings.timezone);

    if (isNaN(offset)) {
      return new Date(NaN);
    }
  } else {
    var dirtyDate = new Date(timestamp + time); // js parsed string assuming it's in UTC timezone
    // but we need it to be parsed in our timezone
    // so we use utc values to build date in our timezone.
    // Year values from 0 to 99 map to the years 1900 to 1999
    // so set year explicitly with setFullYear.

    var result = new Date(0);
    result.setFullYear(dirtyDate.getUTCFullYear(), dirtyDate.getUTCMonth(), dirtyDate.getUTCDate());
    result.setHours(dirtyDate.getUTCHours(), dirtyDate.getUTCMinutes(), dirtyDate.getUTCSeconds(), dirtyDate.getUTCMilliseconds());
    return result;
  }

  return new Date(timestamp + time + offset);
}

function splitDateString(dateString) {
  var dateStrings = {};
  var array = dateString.split(patterns.dateTimeDelimiter);
  var timeString; // The regex match should only return at maximum two array elements.
  // [date], [time], or [date, time].

  if (array.length > 2) {
    return dateStrings;
  }

  if (/:/.test(array[0])) {
    dateStrings.date = null;
    timeString = array[0];
  } else {
    dateStrings.date = array[0];
    timeString = array[1];

    if (patterns.timeZoneDelimiter.test(dateStrings.date)) {
      dateStrings.date = dateString.split(patterns.timeZoneDelimiter)[0];
      timeString = dateString.substr(dateStrings.date.length, dateString.length);
    }
  }

  if (timeString) {
    var token = patterns.timezone.exec(timeString);

    if (token) {
      dateStrings.time = timeString.replace(token[1], '');
      dateStrings.timezone = token[1];
    } else {
      dateStrings.time = timeString;
    }
  }

  return dateStrings;
}

function parseYear(dateString, additionalDigits) {
  var regex = new RegExp('^(?:(\\d{4}|[+-]\\d{' + (4 + additionalDigits) + '})|(\\d{2}|[+-]\\d{' + (2 + additionalDigits) + '})$)');
  var captures = dateString.match(regex); // Invalid ISO-formatted year

  if (!captures) return {
    year: null
  };
  var year = captures[1] && parseInt(captures[1]);
  var century = captures[2] && parseInt(captures[2]);
  return {
    year: century == null ? year : century * 100,
    restDateString: dateString.slice((captures[1] || captures[2]).length)
  };
}

function parseDate(dateString, year) {
  // Invalid ISO-formatted year
  if (year === null) return null;
  var captures = dateString.match(dateRegex); // Invalid ISO-formatted string

  if (!captures) return null;
  var isWeekDate = !!captures[4];
  var dayOfYear = parseDateUnit(captures[1]);
  var month = parseDateUnit(captures[2]) - 1;
  var day = parseDateUnit(captures[3]);
  var week = parseDateUnit(captures[4]);
  var dayOfWeek = parseDateUnit(captures[5]) - 1;

  if (isWeekDate) {
    if (!validateWeekDate(year, week, dayOfWeek)) {
      return new Date(NaN);
    }

    return dayOfISOWeekYear(year, week, dayOfWeek);
  } else {
    var date = new Date(0);

    if (!validateDate(year, month, day) || !validateDayOfYearDate(year, dayOfYear)) {
      return new Date(NaN);
    }

    date.setUTCFullYear(year, month, Math.max(dayOfYear, day));
    return date;
  }
}

function parseDateUnit(value) {
  return value ? parseInt(value) : 1;
}

function parseTime(timeString) {
  var captures = timeString.match(timeRegex);
  if (!captures) return null; // Invalid ISO-formatted time

  var hours = parseTimeUnit(captures[1]);
  var minutes = parseTimeUnit(captures[2]);
  var seconds = parseTimeUnit(captures[3]);

  if (!validateTime(hours, minutes, seconds)) {
    return NaN;
  }

  return hours * MILLISECONDS_IN_HOUR$1 + minutes * MILLISECONDS_IN_MINUTE + seconds * 1000;
}

function parseTimeUnit(value) {
  return value && parseFloat(value.replace(',', '.')) || 0;
}

function parseTimezone(timezoneString) {
  if (timezoneString === 'Z') return 0;
  var captures = timezoneString.match(timezoneRegex);
  if (!captures) return 0;
  var sign = captures[1] === '+' ? -1 : 1;
  var hours = parseInt(captures[2]);
  var minutes = captures[3] && parseInt(captures[3]) || 0;

  if (!validateTimezone(hours, minutes)) {
    return NaN;
  }

  return sign * (hours * MILLISECONDS_IN_HOUR$1 + minutes * MILLISECONDS_IN_MINUTE);
}

function dayOfISOWeekYear(isoWeekYear, week, day) {
  var date = new Date(0);
  date.setUTCFullYear(isoWeekYear, 0, 4);
  var fourthOfJanuaryDay = date.getUTCDay() || 7;
  var diff = (week - 1) * 7 + day + 1 - fourthOfJanuaryDay;
  date.setUTCDate(date.getUTCDate() + diff);
  return date;
} // Validation functions
// February is null to handle the leap year (using ||)


var daysInMonths = [31, null, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function isLeapYearIndex(year) {
  return year % 400 === 0 || year % 4 === 0 && year % 100;
}

function validateDate(year, month, date) {
  return month >= 0 && month <= 11 && date >= 1 && date <= (daysInMonths[month] || (isLeapYearIndex(year) ? 29 : 28));
}

function validateDayOfYearDate(year, dayOfYear) {
  return dayOfYear >= 1 && dayOfYear <= (isLeapYearIndex(year) ? 366 : 365);
}

function validateWeekDate(_year, week, day) {
  return week >= 1 && week <= 53 && day >= 0 && day <= 6;
}

function validateTime(hours, minutes, seconds) {
  if (hours === 24) {
    return minutes === 0 && seconds === 0;
  }

  return seconds >= 0 && seconds < 60 && minutes >= 0 && minutes < 60 && hours >= 0 && hours < 25;
}

function validateTimezone(_hours, minutes) {
  return minutes >= 0 && minutes <= 59;
}

var _ESCROW_TO_WAIVE_ESCR;
var _constants$loanFile$6 = constants.loanFile,
    AMORTIZATION_TYPE_LOOKUP = _constants$loanFile$6.AMORTIZATION_TYPE_LOOKUP,
    ESCROW_TYPES_LOOKUP = _constants$loanFile$6.ESCROW_TYPES_LOOKUP,
    FIXED_IN_MONTHS = _constants$loanFile$6.FIXED_IN_MONTHS,
    PROGRAM_TYPES_LOOKUP = _constants$loanFile$6.PROGRAM_TYPES_LOOKUP;
var API_TAXES = 'Taxes';
var API_INSURANCE = 'Insurance';
var getEscrowFromWaiveEscrow = function getEscrowFromWaiveEscrow(waiveEscrow) {
  if (waiveEscrow === void 0) {
    waiveEscrow = [];
  }

  if (waiveEscrow.includes(API_TAXES) && waiveEscrow.includes(API_INSURANCE)) {
    return ESCROW_TYPES_LOOKUP.WAIVE;
  } else if (waiveEscrow.includes(API_TAXES)) {
    return ESCROW_TYPES_LOOKUP.INSURANCE;
  } else if (waiveEscrow.includes(API_INSURANCE)) {
    return ESCROW_TYPES_LOOKUP.TAXES;
  }

  return ESCROW_TYPES_LOOKUP.TAXES_AND_INSURANCE;
};
var ESCROW_TO_WAIVE_ESCROW_MAP = (_ESCROW_TO_WAIVE_ESCR = {}, _ESCROW_TO_WAIVE_ESCR[ESCROW_TYPES_LOOKUP.TAXES_AND_INSURANCE] = undefined, _ESCROW_TO_WAIVE_ESCR[ESCROW_TYPES_LOOKUP.TAXES] = [API_INSURANCE], _ESCROW_TO_WAIVE_ESCR[ESCROW_TYPES_LOOKUP.INSURANCE] = [API_TAXES], _ESCROW_TO_WAIVE_ESCR[ESCROW_TYPES_LOOKUP.WAIVE] = [API_TAXES, API_INSURANCE], _ESCROW_TO_WAIVE_ESCR);
var scenarioFromAPI = function scenarioFromAPI(_ref) {
  var scenario = _ref.scenario,
      _ref$countyDetails = _ref.countyDetails,
      name = _ref$countyDetails.name,
      state = _ref$countyDetails.state;
  var dti = scenario.dti,
      loanProduct = scenario.loanProduct,
      militaryService = scenario.militaryService,
      homeownersInsurance = scenario.homeownersInsurance,
      taxes = scenario.taxes,
      refinance = scenario.refinance,
      waiveEscrow = scenario.waiveEscrow,
      closingDate = scenario.closingDate,
      homeownersInsuranceDue = scenario.homeownersInsuranceDue,
      loanOption = scenario.loanOption,
      mortgageType = scenario.mortgageType,
      term = scenario.term,
      valuation = scenario.valuation,
      targetLoanAmount = scenario.targetLoanAmount;
  var amortizationType = loanProduct === AMORTIZATION_TYPE_LOOKUP.FIXED ? loanProduct : AMORTIZATION_TYPE_LOOKUP.ARM;
  var amortizationSchedule = loanProduct === AMORTIZATION_TYPE_LOOKUP.FIXED ? Object.entries(FIXED_IN_MONTHS).find(function (_ref2) {
    var _ref3 = _slicedToArray(_ref2, 2),
        value = _ref3[1];

    return value === term;
  })[0] : loanProduct;
  var ltv = targetLoanAmount / valuation * 100;
  var downPaymentPercent = 100 - ltv;
  var computedMortgageType = loanOption === PROGRAM_TYPES_LOOKUP.REFI_NOW ? PROGRAM_TYPES_LOOKUP.REFI_NOW : mortgageType;
  return Object.assign({}, scenario, refinance ? {
    refinance: Object.assign({}, refinance, {
      hasCashOut: !!refinance.cashOut
    })
  } : {}, {
    propertyLocation: {
      subdivision: state,
      county: name
    },
    mortgageType: computedMortgageType,
    militaryStatusType: getMilitaryStatusFromMilitaryServiceOrNA(militaryService),
    escrow: getEscrowFromWaiveEscrow(waiveEscrow),
    amortizationType: amortizationType,
    amortizationSchedule: amortizationSchedule,
    dti: dti * 100,
    downPaymentPercent: downPaymentPercent,
    ltv: ltv,
    homeownersInsurance: Math.floor((homeownersInsurance !== null && homeownersInsurance !== void 0 ? homeownersInsurance : 0) * 12),
    taxes: Math.floor((taxes !== null && taxes !== void 0 ? taxes : 0) * 12),
    vaFirstTimeUse: militaryService.vaFirstTimeUse,
    vaFundingFeeExemption: militaryService.vaFundingFeeExemption,
    closingDate: closingDate && parseISO(closingDate),
    homeownersInsuranceDue: homeownersInsuranceDue && parseISO(homeownersInsuranceDue)
  });
};

var _excluded$d = ["projectType", "financeEnergyImprovements", "existingCleanEnergyLien", "refinance", "transaction", "estateHeldIn", "leaseholdExpirationDate", "addressZIP", "addressCounty"],
    _excluded2$3 = ["hasCashOut"];
var _constants$loanFile$7 = constants.loanFile,
    PROJECT_TYPES_LOOKUP = _constants$loanFile$7.PROJECT_TYPES_LOOKUP,
    LOAN_TYPES_LOOKUP$1 = _constants$loanFile$7.LOAN_TYPES_LOOKUP,
    TRANSACTION_TYPES_LOOKUP = _constants$loanFile$7.TRANSACTION_TYPES_LOOKUP,
    ESTATE_HELD_IN_LOOKUP = _constants$loanFile$7.ESTATE_HELD_IN_LOOKUP,
    STRING_TRUE = constants.STRING_TRUE;

var getStringFromBoolean = function getStringFromBoolean(input) {
  return typeof input === 'boolean' ? String(input) : input;
};

var getBooleanFromString = function getBooleanFromString(input) {
  return typeof input !== 'string' || input === '' ? null : input === STRING_TRUE;
};

var fromAPI$1 = function fromAPI(_ref) {
  var _computed$addressCoun;

  var attributes = _ref.attributes,
      computed = _ref.computed;
  return Object.assign({}, attributes, {
    addressCounty: computed === null || computed === void 0 ? void 0 : (_computed$addressCoun = computed.addressCountyDetails) === null || _computed$addressCoun === void 0 ? void 0 : _computed$addressCoun.name,
    financeEnergyImprovements: getStringFromBoolean(attributes === null || attributes === void 0 ? void 0 : attributes.financeEnergyImprovements),
    existingCleanEnergyLien: getStringFromBoolean(attributes === null || attributes === void 0 ? void 0 : attributes.existingCleanEnergyLien),
    projectType: attributes !== null && attributes !== void 0 && attributes.pud ? PROJECT_TYPES_LOOKUP.PLANNED_UNIT_DEVELOPMENT : PROJECT_TYPES_LOOKUP.NONE,
    estateHeldIn: attributes !== null && attributes !== void 0 && attributes.leaseholdExpirationDate ? ESTATE_HELD_IN_LOOKUP.LEASEHOLD : ESTATE_HELD_IN_LOOKUP.FEE_SIMPLE
  }, attributes !== null && attributes !== void 0 && attributes.refinance ? {
    refinance: Object.assign({}, attributes.refinance, {
      hasCashOut: !!attributes.refinance.cashOut
    })
  } : {});
};
var toAPI$1 = function toAPI(_ref2) {
  var _transaction$construc;

  var projectType = _ref2.projectType,
      financeEnergyImprovements = _ref2.financeEnergyImprovements,
      existingCleanEnergyLien = _ref2.existingCleanEnergyLien,
      refinance = _ref2.refinance,
      transaction = _ref2.transaction,
      estateHeldIn = _ref2.estateHeldIn,
      leaseholdExpirationDate = _ref2.leaseholdExpirationDate,
      addressZIP = _ref2.addressZIP,
      addressCounty = _ref2.addressCounty,
      otherAttributes = _objectWithoutProperties(_ref2, _excluded$d);

  var _ref3 = refinance !== null && refinance !== void 0 ? refinance : {},
      hasCashOut = _ref3.hasCashOut,
      restOfRefinance = _objectWithoutProperties(_ref3, _excluded2$3);

  return Object.assign({}, otherAttributes, {
    refinance: (otherAttributes === null || otherAttributes === void 0 ? void 0 : otherAttributes.loanPurpose) === LOAN_TYPES_LOOKUP$1.REFINANCE ? restOfRefinance : null,
    pud: projectType === PROJECT_TYPES_LOOKUP.PLANNED_UNIT_DEVELOPMENT,
    financeEnergyImprovements: getBooleanFromString(financeEnergyImprovements),
    existingCleanEnergyLien: getBooleanFromString(existingCleanEnergyLien),
    leaseholdExpirationDate: toDateString(leaseholdExpirationDate),
    addressZIP: addressZIP || null,
    transaction: transaction !== null && transaction !== void 0 && transaction.type ? Object.assign({}, transaction, {
      construction: (transaction === null || transaction === void 0 ? void 0 : transaction.type) === TRANSACTION_TYPES_LOOKUP.CONSTRUCTION ? Object.assign({}, transaction.construction, {
        lotAcquiredDate: toDateString((_transaction$construc = transaction.construction) === null || _transaction$construc === void 0 ? void 0 : _transaction$construc.lotAcquiredDate)
      }) : null
    }) : null
  });
};
var fromPreQualifyFieldsToLoanAttributes = function fromPreQualifyFieldsToLoanAttributes(_ref4) {
  var state = _ref4.state,
      city = _ref4.city,
      countyFIPS = _ref4.countyFIPS,
      zipCode = _ref4.zipCode;
  return {
    addressCountyFIPS: countyFIPS,
    addressCity: city,
    addressState: state,
    addressZIP: zipCode === '' ? undefined : zipCode
  };
};

var _PRICING_ERROR_MESSAG;
var _constants$loanFile$8 = constants.loanFile,
    REQUESTED = _constants$loanFile$8.PRICING_LOAN_QUOTE_LOCK_STATUS_LOOKUP.REQUESTED,
    PRICING_LOAN_QUOTE_LOCK_STATUSES = _constants$loanFile$8.PRICING_LOAN_QUOTE_LOCK_STATUSES,
    LOAN_TYPES_LOOKUP$2 = _constants$loanFile$8.LOAN_TYPES_LOOKUP,
    LFS_ERROR_TYPES$1 = constants.LFS_ERROR_TYPES;
var pricingSelector = createSelector([loanFileSelector], function (loanFile) {
  var _loanFile$pricing;

  return (_loanFile$pricing = loanFile === null || loanFile === void 0 ? void 0 : loanFile.pricing) !== null && _loanFile$pricing !== void 0 ? _loanFile$pricing : null;
});
var feesByIDSelector = createSelector([pricingSelector], function (pricing) {
  return pricing === null || pricing === void 0 ? void 0 : pricing.feesByID;
});

var sumBorrowerAmount = function sumBorrowerAmount(collection) {
  return collection.reduce(function (sum, _ref) {
    var _ref$buyerAmount = _ref.buyerAmount,
        buyerAmount = _ref$buyerAmount === void 0 ? 0 : _ref$buyerAmount;
    return sum + buyerAmount;
  }, 0);
};

var feeSumByIDSelector = createSelector([feesByIDSelector], function (feesByID) {
  return Object.entries(feesByID !== null && feesByID !== void 0 ? feesByID : {}).reduce(function (sumsByID, _ref2) {
    var _Object$assign;

    var _ref3 = _slicedToArray(_ref2, 2),
        id = _ref3[0],
        _ref3$ = _ref3[1],
        feeItems = _ref3$.feeItems,
        taxItems = _ref3$.taxItems;

    return Object.assign({}, sumsByID, (_Object$assign = {}, _Object$assign[id] = sumBorrowerAmount(feeItems) + sumBorrowerAmount(taxItems), _Object$assign));
  }, {});
});
var currentFeeIDSelector = createSelector([pricingSelector], function (pricing) {
  return pricing === null || pricing === void 0 ? void 0 : pricing.currentFeeID;
});
var areFeesLoadingSelector = createSelector([pricingSelector], function (pricing) {
  return pricing === null || pricing === void 0 ? void 0 : pricing.areFeesLoading;
});
var currentFeesSelector = createSelector([currentFeeIDSelector, feesByIDSelector], function (feeID, feesByID) {
  return feesByID === null || feesByID === void 0 ? void 0 : feesByID[feeID];
});
var currentFeeSumSelector = createSelector([feeSumByIDSelector, currentFeeIDSelector], function (feeSumByID, currentFeeID) {
  var _feeSumByID$currentFe;

  return (_feeSumByID$currentFe = feeSumByID === null || feeSumByID === void 0 ? void 0 : feeSumByID[currentFeeID]) !== null && _feeSumByID$currentFe !== void 0 ? _feeSumByID$currentFe : 0;
});
var primaryScenarioLabelSelector = createSelector([pricingSelector], function (pricing) {
  var _pricing$primaryScena;

  return (_pricing$primaryScena = pricing === null || pricing === void 0 ? void 0 : pricing.primaryScenarioLabel) !== null && _pricing$primaryScena !== void 0 ? _pricing$primaryScena : null;
});
var loanRateQuoteSelector = createSelector([pricingSelector], function (pricing) {
  var _pricing$loanRateQuot;

  return (_pricing$loanRateQuot = pricing === null || pricing === void 0 ? void 0 : pricing.loanRateQuote) !== null && _pricing$loanRateQuot !== void 0 ? _pricing$loanRateQuot : null;
});
var loanRateQuoteLockStatusSelector = createSelector([loanRateQuoteSelector], function (loanRateQuote) {
  var _loanRateQuote$attrib, _loanRateQuote$attrib2;

  return (_loanRateQuote$attrib = loanRateQuote === null || loanRateQuote === void 0 ? void 0 : (_loanRateQuote$attrib2 = loanRateQuote.attributes) === null || _loanRateQuote$attrib2 === void 0 ? void 0 : _loanRateQuote$attrib2.lockStatus) !== null && _loanRateQuote$attrib !== void 0 ? _loanRateQuote$attrib : null;
});
var loanRateQuoteIsLockedSelector = createSelector([loanRateQuoteLockStatusSelector], function (loanRateQuoteLockStatus) {
  return loanRateQuoteLockStatus === PRICING_LOAN_QUOTE_LOCK_STATUSES[REQUESTED];
});
var pricingScenariosSelector = createSelector([pricingSelector], function (pricing) {
  return pricing !== null && pricing !== void 0 && pricing.scenarios ? Object.entries(pricing.scenarios).reduce(function (acc, _ref4) {
    var _Object$assign2;

    var _ref5 = _slicedToArray(_ref4, 2),
        id = _ref5[0],
        scenario = _ref5[1];

    return Object.assign({}, acc, (_Object$assign2 = {}, _Object$assign2[id] = Object.assign({}, scenario, {
      scenario: scenarioFromAPI(scenario),
      selectedRateQuote: scenario.rateQuotes[scenario.selectedRateQuoteID]
    }), _Object$assign2));
  }, {}) : null;
});
var primaryScenarioSelector = createSelector([pricingScenariosSelector, primaryScenarioLabelSelector], function (scenarios, primaryScenarioLabel) {
  return (scenarios === null || scenarios === void 0 ? void 0 : scenarios[primaryScenarioLabel]) || null;
});
var loanFileHasPrimaryScenarioSelector = createSelector([primaryScenarioSelector], function (primaryScenario) {
  return !!primaryScenario;
});
var isPrimaryScenarioSelector = createSelector([primaryScenarioLabelSelector, scenarioLabelFromSelectedScenarioPropSelector], function (primaryLabel, scenarioLabel) {
  return scenarioLabel === primaryLabel;
});
var primaryScenarioLoanPurposeSelector = createSelector([primaryScenarioSelector], function (scenario) {
  var _scenario$scenario;

  return scenario === null || scenario === void 0 ? void 0 : (_scenario$scenario = scenario.scenario) === null || _scenario$scenario === void 0 ? void 0 : _scenario$scenario.loanPurpose;
});
var isPrimaryScenarioPurchaseSelector = createSelector([primaryScenarioLoanPurposeSelector], function (loanPurpose) {
  return loanPurpose === LOAN_TYPES_LOOKUP$2.PURCHASE;
}); // Please note: Date is only evaluated when the scenario is changed. This will not display the alert
// in real time, but instead upon the scenario being initially populated or updated. The 12 hour window
// is a fuzzy window as is, so this is a known & accepted risk.

var isPricingInvalidSelector = createSelector([primaryScenarioSelector], function (scenario) {
  var _scenario$differingFi, _scenario$differingFi2;

  return ((_scenario$differingFi = scenario === null || scenario === void 0 ? void 0 : (_scenario$differingFi2 = scenario.differingFields) === null || _scenario$differingFi2 === void 0 ? void 0 : _scenario$differingFi2.length) !== null && _scenario$differingFi !== void 0 ? _scenario$differingFi : 0) > 0 || differenceInHours(new Date(), new Date(scenario === null || scenario === void 0 ? void 0 : scenario.createdUTC)) >= 12;
});
var primaryRateQuoteSelector = createSelector([primaryScenarioSelector], function (primaryScenario) {
  var _primaryScenario$sele;

  return (_primaryScenario$sele = primaryScenario === null || primaryScenario === void 0 ? void 0 : primaryScenario.selectedRateQuote) !== null && _primaryScenario$sele !== void 0 ? _primaryScenario$sele : null;
});
var primaryRateQuoteEstimatedMonthlyPaymentSelector = createSelector([primaryRateQuoteSelector], function (primaryRateQuote) {
  var _primaryRateQuote$com, _primaryRateQuote$com2;

  return (_primaryRateQuote$com = primaryRateQuote === null || primaryRateQuote === void 0 ? void 0 : (_primaryRateQuote$com2 = primaryRateQuote.computed) === null || _primaryRateQuote$com2 === void 0 ? void 0 : _primaryRateQuote$com2.estimatedMonthlyPayment) !== null && _primaryRateQuote$com !== void 0 ? _primaryRateQuote$com : null;
});
var forcedRateQuoteInfoSelector = createSelector([pricingSelector], function (pricing) {
  var _pricing$scenarios;

  return pricing !== null && pricing !== void 0 && pricing.forceRateQuoteSelection ? {
    previouslySelectedRateQuote: pricing === null || pricing === void 0 ? void 0 : pricing.previouslySelectedRateQuote,
    scenario: pricing === null || pricing === void 0 ? void 0 : (_pricing$scenarios = pricing.scenarios) === null || _pricing$scenarios === void 0 ? void 0 : _pricing$scenarios[pricing === null || pricing === void 0 ? void 0 : pricing.scenarioLabelToSelectFor]
  } : null;
});
var hasLoadedScenariosSelector = createSelector([pricingSelector], function (pricing) {
  return !!(pricing !== null && pricing !== void 0 && pricing.hasLoadedScenarios);
});
var hasInitializedScenarioBuilderSelector = createSelector([pricingSelector], function (pricing) {
  return !!(pricing !== null && pricing !== void 0 && pricing.hasInitializedScenarioBuilder);
});
var pricingErrorsSelector = createSelector([pricingSelector], function (pricing) {
  var _pricing$errors;

  return (_pricing$errors = pricing === null || pricing === void 0 ? void 0 : pricing.errors) !== null && _pricing$errors !== void 0 ? _pricing$errors : null;
});
var PRICING_ERRORS = {
  INELIGIBLE: 'Ineligible',
  CREATE_SCENARIO_FAILURE: 'CreateScenarioFailure',
  FETCH_SCENARIO_RATE_QUOTES_ERROR: 'FetchScenarioRateQuotesError',
  FETCH_SCENARIO_FAILURE: 'FetchScenarioFailure',
  FEES_ERROR: 'FeesErrors'
};
var PRICING_ERROR_MESSAGES = (_PRICING_ERROR_MESSAG = {}, _PRICING_ERROR_MESSAG[PRICING_ERRORS.INELIGIBLE] = 'The submitted request was ineligible to receive pricing', _PRICING_ERROR_MESSAG[LFS_ERROR_TYPES$1.VALIDATION_ERROR] = 'Invalid request', _PRICING_ERROR_MESSAG[PRICING_ERRORS.FEES_ERROR] = 'An error occurred requesting fees', _PRICING_ERROR_MESSAG);
var DEFAULT_ERROR_MESSAGE = 'An unknown error has occurred with pricing';
var INVALID_CITY_ZIP_ERROR = 'This zip code was unable to be matched to a city which is preventing Fees from being pulled in LOP. You will be able to pull fees once the loan is in Encompass.';

var isAutocompleteFeesMismatchError = function isAutocompleteFeesMismatchError(error) {
  var _error$msg, _error$msg2;

  return error.type === 'InvalidModelState' && ((_error$msg = error.msg) === null || _error$msg === void 0 ? void 0 : _error$msg.includes('PropertyInformation.City')) || error.type === 'ArgumentException' && ((_error$msg2 = error.msg) === null || _error$msg2 === void 0 ? void 0 : _error$msg2.includes('Could not find city'));
};

var getFeeErrorMessage = function getFeeErrorMessage(error) {
  var _safeError$title;

  var safeError = error !== null && error !== void 0 ? error : {};

  if (isAutocompleteFeesMismatchError(safeError)) {
    return INVALID_CITY_ZIP_ERROR;
  }

  return (_safeError$title = safeError.title) !== null && _safeError$title !== void 0 ? _safeError$title : PRICING_ERROR_MESSAGES[PRICING_ERRORS.FEES_ERROR];
};

var pricingErrorMessagesSelector = createSelector([pricingErrorsSelector], function (errors) {
  return Object.entries(errors !== null && errors !== void 0 ? errors : {}).reduce(function (acc, _ref6) {
    var _ref7 = _slicedToArray(_ref6, 2),
        key = _ref7[0],
        error = _ref7[1];

    if (key === PRICING_ERRORS.FEES_ERROR && error) {
      var _Object$assign3;

      return Object.assign({}, acc, (_Object$assign3 = {}, _Object$assign3[key] = getFeeErrorMessage(error.error), _Object$assign3));
    }

    if (error) {
      var _ref8, _error$detail, _Object$assign4;

      return Object.assign({}, acc, (_Object$assign4 = {}, _Object$assign4[key] = (_ref8 = (_error$detail = error === null || error === void 0 ? void 0 : error.detail) !== null && _error$detail !== void 0 ? _error$detail : PRICING_ERROR_MESSAGES[error.type]) !== null && _ref8 !== void 0 ? _ref8 : DEFAULT_ERROR_MESSAGE, _Object$assign4));
    }

    return acc;
  }, {});
});
var orderedPricingScenarios = createSelector([pricingSelector, pricingScenariosSelector], function (pricing, pricingScenarios) {
  return pricing ? pricing.scenarioOrder.map(function (scenarioLabel) {
    return pricingScenarios[scenarioLabel];
  }) : [];
});
var pricingFieldsSelector = createSelector([pricingSelector], function (pricing) {
  return (pricing === null || pricing === void 0 ? void 0 : pricing.fields) || null;
});
var lockTermOptionsSelector = createSelector([pricingSelector, scenarioLabelPropSelector], function (pricing, scenarioLabel) {
  var _pricing$scenarios$sc, _pricing$scenarios$sc2;

  return Object.keys((_pricing$scenarios$sc = (_pricing$scenarios$sc2 = pricing.scenarios[scenarioLabel]) === null || _pricing$scenarios$sc2 === void 0 ? void 0 : _pricing$scenarios$sc2.rateQuoteIDsByLockTerm) !== null && _pricing$scenarios$sc !== void 0 ? _pricing$scenarios$sc : {});
});
var isPricingLoadingSelector = createSelector([pricingSelector], function (pricing) {
  var _pricing$isLoading;

  return (_pricing$isLoading = pricing === null || pricing === void 0 ? void 0 : pricing.isLoading) !== null && _pricing$isLoading !== void 0 ? _pricing$isLoading : false;
});
var feeIDsForScenariosSelector = createSelector([pricingScenariosSelector], function (scenarios) {
  return scenarios ? Object.values(scenarios).map(function (_ref9) {
    var feeID = _ref9.scenario.feeID;
    return feeID;
  }).filter(Boolean) : null;
});

var pricingBase = /*#__PURE__*/Object.freeze({
  __proto__: null,
  pricingSelector: pricingSelector,
  feesByIDSelector: feesByIDSelector,
  feeSumByIDSelector: feeSumByIDSelector,
  currentFeeIDSelector: currentFeeIDSelector,
  areFeesLoadingSelector: areFeesLoadingSelector,
  currentFeesSelector: currentFeesSelector,
  currentFeeSumSelector: currentFeeSumSelector,
  primaryScenarioLabelSelector: primaryScenarioLabelSelector,
  loanRateQuoteSelector: loanRateQuoteSelector,
  loanRateQuoteLockStatusSelector: loanRateQuoteLockStatusSelector,
  loanRateQuoteIsLockedSelector: loanRateQuoteIsLockedSelector,
  pricingScenariosSelector: pricingScenariosSelector,
  primaryScenarioSelector: primaryScenarioSelector,
  loanFileHasPrimaryScenarioSelector: loanFileHasPrimaryScenarioSelector,
  isPrimaryScenarioSelector: isPrimaryScenarioSelector,
  primaryScenarioLoanPurposeSelector: primaryScenarioLoanPurposeSelector,
  isPrimaryScenarioPurchaseSelector: isPrimaryScenarioPurchaseSelector,
  isPricingInvalidSelector: isPricingInvalidSelector,
  primaryRateQuoteSelector: primaryRateQuoteSelector,
  primaryRateQuoteEstimatedMonthlyPaymentSelector: primaryRateQuoteEstimatedMonthlyPaymentSelector,
  forcedRateQuoteInfoSelector: forcedRateQuoteInfoSelector,
  hasLoadedScenariosSelector: hasLoadedScenariosSelector,
  hasInitializedScenarioBuilderSelector: hasInitializedScenarioBuilderSelector,
  pricingErrorsSelector: pricingErrorsSelector,
  PRICING_ERRORS: PRICING_ERRORS,
  pricingErrorMessagesSelector: pricingErrorMessagesSelector,
  orderedPricingScenarios: orderedPricingScenarios,
  pricingFieldsSelector: pricingFieldsSelector,
  lockTermOptionsSelector: lockTermOptionsSelector,
  isPricingLoadingSelector: isPricingLoadingSelector,
  feeIDsForScenariosSelector: feeIDsForScenariosSelector
});

var loanFileComputedSelector = createSelector([loanFileSelector], function (loanFile) {
  var _loanFile$computed;

  return (_loanFile$computed = loanFile === null || loanFile === void 0 ? void 0 : loanFile.computed) !== null && _loanFile$computed !== void 0 ? _loanFile$computed : null;
});
var totalMonthlyIncomeSelector = createSelector([loanFileComputedSelector], function (loanFileComputed) {
  var _loanFileComputed$tot;

  return (_loanFileComputed$tot = loanFileComputed === null || loanFileComputed === void 0 ? void 0 : loanFileComputed.totalMonthlyIncome) !== null && _loanFileComputed$tot !== void 0 ? _loanFileComputed$tot : null;
});

var computed$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  loanFileComputedSelector: loanFileComputedSelector,
  totalMonthlyIncomeSelector: totalMonthlyIncomeSelector
});

var combinedDTIWithoutPricingSelector = createSelector([totalMonthlyLiabilitiesSelector, totalMonthlyIncomeSelector], function (totalMonthlyLiabilities, totalMonthlyIncome) {
  return totalMonthlyIncome ? totalMonthlyLiabilities / totalMonthlyIncome : null;
});
var combinedDTISelector = createSelector([totalMonthlyLiabilitiesSelector, totalMonthlyIncomeSelector, primaryRateQuoteEstimatedMonthlyPaymentSelector], function (totalMonthlyLiabilities, totalMonthlyIncome, primaryRateQuoteEstimatedMonthlyPayment) {
  return totalMonthlyIncome ? (totalMonthlyLiabilities + (primaryRateQuoteEstimatedMonthlyPayment !== null && primaryRateQuoteEstimatedMonthlyPayment !== void 0 ? primaryRateQuoteEstimatedMonthlyPayment : 0)) / totalMonthlyIncome : null;
});

var liabilitiesAndIncome = /*#__PURE__*/Object.freeze({
  __proto__: null,
  combinedDTIWithoutPricingSelector: combinedDTIWithoutPricingSelector,
  combinedDTISelector: combinedDTISelector
});

var _excluded$e = ["valuation", "propertyType", "attachmentType", "constructionMethod", "propertyUse", "fico", "loanPurpose", "propertyLocation", "propertyZIP", "countyFIPS", "firstTimeBuyer", "refinance", "targetLoanAmount", "mortgageType", "loanOption", "feeID", "downPayment"],
    _excluded2$4 = ["id", "downPaymentPercent", "ltv", "propertyLocation", "totalFees", "amortizationSchedule", "amortizationType", "dti", "financeFees", "homeownersInsurance", "militaryStatusType", "escrow", "taxes", "vaFirstTimeUse", "vaFundingFeeExemption", "loanPurpose", "refinance", "firstTimeBuyer", "closingDate", "homeownersInsuranceDue", "mortgageType"],
    _excluded3$1 = ["hasCashOut", "cashOut"],
    _excluded4$1 = ["cashOutAmount"],
    _excluded5$1 = ["hasCashOut", "currentBalance", "cashOut"];
var _constants$loanFile$9 = constants.loanFile,
    ADJUSTABLE_AMORTIZATION_IN_MONTHS = _constants$loanFile$9.ADJUSTABLE_AMORTIZATION_IN_MONTHS,
    ADJUSTABLE_AMORTIZATION_PERIODS = _constants$loanFile$9.ADJUSTABLE_AMORTIZATION_PERIODS,
    AMORTIZATION_TYPES = _constants$loanFile$9.AMORTIZATION_TYPES,
    AMORTIZATION_TYPE_LOOKUP$1 = _constants$loanFile$9.AMORTIZATION_TYPE_LOOKUP,
    ATTACHMENT_TYPES_LOOKUP = _constants$loanFile$9.ATTACHMENT_TYPES_LOOKUP,
    CLOSING_COST_CATEGORY = _constants$loanFile$9.CLOSING_COST_CATEGORY,
    CONSTRUCTION_TYPES_LOOKUP = _constants$loanFile$9.CONSTRUCTION_TYPES_LOOKUP,
    ESCROW_TYPES_LOOKUP$1 = _constants$loanFile$9.ESCROW_TYPES_LOOKUP,
    FIXED_AMORTIZATION_PERIODS = _constants$loanFile$9.FIXED_AMORTIZATION_PERIODS,
    FIXED_AMORTIZATION_PERIODS_LOOKUP = _constants$loanFile$9.FIXED_AMORTIZATION_PERIODS_LOOKUP,
    FIXED_IN_MONTHS$1 = _constants$loanFile$9.FIXED_IN_MONTHS,
    LEAD_SOURCES = _constants$loanFile$9.LEAD_SOURCES,
    LOAN_TYPES_LOOKUP$3 = _constants$loanFile$9.LOAN_TYPES_LOOKUP,
    MILITARY_STATUS_TYPE_LOOKUP$1 = _constants$loanFile$9.MILITARY_STATUS_TYPE_LOOKUP,
    PRICING_FEE_PAYER = _constants$loanFile$9.PRICING_FEE_PAYER,
    PROGRAM_TYPES_LOOKUP$1 = _constants$loanFile$9.PROGRAM_TYPES_LOOKUP,
    PROJECT_TYPES_LOOKUP$1 = _constants$loanFile$9.PROJECT_TYPES_LOOKUP,
    PROPERTY_TYPES_LOOKUP$1 = _constants$loanFile$9.PROPERTY_TYPES_LOOKUP,
    PROPERTY_USE_LOOKUP = _constants$loanFile$9.PROPERTY_USE_LOOKUP,
    REFINANCE_PROGRAMS_LOOKUP = _constants$loanFile$9.REFINANCE_PROGRAMS_LOOKUP,
    TRANSACTION_TYPES_LOOKUP$1 = _constants$loanFile$9.TRANSACTION_TYPES_LOOKUP,
    VALID_MILITARY_STATUS_VALUES = _constants$loanFile$9.VALID_MILITARY_STATUS_VALUES;
var float = formatters.float,
    inputFormatters = formatters.inputFormatters;
var isoDate = inputFormatters.isoDate;
var floatParser = parsers.float;
var LENDER_CREDIT_COST_FEE = 'Lender credit/cost';
var UPFRONT_FEE = 'Upfront Fee';
var CLOSING_COSTS = 'Closing costs';

function generateDisplayNameParts(loanProduct, scenario) {
  var result = [];

  if (!scenario.scenario) {
    return result;
  }

  var _scenario$scenario = scenario.scenario,
      loanPurpose = _scenario$scenario.loanPurpose,
      downPaymentPercent = _scenario$scenario.downPaymentPercent,
      description = _scenario$scenario.description,
      ltv = _scenario$scenario.ltv;

  if (description) {
    result.push(description);
  } else {
    result.push(loanProduct);
    result.push(' ');

    if (loanPurpose === LOAN_TYPES_LOOKUP$3.PURCHASE) {
      result.push(float(downPaymentPercent, 2));
      result.push('% down');
    } else {
      result.push(float(ltv, 2));
      result.push('% LTV');
    }
  }

  return result;
}

var generateFeeMapWithTotals = function generateFeeMapWithTotals(allPossibleFees, _ref, feeServiceClosingCosts) {
  var _ref$selectedRateQuot = _ref.selectedRateQuote,
      selectedRateQuote = _ref$selectedRateQuot === void 0 ? {} : _ref$selectedRateQuot,
      _ref$scenario = _ref.scenario;
  _ref$scenario = _ref$scenario === void 0 ? {} : _ref$scenario;
  var rollFeesIntoLoan = _ref$scenario.rollFeesIntoLoan;

  if (feeServiceClosingCosts === void 0) {
    feeServiceClosingCosts = 0;
  }

  var _selectedRateQuote$cl = selectedRateQuote.closingCosts,
      closingCosts = _selectedRateQuote$cl === void 0 ? [] : _selectedRateQuote$cl,
      costOfPrice = selectedRateQuote.costOfPrice,
      upfrontFee = selectedRateQuote.upfrontFee;

  var scenarioFees = _toConsumableArray(closingCosts);

  if (costOfPrice) {
    scenarioFees.push({
      amount: costOfPrice,
      description: LENDER_CREDIT_COST_FEE,
      category: CLOSING_COST_CATEGORY.OTHER,
      payer: PRICING_FEE_PAYER.BORROWER
    });
  }

  if (upfrontFee) {
    scenarioFees.push({
      amount: upfrontFee,
      description: UPFRONT_FEE,
      category: CLOSING_COST_CATEGORY.OTHER,
      payer: PRICING_FEE_PAYER.BORROWER
    });
  }

  if (!rollFeesIntoLoan && feeServiceClosingCosts) {
    scenarioFees.push({
      amount: feeServiceClosingCosts,
      description: CLOSING_COSTS,
      category: CLOSING_COST_CATEGORY.ORIGINATION_CHARGES,
      payer: PRICING_FEE_PAYER.BORROWER
    });
  }

  return Object.keys(allPossibleFees).reduce(function (fees, feeCategory) {
    var _Object$assign;

    return Object.assign({}, fees, (_Object$assign = {}, _Object$assign[feeCategory] = allPossibleFees[feeCategory].map(function (feeDescription) {
      var scenarioFee = scenarioFees.find(function (_ref2) {
        var category = _ref2.category,
            description = _ref2.description;
        return category === feeCategory && description === feeDescription;
      });
      return scenarioFee || {
        description: feeDescription,
        amount: 0
      };
    }).reduce(function (result, currentFee) {
      return {
        total: result.total + currentFee.amount,
        fees: result.fees.concat(currentFee)
      };
    }, {
      total: 0,
      fees: []
    }), _Object$assign));
  }, {});
};

var generateDisplayValuesForScenario = function generateDisplayValuesForScenario(scenario, buyerPaidFees, feeSumByID, totalLiabilitiesPaidAtClosing, totalLiabilitiesPaidOffThroughLoan) {
  var _scenario$scenario2;

  var _ref3 = (_scenario$scenario2 = scenario.scenario) !== null && _scenario$scenario2 !== void 0 ? _scenario$scenario2 : {},
      loanPurpose = _ref3.loanPurpose,
      _ref3$otherMonthlyPay = _ref3.otherMonthlyPayment,
      otherMonthlyPayment = _ref3$otherMonthlyPay === void 0 ? 0 : _ref3$otherMonthlyPay,
      _ref3$homeownersAssoc = _ref3.homeownersAssociationDues,
      homeownersAssociationDues = _ref3$homeownersAssoc === void 0 ? 0 : _ref3$homeownersAssoc,
      _ref3$homeownersInsur = _ref3.homeownersInsurance,
      homeownersInsurance = _ref3$homeownersInsur === void 0 ? 0 : _ref3$homeownersInsur,
      _ref3$taxes = _ref3.taxes,
      taxes = _ref3$taxes === void 0 ? 0 : _ref3$taxes,
      _ref3$downPayment = _ref3.downPayment,
      downPayment = _ref3$downPayment === void 0 ? 0 : _ref3$downPayment,
      mortgageType = _ref3.mortgageType,
      amortizationType = _ref3.amortizationType,
      amortizationSchedule = _ref3.amortizationSchedule,
      feeID = _ref3.feeID;

  var feeMapForScenario = generateFeeMapWithTotals(buyerPaidFees, scenario, feeSumByID === null || feeSumByID === void 0 ? void 0 : feeSumByID[feeID]);

  var _ref4 = (scenario === null || scenario === void 0 ? void 0 : scenario.selectedRateQuote) || {},
      _ref4$pi = _ref4.pi,
      pi = _ref4$pi === void 0 ? 0 : _ref4$pi,
      _ref4$mortgageInsuran = _ref4.mortgageInsurance,
      mortgageInsurance = _ref4$mortgageInsuran === void 0 ? 0 : _ref4$mortgageInsuran,
      _ref4$loanAmount = _ref4.loanAmount,
      loanAmount = _ref4$loanAmount === void 0 ? 0 : _ref4$loanAmount,
      _ref4$lenderCredit = _ref4.lenderCredit,
      lenderCredit = _ref4$lenderCredit === void 0 ? 0 : _ref4$lenderCredit;

  var monthlyHomeownersInsurance = homeownersInsurance / 12;
  var monthlyTaxes = taxes / 12;
  var monthlyPayment = pi + otherMonthlyPayment + monthlyTaxes + homeownersAssociationDues + monthlyHomeownersInsurance + mortgageInsurance;
  var loanTerm = (amortizationType === AMORTIZATION_TYPE_LOOKUP$1.FIXED ? FIXED_AMORTIZATION_PERIODS : ADJUSTABLE_AMORTIZATION_PERIODS)[amortizationSchedule];
  var loanProduct = mortgageType + " " + loanTerm + " " + AMORTIZATION_TYPES[amortizationType];
  var totalClosingCosts = Object.keys(feeMapForScenario).reduce(function (total, category) {
    return total + feeMapForScenario[category].total;
  }, 0) + lenderCredit;
  var cashToClose = (loanPurpose === LOAN_TYPES_LOOKUP$3.REFINANCE ? totalLiabilitiesPaidOffThroughLoan - loanAmount // For refinance, the loan amount is applied to any liabilities that are being paid through the loan
  : // For purchase, the loan amount is going to the cost of the home (valuation), and any liabilities that are to be paid
  // off by the loan (still a viable option in liabilities), those must be brought to the table by the borrower
  totalLiabilitiesPaidOffThroughLoan) + totalLiabilitiesPaidAtClosing + (loanPurpose === LOAN_TYPES_LOOKUP$3.PURCHASE ? downPayment : 0) + totalClosingCosts;
  return Object.assign({}, scenario, {
    scenario: scenario.scenario,
    monthlyHomeownersInsurance: monthlyHomeownersInsurance,
    monthlyTaxes: monthlyTaxes,
    monthlyPayment: monthlyPayment,
    loanProduct: loanProduct,
    displayName: Array.from(generateDisplayNameParts(loanProduct, scenario)).join(''),
    closingCosts: {
      total: totalClosingCosts,
      fees: feeMapForScenario
    },
    cashToClose: cashToClose
  });
};

var getAllBuyerPaidClosingFeesInScenarios = function getAllBuyerPaidClosingFeesInScenarios(scenarios) {
  var categoryMap = {};
  scenarios.forEach(function (_ref5) {
    var _ref5$selectedRateQuo = _ref5.selectedRateQuote;
    _ref5$selectedRateQuo = _ref5$selectedRateQuo === void 0 ? {} : _ref5$selectedRateQuo;
    var _ref5$selectedRateQuo2 = _ref5$selectedRateQuo.closingCosts,
        closingCosts = _ref5$selectedRateQuo2 === void 0 ? [] : _ref5$selectedRateQuo2;
    return closingCosts.filter(function (_ref6) {
      var payer = _ref6.payer;
      return payer === PRICING_FEE_PAYER.BORROWER;
    }).forEach(function (_ref7) {
      var category = _ref7.category,
          description = _ref7.description;
      var currentList = categoryMap[category] || [];
      categoryMap[category] = currentList.indexOf(description) === -1 ? currentList.concat(description) : currentList;
    });
  });

  if (scenarios.some(function (_ref8) {
    var _ref8$selectedRateQuo = _ref8.selectedRateQuote;
    _ref8$selectedRateQuo = _ref8$selectedRateQuo === void 0 ? {} : _ref8$selectedRateQuo;
    var costOfPrice = _ref8$selectedRateQuo.costOfPrice;
    return !!costOfPrice;
  })) {
    var _categoryMap$CLOSING_;

    categoryMap[CLOSING_COST_CATEGORY.OTHER] = [].concat(_toConsumableArray((_categoryMap$CLOSING_ = categoryMap[CLOSING_COST_CATEGORY.OTHER]) !== null && _categoryMap$CLOSING_ !== void 0 ? _categoryMap$CLOSING_ : []), [LENDER_CREDIT_COST_FEE]);
  }

  if (scenarios.some(function (_ref9) {
    var _ref9$selectedRateQuo = _ref9.selectedRateQuote;
    _ref9$selectedRateQuo = _ref9$selectedRateQuo === void 0 ? {} : _ref9$selectedRateQuo;
    var upfrontFee = _ref9$selectedRateQuo.upfrontFee;
    return !!upfrontFee;
  })) {
    var _categoryMap$CLOSING_2;

    categoryMap[CLOSING_COST_CATEGORY.OTHER] = [].concat(_toConsumableArray((_categoryMap$CLOSING_2 = categoryMap[CLOSING_COST_CATEGORY.OTHER]) !== null && _categoryMap$CLOSING_2 !== void 0 ? _categoryMap$CLOSING_2 : []), [UPFRONT_FEE]);
  }

  if (scenarios.some(function (_ref10) {
    var _ref10$scenario = _ref10.scenario;
    _ref10$scenario = _ref10$scenario === void 0 ? {} : _ref10$scenario;
    var feeID = _ref10$scenario.feeID;
    return feeID;
  })) {
    var _categoryMap$CLOSING_3;

    categoryMap[CLOSING_COST_CATEGORY.ORIGINATION_CHARGES] = [].concat(_toConsumableArray((_categoryMap$CLOSING_3 = categoryMap[CLOSING_COST_CATEGORY.ORIGINATION_CHARGES]) !== null && _categoryMap$CLOSING_3 !== void 0 ? _categoryMap$CLOSING_3 : []), [CLOSING_COSTS]);
  }

  return categoryMap;
};

var displayScenariosSelector = createSelector([orderedPricingScenarios, totalLiabilitiesPaidAtClosingSelector, totalLiabilitiesPaidOffThroughLoanSelector, loanRateQuoteIsLockedSelector, primaryScenarioLabelSelector, feeSumByIDSelector], function (scenarios, totalLiabilitiesPaidAtClosing, totalLiabilitiesPaidOffThroughLoan, loanRateQuoteIsLocked, primaryScenarioLabel, feeSumByID) {
  var buyerPaidFees = getAllBuyerPaidClosingFeesInScenarios(scenarios);
  var mappedScenarios = scenarios.map(function (scenario) {
    return generateDisplayValuesForScenario(scenario, buyerPaidFees, feeSumByID, totalLiabilitiesPaidAtClosing, totalLiabilitiesPaidOffThroughLoan);
  });
  mappedScenarios.forEach(function (scenario, i, arr) {
    var _arr;

    if (scenario.displayName === ((_arr = arr[i - 1]) === null || _arr === void 0 ? void 0 : _arr.displayName)) {
      scenario.displayName = scenario.displayName + " (1)";
    }
  }); // Only show the primary scenario if the rate has been locked.

  return mappedScenarios.filter(function (scenario) {
    return loanRateQuoteIsLocked ? scenario.scenario.scenarioLabel === primaryScenarioLabel : scenario;
  });
});
var rateQuotesSelector = createSelector([displayScenariosSelector, pricingFieldsSelector, scenarioLabelPropSelector], function (scenarios, fields, id) {
  var scenario = scenarios.find(function (_ref11) {
    var scenarioLabel = _ref11.scenario.scenarioLabel;
    return id === scenarioLabel;
  });

  if (scenario) {
    var rateQuotes = scenario.rateQuoteIDsByLockTerm[(fields === null || fields === void 0 ? void 0 : fields.lockTerm) || scenario.selectedRateQuote.lockTerm];
    return rateQuotes ? rateQuotes.map(function (rateQuoteId) {
      return scenario.rateQuotes[rateQuoteId];
    }).sort(function (_ref12, _ref13) {
      var a = _ref12.noteRate;
      var b = _ref13.noteRate;
      return a - b;
    }) : null;
  }

  return null;
});
var primaryScenarioDisplayValueSelector = createSelector([primaryScenarioSelector, totalLiabilitiesPaidAtClosingSelector, totalLiabilitiesPaidOffThroughLoanSelector, feeSumByIDSelector], function (primaryScenario, totalLiabilitiesPaidAtClosing, totalLiabilitiesPaidOffThroughLoan, feeSumByID) {
  return primaryScenario ? generateDisplayValuesForScenario(primaryScenario, getAllBuyerPaidClosingFeesInScenarios([primaryScenario]), feeSumByID, totalLiabilitiesPaidAtClosing, totalLiabilitiesPaidOffThroughLoan) : null;
});
var DEFAULT_FIELDS = {
  amortizationType: AMORTIZATION_TYPE_LOOKUP$1.FIXED,
  amortizationSchedule: FIXED_AMORTIZATION_PERIODS_LOOKUP.THIRTY_YEAR,
  attachmentType: ATTACHMENT_TYPES_LOOKUP.DETACHED,
  constructionMethod: CONSTRUCTION_TYPES_LOOKUP.SITE_BUILT,
  downPayment: 0,
  downPaymentPercent: 20,
  escrow: ESCROW_TYPES_LOOKUP$1.TAXES_AND_INSURANCE,
  homeownersAssociationDues: 0,
  homeownersInsurance: 0,
  leadSource: LEAD_SOURCES.Marksman,
  loanPurpose: LOAN_TYPES_LOOKUP$3.PURCHASE,
  ltv: 80,
  militaryStatusType: MILITARY_STATUS_TYPE_LOOKUP$1.NONE,
  mortgageType: PROGRAM_TYPES_LOOKUP$1.CONVENTIONAL,
  otherMonthlyPayment: 0,
  propertyType: PROPERTY_TYPES_LOOKUP$1.SINGLE,
  propertyUse: PROPERTY_USE_LOOKUP.PRIMARY,
  rollFeesIntoLoan: false,
  taxes: 0,
  valuation: 0,
  targetLoanAmount: 0
};
var loanFilePricingFieldsSelector = createSelector([loanAttributesSelector, primaryScenarioSelector, loanFileCountyNameSelector, combinedDTIWithoutPricingSelector, computedLoanTotalScoreSelector, loanFileMilitaryServiceSelector, loanFileFirstTimeHomeBuyerSelector, feeSumByIDSelector], function (loanAttributes, primaryScenario, county, dtiPercentage, fico, loanFileMilitaryService, loanFileFirstTimeHomeBuyer, feeSumByID) {
  var _loanFileMilitaryServ, _feeSumByID$feeID;

  var _ref14 = primaryScenario !== null && primaryScenario !== void 0 ? primaryScenario : {},
      _ref14$scenario = _ref14.scenario;

  _ref14$scenario = _ref14$scenario === void 0 ? DEFAULT_FIELDS : _ref14$scenario;
  var _ref14$scenario$valua = _ref14$scenario.valuation,
      scenarioValuation = _ref14$scenario$valua === void 0 ? DEFAULT_FIELDS.valuation : _ref14$scenario$valua,
      scenarioPropertyType = _ref14$scenario.propertyType,
      scenarioAttachmentType = _ref14$scenario.attachmentType,
      scenarioConstructionMethod = _ref14$scenario.constructionMethod,
      scenarioPropertyUse = _ref14$scenario.propertyUse,
      scenarioFico = _ref14$scenario.fico,
      scenarioLoanPurpose = _ref14$scenario.loanPurpose,
      _ref14$scenario$prope = _ref14$scenario.propertyLocation;
  _ref14$scenario$prope = _ref14$scenario$prope === void 0 ? {} : _ref14$scenario$prope;

  var subdivision = _ref14$scenario$prope.subdivision,
      scenarioCounty = _ref14$scenario$prope.county,
      propertyZIP = _ref14$scenario.propertyZIP,
      countyFIPS = _ref14$scenario.countyFIPS,
      scenarioFirstTimeBuyer = _ref14$scenario.firstTimeBuyer,
      scenarioRefinance = _ref14$scenario.refinance,
      _ref14$scenario$targe = _ref14$scenario.targetLoanAmount,
      scenarioTargetLoanAmount = _ref14$scenario$targe === void 0 ? DEFAULT_FIELDS.targetLoanAmount : _ref14$scenario$targe,
      scenarioMortgageType = _ref14$scenario.mortgageType,
      scenarioLoanOption = _ref14$scenario.loanOption,
      feeID = _ref14$scenario.feeID,
      downPayment = _ref14$scenario.downPayment,
      scenario = _objectWithoutProperties(_ref14$scenario, _excluded$e);

  var _ref15 = loanAttributes !== null && loanAttributes !== void 0 ? loanAttributes : {},
      addressCity = _ref15.addressCity,
      _ref15$addressCountyF = _ref15.addressCountyFIPS,
      addressCountyFIPS = _ref15$addressCountyF === void 0 ? countyFIPS : _ref15$addressCountyF,
      addressZIP = _ref15.addressZIP,
      addressState = _ref15.addressState,
      _ref15$valuation = _ref15.valuation,
      valuation = _ref15$valuation === void 0 ? scenarioValuation : _ref15$valuation,
      _ref15$targetLoanAmou = _ref15.targetLoanAmount,
      targetLoanAmount = _ref15$targetLoanAmou === void 0 ? scenarioTargetLoanAmount : _ref15$targetLoanAmou,
      _ref15$attachmentType = _ref15.attachmentType,
      attachmentType = _ref15$attachmentType === void 0 ? scenarioAttachmentType : _ref15$attachmentType,
      _ref15$constructionMe = _ref15.constructionMethod,
      constructionMethod = _ref15$constructionMe === void 0 ? scenarioConstructionMethod : _ref15$constructionMe,
      _ref15$propertyUse = _ref15.propertyUse,
      propertyUse = _ref15$propertyUse === void 0 ? scenarioPropertyUse : _ref15$propertyUse,
      _ref15$loanPurpose = _ref15.loanPurpose,
      loanPurpose = _ref15$loanPurpose === void 0 ? scenarioLoanPurpose : _ref15$loanPurpose,
      refinance = _ref15.refinance,
      _ref15$propertyType = _ref15.propertyType,
      propertyType = _ref15$propertyType === void 0 ? scenarioPropertyType : _ref15$propertyType;

  var militaryStatusType = (_loanFileMilitaryServ = loanFileMilitaryService === null || loanFileMilitaryService === void 0 ? void 0 : loanFileMilitaryService.militaryStatusType) !== null && _loanFileMilitaryServ !== void 0 ? _loanFileMilitaryServ : MILITARY_STATUS_TYPE_LOOKUP$1.NONE;
  var ltv = valuation === 0 ? DEFAULT_FIELDS.ltv : targetLoanAmount / valuation * 100;
  var downPaymentPercent = 100 - ltv;
  var computedMortgageType = scenarioLoanOption === PROGRAM_TYPES_LOOKUP$1.REFI_NOW ? PROGRAM_TYPES_LOOKUP$1.REFI_NOW : scenarioMortgageType;
  return Object.assign({}, DEFAULT_FIELDS, scenario, {
    // Note: calculating down payment by just down payment percent & valuation can only occur if fees are not established
    // Given that fees can only occur if there is a primary scenario, it's safe to do this at present
    downPayment: primaryScenario ? downPayment : downPaymentPercent / 100 * valuation,
    mortgageType: computedMortgageType,
    firstTimeBuyer: loanFileFirstTimeHomeBuyer !== null && loanFileFirstTimeHomeBuyer !== void 0 ? loanFileFirstTimeHomeBuyer : scenarioFirstTimeBuyer,
    valuation: valuation,
    loanPurpose: loanPurpose,
    propertyType: propertyType,
    targetLoanAmount: targetLoanAmount,
    downPaymentPercent: downPaymentPercent,
    ltv: ltv,
    attachmentType: attachmentType,
    constructionMethod: constructionMethod,
    refinance: loanPurpose === LOAN_TYPES_LOOKUP$3.REFINANCE ? Object.assign({}, _merge2(scenarioRefinance, refinance), {
      currentBalance: primaryScenario ? scenarioRefinance.currentBalance : targetLoanAmount
    }) : null,
    propertyUse: propertyUse,
    propertyLocation: {
      subdivision: addressState !== null && addressState !== void 0 ? addressState : subdivision,
      county: county !== null && county !== void 0 ? county : scenarioCounty,
      city: addressCity
    },
    countyFIPS: addressCountyFIPS,
    propertyZIP: addressZIP !== null && addressZIP !== void 0 ? addressZIP : propertyZIP,
    dti: (dtiPercentage !== null && dtiPercentage !== void 0 ? dtiPercentage : 0) * 100,
    fico: fico !== null && fico !== void 0 ? fico : scenarioFico,
    militaryStatusType: militaryStatusType === MILITARY_STATUS_TYPE_LOOKUP$1.EMPTY ? MILITARY_STATUS_TYPE_LOOKUP$1.NONE : militaryStatusType,
    vaFirstTimeUse: loanFileMilitaryService === null || loanFileMilitaryService === void 0 ? void 0 : loanFileMilitaryService.vaFirstTimeUse,
    vaFundingFeeExemption: loanFileMilitaryService === null || loanFileMilitaryService === void 0 ? void 0 : loanFileMilitaryService.vaFundingFeeExemption,
    feeID: feeID,
    totalFees: (_feeSumByID$feeID = feeSumByID === null || feeSumByID === void 0 ? void 0 : feeSumByID[feeID]) !== null && _feeSumByID$feeID !== void 0 ? _feeSumByID$feeID : 0
  });
});
var DOLLAR_DECIMAL_COUNT = 2;

var mutateForRequest$1 = function mutateForRequest(_ref16, feeID) {
  var id = _ref16.id,
      downPaymentPercent = _ref16.downPaymentPercent,
      ltv = _ref16.ltv,
      propertyLocation = _ref16.propertyLocation,
      totalFees = _ref16.totalFees,
      amortizationSchedule = _ref16.amortizationSchedule,
      amortizationType = _ref16.amortizationType,
      dti = _ref16.dti,
      financeFees = _ref16.financeFees,
      annualHomeOwnersInsurance = _ref16.homeownersInsurance,
      militaryStatusType = _ref16.militaryStatusType,
      escrow = _ref16.escrow,
      annualTaxes = _ref16.taxes,
      vaFirstTimeUse = _ref16.vaFirstTimeUse,
      vaFundingFeeExemption = _ref16.vaFundingFeeExemption,
      loanPurpose = _ref16.loanPurpose,
      refinance = _ref16.refinance,
      firstTimeBuyer = _ref16.firstTimeBuyer,
      closingDate = _ref16.closingDate,
      homeownersInsuranceDue = _ref16.homeownersInsuranceDue,
      mortgageType = _ref16.mortgageType,
      fields = _objectWithoutProperties(_ref16, _excluded2$4);

  var isRefiNow = mortgageType === PROGRAM_TYPES_LOOKUP$1.REFI_NOW;

  var _ref17 = refinance !== null && refinance !== void 0 ? refinance : {},
      hasCashOut = _ref17.hasCashOut,
      cashOut = _ref17.cashOut,
      restOfRefinance = _objectWithoutProperties(_ref17, _excluded3$1);

  return Object.assign({}, fields, {
    feeID: feeID,
    mortgageType: isRefiNow ? PROGRAM_TYPES_LOOKUP$1.CONVENTIONAL : mortgageType,
    loanOption: isRefiNow ? PROGRAM_TYPES_LOOKUP$1.REFI_NOW : null,
    loanPurpose: loanPurpose,
    refinance: loanPurpose === LOAN_TYPES_LOOKUP$3.REFINANCE ? Object.assign({}, restOfRefinance, {
      cashOut: hasCashOut ? cashOut : null
    }) : null,
    term: amortizationType === AMORTIZATION_TYPE_LOOKUP$1.FIXED ? FIXED_IN_MONTHS$1[amortizationSchedule] : ADJUSTABLE_AMORTIZATION_IN_MONTHS,
    loanProduct: amortizationType === AMORTIZATION_TYPE_LOOKUP$1.FIXED ? amortizationType : amortizationSchedule,
    militaryService: VALID_MILITARY_STATUS_VALUES.includes(militaryStatusType) ? {
      onlyNonActivatedReserve: militaryStatusType === MILITARY_STATUS_TYPE_LOOKUP$1.NAT_GUARD_OR_RESERVES,
      vaFirstTimeUse: vaFirstTimeUse,
      vaFundingFeeExemption: vaFundingFeeExemption
    } : {
      na: true
    },
    dti: dti / 100,
    waiveEscrow: ESCROW_TO_WAIVE_ESCROW_MAP[escrow],
    taxes: floatParser(float(annualTaxes / 12, DOLLAR_DECIMAL_COUNT, true)),
    homeownersInsurance: floatParser(float(annualHomeOwnersInsurance / 12, DOLLAR_DECIMAL_COUNT, true)),
    closingDate: isoDate(closingDate),
    homeownersInsuranceDue: isoDate(homeownersInsuranceDue),
    // Would have preferred null, but the field is optional boolean, not nullable boolean
    firstTimeBuyer: loanPurpose === LOAN_TYPES_LOOKUP$3.PURCHASE ? firstTimeBuyer : undefined,
    financeFees: [PROGRAM_TYPES_LOOKUP$1.FHA, PROGRAM_TYPES_LOOKUP$1.VA].includes(mortgageType) ? financeFees : null
  });
};

var pricingRequestSelector = createSelector([pricingFieldsSelector, currentFeeIDSelector], function (fields, feeID) {
  return fields ? mutateForRequest$1(fields, feeID) : null;
});

var mutateForLoanAttributesRequest = function mutateForLoanAttributesRequest(_ref18, _ref19) {
  var loanCountyFIPS = _ref18.addressCountyFIPS,
      loanState = _ref18.addressState,
      loanZIP = _ref18.addressZIP,
      loanCity = _ref18.addressCity;
  var attachmentType = _ref19.attachmentType,
      constructionMethod = _ref19.constructionMethod,
      loanPurpose = _ref19.loanPurpose,
      propertyUse = _ref19.propertyUse,
      propertyType = _ref19.propertyType,
      targetLoanAmount = _ref19.targetLoanAmount,
      valuation = _ref19.valuation,
      _ref19$countyFIPS = _ref19.countyFIPS,
      scenarioCountyFIPS = _ref19$countyFIPS === void 0 ? null : _ref19$countyFIPS,
      _ref19$propertyLocati = _ref19.propertyLocation;
  _ref19$propertyLocati = _ref19$propertyLocati === void 0 ? {} : _ref19$propertyLocati;
  var _ref19$propertyLocati2 = _ref19$propertyLocati.subdivision,
      scenarioState = _ref19$propertyLocati2 === void 0 ? null : _ref19$propertyLocati2,
      scenarioCity = _ref19$propertyLocati.city,
      propertyZIP = _ref19.propertyZIP,
      refinance = _ref19.refinance;

  var _ref20 = refinance !== null && refinance !== void 0 ? refinance : {},
      hasCashOut = _ref20.hasCashOut,
      currentBalance = _ref20.currentBalance,
      _ref20$cashOut = _ref20.cashOut;

  _ref20$cashOut = _ref20$cashOut === void 0 ? {} : _ref20$cashOut;

  var cashOutAmount = _ref20$cashOut.cashOutAmount,
      cashOut = _objectWithoutProperties(_ref20$cashOut, _excluded4$1),
      restOfRefinance = _objectWithoutProperties(_ref20, _excluded5$1); // Only overwrite loan address if scenario has differing nonempty values
  // for empty loan values


  var hasDifferingNonEmptyAddressFields = scenarioCountyFIPS && scenarioCountyFIPS !== loanCountyFIPS || scenarioState && scenarioState !== loanState || propertyZIP && propertyZIP !== loanZIP || scenarioCity && scenarioCity !== loanCity;
  return Object.assign({
    attachmentType: attachmentType,
    constructionMethod: constructionMethod,
    loanPurpose: loanPurpose,
    propertyUse: propertyUse,
    propertyType: propertyType,
    targetLoanAmount: targetLoanAmount,
    valuation: valuation,
    refinance: loanPurpose === LOAN_TYPES_LOOKUP$3.REFINANCE ? Object.assign({}, restOfRefinance, {
      cashOut: hasCashOut ? cashOut : null
    }) : null
  }, hasDifferingNonEmptyAddressFields ? {
    addressCountyFIPS: scenarioCountyFIPS,
    addressState: scenarioState,
    addressZIP: propertyZIP,
    addressCity: scenarioCity
  } : {});
};

var loanAttributesUpdateRequestSelector = createSelector([loanAttributesSelector, pricingFieldsSelector], function (loanAttributes, request) {
  return request ? toAPI$1(Object.assign({}, loanAttributes, mutateForLoanAttributesRequest(loanAttributes, request))) : null;
});
var createFeesRequestSelector = createSelector([pricingFieldsSelector, loanAttributesSelector, currentFeeIDSelector, loanIDPropSelector], function (fields, loanAttributes, feeID, loanID) {
  var amortizationType = fields.amortizationType,
      countyFIPS = fields.countyFIPS,
      loanPurpose = fields.loanPurpose,
      mortgageType = fields.mortgageType,
      propertyType = fields.propertyType,
      propertyUse = fields.propertyUse,
      targetLoanAmount = fields.targetLoanAmount,
      valuation = fields.valuation,
      propertyZIP = fields.propertyZIP,
      _fields$propertyLocat = fields.propertyLocation,
      subdivision = _fields$propertyLocat.subdivision,
      county = _fields$propertyLocat.county,
      city = _fields$propertyLocat.city,
      refinance = fields.refinance;
  var addressStreet = loanAttributes.addressStreet,
      addressUnit = loanAttributes.addressUnit,
      projectType = loanAttributes.projectType,
      transaction = loanAttributes.transaction;

  var _ref21 = refinance !== null && refinance !== void 0 ? refinance : {},
      hasCashOut = _ref21.hasCashOut,
      program = _ref21.program,
      appraisalRequired = _ref21.appraisalRequired;

  var _ref22 = transaction !== null && transaction !== void 0 ? transaction : {},
      construction = _ref22.construction,
      type = _ref22.type;

  return {
    loanInformation: {
      hasPropertyInspectionWaiver: !appraisalRequired,
      isRefinanceCashOut: hasCashOut,
      isRefinanceStreamline: program === REFINANCE_PROGRAMS_LOOKUP.STREAMLINED,
      loanAmount: targetLoanAmount,
      loanID: loanID,
      loanPurpose: loanPurpose,
      loanProduct: amortizationType === AMORTIZATION_TYPE_LOOKUP$1.FIXED ? 'Fixed' : 'ARM',
      // The fees service does not use LFS standard enums to represent fixed amortizations, translating between the two enums
      mortgageType: mortgageType
    },
    propertyInformation: {
      addressStreet: addressStreet,
      addressUnit: addressUnit,
      city: city,
      county: county,
      countyFIPS: countyFIPS,
      hasConstructionToPerm: type === TRANSACTION_TYPES_LOOKUP$1.CONSTRUCTION,
      isPlannedUnitDevelopment: projectType === PROJECT_TYPES_LOOKUP$1.PLANNED_UNIT_DEVELOPMENT,
      propertyType: propertyType,
      propertyUse: propertyUse,
      propertyValue: valuation,
      rehabilitationCosts: construction === null || construction === void 0 ? void 0 : construction.constructionCosts,
      subdivision: subdivision,
      code: propertyZIP
    },
    feeID: feeID
  };
});

var compositePricing = /*#__PURE__*/Object.freeze({
  __proto__: null,
  LENDER_CREDIT_COST_FEE: LENDER_CREDIT_COST_FEE,
  UPFRONT_FEE: UPFRONT_FEE,
  CLOSING_COSTS: CLOSING_COSTS,
  displayScenariosSelector: displayScenariosSelector,
  rateQuotesSelector: rateQuotesSelector,
  primaryScenarioDisplayValueSelector: primaryScenarioDisplayValueSelector,
  DEFAULT_FIELDS: DEFAULT_FIELDS,
  loanFilePricingFieldsSelector: loanFilePricingFieldsSelector,
  pricingRequestSelector: pricingRequestSelector,
  loanAttributesUpdateRequestSelector: loanAttributesUpdateRequestSelector,
  createFeesRequestSelector: createFeesRequestSelector
});

var borrowerAttributesSchema = loanFile$3.borrowerAttributesSchema;
var getCurrentResidence = function getCurrentResidence(addresses) {
  var _addresses$find;

  return (_addresses$find = addresses.find(function (addr) {
    return addr.current;
  })) !== null && _addresses$find !== void 0 ? _addresses$find : {};
};
var CREDIT_NOT_PULLED_MESSAGE = 'You must pull the credit scores for the borrowers on this loan before you can start a Finicity session'; // (ZHLCX-715) There is discussion as of 9 DEC 2021 with replacing this with an API driven message.

var verificationDisabledReasonsSelector = createSelector([borrowerListSelector, creditHasBeenPulledSelector$1, borrowerDisplayNamesSelector], function (borrowers, creditHasBeenPulled, borrowerDisplayNames) {
  var reasons = new Set();

  if (!creditHasBeenPulled) {
    reasons.add(CREDIT_NOT_PULLED_MESSAGE);
  }

  borrowers.forEach(function (borrower) {
    var _borrower$attributes$, _borrower$attributes;

    var residence = getCurrentResidence((_borrower$attributes$ = (_borrower$attributes = borrower.attributes) === null || _borrower$attributes === void 0 ? void 0 : _borrower$attributes.residences) !== null && _borrower$attributes$ !== void 0 ? _borrower$attributes$ : []);

    try {
      /* this reduce is necessary because on initial load, empty values are omitted from borrower.attributes
      which correctly causes the "<Field> is required" message, however when the user saves the form with empty fields,
      the fields are included with a null value. This doesn't cause the expected message, and instead causes
      the "Invalid value" error message which is not useful when not associated to a field. So in order to
      cause the intended error message for informing the user why verifications are disabled sans form
      context, we must remove the nulled fields from the object.
      */
      var nonNullAttributes = Object.entries(borrower.attributes).reduce(function (acc, _ref) {
        var _Object$assign;

        var _ref2 = _slicedToArray(_ref, 2),
            key = _ref2[0],
            value = _ref2[1];

        return value === null ? Object.assign({}, acc) : Object.assign({}, acc, (_Object$assign = {}, _Object$assign[key] = value, _Object$assign));
      }, {});
      borrowerAttributesSchema.validateSync(nonNullAttributes, {
        abortEarly: false,
        context: {
          verificationEmailCTARequired: true
        }
      });
    } catch (error) {
      error.errors.forEach(function (errorMessage) {
        return reasons.add(errorMessage + " for " + borrowerDisplayNames[borrower.id]);
      });
    }

    try {
      address.validateSync(Object.assign({}, residence.address, {
        country: 'US'
      }), {
        abortEarly: false
      });
    } catch (error) {
      error.errors.forEach(function (errorMessage) {
        return reasons.add(errorMessage + " for " + borrowerDisplayNames[borrower.id]);
      });
    }
  });
  return Array.from(reasons);
});
var verificationCanBeSubmittedSelector = createSelector([borrowerListSelector, creditHasBeenPulledSelector$1], function (borrowers, creditHasBeenPulled) {
  return Array.isArray(borrowers) && !!borrowers.length && borrowers.every(function (borrower) {
    var _borrower$attributes$2, _borrower$attributes2;

    var residence = getCurrentResidence((_borrower$attributes$2 = (_borrower$attributes2 = borrower.attributes) === null || _borrower$attributes2 === void 0 ? void 0 : _borrower$attributes2.residences) !== null && _borrower$attributes$2 !== void 0 ? _borrower$attributes$2 : []);
    var isBorrowerValid = borrowerAttributesSchema.isValidSync(borrower.attributes, {
      context: {
        verificationEmailCTARequired: true
      }
    });
    var isAddressValid = address.isValidSync(Object.assign({}, residence.address, {
      country: 'US'
    }));
    return isBorrowerValid && isAddressValid;
  }) && creditHasBeenPulled;
});

var compositeVerifications = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getCurrentResidence: getCurrentResidence,
  CREDIT_NOT_PULLED_MESSAGE: CREDIT_NOT_PULLED_MESSAGE,
  verificationDisabledReasonsSelector: verificationDisabledReasonsSelector,
  verificationCanBeSubmittedSelector: verificationCanBeSubmittedSelector
});

function generateParts(loanNumber, primaryBorrowerName) {
  var result = [];

  if (loanNumber) {
    result.push(loanNumber);
    result.push(' - ');
  }

  if (primaryBorrowerName) {
    result.push(primaryBorrowerName);
  }

  if (!primaryBorrowerName && !loanNumber) {
    result.push('New application');
  }

  return result;
}

var loanTitleSelector = createSelector([loanNumberSelector, primaryBorrowerNameSelector], function () {
  return Array.from(generateParts.apply(void 0, arguments)).join('');
});

var loanTitle = /*#__PURE__*/Object.freeze({
  __proto__: null,
  loanTitleSelector: loanTitleSelector
});

var RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE_LOOKUP = constants.loanFile.RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE_LOOKUP;
var declarationsSelector = createSelector([loanFileSelector], function (loanFile) {
  var _loanFile$declaration;

  return (_loanFile$declaration = loanFile === null || loanFile === void 0 ? void 0 : loanFile.declarations) !== null && _loanFile$declaration !== void 0 ? _loanFile$declaration : {};
});
var declarationItemsSelector = createSelector([declarationsSelector], function (declarations) {
  var _declarations$items;

  return (_declarations$items = declarations === null || declarations === void 0 ? void 0 : declarations.items) !== null && _declarations$items !== void 0 ? _declarations$items : {};
});
var RADIO_BUTTON_INPUTS = ['intentToOccupy', 'recentOwnershipInterestDerived', 'specialRelationshipWithSeller', 'undisclosedFunds', 'undisclosedMortgage', 'undisclosedCredit', 'subjectToLien', 'undisclosedGuarantor', 'outstandingJudgments', 'delinquent', 'partyToLawsuit', 'conveyedTitleInLieuOfForeclosure', 'shortSale', 'foreclosure', 'hasBankruptcy'];

var getDeclarationsByBorrower = function getDeclarationsByBorrower(borrower) {
  var _ref = borrower !== null && borrower !== void 0 ? borrower : {},
      _ref$bankruptcy = _ref.bankruptcy,
      bankruptcy = _ref$bankruptcy === void 0 ? null : _ref$bankruptcy,
      _ref$delinquent = _ref.delinquent,
      delinquent = _ref$delinquent === void 0 ? null : _ref$delinquent,
      _ref$foreclosure = _ref.foreclosure,
      foreclosure = _ref$foreclosure === void 0 ? null : _ref$foreclosure,
      _ref$outstandingJudgm = _ref.outstandingJudgments,
      outstandingJudgments = _ref$outstandingJudgm === void 0 ? null : _ref$outstandingJudgm,
      _ref$partyToLawsuit = _ref.partyToLawsuit,
      partyToLawsuit = _ref$partyToLawsuit === void 0 ? null : _ref$partyToLawsuit,
      _ref$intentToOccupy = _ref.intentToOccupy,
      intentToOccupy = _ref$intentToOccupy === void 0 ? null : _ref$intentToOccupy,
      _ref$recentOwnershipI = _ref.recentOwnershipInterest,
      recentOwnershipInterest = _ref$recentOwnershipI === void 0 ? null : _ref$recentOwnershipI,
      _ref$specialRelations = _ref.specialRelationshipWithSeller,
      specialRelationshipWithSeller = _ref$specialRelations === void 0 ? null : _ref$specialRelations,
      _ref$undisclosedFunds = _ref.undisclosedFundsAmount,
      undisclosedFundsAmount = _ref$undisclosedFunds === void 0 ? null : _ref$undisclosedFunds,
      _ref$undisclosedMortg = _ref.undisclosedMortgage,
      undisclosedMortgage = _ref$undisclosedMortg === void 0 ? null : _ref$undisclosedMortg,
      _ref$undisclosedCredi = _ref.undisclosedCredit,
      undisclosedCredit = _ref$undisclosedCredi === void 0 ? null : _ref$undisclosedCredi,
      _ref$subjectToLien = _ref.subjectToLien,
      subjectToLien = _ref$subjectToLien === void 0 ? null : _ref$subjectToLien,
      _ref$undisclosedGuara = _ref.undisclosedGuarantor,
      undisclosedGuarantor = _ref$undisclosedGuara === void 0 ? null : _ref$undisclosedGuara,
      _ref$conveyedTitleInL = _ref.conveyedTitleInLieuOfForeclosure,
      conveyedTitleInLieuOfForeclosure = _ref$conveyedTitleInL === void 0 ? null : _ref$conveyedTitleInL,
      shortSale = _ref.shortSale;

  var result = {
    delinquent: delinquent,
    foreclosure: foreclosure,
    intentToOccupy: intentToOccupy,
    outstandingJudgments: outstandingJudgments,
    partyToLawsuit: partyToLawsuit,
    specialRelationshipWithSeller: specialRelationshipWithSeller,
    undisclosedMortgage: undisclosedMortgage,
    undisclosedCredit: undisclosedCredit,
    subjectToLien: subjectToLien,
    undisclosedGuarantor: undisclosedGuarantor,
    conveyedTitleInLieuOfForeclosure: conveyedTitleInLieuOfForeclosure,
    shortSale: shortSale,
    // recentOwnershipInterest can take one of following values:
    // - null, which means the  attribute was not set
    // - an object with na: true, which means there was no recent ownership interest
    // - an object with propertyUse, fhaSecondaryResidence and ownershipType fields set,
    //   which means there was recent ownership interest
    // Assign null explicitly to indicate the field was not set.
    recentOwnershipInterestDerived: recentOwnershipInterest ? !(recentOwnershipInterest !== null && recentOwnershipInterest !== void 0 && recentOwnershipInterest.na) : null,
    propertyUse: recentOwnershipInterest !== null && recentOwnershipInterest !== void 0 && recentOwnershipInterest.fhaSecondaryResidence ? RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE_LOOKUP.FHA_SECONDARY_RESIDENCE : recentOwnershipInterest === null || recentOwnershipInterest === void 0 ? void 0 : recentOwnershipInterest.propertyUse,
    ownershipType: recentOwnershipInterest === null || recentOwnershipInterest === void 0 ? void 0 : recentOwnershipInterest.ownershipType,
    // Explicit check for null, since null means that the field was not set. Zero means
    // there are no undisclosed funds and a positive number means some non-zero amount
    // of undisclosed funds.
    // Assign null explicitly to indicate the field was not set.
    undisclosedFunds: undisclosedFundsAmount === null ? null : !!undisclosedFundsAmount,
    // Intentionally not using nullish operator here, because 0 should be interpreted as null
    undisclosedFundsAmount: undisclosedFundsAmount || null,
    // Assign null explicitly to indicate the field was not set.
    hasBankruptcy: bankruptcy ? !(bankruptcy !== null && bankruptcy !== void 0 && bankruptcy.na) : null,
    // Bankruptcy chapters are checkboxes. Hence just true/false values will suffice.
    bankruptcyChapter7: !!(bankruptcy !== null && bankruptcy !== void 0 && bankruptcy.chapter7),
    bankruptcyChapter11: !!(bankruptcy !== null && bankruptcy !== void 0 && bankruptcy.chapter11),
    bankruptcyChapter12: !!(bankruptcy !== null && bankruptcy !== void 0 && bankruptcy.chapter12),
    bankruptcyChapter13: !!(bankruptcy !== null && bankruptcy !== void 0 && bankruptcy.chapter13)
  };
  RADIO_BUTTON_INPUTS.forEach( // All radio button fields value should be string. Hence convert boolean
  // values to string if the field was set
  function (inputField) {
    result[inputField] = typeof result[inputField] === 'boolean' ? result[inputField].toString() : null;
  });
  return result;
};

var borrowerDeclarationsSelector = createSelector([declarationItemsSelector, borrowerIDPropSelector], function (items, borrowerID) {
  return getDeclarationsByBorrower(items[borrowerID]);
});

var declarations = /*#__PURE__*/Object.freeze({
  __proto__: null,
  declarationsSelector: declarationsSelector,
  declarationItemsSelector: declarationItemsSelector,
  borrowerDeclarationsSelector: borrowerDeclarationsSelector
});



var loanActions = /*#__PURE__*/Object.freeze({
  __proto__: null
});

var loanFileNotesSelector = createSelector([loanFileSelector], function (loanFile) {
  var _loanFile$notes;

  return (_loanFile$notes = loanFile === null || loanFile === void 0 ? void 0 : loanFile.notes) !== null && _loanFile$notes !== void 0 ? _loanFile$notes : null;
});
var notesFieldsSelector = createSelector([loanFileNotesSelector], function (notes) {
  var _notes$fields;

  return (_notes$fields = notes === null || notes === void 0 ? void 0 : notes.fields) !== null && _notes$fields !== void 0 ? _notes$fields : null;
});
var notesSelector = createSelector([loanFileNotesSelector], function (loanFileNotes) {
  var _loanFileNotes$notes;

  return (_loanFileNotes$notes = loanFileNotes === null || loanFileNotes === void 0 ? void 0 : loanFileNotes.notes) !== null && _loanFileNotes$notes !== void 0 ? _loanFileNotes$notes : null;
});

var notes = /*#__PURE__*/Object.freeze({
  __proto__: null,
  loanFileNotesSelector: loanFileNotesSelector,
  notesFieldsSelector: notesFieldsSelector,
  notesSelector: notesSelector
});

var otherNewMortgagesSelector = createSelector([loanFileSelector], function (loanFile) {
  return (loanFile === null || loanFile === void 0 ? void 0 : loanFile.otherNewMortgages) || null;
});
var otherNewMortgagesFormFieldsSelector = createSelector([otherNewMortgagesSelector], function (otherNewMortgages) {
  return (otherNewMortgages === null || otherNewMortgages === void 0 ? void 0 : otherNewMortgages.fields) || null;
});
var otherNewMortgagesItemsSelector = createSelector([otherNewMortgagesSelector], function (otherNewMortgages) {
  return (otherNewMortgages === null || otherNewMortgages === void 0 ? void 0 : otherNewMortgages.items) || [];
});

var otherNewMortgages = /*#__PURE__*/Object.freeze({
  __proto__: null,
  otherNewMortgagesSelector: otherNewMortgagesSelector,
  otherNewMortgagesFormFieldsSelector: otherNewMortgagesFormFieldsSelector,
  otherNewMortgagesItemsSelector: otherNewMortgagesItemsSelector
});

var personalInfoSelector = createSelector([loanFileSelector], function (loanFile) {
  return (loanFile === null || loanFile === void 0 ? void 0 : loanFile.personalInfo) || null;
});
var personalInfoFieldsSelector = createSelector([personalInfoSelector], function (personalInfo) {
  return (personalInfo === null || personalInfo === void 0 ? void 0 : personalInfo.fields) || null;
});
var personalInfoErrorsSelector = createSelector([personalInfoSelector], function (personalInfo) {
  return (personalInfo === null || personalInfo === void 0 ? void 0 : personalInfo.errors) || null;
});

var personalInfo = /*#__PURE__*/Object.freeze({
  __proto__: null,
  personalInfoSelector: personalInfoSelector,
  personalInfoFieldsSelector: personalInfoFieldsSelector,
  personalInfoErrorsSelector: personalInfoErrorsSelector
});

var preApprovalsSelector = createSelector([loanFileSelector], function (_ref) {
  var preApprovals = _ref.preApprovals;
  return preApprovals;
});

var getLetter = function getLetter(_ref2) {
  var id = _ref2.id,
      _ref2$computed = _ref2.computed,
      downloadURL = _ref2$computed.downloadURL,
      documentURL = _ref2$computed.documentURL,
      _ref2$parameters = _ref2.parameters,
      loanAmount = _ref2$parameters.loanAmount,
      issueDate = _ref2$parameters.issueDate,
      validDays = _ref2$parameters.validDays;
  return {
    id: id,
    downloadURL: downloadURL,
    documentURL: documentURL,
    loanAmount: loanAmount,
    expirationDate: add(parsers.date(issueDate), {
      days: validDays
    })
  };
};

var preApprovalLettersSelector = createSelector([preApprovalsSelector], function (_ref3) {
  var letters = _ref3.letters;
  return letters.map(getLetter);
});
var newPreApprovalLoadingSelector = createSelector([preApprovalsSelector], function (_ref4) {
  var createLoading = _ref4.createLoading;
  return createLoading;
});
var preApprovalsErrorSelector = createSelector([preApprovalsSelector], function (_ref5) {
  var fetchError = _ref5.fetchError,
      createError = _ref5.createError;
  return createError || fetchError || null;
});

var preApprovals = /*#__PURE__*/Object.freeze({
  __proto__: null,
  preApprovalLettersSelector: preApprovalLettersSelector,
  newPreApprovalLoadingSelector: newPreApprovalLoadingSelector,
  preApprovalsErrorSelector: preApprovalsErrorSelector
});

var _constants$loanFile$a = constants.loanFile,
    UNDERWRITING_DECLARATION_SECTIONS = _constants$loanFile$a.UNDERWRITING_DECLARATION_SECTIONS,
    UNDERWRITING_FRIENDLY_UI_FIELD_NAMES = _constants$loanFile$a.UNDERWRITING_FRIENDLY_UI_FIELD_NAMES,
    UNDERWRITER_FRIENDLY_NAMES = _constants$loanFile$a.UNDERWRITER_FRIENDLY_NAMES,
    AUS_TIMEOUT = constants.AUS_TIMEOUT,
    AUS_LOAN_APPLICATION_INCOMPLETE_ERROR = constants.AUS_LOAN_APPLICATION_INCOMPLETE_ERROR,
    AUS_LOAN_APPLICATION_INCOMPLETE_REASONS = constants.AUS_LOAN_APPLICATION_INCOMPLETE_REASONS,
    AUS_DESKTOP_UNDERWRITER_CONTROL_ERROR = constants.AUS_DESKTOP_UNDERWRITER_CONTROL_ERROR,
    AUS_DESKTOP_UNDERWRITER_ROUTING_ERROR = constants.AUS_DESKTOP_UNDERWRITER_ROUTING_ERROR,
    AUS_LOAN_PRODUCT_ADVISOR_ERROR = constants.AUS_LOAN_PRODUCT_ADVISOR_ERROR,
    AUS_UNKNOWN_ERROR = constants.AUS_UNKNOWN_ERROR,
    AUS_UNDERWRITER_TIMEOUT = constants.AUS_UNDERWRITER_TIMEOUT,
    AUS_UNDERWRITER_INVALID_RESPONSE = constants.AUS_UNDERWRITER_INVALID_RESPONSE,
    AUS_UNCATEGORIZED_VALUE = constants.AUS_UNCATEGORIZED_VALUE;

var strMissingDeclarations = function strMissingDeclarations(missingDeclarations) {
  return missingDeclarations !== null && missingDeclarations !== void 0 && missingDeclarations.length ? "declarations " + missingDeclarations.sort().join(', ') : null;
};

var strMissingFields = function strMissingFields(missingFields) {
  return missingFields !== null && missingFields !== void 0 && missingFields.length ? missingFields.join(', ') : null;
};

var friendlyNameForUnderwriter = function friendlyNameForUnderwriter(underwriter) {
  var _UNDERWRITER_FRIENDLY, _UNDERWRITER_FRIENDLY2;

  return (_UNDERWRITER_FRIENDLY = (_UNDERWRITER_FRIENDLY2 = UNDERWRITER_FRIENDLY_NAMES[underwriter]) === null || _UNDERWRITER_FRIENDLY2 === void 0 ? void 0 : _UNDERWRITER_FRIENDLY2.shortName) !== null && _UNDERWRITER_FRIENDLY !== void 0 ? _UNDERWRITER_FRIENDLY : 'automated underwriting';
};

var underwritingSelector = createSelector([loanFileSelector], function (loanFile) {
  return (loanFile === null || loanFile === void 0 ? void 0 : loanFile.underwriting) || null;
});
var underwritingFindingsByUnderwriterSelector = createSelector([underwritingSelector, underwriterPropSelector], function (underwriting, underwriter) {
  var _underwriting$finding, _underwriting$finding2;

  return (_underwriting$finding = underwriting === null || underwriting === void 0 ? void 0 : (_underwriting$finding2 = underwriting.findings) === null || _underwriting$finding2 === void 0 ? void 0 : _underwriting$finding2[underwriter]) !== null && _underwriting$finding !== void 0 ? _underwriting$finding : [];
});
var underwritingFindingsCurrentFindingsRecommendationSelector = createSelector([underwritingFindingsByUnderwriterSelector], function (underwritingFindings) {
  var _underwritingFindings, _underwritingFindings2;

  return (_underwritingFindings = underwritingFindings === null || underwritingFindings === void 0 ? void 0 : (_underwritingFindings2 = underwritingFindings[0]) === null || _underwritingFindings2 === void 0 ? void 0 : _underwritingFindings2.recommendation) !== null && _underwritingFindings !== void 0 ? _underwritingFindings : null;
});
var underwritingFindingsStatusSelector = createSelector([underwritingSelector], function (underwriting) {
  return (underwriting === null || underwriting === void 0 ? void 0 : underwriting.findingsStatus) || null;
});
var underwritingSubmissionStatusSelector = createSelector([underwritingSelector], function (underwriting) {
  return (underwriting === null || underwriting === void 0 ? void 0 : underwriting.submissionStatus) || null;
});
var underwritingSubmissionStatusByUnderwriterSelector = createSelector([underwritingSubmissionStatusSelector, underwriterPropSelector], function (submissionStatus, underwriter) {
  return (submissionStatus === null || submissionStatus === void 0 ? void 0 : submissionStatus[underwriter]) || null;
});
var underwritingErrorFieldsSelector = createSelector([underwritingSubmissionStatusByUnderwriterSelector], function (submissionStatus) {
  var _submissionStatus$err, _submissionStatus$err2;

  return (_submissionStatus$err = submissionStatus === null || submissionStatus === void 0 ? void 0 : (_submissionStatus$err2 = submissionStatus.error) === null || _submissionStatus$err2 === void 0 ? void 0 : _submissionStatus$err2.fields) !== null && _submissionStatus$err !== void 0 ? _submissionStatus$err : null;
});
var underwritingLoanErrorFieldsSelector = createSelector([underwritingErrorFieldsSelector], function (errorFields) {
  var _errorFields$loan$map;

  return (_errorFields$loan$map = errorFields === null || errorFields === void 0 ? void 0 : errorFields.loan.map(function (field) {
    var _UNDERWRITING_FRIENDL;

    return (_UNDERWRITING_FRIENDL = UNDERWRITING_FRIENDLY_UI_FIELD_NAMES[field]) !== null && _UNDERWRITING_FRIENDL !== void 0 ? _UNDERWRITING_FRIENDL : field;
  })) !== null && _errorFields$loan$map !== void 0 ? _errorFields$loan$map : null;
});
var underwritingBorrowerErrorFieldsSelector = createSelector([borrowerListSelector, borrowerDisplayNamesSelector, underwritingErrorFieldsSelector], function (borrowerList, borrowerDisplayNames, errorFields) {
  return errorFields !== null && errorFields !== void 0 && errorFields.borrowers ? borrowerList.map(function (borrower) {
    var _errorFields$borrower, _errorFields$borrower2;

    var borrowerID = borrower.id;
    var displayName = borrowerDisplayNames[borrowerID];
    var missingFields = errorFields === null || errorFields === void 0 ? void 0 : (_errorFields$borrower = errorFields.borrowers[borrowerID]) === null || _errorFields$borrower === void 0 ? void 0 : _errorFields$borrower.flatMap(function (field) {
      var _UNDERWRITING_FRIENDL2;

      return (_UNDERWRITING_FRIENDL2 = UNDERWRITING_FRIENDLY_UI_FIELD_NAMES[field]) !== null && _UNDERWRITING_FRIENDL2 !== void 0 ? _UNDERWRITING_FRIENDL2 : !UNDERWRITING_DECLARATION_SECTIONS[field] ? field : [];
    });
    var missingDeclarations = errorFields === null || errorFields === void 0 ? void 0 : (_errorFields$borrower2 = errorFields.borrowers[borrowerID]) === null || _errorFields$borrower2 === void 0 ? void 0 : _errorFields$borrower2.flatMap(function (field) {
      var _UNDERWRITING_DECLARA;

      return (_UNDERWRITING_DECLARA = UNDERWRITING_DECLARATION_SECTIONS[field]) !== null && _UNDERWRITING_DECLARA !== void 0 ? _UNDERWRITING_DECLARA : [];
    });
    var displayedError = [strMissingFields(missingFields), strMissingDeclarations(missingDeclarations)].filter(Boolean).join(', ');
    return {
      borrowerID: borrowerID,
      displayName: displayName,
      missingFields: missingFields,
      missingDeclarations: missingDeclarations,
      displayedError: displayedError
    };
  }) : null;
});
var underwritingErrorSelector = createSelector([underwritingSubmissionStatusByUnderwriterSelector, underwritingFindingsStatusSelector, underwriterPropSelector], function (submissionStatus, findingsStatus, underwriter) {
  var _submissionStatus$err3, _AUS_LOAN_APPLICATION, _AUS_TIMEOUT$AUS_LOAN;

  var errorExists = [submissionStatus === null || submissionStatus === void 0 ? void 0 : submissionStatus.type, findingsStatus === null || findingsStatus === void 0 ? void 0 : findingsStatus.type].includes(STATUS.ERROR);

  if (!errorExists) {
    return null;
  }

  var error = (_submissionStatus$err3 = submissionStatus === null || submissionStatus === void 0 ? void 0 : submissionStatus.error) !== null && _submissionStatus$err3 !== void 0 ? _submissionStatus$err3 : {};
  var checklist = groupBy(error === null || error === void 0 ? void 0 : error.checklist, function (item) {
    var _item$category;

    return (_item$category = item.category) !== null && _item$category !== void 0 ? _item$category : AUS_UNCATEGORIZED_VALUE;
  });
  var errorDetails = (_AUS_TIMEOUT$AUS_LOAN = {}, _AUS_TIMEOUT$AUS_LOAN[AUS_TIMEOUT] = {
    appearance: 'warning',
    message: "Request took too long to complete. " + DEFAULT_LOP_API_FAILURE_CTA
  }, _AUS_TIMEOUT$AUS_LOAN[AUS_LOAN_APPLICATION_INCOMPLETE_ERROR] = {
    appearance: 'warning',
    message: (_AUS_LOAN_APPLICATION = {}, _AUS_LOAN_APPLICATION[AUS_LOAN_APPLICATION_INCOMPLETE_REASONS.CREDIT_REPORT_MISSING] = "Loan application incomplete: a credit report is required before running " + friendlyNameForUnderwriter(underwriter), _AUS_LOAN_APPLICATION[AUS_LOAN_APPLICATION_INCOMPLETE_REASONS.CREDIT_REPORT_MISMATCH] = 'Loan application incomplete: the credit reports on loan are from multiple providers', _AUS_LOAN_APPLICATION[AUS_LOAN_APPLICATION_INCOMPLETE_REASONS.LOAN_FILE_INCOMPLETE] = 'Loan application incomplete: missing required information on loan file', _AUS_LOAN_APPLICATION[AUS_LOAN_APPLICATION_INCOMPLETE_REASONS.LOAN_PRICING_MISSING] = "Loan application incomplete: a pricing scenario is required before running " + friendlyNameForUnderwriter(underwriter), _AUS_LOAN_APPLICATION)[error.reason] || "Loan application incomplete: " + error.reason + "."
  }, _AUS_TIMEOUT$AUS_LOAN[AUS_DESKTOP_UNDERWRITER_CONTROL_ERROR] = {
    appearance: 'error',
    message: 'Unexpected error from Desktop Underwriter',
    rawError: error,
    checklist: checklist
  }, _AUS_TIMEOUT$AUS_LOAN[AUS_DESKTOP_UNDERWRITER_ROUTING_ERROR] = {
    appearance: 'error',
    message: 'Unexpected error from Desktop Underwriter',
    rawError: error,
    checklist: checklist
  }, _AUS_TIMEOUT$AUS_LOAN[AUS_LOAN_PRODUCT_ADVISOR_ERROR] = {
    appearance: 'error',
    message: 'Unexpected error from Loan Product Advisor',
    rawError: error,
    checklist: checklist
  }, _AUS_TIMEOUT$AUS_LOAN[AUS_UNDERWRITER_INVALID_RESPONSE] = {
    appearance: 'error',
    message: "Unexpected response from Desktop Underwriter. " + DEFAULT_LOP_API_FAILURE_CTA
  }, _AUS_TIMEOUT$AUS_LOAN[AUS_UNKNOWN_ERROR] = {
    appearance: 'error',
    message: DEFAULT_LOP_API_FAILURE_ERROR_MESSAGE
  }, _AUS_TIMEOUT$AUS_LOAN[AUS_UNDERWRITER_TIMEOUT] = {
    appearance: 'error',
    message: "Desktop Underwriter took to long to respond. " + DEFAULT_LOP_API_FAILURE_CTA
  }, _AUS_TIMEOUT$AUS_LOAN)[error.type] || {
    appearance: 'error',
    message: DEFAULT_LOP_API_FAILURE_ERROR_MESSAGE
  };
  return errorDetails;
});
var underwritingLoadingStatusSelector = createSelector([underwritingSubmissionStatusByUnderwriterSelector, underwritingFindingsStatusSelector], function (submissionStatus, findingsStatus) {
  return [submissionStatus === null || submissionStatus === void 0 ? void 0 : submissionStatus.type, findingsStatus === null || findingsStatus === void 0 ? void 0 : findingsStatus.type].includes(STATUS.LOADING);
});
var underwritingSelectedUnderwriterSelector = createSelector([underwritingSelector], function (underwriting) {
  var _underwriting$selecte;

  return (_underwriting$selecte = underwriting === null || underwriting === void 0 ? void 0 : underwriting.selectedUnderwriter) !== null && _underwriting$selecte !== void 0 ? _underwriting$selecte : null;
});
var selectedUnderwriterSubmissionStatusSelector = createSelector([underwritingSelector], function (underwriting) {
  var _underwriting$selecte2;

  return (_underwriting$selecte2 = underwriting === null || underwriting === void 0 ? void 0 : underwriting.selectedUnderwriterSubmissionStatus) !== null && _underwriting$selecte2 !== void 0 ? _underwriting$selecte2 : {};
});

var underwriting = /*#__PURE__*/Object.freeze({
  __proto__: null,
  underwritingSelector: underwritingSelector,
  underwritingFindingsByUnderwriterSelector: underwritingFindingsByUnderwriterSelector,
  underwritingFindingsCurrentFindingsRecommendationSelector: underwritingFindingsCurrentFindingsRecommendationSelector,
  underwritingFindingsStatusSelector: underwritingFindingsStatusSelector,
  underwritingSubmissionStatusSelector: underwritingSubmissionStatusSelector,
  underwritingSubmissionStatusByUnderwriterSelector: underwritingSubmissionStatusByUnderwriterSelector,
  underwritingErrorFieldsSelector: underwritingErrorFieldsSelector,
  underwritingLoanErrorFieldsSelector: underwritingLoanErrorFieldsSelector,
  underwritingBorrowerErrorFieldsSelector: underwritingBorrowerErrorFieldsSelector,
  underwritingErrorSelector: underwritingErrorSelector,
  underwritingLoadingStatusSelector: underwritingLoadingStatusSelector,
  underwritingSelectedUnderwriterSelector: underwritingSelectedUnderwriterSelector,
  selectedUnderwriterSubmissionStatusSelector: selectedUnderwriterSubmissionStatusSelector
});

var _constants$loanFile$b = constants.loanFile,
    ETHNICITIES = _constants$loanFile$b.ETHNICITIES,
    ETHNICITIES_LOOKUP = _constants$loanFile$b.ETHNICITIES_LOOKUP,
    HISPANIC_OR_LATINO_ETHNICITIES = _constants$loanFile$b.HISPANIC_OR_LATINO_ETHNICITIES,
    SEXES_LOOKUP = _constants$loanFile$b.SEXES_LOOKUP,
    SEXES = _constants$loanFile$b.SEXES,
    RACES_LOOKUP = _constants$loanFile$b.RACES_LOOKUP,
    ALL_RACES = _constants$loanFile$b.ALL_RACES,
    DEMOGRAPHICS_COLLECTION_INFERRED_TYPES = _constants$loanFile$b.DEMOGRAPHICS_COLLECTION_INFERRED_TYPES,
    EMPTY_SELECT_VALUE = constants.EMPTY_SELECT_VALUE,
    STRING_FALSE = constants.STRING_FALSE;
var demographicsSelector = createSelector([loanFileSelector], function (loanFile) {
  var _loanFile$borrowersDe;

  return (_loanFile$borrowersDe = loanFile === null || loanFile === void 0 ? void 0 : loanFile.borrowersDemographics) !== null && _loanFile$borrowersDe !== void 0 ? _loanFile$borrowersDe : {};
});
var demographicsItemsSelector = createSelector([demographicsSelector], function (demographics) {
  var _demographics$items;

  return (_demographics$items = demographics === null || demographics === void 0 ? void 0 : demographics.items) !== null && _demographics$items !== void 0 ? _demographics$items : {};
});
var DEFAULT_DEMOGRAPHICS = {
  ethnicity: Object.keys(HISPANIC_OR_LATINO_ETHNICITIES).reduce(function (acc, curr) {
    var _Object$assign;

    return Object.assign({}, acc, (_Object$assign = {}, _Object$assign[curr] = false, _Object$assign));
  }, {
    base: null,
    otherHispanicOrLatinoSpecified: null
  }),
  sex: null,
  race: Object.keys(ALL_RACES).reduce(function (acc, curr) {
    var _Object$assign2;

    return Object.assign({}, acc, (_Object$assign2 = {}, _Object$assign2[curr] = false, _Object$assign2));
  }, {
    americanIndianOrAlaskaNativeTribe: null,
    otherAsianSpecified: null,
    otherPacificIslanderSpecified: null
  })
};
var DEFAULT_DEMOGRAPHICS_COLLECTION = Object.keys(DEMOGRAPHICS_COLLECTION_INFERRED_TYPES).reduce(function (acc, curr) {
  var _Object$assign3;

  return Object.assign({}, acc, (_Object$assign3 = {}, _Object$assign3[curr] = STRING_FALSE, _Object$assign3));
}, {
  method: EMPTY_SELECT_VALUE
});

var getDemographics = function getDemographics(_ref) {
  var _Object$keys$find, _ref2;

  var ethnicity = _ref.ethnicity,
      sex = _ref.sex,
      race = _ref.race;
  return {
    ethnicity: Object.keys(HISPANIC_OR_LATINO_ETHNICITIES).reduce(function (acc, curr) {
      var _Object$assign4;

      return Object.assign({}, acc, (_Object$assign4 = {}, _Object$assign4[curr] = ethnicity === null || ethnicity === void 0 ? void 0 : ethnicity[curr], _Object$assign4));
    }, {
      base: ethnicity ? Object.keys(ETHNICITIES).find(function (key) {
        return ethnicity[key];
      }) : ETHNICITIES_LOOKUP.NOT_DISCLOSED,
      otherHispanicOrLatinoSpecified: ethnicity === null || ethnicity === void 0 ? void 0 : ethnicity.otherHispanicOrLatinoSpecified
    }),
    sex: (_Object$keys$find = Object.keys(SEXES).find(function (key) {
      return sex === null || sex === void 0 ? void 0 : sex[key];
    })) !== null && _Object$keys$find !== void 0 ? _Object$keys$find : SEXES_LOOKUP.NOT_DISCLOSED,
    race: race !== null && race !== void 0 ? race : (_ref2 = {}, _ref2[RACES_LOOKUP.NOT_DISCLOSED] = true, _ref2)
  };
};

var getDemographicsCollection = function getDemographicsCollection(demographicsCollection) {
  return Object.assign({}, demographicsCollection, {
    ethnicityInferred: String(demographicsCollection === null || demographicsCollection === void 0 ? void 0 : demographicsCollection.ethnicityInferred),
    sexInferred: String(demographicsCollection === null || demographicsCollection === void 0 ? void 0 : demographicsCollection.sexInferred),
    raceInferred: String(demographicsCollection === null || demographicsCollection === void 0 ? void 0 : demographicsCollection.raceInferred)
  });
};

var getDemographicsByBorrower = function getDemographicsByBorrower(_ref3) {
  var demographics = _ref3.demographics,
      demographicsCollection = _ref3.demographicsCollection;
  return {
    demographics: demographics ? getDemographics(demographics) : DEFAULT_DEMOGRAPHICS,
    demographicsCollection: demographicsCollection ? getDemographicsCollection(demographicsCollection) : DEFAULT_DEMOGRAPHICS_COLLECTION
  };
};

var borrowerDemographicsSelector = createSelector([demographicsItemsSelector, borrowerIDPropSelector], function (items, borrowerID) {
  var _items$borrowerID;

  return getDemographicsByBorrower((_items$borrowerID = items[borrowerID]) !== null && _items$borrowerID !== void 0 ? _items$borrowerID : {});
});

var borrowersDemographics = /*#__PURE__*/Object.freeze({
  __proto__: null,
  demographicsSelector: demographicsSelector,
  demographicsItemsSelector: demographicsItemsSelector,
  borrowerDemographicsSelector: borrowerDemographicsSelector
});

var agentSelector = createSelector([loanFileSelector], function (loanFile) {
  return loanFile === null || loanFile === void 0 ? void 0 : loanFile.agent;
});
var homestorySelector = createSelector([agentSelector], function (agent) {
  return agent === null || agent === void 0 ? void 0 : agent.homestory;
});
var isHomestoryEligibleSelector = createSelector([homestorySelector], function (homestory) {
  return homestory === null || homestory === void 0 ? void 0 : homestory.isEligible;
});
var homestoryIneligibilityReasonSelector = createSelector([homestorySelector, isHomestoryEligibleSelector], function (homestory, isHomestoryEligible) {
  return isHomestoryEligible ? null : homestory === null || homestory === void 0 ? void 0 : homestory.reason;
});
var homestoryErrorSelector = createSelector([homestorySelector], function (homestory) {
  var _homestory$error;

  return homestory === null || homestory === void 0 ? void 0 : (_homestory$error = homestory.error) === null || _homestory$error === void 0 ? void 0 : _homestory$error.msg;
});
var isHomestoryRequestSuccessfulSelector = createSelector([homestorySelector], function (homestory) {
  return homestory === null || homestory === void 0 ? void 0 : homestory.isSuccessful;
});
var shouldEvaluateHomestoryEligibilitySelector = createSelector([homestorySelector], function (homestory) {
  return homestory === null || homestory === void 0 ? void 0 : homestory.shouldEvaluateEligibility;
});

var agent = /*#__PURE__*/Object.freeze({
  __proto__: null,
  agentSelector: agentSelector,
  homestorySelector: homestorySelector,
  isHomestoryEligibleSelector: isHomestoryEligibleSelector,
  homestoryIneligibilityReasonSelector: homestoryIneligibilityReasonSelector,
  homestoryErrorSelector: homestoryErrorSelector,
  isHomestoryRequestSuccessfulSelector: isHomestoryRequestSuccessfulSelector,
  shouldEvaluateHomestoryEligibilitySelector: shouldEvaluateHomestoryEligibilitySelector
});

var verificationsSelector = createSelector([loanFileSelector], function (loanFile) {
  var _loanFile$verificatio;

  return (_loanFile$verificatio = loanFile === null || loanFile === void 0 ? void 0 : loanFile.verifications) !== null && _loanFile$verificatio !== void 0 ? _loanFile$verificatio : {};
});
var borrowerVerificationsSelector = createSelector([verificationsSelector], function (verifications) {
  var _verifications$borrow;

  return (_verifications$borrow = verifications.borrowerVerifications) !== null && _verifications$borrow !== void 0 ? _verifications$borrow : {};
});
var borrowerReportsSelector = createSelector([verificationsSelector], function (verifications) {
  var _verifications$borrow2;

  return (_verifications$borrow2 = verifications.borrowerReports) !== null && _verifications$borrow2 !== void 0 ? _verifications$borrow2 : {};
});
var verificationIDSelector = createSelector([borrowerVerificationsSelector], function (verifications) {
  var _verifications$verifi;

  return (_verifications$verifi = verifications === null || verifications === void 0 ? void 0 : verifications.verificationID) !== null && _verifications$verifi !== void 0 ? _verifications$verifi : null;
});
var reportsIsSuccessfulSelector = createSelector([borrowerReportsSelector], function (borrowerReports) {
  return borrowerReports.isSuccessful;
});
var reportsIsLoadingSelector = createSelector([borrowerReportsSelector], function (borrowerReports) {
  return borrowerReports.isLoading;
});
var reportsSelector = createSelector([borrowerReportsSelector], function (borrowerReports) {
  var _borrowerReports$repo;

  return (_borrowerReports$repo = borrowerReports === null || borrowerReports === void 0 ? void 0 : borrowerReports.reports) !== null && _borrowerReports$repo !== void 0 ? _borrowerReports$repo : {};
});
var verificationIsSuccessfulSelector = createSelector([borrowerVerificationsSelector], function (verifications) {
  return verifications.isSuccessful;
});
var verificationLinkSelector = createSelector([borrowerVerificationsSelector], function (verifications) {
  var _verifications$verifi2;

  return (_verifications$verifi2 = verifications === null || verifications === void 0 ? void 0 : verifications.verificationLink) !== null && _verifications$verifi2 !== void 0 ? _verifications$verifi2 : null;
});
var verificationErrorsSelector = createSelector([borrowerVerificationsSelector], function (verifications) {
  var _verifications$errors;

  return (_verifications$errors = verifications === null || verifications === void 0 ? void 0 : verifications.errors) !== null && _verifications$errors !== void 0 ? _verifications$errors : null;
});
var verificationLoadingSelector = createSelector([borrowerVerificationsSelector], function (verifications) {
  return verifications.isLoading;
});
var refreshByBorrowerSelector = createSelector([verificationsSelector], function (verifications) {
  var _verifications$refres;

  return (_verifications$refres = verifications === null || verifications === void 0 ? void 0 : verifications.refreshByBorrower) !== null && _verifications$refres !== void 0 ? _verifications$refres : {};
});
var refreshByBorrowerIDPropSelector = createSelector([refreshByBorrowerSelector, borrowerIDPropSelector], function (refreshByBorrower, borrowerID) {
  var _refreshByBorrower$bo;

  return (_refreshByBorrower$bo = refreshByBorrower === null || refreshByBorrower === void 0 ? void 0 : refreshByBorrower[borrowerID]) !== null && _refreshByBorrower$bo !== void 0 ? _refreshByBorrower$bo : {};
});
var reportsAreRefreshingSelector = createSelector([refreshByBorrowerSelector], function (refreshByBorrower) {
  return !!Object.values(refreshByBorrower).some(function (_ref) {
    var isRefreshing = _ref.isRefreshing;
    return isRefreshing;
  });
});
var refreshAPISuccessfulSelector = createSelector([refreshByBorrowerSelector], function (refreshByBorrower) {
  var statusByBorrower = Object.values(refreshByBorrower);
  return statusByBorrower.length > 0 && statusByBorrower.every(function (_ref2) {
    var isSuccessful = _ref2.isSuccessful;
    return isSuccessful;
  });
});
var refreshAPICompletelyFailedSelector = createSelector([refreshByBorrowerSelector], function (refreshByBorrower) {
  return Object.values(refreshByBorrower).every(function (_ref3) {
    var reportRefreshStatuses = _ref3.reportRefreshStatuses;
    return Object.values(reportRefreshStatuses).every(function (_ref4) {
      var hasError = _ref4.hasError;
      return hasError;
    });
  }) && Object.values(refreshByBorrower).some(function (_ref5) {
    var reportRefreshStatuses = _ref5.reportRefreshStatuses;
    return !!Object.values(reportRefreshStatuses).length;
  });
});
var verificationHadNetworkErrorSelector = createSelector([verificationsSelector], function (verifications) {
  return !!verifications.hadNetworkError;
});
var individualReportRefreshHasErrorsSelector = createSelector([refreshByBorrowerSelector], function (refreshByBorrower) {
  return Object.values(refreshByBorrower).some(function (_ref6) {
    var reportRefreshStatuses = _ref6.reportRefreshStatuses;
    return Object.values(reportRefreshStatuses).some(function (_ref7) {
      var hasError = _ref7.hasError;
      return hasError;
    });
  });
});

var verificationsBase = /*#__PURE__*/Object.freeze({
  __proto__: null,
  verificationsSelector: verificationsSelector,
  borrowerVerificationsSelector: borrowerVerificationsSelector,
  borrowerReportsSelector: borrowerReportsSelector,
  verificationIDSelector: verificationIDSelector,
  reportsIsSuccessfulSelector: reportsIsSuccessfulSelector,
  reportsIsLoadingSelector: reportsIsLoadingSelector,
  reportsSelector: reportsSelector,
  verificationIsSuccessfulSelector: verificationIsSuccessfulSelector,
  verificationLinkSelector: verificationLinkSelector,
  verificationErrorsSelector: verificationErrorsSelector,
  verificationLoadingSelector: verificationLoadingSelector,
  refreshByBorrowerSelector: refreshByBorrowerSelector,
  refreshByBorrowerIDPropSelector: refreshByBorrowerIDPropSelector,
  reportsAreRefreshingSelector: reportsAreRefreshingSelector,
  refreshAPISuccessfulSelector: refreshAPISuccessfulSelector,
  refreshAPICompletelyFailedSelector: refreshAPICompletelyFailedSelector,
  verificationHadNetworkErrorSelector: verificationHadNetworkErrorSelector,
  individualReportRefreshHasErrorsSelector: individualReportRefreshHasErrorsSelector
});

var credit$2 = Object.assign({}, creditBase, borrowersCredit, compositeCredit);
var detail = Object.assign({}, detailBase, borrowersDetail);
var pricing = Object.assign({}, pricingBase, compositePricing);
var ownedRealEstates$2 = Object.assign({}, ownedRealEstatesBase, borrowersOwnedRealEstates);
var liabilities$2 = Object.assign({}, liabilitiesBase, borrowersLiabilities);
var verifications = Object.assign({}, verificationsBase, compositeVerifications);

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  addresses: addresses,
  agent: agent,
  assets: assets,
  assetsAndGifts: assetsAndGifts,
  borrowers: borrowers,
  borrowersDemographics: borrowersDemographics,
  borrowerPairs: borrowerPairs,
  computed: computed$1,
  credit: credit$2,
  declarations: declarations,
  detail: detail,
  employments: employments,
  employmentsAndIncome: employmentsAndIncome,
  gifts: gifts,
  income: income,
  liabilities: liabilities$2,
  liabilitiesAndIncome: liabilitiesAndIncome,
  loanActions: loanActions,
  loanAttributes: loanAttributes,
  loanFile: loanFile,
  loanTitle: loanTitle,
  otherNewMortgages: otherNewMortgages,
  ownedRealEstates: ownedRealEstates$2,
  notes: notes,
  personalInfo: personalInfo,
  preApprovals: preApprovals,
  pricing: pricing,
  propsSelectors: propsSelectors,
  underwriting: underwriting,
  verifications: verifications
});

/* global window */
/* eslint
    no-underscore-dangle: off,
    no-var: off,
    prefer-rest-params: off,
    vars-on-top: off,
*/



var trackEvent = _trackEvent;
var trackPageView = _trackPageView;
var setCustomDimensions = _setCustomDimensions;
var shouldDispatchToNative_1 = shouldDispatchToNative;

function logError() {
    if (typeof window !== 'undefined' && window.console && window.console.error) {
        window.console.error.apply(console, arguments);
    }
}

// eslint-disable-next-line consistent-return
function _getMobNativeApi() {
    if (typeof window !== 'undefined' && window.ZMOB_nativeAPI) {
        return window.ZMOB_nativeAPI;
    }
}

var customDimensions = {};
function _setCustomDimensions(incomingCustomDimensions) {
    var coercedCustomDimensions = {};
    if (incomingCustomDimensions) {
        var keys = Object.keys(incomingCustomDimensions);
        // eslint-disable-next-line prefer-arrow-callback
        keys.forEach(function coerceDimension(element) {
            // remove dimension from the key
            // the iOS/Android client does not expect the leading qualifier in the string.
            // gross.
            var key = element.replace('dimension', '');
            coercedCustomDimensions[key] = incomingCustomDimensions[element];
        });
    }
    customDimensions = assign(customDimensions, coercedCustomDimensions);
}

function _getCustomDimensions(incomingCustomDimensions) {
    var currentCustomDimensions = customDimensions;
    if (incomingCustomDimensions) {
        currentCustomDimensions = assign(currentCustomDimensions, incomingCustomDimensions);
    }
    var keys = Object.keys(currentCustomDimensions);
    var slashStrippedCustomDimensions = {};
    // eslint-disable-next-line prefer-arrow-callback
    keys.forEach(function coerceDimension(element) {
        var key = _stripSlashes(element);
        var value = _stripSlashes(currentCustomDimensions[element]);
        slashStrippedCustomDimensions[key] = value;
    });
    return slashStrippedCustomDimensions;
}

/**
 * Overrides the call to GA with a
 * call to the native iOS and android
 * tracking method
 *
 * @method trackEvent
 * @private
 * @param {object} config
 */
function _trackEvent(config) {
    var category = _stripSlashes(config.category);
    var action = _stripSlashes(config.action);
    var label = _stripSlashes(config.label);
    var value = parseInt(config.value, 10);
    var called;

    var mobNativeAPI = _getMobNativeApi();
    if (mobNativeAPI) {
        if (mobNativeAPI.trackUAWebviewEvent) {
            var nativeCustomDimensions = _getCustomDimensions(config.customDimensions);
            mobNativeAPI.trackUAWebviewEvent(
                category,
                action,
                label,
                value,
                JSON.stringify(nativeCustomDimensions)
            );
            called = true;
        } else if (mobNativeAPI.trackWebviewEvent) {
            mobNativeAPI.trackWebviewEvent(category, action, label, value);
            called = true;
        }
    }

    if (typeof config.hitCallback === 'function') {
        config.hitCallback();
    }

    if (!called) {
        logError('There was no native method for trackEvent');
    }
}

function _trackPageView(config) {
    var page = config.pageURL;
    var called;

    var mobNativeAPI = _getMobNativeApi();
    if (mobNativeAPI) {
        var nativeCustomDimensions = _getCustomDimensions(config.customDimensions);
        if (mobNativeAPI.trackUAPageview) {
            mobNativeAPI.trackUAPageview(page, JSON.stringify(nativeCustomDimensions));
            called = true;
        } else if (mobNativeAPI.trackWebviewPageView) {
            mobNativeAPI.trackWebviewPageView(page);
            called = true;
        }
    }

    if (typeof config.hitCallback === 'function') {
        config.hitCallback();
    }

    if (!called) {
        logError('There was no native method for trackPageView');
    }
}

/**
Removes '/' from analytics params (action, category, label).
Native analytics api uses '/' as a property delimeter. This ensures that arguments
passed to the native analytics api don't get borked due to the presence of a '/'.
See: HDP-5046
@method _stripSlashes
@param {String} field
@return field if it is a string.
@private
*/
function _stripSlashes(field) {
    if (typeof field !== 'string') {
        return undefined;
    }

    return field.replace(/\//g, '-');
}

/**
 * Determines if we should dispatch calls to the native api
 *
 * @method shouldDispatchToNative
 * @private
 */
function shouldDispatchToNative() {
    return !!_getMobNativeApi();
}

var nativeApi = {
	trackEvent: trackEvent,
	trackPageView: trackPageView,
	setCustomDimensions: setCustomDimensions,
	shouldDispatchToNative: shouldDispatchToNative_1
};

var trackEvent_1 = trackEvent$1;
var gaCommand_1 = gaCommand;
var _globalName = 'ga';

// Max retry count and timeout for ZA function calls
var ZA_RETRY_MAX_COUNT = 2;
var ZA_RETRY_AFTER_MS = 500;

function logInfo() {
    if (typeof window !== 'undefined' && window.console && window.console.info) {
        window.console.info.apply(console, arguments);
    }
}

function logWarn() {
    if (typeof window !== 'undefined' && window.console && window.console.warn) {
        window.console.warn.apply(console, arguments);
    }
}

/**
 * Check if href is a tel link
 *
 * @method _isTelLink
 * @private
 */
function _isTelLink(href) {
    return /^tel:/i.test(href);
}

/**
 * Get the global Google Analytics object. This is generally window.ga,
 * but the global may be renamed on domains other than zillow.com. For
 * that reason, it is important to only call UA functions through the
 * object returned by this function.
 *
 * @method getGA
 * @return {Object} The GA global object.
 */
function getGA() {
    return typeof window !== 'undefined' && window[_globalName];
}

/**
 * Test to see if Google Universal Analytics is initialized.
 *
 * @method isInitialized
 */
function isInitialized() {
    return isFunction(getGA());
}

/**
 * Guarded window.open redirect callback generator.
 *
 * @method _getRedirect
 * @param {Object} config
 * @param {String} config.href Redirect to URL after event fires
 * @param {String} config.target The URL target
 */
function _getRedirect(config) {
    if (typeof window !== 'undefined' && window.open) {
        return window.open.bind(window, config.href, config.target);
    }

    return logInfo.bind(null, 'ga redirect window.open("%s", "%s")', config.href, config.target);
}

/**
 * Extract the metrics and dimensions from the object provided to the track API
 * and inject the corresponding entries into the UA options provided.
 * @method _insertDimensionsAndMetrics
 * @private
 * @param {Object} googleOptions The options being prepared for a call to ga('send', ...)
 * @param {Object} trackingParams The options provided to the track...
 * API optionally containing dimensions/metrics objects
 */
function _insertDimensionsAndMetrics(googleOptions, trackingParams) {
    _insertEntries(googleOptions, 'dimension', trackingParams.dimensions);
    _insertEntries(googleOptions, 'metric', trackingParams.metrics);
}

/**
 * Add the values in the entries object to the UA options provided.
 * @method _insertEntries
 * @private
 * @param {Object} googleOptions The options being prepared for a call to ga('send', ...)
 * @param {String} prefix The type of option being added - 'dimension' | 'metric'
 * @param {Object} entries Mapping of int index to value for the dimension/metric to be set
 */
function _insertEntries(googleOptions, prefix, entries) {
    if (entries) {
        assign(googleOptions, Object.keys(entries).reduce(reducer, {}));
    }

    function reducer(acc, key) {
        // Convert key to a number (strings that aren't numbers result in NaN)
        var index = Number(key);

        // ensure index is a positive integer
        if (index > 0 && index === Math.floor(index)) {
            acc[prefix + index] = entries[key];
        } else {
            logWarn('Could not set UA option ' + prefix + key);
        }

        return acc;
    }
}

/**
 * Retry a call to ZillowAnalytics object after a small wait
 * if ZillowAnalytics is not initialized when function is initially called.
 *
 * @param {function} fn
 * @param {number} maxRetryCount
 * @returns {void}
 */
function _zaWithRetry(fn, maxRetryCount) {
    if (typeof fn !== 'function') {
        logWarn('zaWithRetry was called without a function to retry.');
        return;
    }

    if (isZillowAnalyticsInitialized()) {
        fn();
    } else if (maxRetryCount > 0) {
        var retry = function zRetry() {
            _zaWithRetry(fn, maxRetryCount - 1);
        };

        setTimeout(retry, ZA_RETRY_AFTER_MS);
    } else {
        logWarn('ZillowAnalytics is not initialized causing a call to drop.');
    }
}

/**
 * Custom dimension Message UUID is being set for
 * Google Analytics and ZAnalytics. It helps to track potentially
 * lost events for both destinations
 */
var CustDimMessageUuid = 'dimension193';

/**
 * Sets dimension193 to unique id before sending track and page event
 * Original ticket: https://zbrt.atl.zillow.net/browse/DSP-2620
 * Fix ticket: https://zbrt.atl.zillow.net/browse/DSP-3046
 *
 * @param {any} obj an object to merge the custom dimension for unique event id onto
 * @param {string} uniqueEventId the unique event id (default: _uuid())
 * @param {string} objKey the object key to use (default: uses value from var CustDimMessageUuid)
 */
function _addUniqueEventIdDimension(obj, uniqueEventId, objKey) {
    uniqueEventId = uniqueEventId || v4_1();
    objKey = objKey || CustDimMessageUuid;

    var uniqueEventIdDim = {};
    uniqueEventIdDim[objKey] = uniqueEventId;

    obj = _merge2(obj, uniqueEventIdDim);
}

/**
 * Default analytics event tracking.
 * This may be overriden.
 *
 * @method _trackEvent
 * @private
 * @param {Object} config
 */
function _trackEvent$1(config) {
    var googleUAObj = {
        hitType: 'event',
        eventCategory: config.category,
        eventAction: config.action,
        eventLabel: config.label,
        eventValue: config.value,
        nonInteraction: config.noninteraction,
    };

    var uniqueEventId = v4_1();

    if (config.hitCallback) {
        googleUAObj.hitCallback = config.hitCallback;
    }

    if (isInitialized()) {
        _insertDimensionsAndMetrics(googleUAObj, config);
        _addUniqueEventIdDimension(googleUAObj, uniqueEventId);
        getGA()('send', googleUAObj);
    } else {
        logWarn('Would send event to GA but GA not initialized: ', googleUAObj);
    }

    var zaObj = {
        category: config.category,
        action: config.action,
        label: config.label,
        value: config.value,
        nonInteraction: config.noninteraction,
    };
    var zaOpts = {};
    _insertDimensionsAndMetrics(zaObj, config);
    _addUniqueEventIdDimension(zaOpts, uniqueEventId, 'uniqueEventId');

    var zaFn = function zaRetryFn() {
        getZA().track('event', zaObj, zaOpts);
    };
    _zaWithRetry(zaFn, ZA_RETRY_MAX_COUNT);
}

/**
 * Track a Goggle Analytics event
 * (<a href="https://zwiki.zillowgroup.net/display/uxdev/Google+Analytics">read more about the GA tracking</a>).
 *
 * ### Examples
 *
 *     import { trackEvent } from '@zillow/universal-analytics';
 *
 *     // Track an event
 *     trackEvent({
 *         category: 'Mortgages',
 *         action: 'Upsell clicked'
 *         label: 'Search results list'
 *     });
 *
 *     // Track an event and then redirect
 *     trackEvent({
 *         category: 'Mortgages',
 *         action: 'Upsell clicked'
 *         label: 'Search results list',
 *         href: 'http://www.zillow.com/mortgage-rates/'
 *     });
 *
 *     // Track an event and open url in a new window
 *     trackEvent({
 *         category: 'Mortgages',
 *         action: 'Upsell clicked'
 *         label: 'Search results list',
 *         href: 'http://www.zillow.com/mortgage-rates/',
 *         target: '_blank'
 *     });
 *
 * @method trackEvent
 * @param {Object} config
 * @param {String} config.category Event category
 * @param {String} config.action Event action
 * @param {String} [config.label] Event label
 * @param {Number} [config.value] Event value
 * @param {Boolean} [config.noninteraction] Noninteraction event
 * @param {String} [config.href] Redirect to URL after event fires
 * @param {String} [config.target] The URL target, default: '_self'
 */
function trackEvent$1(config) {
    config = config || {};

    // Handle href redirects
    if (config.href) {
        // eslint-disable-next-line no-param-reassign
        config.target = config.target || '_self';

        // Bind href and target to the open function so it can be used
        // with setTimeout.
        var redirect = _getRedirect(config);

        // Not necessary to wait for URLs opened in a new target, and
        // waiting will cause the URL to be opened in a window instead
        // of a tab because it is no longer "user-initiated".
        if (config.target === '_blank' || _isTelLink(config.href)) {
            redirect.call();
        } else if (isInitialized()) {
            // When GA is intitialized, the redirect will occur when the
            // hit callback is called (usually very fast). If for some
            // reason GA fails or is really slow, the timeout will
            // guarantee the redirect occurs in a timely manner.
            var timeout = setTimeout(redirect, 500);

            // eslint-disable-next-line no-param-reassign
            config.hitCallback = function callRedirect() {
                // Make sure to clear timeout as anchor hrefs do not
                // change the page.
                clearTimeout(timeout);
                redirect.call();
            };
        } else {
            // Old school redirect in case GA hasn't been initialized
            setTimeout(redirect, 100);
        }
    }

    // Category and action are required fields
    if (!config.category) {
        logWarn('trackEvent: missing category');
    } else if (!config.action) {
        logWarn('trackEvent: missing action');
    } else if (nativeApi.shouldDispatchToNative()) {
        nativeApi.trackEvent(config);
    } else {
        _trackEvent$1(config);
    }
}

/**
 * Applies any passed arguments to the ga function.
 *
 * @method gaCommand
 */
function gaCommand() {
    if (isInitialized()) {
        getGA().apply(window, [].slice.call(arguments));
    } else {
        logWarn('gaCommand called, but UA was not initialized!');
    }
}

/**
 * Find out if zillow analytics script is initialized
 * Also make sure that window.analytics is not a HTML element
 *
 * @method isZillowAnalyticsInitialized
 */
function isZillowAnalyticsInitialized() {
    return (
        typeof window !== 'undefined' &&
        typeof getZA() !== 'undefined' &&
        typeof getZA().nodeName === 'undefined'
    );
}

/**
 * Get the global Zillow Analytics object. This is generally window.analytics,
 * but the global may be renamed on domains other than zillow.com.
 *
 * @method getZA
 * @return {Object} The Zillow Analytics global object.
 */
function getZA() {
    return typeof window !== 'undefined' && window[window.ZillowAnalyticsObject];
}

var getGAClientID = function getGAClientID() {
  var gaClientID;
  gaCommand_1(function (tracker) {
    gaClientID = tracker.get('clientId');
  });
  return gaClientID;
};

var _excluded$f = ["downPayment", "recentOwnership"];
var DIRECT_LEAD_FORMATTERS = {
  phoneNumber: toPhoneString
};
var YUP_TO_DIRECT_LEAD_MAPPING = {
  'name.first': 'firstName',
  'name.last': 'lastName',
  email: 'emailAddress',
  phone: 'phoneNumber',
  address: 'borrowerAddress',
  state: 'stateAbbreviation',
  propertyType: 'propertyType',
  propertyUse: 'propertyUse',
  purchasePrice: 'propertyValue',
  purchaseDate: 'transactionTimeframe',
  journeyStage: 'journeyStage',
  hasBankruptcy: 'hasBankruptcy',
  eSignatureConsent: 'eSignatureConsent',
  tcpaConsent: 'tcpaConsent'
};
var DIRECT_LEAD_TO_YUP_MAPPING = Object.entries(YUP_TO_DIRECT_LEAD_MAPPING).reduce(function (acc, _ref) {
  var _Object$assign;

  var _ref2 = _slicedToArray(_ref, 2),
      key = _ref2[0],
      name = _ref2[1];

  return Object.assign({}, acc, (_Object$assign = {}, _Object$assign[name] = key, _Object$assign));
}, {}); // used to convert API server side validation errors named by the schema the server expects back into errors expected by
// the schema defined in Yup

var toYupSchema = function toYupSchema(serverErrors) {
  return Object.entries(serverErrors).reduce(function (acc, _ref3) {
    var _ref4 = _slicedToArray(_ref3, 2),
        serverName = _ref4[0],
        serverError = _ref4[1];

    var errors = Object.assign({}, acc);
    var field = DIRECT_LEAD_TO_YUP_MAPPING[serverName]; // explicitly checking if field is defined because we don't want to include a key with value undefined if
    // the server returns an error that does not match to a Yup schema field

    if (field) {
      errors[field] = serverError;
    }

    return errors;
  }, {});
};
var toDirectLead = function toDirectLead(_ref5, offrampReason, _ref6) {
  var downPayment = _ref5.downPayment,
      recentOwnership = _ref5.recentOwnership,
      formData = _objectWithoutProperties(_ref5, _excluded$f);

  var source = _ref6.source,
      channel = _ref6.channel;
  var directLeadData = Object.entries(YUP_TO_DIRECT_LEAD_MAPPING).reduce(function (acc, _ref7) {
    var _Object$assign2;

    var _ref8 = _slicedToArray(_ref7, 2),
        yupName = _ref8[0],
        directLeadName = _ref8[1];

    var formatter = DIRECT_LEAD_FORMATTERS[directLeadName];
    var formValue = yupName.includes('.') ? yupName.split('.').reduce(function (iteratedFormData, currentFieldSegment) {
      return iteratedFormData === null || iteratedFormData === void 0 ? void 0 : iteratedFormData[currentFieldSegment];
    }, formData) : formData[yupName];
    return Object.assign({}, acc, (_Object$assign2 = {}, _Object$assign2[directLeadName] = formatter ? formatter(formValue) : formValue, _Object$assign2));
  }, {});
  return Object.assign({}, directLeadData, {
    gaClientID: getGAClientID(),
    isPrequalificationLead: true,
    channel: channel,
    source: source,
    offrampReason: offrampReason,
    // this is not handled by the dynamic mapping because it's unlikely we would need to reverse this mapping and
    // it is not trivial to support this mapping reversal
    details: {
      purchase: {
        downPayment: downPayment,
        firstTimeBuyer: !recentOwnership
      }
    }
  });
};

var COUNTRIES_LOOKUP$1 = constants.COUNTRIES_LOOKUP,
    _constants$leads = constants.leads,
    OFF_RAMP_REASON_LOOKUP = _constants$leads.OFF_RAMP_REASON_LOOKUP,
    PROPERTY_USES_LOOKUP = _constants$leads.PROPERTY_USES_LOOKUP,
    PROPERTY_TYPES_LOOKUP$2 = _constants$leads.PROPERTY_TYPES_LOOKUP,
    UNDERWRITING_RECOMMENDATION_LOOKUP = constants.loanFile.UNDERWRITING_RECOMMENDATION_LOOKUP;
var incomeSchema = preQualification.incomeSchema,
    assetsSchema = preQualification.assetsSchema;
var preQualifySelector = function preQualifySelector(_ref) {
  var preQualify = _ref.preQualify;
  return preQualify;
};
var preQualifyFieldsSelector = createSelector([preQualifySelector], function (_ref2) {
  var fields = _ref2.fields;
  return fields;
});

var fieldsForSchemaSelector = function fieldsForSchemaSelector(schema) {
  return createSelector([preQualifyFieldsSelector], function (fields) {
    return pick(fields, Object.keys(schema.fields));
  });
};

var preQualifyIncomesSelector = fieldsForSchemaSelector(incomeSchema);
var preQualifyAssetsSelector = fieldsForSchemaSelector(assetsSchema);

var itemsByIDSelector = function itemsByIDSelector(key) {
  return byID(createSelector([preQualifySelector], function (preQualify) {
    var _preQualify$key$items, _preQualify$key;

    return (_preQualify$key$items = (_preQualify$key = preQualify[key]) === null || _preQualify$key === void 0 ? void 0 : _preQualify$key.items) !== null && _preQualify$key$items !== void 0 ? _preQualify$key$items : [];
  }));
};

var employmentsByIDSelector = itemsByIDSelector('employments');
var incomesByIDSelector = itemsByIDSelector('income');
var assetsByIDSelector = itemsByIDSelector('assets');
var createdEmploymentsAndIncomesSelector = createSelector([preQualifySelector], function (_ref3) {
  var createdEmploymentsAndIncomes = _ref3.createdEmploymentsAndIncomes;
  return createdEmploymentsAndIncomes;
});
var employmentsAndIncomesSelector = createSelector([createdEmploymentsAndIncomesSelector, employmentsByIDSelector, incomesByIDSelector], function (createdEmploymentsAndIncomes, employmentsByID, incomesByID) {
  return mapValues(createdEmploymentsAndIncomes, function (id) {
    var _employmentsByID$id;

    return id ? (_employmentsByID$id = employmentsByID[id]) !== null && _employmentsByID$id !== void 0 ? _employmentsByID$id : incomesByID[id] : null;
  });
});
var createdAssetsSelector = createSelector([preQualifySelector], function (_ref4) {
  var createdAssets = _ref4.createdAssets;
  return createdAssets;
});
var assetsSelector$1 = createSelector([createdAssetsSelector, assetsByIDSelector], function (createdAssets, assetsByID) {
  return mapValues(createdAssets, function (id) {
    return id ? assetsByID[id] : null;
  });
});
var preQualifyIncomesAndAssetsSelector = createSelector([preQualifyIncomesSelector, preQualifyAssetsSelector], function (incomes, assets) {
  return Object.assign({}, incomes, assets);
});
var preQualifyHasAPIErrorsSelector = createSelector([preQualifySelector], function (_ref5) {
  var hasAPIErrors = _ref5.hasAPIErrors;
  return hasAPIErrors;
});

var shouldOffRampDueToPropertyUse = function shouldOffRampDueToPropertyUse(_ref6) {
  var propertyUse = _ref6.propertyUse;
  return propertyUse === PROPERTY_USES_LOOKUP.SECONDARY || propertyUse === PROPERTY_USES_LOOKUP.INVESTMENT;
};

var shouldOffRampDueToPropertyType = function shouldOffRampDueToPropertyType(_ref7) {
  var propertyType = _ref7.propertyType;
  return propertyType === PROPERTY_TYPES_LOOKUP$2.MOBILE_OR_MANUFACTURED;
}; // us citizen will initially be undefined, so we should explicitly check for false


var shouldOffRampDueToCitizenship = function shouldOffRampDueToCitizenship(_ref8) {
  var usCitizen = _ref8.usCitizen;
  return usCitizen === false;
};

var shouldOffRampDueToBankruptcy = function shouldOffRampDueToBankruptcy(_ref9) {
  var hasBankruptcy = _ref9.hasBankruptcy;
  return hasBankruptcy;
};

var shouldOffRampDueToDownPayment = function shouldOffRampDueToDownPayment(_ref10) {
  var downPaymentPercent = _ref10.downPaymentPercent;
  return downPaymentPercent < 3;
};

var shouldOffRampDueToAPIErrors = function shouldOffRampDueToAPIErrors(_ref11) {
  var hasAPIErrors = _ref11.hasAPIErrors;
  return hasAPIErrors === true;
};

var ESIGNATURE_DOCUMENT_URL_SETTINGS_KEY = 'zdocs_urls.esignature_consent';
var eSignatureConsentDocumentURLSelector = createSettingsValueSelector(ESIGNATURE_DOCUMENT_URL_SETTINGS_KEY);
var isESignatureConsentDocumentURLUnavailableSelector = createSelector([settingsFetchValueErrorsSelector], function (fetchValueErrors) {
  return !!fetchValueErrors[ESIGNATURE_DOCUMENT_URL_SETTINGS_KEY];
});
var offRampReasonSelector = createSelector([isESignatureConsentDocumentURLUnavailableSelector, preQualifyFieldsSelector, preQualifyHasAPIErrorsSelector], function (isESignatureConsentDocumentURLUnavailable, preQualifyFields, hasAPIErrors) {
  if (isESignatureConsentDocumentURLUnavailable) {
    return OFF_RAMP_REASON_LOOKUP.ESIGNATURE_CONSENT_UNAVAILABLE;
  }

  var determineFieldBasedOffRampReason = cond([[shouldOffRampDueToPropertyUse, function () {
    return OFF_RAMP_REASON_LOOKUP.PROPERTY_USE;
  }], [shouldOffRampDueToPropertyType, function () {
    return OFF_RAMP_REASON_LOOKUP.PROPERTY_TYPE;
  }], [shouldOffRampDueToCitizenship, function () {
    return OFF_RAMP_REASON_LOOKUP.CITIZENSHIP;
  }], [shouldOffRampDueToBankruptcy, function () {
    return OFF_RAMP_REASON_LOOKUP.BANKRUPTCY;
  }], [shouldOffRampDueToDownPayment, function () {
    return OFF_RAMP_REASON_LOOKUP.DOWN_PAYMENT;
  }], [shouldOffRampDueToAPIErrors, function () {
    return OFF_RAMP_REASON_LOOKUP.API_ERRORS;
  }], [function () {
    return true;
  }, function () {
    return undefined;
  }]]);
  return determineFieldBasedOffRampReason(Object.assign({
    hasAPIErrors: hasAPIErrors
  }, preQualifyFields));
});
var preQualifyDirectLeadSelector = createSelector([preQualifyFieldsSelector, offRampReasonSelector, queryParamsSelector], toDirectLead);
var preQualifyLoanAttributesSelector = createSelector([preQualifyFieldsSelector], fromPreQualifyFieldsToLoanAttributes);
var preQualifyBorrowerAttributesSelector = createSelector([preQualifyFieldsSelector], fromPreQualifyFieldsToBorrowerAttributes);
var createPreQualifyFieldsSubsetSelector = function createPreQualifyFieldsSubsetSelector(fieldNames) {
  return createSelector([preQualifyFieldsSelector], function (fields) {
    return pick(fields, fieldNames);
  });
};
var preQualifyDefaultFieldsSelector = createSelector([contactInfoSelector, queryParamsSelector], function (contactInfo, queryParams) {
  var _queryParams$property;

  return Object.assign({}, contactInfo, {
    propertyType: queryParams === null || queryParams === void 0 ? void 0 : queryParams.propertyType,
    purchasePrice: (_queryParams$property = queryParams === null || queryParams === void 0 ? void 0 : queryParams.propertyValue) !== null && _queryParams$property !== void 0 ? _queryParams$property : 0,
    downPayment: 0,
    downPaymentPercent: 5,
    address: {
      country: COUNTRIES_LOOKUP$1.US
    }
  });
});
var preQualifyLeadSelector = createSelector([preQualifySelector], function (_ref12) {
  var lead = _ref12.lead;
  return lead;
});
var preQualifyBorrowerIDSelector = createSelector([preQualifyLeadSelector], function (_ref13) {
  var borrowerID = _ref13.borrowerID;
  return borrowerID;
});
var preQualifyLoanIDSelector = createSelector([preQualifyLeadSelector], function (_ref14) {
  var loanID = _ref14.loanID;
  return loanID;
});
var preQualifyLeadIDSelector = createSelector([preQualifyLeadSelector], function (_ref15) {
  var id = _ref15.id;
  return id;
});
var preApprovalURLSelector = createSelector([preQualifyLeadSelector], function (_ref16) {
  var applicationURL = _ref16.applicationURL;
  return applicationURL;
});
var preQualifyUnderwritingSelector = createSelector([preQualifySelector], function (_ref17) {
  var underwriting = _ref17.underwriting;
  return underwriting;
});
var preQualifyUnderwritingRecommendationSelector = createSelector([preQualifyUnderwritingSelector], function (_ref18) {
  var recommendation = _ref18.recommendation;
  return recommendation;
});
var preQualifyUnderwritingApprovedSelector = createSelector([preQualifyUnderwritingRecommendationSelector], function (recommendation) {
  return recommendation === UNDERWRITING_RECOMMENDATION_LOOKUP.APPROVE_ELIGIBLE;
});
var preQualifyHasSubmittedSelector = createSelector([preQualifySelector], function (_ref19) {
  var hasSubmitted = _ref19.hasSubmitted;
  return hasSubmitted;
});

var preQualify = /*#__PURE__*/Object.freeze({
  __proto__: null,
  preQualifySelector: preQualifySelector,
  preQualifyFieldsSelector: preQualifyFieldsSelector,
  preQualifyIncomesSelector: preQualifyIncomesSelector,
  preQualifyAssetsSelector: preQualifyAssetsSelector,
  employmentsByIDSelector: employmentsByIDSelector,
  incomesByIDSelector: incomesByIDSelector,
  assetsByIDSelector: assetsByIDSelector,
  employmentsAndIncomesSelector: employmentsAndIncomesSelector,
  assetsSelector: assetsSelector$1,
  preQualifyIncomesAndAssetsSelector: preQualifyIncomesAndAssetsSelector,
  preQualifyHasAPIErrorsSelector: preQualifyHasAPIErrorsSelector,
  ESIGNATURE_DOCUMENT_URL_SETTINGS_KEY: ESIGNATURE_DOCUMENT_URL_SETTINGS_KEY,
  eSignatureConsentDocumentURLSelector: eSignatureConsentDocumentURLSelector,
  offRampReasonSelector: offRampReasonSelector,
  preQualifyDirectLeadSelector: preQualifyDirectLeadSelector,
  preQualifyLoanAttributesSelector: preQualifyLoanAttributesSelector,
  preQualifyBorrowerAttributesSelector: preQualifyBorrowerAttributesSelector,
  createPreQualifyFieldsSubsetSelector: createPreQualifyFieldsSubsetSelector,
  preQualifyDefaultFieldsSelector: preQualifyDefaultFieldsSelector,
  preQualifyLeadSelector: preQualifyLeadSelector,
  preQualifyBorrowerIDSelector: preQualifyBorrowerIDSelector,
  preQualifyLoanIDSelector: preQualifyLoanIDSelector,
  preQualifyLeadIDSelector: preQualifyLeadIDSelector,
  preApprovalURLSelector: preApprovalURLSelector,
  preQualifyUnderwritingSelector: preQualifyUnderwritingSelector,
  preQualifyUnderwritingRecommendationSelector: preQualifyUnderwritingRecommendationSelector,
  preQualifyUnderwritingApprovedSelector: preQualifyUnderwritingApprovedSelector,
  preQualifyHasSubmittedSelector: preQualifyHasSubmittedSelector
});

var FETCH_BULK_UPDATE_CONTENT = createActionObject('FETCH_BULK_UPDATE_CONTENT');
var SUBMIT_BULK_UPDATE_FORM = createActionObject('SUBMIT_BULK_UPDATE_FORM');
var FETCH_BULK_UPDATE_JOB_STATUSES = createActionObject('FETCH_BULK_UPDATE_JOB_STATUSES');
var CLEAR_STATUS$1 = 'CLEAR_STATUS';
var SET_SORT = 'SET_SORT';
var SET_JOB_ID = 'SET_JOB_ID';
var clearStatus = function clearStatus() {
  return {
    type: CLEAR_STATUS$1
  };
};
var setJobId = function setJobId(jobId) {
  return {
    type: SET_JOB_ID,
    payload: jobId
  };
};
var setSort = function setSort(columnName, type) {
  return {
    type: SET_SORT,
    payload: {
      columnName: columnName,
      type: type
    }
  };
};
var STATUS$1 = {
  LOADING: 'loading',
  ERROR: 'error',
  SUCCESS: 'success'
};
var fetchBulkUpdateContent = function fetchBulkUpdateContent() {
  return API['bulk-update'].GET.thunk({
    action: 'v1/bulk-update',
    authenticated: true,
    handleLogin: false
  }, FETCH_BULK_UPDATE_CONTENT);
};
var fetchBulkUpdateJobStatuses = function fetchBulkUpdateJobStatuses() {
  return API['bulk-update'].GET.thunk({
    action: 'v1/bulk-update/jobs',
    authenticated: true,
    handleLogin: false
  }, FETCH_BULK_UPDATE_JOB_STATUSES);
};
var submitBulkUpdateForm = function submitBulkUpdateForm(file, jobTypeId) {
  var formData = new FormData();
  formData.append('fileUpload', file);
  return API['bulk-update'].POST.thunk({
    action: "v1/bulk-update/jobs?jobTypeId=" + jobTypeId,
    authenticated: true,
    handleLogin: false,
    input: formData
  }, SUBMIT_BULK_UPDATE_FORM);
};
var DEFAULT_STATE$9 = {
  supportedJobTypes: [],
  status: {},
  jobStatuses: {},
  sort: {
    columnName: 'completedTimestamp',
    type: SORT_TYPE.DESCENDING
  },
  jobId: null
};
var bulkUpdate = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$9;
  }

  var type = _ref.type,
      payload = _ref.payload;

  switch (type) {
    case FETCH_BULK_UPDATE_CONTENT.BEGIN:
      return Object.assign({}, state, {
        status: {
          type: STATUS$1.LOADING
        }
      });

    case FETCH_BULK_UPDATE_CONTENT.SUCCESS:
      {
        var _payload$jobTypes;

        var isValidPayload = (payload === null || payload === void 0 ? void 0 : (_payload$jobTypes = payload.jobTypes) === null || _payload$jobTypes === void 0 ? void 0 : _payload$jobTypes.length) > 0;

        if (!isValidPayload) {
          return Object.assign({}, state, {
            status: {
              type: STATUS$1.ERROR,
              action: FETCH_BULK_UPDATE_CONTENT.FAILURE
            }
          });
        }

        return Object.assign({}, state, {
          supportedJobTypes: payload.jobTypes,
          status: {}
        });
      }

    case FETCH_BULK_UPDATE_CONTENT.FAILURE:
      return Object.assign({}, state, {
        status: {
          type: STATUS$1.ERROR,
          action: FETCH_BULK_UPDATE_CONTENT.FAILURE
        }
      });

    case SUBMIT_BULK_UPDATE_FORM.BEGIN:
      return Object.assign({}, state, {
        status: {
          type: STATUS$1.LOADING
        }
      });

    case SUBMIT_BULK_UPDATE_FORM.SUCCESS:
      {
        var _isValidPayload = payload === null || payload === void 0 ? void 0 : payload.jobId;

        if (!_isValidPayload) {
          return Object.assign({}, state, {
            status: {
              type: STATUS$1.ERROR,
              message: payload.message
            }
          });
        }

        return Object.assign({}, state, {
          status: {
            type: STATUS$1.SUCCESS,
            action: SUBMIT_BULK_UPDATE_FORM.SUCCESS
          }
        });
      }

    case SUBMIT_BULK_UPDATE_FORM.FAILURE:
      return Object.assign({}, state, {
        status: {
          type: STATUS$1.ERROR,
          action: SUBMIT_BULK_UPDATE_FORM.FAILURE,
          message: payload === null || payload === void 0 ? void 0 : payload.message
        }
      });

    case FETCH_BULK_UPDATE_JOB_STATUSES.BEGIN:
      return Object.assign({}, state, {
        status: {
          type: STATUS$1.LOADING
        }
      });

    case FETCH_BULK_UPDATE_JOB_STATUSES.SUCCESS:
      {
        if (!(payload !== null && payload !== void 0 && payload.jobStatuses)) {
          return Object.assign({}, state, {
            status: {
              type: STATUS$1.ERROR,
              action: FETCH_BULK_UPDATE_JOB_STATUSES.FAILURE,
              message: payload === null || payload === void 0 ? void 0 : payload.message
            }
          });
        }

        return Object.assign({}, state, {
          jobStatuses: payload === null || payload === void 0 ? void 0 : payload.jobStatuses,
          status: {
            type: STATUS$1.SUCCESS
          }
        });
      }

    case FETCH_BULK_UPDATE_JOB_STATUSES.FAILURE:
      return Object.assign({}, state, {
        status: {
          type: STATUS$1.ERROR,
          action: FETCH_BULK_UPDATE_JOB_STATUSES.FAILURE
        }
      });

    case CLEAR_STATUS$1:
      return Object.assign({}, state, {
        status: {}
      });

    case SET_SORT:
      return Object.assign({}, state, {
        sort: payload
      });

    case SET_JOB_ID:
      return Object.assign({}, state, {
        jobId: payload
      });

    default:
      return state;
  }
});

var _bulkUpdate = /*#__PURE__*/Object.freeze({
  __proto__: null,
  FETCH_BULK_UPDATE_CONTENT: FETCH_BULK_UPDATE_CONTENT,
  SUBMIT_BULK_UPDATE_FORM: SUBMIT_BULK_UPDATE_FORM,
  FETCH_BULK_UPDATE_JOB_STATUSES: FETCH_BULK_UPDATE_JOB_STATUSES,
  CLEAR_STATUS: CLEAR_STATUS$1,
  SET_SORT: SET_SORT,
  SET_JOB_ID: SET_JOB_ID,
  clearStatus: clearStatus,
  setJobId: setJobId,
  setSort: setSort,
  STATUS: STATUS$1,
  fetchBulkUpdateContent: fetchBulkUpdateContent,
  fetchBulkUpdateJobStatuses: fetchBulkUpdateJobStatuses,
  submitBulkUpdateForm: submitBulkUpdateForm,
  DEFAULT_STATE: DEFAULT_STATE$9,
  'default': bulkUpdate
});

var mapToArrayWithId = function mapToArrayWithId(data, idPropertyName) {
  return data && Object.keys(data).map(function (key) {
    var _Object$assign;

    return Object.assign((_Object$assign = {}, _Object$assign[idPropertyName] = key, _Object$assign), data[key]);
  });
};

var _BULK_UPDATE_ROUTES_P, _BULK_UPDATE_STATUS_M;
var ROUTE_MAP$1 = {
  BULK_UPDATE: 'BULK_UPDATE',
  BULK_JOBS: 'BULK_JOBS'
};
var BULK_UPDATE_ROUTES_PERMISSION = (_BULK_UPDATE_ROUTES_P = {}, _BULK_UPDATE_ROUTES_P[ROUTE_MAP$1.BULK_UPDATE] = ['zhl-processor', 'zhl-processor-manager'], _BULK_UPDATE_ROUTES_P[ROUTE_MAP$1.BULK_JOBS] = ['zhl-processor', 'zhl-processor-manager'], _BULK_UPDATE_ROUTES_P);
var BULK_UPDATE_STATUS_MESSAGES = (_BULK_UPDATE_STATUS_M = {}, _BULK_UPDATE_STATUS_M[FETCH_BULK_UPDATE_CONTENT.FAILURE] = CONNECTION_ERROR_MESSAGE, _BULK_UPDATE_STATUS_M[SUBMIT_BULK_UPDATE_FORM.SUCCESS] = 'Successfully updated loan fields', _BULK_UPDATE_STATUS_M[SUBMIT_BULK_UPDATE_FORM.FAILURE] = 'Failed to update loan fields', _BULK_UPDATE_STATUS_M);
var bulkUpdateSelector = function bulkUpdateSelector(_ref) {
  var processorPortal = _ref.processorPortal;
  return processorPortal || null;
};
var bulkUpdateRoutesSelector = getAccessibleRoutesSelector(BULK_UPDATE_ROUTES_PERMISSION);
var bulkUpdateSupportedJobTypesSelector = createSelector([bulkUpdateSelector], function (processorPortal) {
  var _processorPortal$bulk, _processorPortal$bulk2;

  return processorPortal === null || processorPortal === void 0 ? void 0 : (_processorPortal$bulk = processorPortal.bulkUpdate) === null || _processorPortal$bulk === void 0 ? void 0 : (_processorPortal$bulk2 = _processorPortal$bulk.supportedJobTypes) === null || _processorPortal$bulk2 === void 0 ? void 0 : _processorPortal$bulk2.map(function (_ref2) {
    var value = _ref2.value,
        text = _ref2.text;
    return {
      key: text,
      value: value
    };
  });
});
var bulkUpdateStatusSelector = createSelector([bulkUpdateSelector], function (processorPortal) {
  var _processorPortal$bulk3, _processorPortal$bulk4, _processorPortal$bulk5, _processorPortal$bulk6, _processorPortal$bulk7, _processorPortal$bulk8, _processorPortal$bulk9;

  var message = BULK_UPDATE_STATUS_MESSAGES[processorPortal === null || processorPortal === void 0 ? void 0 : (_processorPortal$bulk3 = processorPortal.bulkUpdate) === null || _processorPortal$bulk3 === void 0 ? void 0 : (_processorPortal$bulk4 = _processorPortal$bulk3.status) === null || _processorPortal$bulk4 === void 0 ? void 0 : _processorPortal$bulk4.action];
  return processorPortal !== null && processorPortal !== void 0 && (_processorPortal$bulk5 = processorPortal.bulkUpdate) !== null && _processorPortal$bulk5 !== void 0 && _processorPortal$bulk5.status ? {
    type: processorPortal === null || processorPortal === void 0 ? void 0 : (_processorPortal$bulk6 = processorPortal.bulkUpdate) === null || _processorPortal$bulk6 === void 0 ? void 0 : (_processorPortal$bulk7 = _processorPortal$bulk6.status) === null || _processorPortal$bulk7 === void 0 ? void 0 : _processorPortal$bulk7.type,
    message: (processorPortal === null || processorPortal === void 0 ? void 0 : (_processorPortal$bulk8 = processorPortal.bulkUpdate) === null || _processorPortal$bulk8 === void 0 ? void 0 : (_processorPortal$bulk9 = _processorPortal$bulk8.status) === null || _processorPortal$bulk9 === void 0 ? void 0 : _processorPortal$bulk9.message) || message
  } : {};
});

var convertDateFieldToLocaleString = function convertDateFieldToLocaleString(obj, fields) {
  var newObj = Object.assign({}, obj);
  fields && fields.forEach(function (field) {
    if (newObj.hasOwnProperty(field)) {
      newObj[field] = newObj[field] ? new Date(obj[field]).toLocaleString() : 'N/A';
    }
  });
  return newObj;
};

var JOB_STATUS_DATE_FIELDS = ['createdTimestamp', 'completedTimestamp'];

var getJobStatuses = function getJobStatuses(jobStatuses) {
  if (jobStatuses && Object.keys(jobStatuses).length > 0) {
    var statuses = mapToArrayWithId(jobStatuses, 'jobId');
    return statuses.map(function (jobStatus) {
      return convertDateFieldToLocaleString(jobStatus, JOB_STATUS_DATE_FIELDS);
    });
  }

  return null;
};

var isAscending = function isAscending(type) {
  return type === SORT_TYPE.ASCENDING;
};

var simpleSortComparator = function simpleSortComparator(a, b, type) {
  return isAscending(type) ? a - b : b - a;
};

var dateSortComparator = function dateSortComparator(a, b, type) {
  return simpleSortComparator(new Date(a), new Date(b), type);
};

var JOB_STATUS_SORT_MAP = {
  createdTimestamp: dateSortComparator,
  completedTimestamp: dateSortComparator
};

var sortJobStatuses = function sortJobStatuses(jobStatuses, sort) {
  if (sort !== null && sort !== void 0 && sort.columnName && sort !== null && sort !== void 0 && sort.type) {
    var sortedJobStatuses = _toConsumableArray(jobStatuses);

    var columnName = sort.columnName;
    var sortFunction = JOB_STATUS_SORT_MAP[columnName] || simpleSortComparator;
    sortedJobStatuses.sort(function (a, b) {
      return sortFunction(a[columnName], b[columnName], sort.type);
    });
    return sortedJobStatuses;
  }

  return jobStatuses;
};

var bulkUpdateJobStatusesSelector = createSelector([bulkUpdateSelector], function (processorPortal) {
  var _processorPortal$bulk10, _processorPortal$bulk11;

  var sort = processorPortal === null || processorPortal === void 0 ? void 0 : (_processorPortal$bulk10 = processorPortal.bulkUpdate) === null || _processorPortal$bulk10 === void 0 ? void 0 : _processorPortal$bulk10.sort;
  var jobStatuses = getJobStatuses(processorPortal === null || processorPortal === void 0 ? void 0 : (_processorPortal$bulk11 = processorPortal.bulkUpdate) === null || _processorPortal$bulk11 === void 0 ? void 0 : _processorPortal$bulk11.jobStatuses);
  return jobStatuses ? sortJobStatuses(jobStatuses, sort) : [];
});

var getJobStatusById = function getJobStatusById(jobStatuses, jobId) {
  if (jobStatuses && jobId && Object.keys(jobStatuses).length > 0) {
    var jobStatus = jobStatuses[jobId];

    if (jobStatus) {
      return Object.assign({
        jobId: jobId
      }, convertDateFieldToLocaleString(jobStatus, JOB_STATUS_DATE_FIELDS));
    }
  }

  return null;
};

var bulkUpdateJobStatusByIdSelector = createSelector([bulkUpdateSelector], function (processorPortal) {
  var _processorPortal$bulk12, _processorPortal$bulk13;

  var jobId = processorPortal === null || processorPortal === void 0 ? void 0 : (_processorPortal$bulk12 = processorPortal.bulkUpdate) === null || _processorPortal$bulk12 === void 0 ? void 0 : _processorPortal$bulk12.jobId;
  return getJobStatusById(processorPortal === null || processorPortal === void 0 ? void 0 : (_processorPortal$bulk13 = processorPortal.bulkUpdate) === null || _processorPortal$bulk13 === void 0 ? void 0 : _processorPortal$bulk13.jobStatuses, jobId) || {};
});

var bulkUpdate$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  ROUTE_MAP: ROUTE_MAP$1,
  BULK_UPDATE_ROUTES_PERMISSION: BULK_UPDATE_ROUTES_PERMISSION,
  bulkUpdateSelector: bulkUpdateSelector,
  bulkUpdateRoutesSelector: bulkUpdateRoutesSelector,
  bulkUpdateSupportedJobTypesSelector: bulkUpdateSupportedJobTypesSelector,
  bulkUpdateStatusSelector: bulkUpdateStatusSelector,
  bulkUpdateJobStatusesSelector: bulkUpdateJobStatusesSelector,
  bulkUpdateJobStatusByIdSelector: bulkUpdateJobStatusByIdSelector
});

var FETCH_LOAN_CONTENT = createActionObject('FETCH_LOAN_CONTENT');
var SET_LOAN_ID = 'SET_LOAN_ID';
var setLoanId = function setLoanId(loanId) {
  return {
    type: SET_LOAN_ID,
    payload: loanId
  };
};
var fetchAllLoans = function fetchAllLoans() {
  return API.loan.GET.thunk({
    action: 'loans',
    authenticated: true,
    handleLogin: false
  }, FETCH_LOAN_CONTENT);
};
var DEFAULT_STATE$a = {
  loans: {},
  status: {},
  loanId: null
};
var processorPipeline = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$a;
  }

  var type = _ref.type,
      payload = _ref.payload;

  switch (type) {
    case FETCH_LOAN_CONTENT.BEGIN:
      return Object.assign({}, state, {
        status: {
          type: STATUS.LOADING
        }
      });

    case FETCH_LOAN_CONTENT.SUCCESS:
      {
        var _payload$loans;

        if (!(payload !== null && payload !== void 0 && payload.loans)) {
          return Object.assign({}, state, {
            status: {
              type: STATUS.ERROR,
              action: FETCH_LOAN_CONTENT.FAILURE,
              message: payload === null || payload === void 0 ? void 0 : payload.message
            }
          });
        }

        return Object.assign({}, state, {
          loans: payload === null || payload === void 0 ? void 0 : (_payload$loans = payload.loans) === null || _payload$loans === void 0 ? void 0 : _payload$loans.reduce(function (loans, loan) {
            var _Object$assign;

            return Object.assign({}, loans, (_Object$assign = {}, _Object$assign[loan.loanId] = loan, _Object$assign));
          }, {}),
          status: {
            type: STATUS.SUCCESS
          }
        });
      }

    case FETCH_LOAN_CONTENT.FAILURE:
      return Object.assign({}, state, {
        status: {
          type: STATUS.ERROR,
          action: FETCH_LOAN_CONTENT.FAILURE
        }
      });

    case SET_LOAN_ID:
      return Object.assign({}, state, {
        loanId: payload
      });

    default:
      return state;
  }
});

var _processorPipeline = /*#__PURE__*/Object.freeze({
  __proto__: null,
  FETCH_LOAN_CONTENT: FETCH_LOAN_CONTENT,
  SET_LOAN_ID: SET_LOAN_ID,
  setLoanId: setLoanId,
  fetchAllLoans: fetchAllLoans,
  DEFAULT_STATE: DEFAULT_STATE$a,
  'default': processorPipeline
});

var _FETCH_LOAN_STATUS_ME;
var ROUTE_MAP$2 = {
  PROCESSOR_PIPELINE: 'PROCESSOR_PIPELINE',
  LOAN_DETAIL: 'LOAN_DETAIL'
};
var PROCESSOR_PIPELINE_ROUTES_PERMISSION = {// Enable/disable features that are not production ready. Uncomment to expose on Okta Landing (production ready)
  // [ROUTE_MAP.PROCESSOR_PIPELINE]: ['zhl-processor', 'zhl-processor-manager'],
  // [ROUTE_MAP.LOAN_DETAIL]: ['zhl-processor', 'zhl-processor-manager']
};
var FETCH_LOAN_STATUS_MESSAGES = (_FETCH_LOAN_STATUS_ME = {}, _FETCH_LOAN_STATUS_ME[FETCH_LOAN_CONTENT.FAILURE] = CONNECTION_ERROR_MESSAGE, _FETCH_LOAN_STATUS_ME);
var processorPipelineSelector = function processorPipelineSelector(_ref) {
  var processorPortal = _ref.processorPortal;
  return processorPortal || null;
};
var processorPipelineRoutesSelector = getAccessibleRoutesSelector(PROCESSOR_PIPELINE_ROUTES_PERMISSION);
var loansSelector = createSelector([processorPipelineSelector], function (processorPortal) {
  var _processorPortal$proc;

  var loans = processorPortal === null || processorPortal === void 0 ? void 0 : (_processorPortal$proc = processorPortal.processorPipeline) === null || _processorPortal$proc === void 0 ? void 0 : _processorPortal$proc.loans;
  return loans ? mapToArrayWithId(loans, 'loanId') : [];
});
var loanByIdSelector = createSelector([processorPipelineSelector], function (processorPortal) {
  var _processorPortal$proc2, _processorPortal$proc3;

  var loanId = processorPortal === null || processorPortal === void 0 ? void 0 : (_processorPortal$proc2 = processorPortal.processorPipeline) === null || _processorPortal$proc2 === void 0 ? void 0 : _processorPortal$proc2.loanId;
  var loans = processorPortal === null || processorPortal === void 0 ? void 0 : (_processorPortal$proc3 = processorPortal.processorPipeline) === null || _processorPortal$proc3 === void 0 ? void 0 : _processorPortal$proc3.loans;
  return (loans === null || loans === void 0 ? void 0 : loans[loanId]) || {};
});
var loansStatusSelector = createSelector([processorPipelineSelector], function (processorPortal) {
  var _processorPortal$proc4, _processorPortal$proc5, _processorPortal$proc6, _processorPortal$proc7, _processorPortal$proc8, _processorPortal$proc9, _processorPortal$proc10;

  var message = FETCH_LOAN_STATUS_MESSAGES[processorPortal === null || processorPortal === void 0 ? void 0 : (_processorPortal$proc4 = processorPortal.processorPipeline) === null || _processorPortal$proc4 === void 0 ? void 0 : (_processorPortal$proc5 = _processorPortal$proc4.status) === null || _processorPortal$proc5 === void 0 ? void 0 : _processorPortal$proc5.action];
  return processorPortal !== null && processorPortal !== void 0 && (_processorPortal$proc6 = processorPortal.processorPipeline) !== null && _processorPortal$proc6 !== void 0 && _processorPortal$proc6.status ? {
    type: processorPortal === null || processorPortal === void 0 ? void 0 : (_processorPortal$proc7 = processorPortal.processorPipeline) === null || _processorPortal$proc7 === void 0 ? void 0 : (_processorPortal$proc8 = _processorPortal$proc7.status) === null || _processorPortal$proc8 === void 0 ? void 0 : _processorPortal$proc8.type,
    message: (processorPortal === null || processorPortal === void 0 ? void 0 : (_processorPortal$proc9 = processorPortal.processorPipeline) === null || _processorPortal$proc9 === void 0 ? void 0 : (_processorPortal$proc10 = _processorPortal$proc9.status) === null || _processorPortal$proc10 === void 0 ? void 0 : _processorPortal$proc10.message) || message
  } : {};
});

var processorPipeline$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  ROUTE_MAP: ROUTE_MAP$2,
  PROCESSOR_PIPELINE_ROUTES_PERMISSION: PROCESSOR_PIPELINE_ROUTES_PERMISSION,
  processorPipelineSelector: processorPipelineSelector,
  processorPipelineRoutesSelector: processorPipelineRoutesSelector,
  loansSelector: loansSelector,
  loanByIdSelector: loanByIdSelector,
  loansStatusSelector: loansStatusSelector
});

var _excluded$g = ["amount", "frequency"];

var _dispatchForType, _dispatchForType2;
var scaleAmountToIncomeFrequency = loanFile$3.scaleAmountToIncomeFrequency;
var INCOME_OR_ASSET_TYPE_LOOKUP = conditions$2.INCOME_OR_ASSET_TYPE_LOOKUP,
    GROUP_LABEL_FOR_INCOME_OR_ASSET_TYPE = conditions$2.GROUP_LABEL_FOR_INCOME_OR_ASSET_TYPE;
var _constants$loanFile$c = constants.loanFile,
    ASSET_TYPES = _constants$loanFile$c.ASSET_TYPES,
    ASSET_TYPES_LOOKUP$1 = _constants$loanFile$c.ASSET_TYPES_LOOKUP,
    GIFT_TYPES$1 = _constants$loanFile$c.GIFT_TYPES,
    GIFT_SOURCES = _constants$loanFile$c.GIFT_SOURCES,
    GIFT_SOURCES_LOOKUP = _constants$loanFile$c.GIFT_SOURCES_LOOKUP,
    INCOME_TYPE = _constants$loanFile$c.INCOME_TYPE,
    INCOME_TYPE_LOOKUP = _constants$loanFile$c.INCOME_TYPE_LOOKUP,
    INCOME_FREQUENCY_LOOKUP = _constants$loanFile$c.INCOME_FREQUENCY_LOOKUP,
    LIABILITY_TYPES = _constants$loanFile$c.LIABILITY_TYPES,
    LIABILITY_ACCOUNT_STATUS_TYPES = _constants$loanFile$c.LIABILITY_ACCOUNT_STATUS_TYPES; // wrap a generator function so that it returns a joined string instead

var generatorToString = function generatorToString(fn) {
  return function () {
    return Array.from(fn.apply(void 0, arguments)).join('');
  };
};

var getAssetLabel = generatorToString(function getAssetLabelParts(_ref) {
  var _ref$attributes = _ref.attributes,
      type = _ref$attributes.type,
      financialInstitution = _ref$attributes.financialInstitution,
      accountNumber = _ref$attributes.accountNumber,
      otherTypeSpecified = _ref$attributes.otherTypeSpecified;
  var result = [];
  var hasDetails = financialInstitution || accountNumber;

  if (type === ASSET_TYPES_LOOKUP$1.OTHER) {
    result.push(otherTypeSpecified);

    if (hasDetails) {
      result.push(': ');
    }
  } else if (!hasDetails) {
    result.push(ASSET_TYPES[type]);
  }

  result.push(financialInstitution);

  if (accountNumber && financialInstitution) {
    result.push(' - ');
  }

  result.push(accountNumber);
  return result;
});

var getGiftLabel = function getGiftLabel(_ref2) {
  var _ref2$attributes = _ref2.attributes,
      type = _ref2$attributes.type,
      source = _ref2$attributes.source,
      otherSourceSpecified = _ref2$attributes.otherSourceSpecified;
  return GIFT_TYPES$1[type] + " from " + (source === GIFT_SOURCES_LOOKUP.OTHER ? otherSourceSpecified : GIFT_SOURCES[source]);
};

var getEmploymentLabel = function getEmploymentLabel(_ref3) {
  var _ref3$attributes = _ref3.attributes,
      name = _ref3$attributes.name,
      position = _ref3$attributes.position,
      startDate = _ref3$attributes.startDate,
      endDate = _ref3$attributes.endDate;
  return [name, position].filter(Boolean).join(' - ') + ", " + formatters.dateRange(startDate, endDate);
};

var getOtherIncomeLabel = function getOtherIncomeLabel(_ref4) {
  var _ref4$attributes = _ref4.attributes,
      source = _ref4$attributes.source,
      otherSourceSpecified = _ref4$attributes.otherSourceSpecified;
  return source === INCOME_TYPE_LOOKUP.OTHER ? otherSourceSpecified : INCOME_TYPE[source];
};

var getOwnedRealEstateLabel = function getOwnedRealEstateLabel(_ref5) {
  var address = _ref5.attributes.address;
  return formatters.displayAddress(address);
};

var getLiabilityLabel = generatorToString(function getLiabilityLabelParts(_ref6) {
  var _ref6$attributes = _ref6.attributes,
      creditor = _ref6$attributes.creditor,
      type = _ref6$attributes.type,
      accountIdentifier = _ref6$attributes.accountIdentifier,
      accountStatus = _ref6$attributes.accountStatus;
  var result = [];
  result.push(LIABILITY_TYPES[type]);
  result.push(': ');
  result.push(creditor);

  if (accountIdentifier) {
    result.push(' - ');
    result.push(accountIdentifier);
  }

  if (accountStatus) {
    result.push(' (');
    result.push(LIABILITY_ACCOUNT_STATUS_TYPES[accountStatus]);
    result.push(')');
  }

  return result;
});

var dispatchForType = function dispatchForType(mapping) {
  return function (obj, type) {
    var handler = mapping[type];
    return handler(obj);
  };
};

var getLabelForIncomeOrAsset = dispatchForType((_dispatchForType = {}, _dispatchForType[INCOME_OR_ASSET_TYPE_LOOKUP.ASSET] = getAssetLabel, _dispatchForType[INCOME_OR_ASSET_TYPE_LOOKUP.GIFT] = getGiftLabel, _dispatchForType[INCOME_OR_ASSET_TYPE_LOOKUP.EMPLOYMENT] = getEmploymentLabel, _dispatchForType[INCOME_OR_ASSET_TYPE_LOOKUP.INCOME] = getOtherIncomeLabel, _dispatchForType[INCOME_OR_ASSET_TYPE_LOOKUP.OWNED_REAL_ESTATE] = getOwnedRealEstateLabel, _dispatchForType[INCOME_OR_ASSET_TYPE_LOOKUP.LIABILITY] = getLiabilityLabel, _dispatchForType));
var EMPLOYMENT_INCOME_BREAKDOWN_KEYS = ['base', 'overtime', 'bonus', 'commission', 'militaryEntitlements', 'other'];

var isEmploymentIncomeBreakdown = function isEmploymentIncomeBreakdown(income) {
  return Object.keys(income).some(function (key) {
    return EMPLOYMENT_INCOME_BREAKDOWN_KEYS.includes(key);
  });
};

var EMPTY_AMOUNT = Object.freeze({
  frequency: null,
  amount: null
});

function getAmountForEmployment(_ref7) {
  var income = _ref7.attributes.income;

  if (!income) {
    return EMPTY_AMOUNT;
  } // IncomeOrLossStream


  if (!isEmploymentIncomeBreakdown(income)) {
    return income;
  } // EmploymentIncomeBreakdown
  // `employmentsItemsSelector` mixes in `amount` and `frequency`,
  // but it assumes that frequency is the same for all parts


  var _ = income.amount,
      __ = income.frequency,
      breakdown = _objectWithoutProperties(income, _excluded$g);

  var incomeParts = Object.values(breakdown);
  var frequencies = incomeParts.map(function (_ref8) {
    var frequency = _ref8.frequency;
    return frequency;
  });

  if (new Set(frequencies).size === 1) {
    return {
      frequency: incomeParts[0].frequency,
      amount: incomeParts.reduce(function (total, _ref9) {
        var amount = _ref9.amount;
        return total + amount;
      }, 0)
    };
  } // In the case that `frequency` is not consistent, scale each part to be monthly


  return {
    frequency: INCOME_FREQUENCY_LOOKUP.MONTHLY,
    amount: incomeParts.reduce(function (total, _ref10) {
      var frequency = _ref10.frequency,
          amount = _ref10.amount;
      return total + scaleAmountToIncomeFrequency(amount, frequency, INCOME_FREQUENCY_LOOKUP.MONTHLY);
    }, 0)
  };
}

var getAmountForOtherIncome = function getAmountForOtherIncome(_ref11) {
  var income = _ref11.attributes.income;
  return income !== null && income !== void 0 ? income : EMPTY_AMOUNT;
};

var getIncomeSummaryFromMarketValue = function getIncomeSummaryFromMarketValue(_ref12) {
  var marketValue = _ref12.attributes.marketValue;
  return {
    amount: marketValue,
    frequency: null
  };
};

var isNullish = function isNullish(value) {
  if (value === void 0) {
    value = null;
  }

  return value === null;
};

var getAmountForLiability = function getAmountForLiability(_ref13) {
  var _ref13$attributes = _ref13.attributes,
      unpaidBalance = _ref13$attributes.unpaidBalance,
      monthlyPayment = _ref13$attributes.monthlyPayment;

  if (!isNullish(monthlyPayment)) {
    return {
      amount: -monthlyPayment,
      frequency: INCOME_FREQUENCY_LOOKUP.MONTHLY
    };
  }

  if (!isNullish(unpaidBalance)) {
    return {
      amount: -unpaidBalance,
      frequency: null
    };
  }

  return {
    amount: null,
    frequency: null
  };
}; // returns a summary of the value of the given income or asset in the form of { amount, frequency }
// `amount` and `frequency` are both nullable


var getIncomeSummaryForIncomeOrAsset = dispatchForType((_dispatchForType2 = {}, _dispatchForType2[INCOME_OR_ASSET_TYPE_LOOKUP.ASSET] = getIncomeSummaryFromMarketValue, _dispatchForType2[INCOME_OR_ASSET_TYPE_LOOKUP.GIFT] = getIncomeSummaryFromMarketValue, _dispatchForType2[INCOME_OR_ASSET_TYPE_LOOKUP.EMPLOYMENT] = getAmountForEmployment, _dispatchForType2[INCOME_OR_ASSET_TYPE_LOOKUP.INCOME] = getAmountForOtherIncome, _dispatchForType2[INCOME_OR_ASSET_TYPE_LOOKUP.OWNED_REAL_ESTATE] = getIncomeSummaryFromMarketValue, _dispatchForType2[INCOME_OR_ASSET_TYPE_LOOKUP.LIABILITY] = getAmountForLiability, _dispatchForType2));
var getIncomeOrAssetDisplayData = function getIncomeOrAssetDisplayData(incomeOrAsset, type) {
  return {
    id: incomeOrAsset.id,
    type: type,
    label: getLabelForIncomeOrAsset(incomeOrAsset, type),
    group: GROUP_LABEL_FOR_INCOME_OR_ASSET_TYPE[type],
    incomeSummary: getIncomeSummaryForIncomeOrAsset(incomeOrAsset, type)
  };
};

var _excluded$h = ["id"];
var loanFileSelector$1 = loanFileSelector;
var borrowerIDsSelector$1 = borrowerIDsSelector,
    borrowerListSelector$1 = borrowerListSelector;
var employmentsItemsSelector$1 = employmentsItemsSelector,
    borrowersEmploymentsSelector$1 = borrowersEmploymentsSelector;
var incomeItemsSelector$1 = incomeItemsSelector,
    borrowersIncomeSelector$1 = borrowersIncomeSelector;
var assetsItemsSelector$1 = assetsItemsSelector,
    borrowersAssetsSelector$1 = borrowersAssetsSelector;
var giftsItemsSelector$1 = giftsItemsSelector,
    borrowersGiftsSelector$1 = borrowersGiftsSelector;
var ownedRealEstatesItemsSelector$1 = ownedRealEstates$2.ownedRealEstatesItemsSelector,
    borrowersOwnedRealEstatesItemsSelector$1 = ownedRealEstates$2.borrowersOwnedRealEstatesItemsSelector;
var liabilitiesItemsSelector$1 = liabilities$2.liabilitiesItemsSelector,
    borrowerLiabilitiesSelector$1 = liabilities$2.borrowerLiabilitiesSelector;
var DOCUMENT_REQUEST_STATUS_LOOKUP = conditions$2.DOCUMENT_REQUEST_STATUS_LOOKUP,
    INCOME_OR_ASSET_TYPE_LOOKUP$1 = conditions$2.INCOME_OR_ASSET_TYPE_LOOKUP;
var documentRequestIDSelector = createPropsSelector(function (_ref) {
  var documentRequestID = _ref.documentRequestID;
  return documentRequestID;
});
var documentRequestsSelector = createSelector([loanFileSelector$1], function (_ref2) {
  var documentRequests = _ref2.documentRequests;
  return documentRequests;
});
var documentRequestsByIDSelector = createSelector([documentRequestsSelector], function (_ref3) {
  var requests = _ref3.requests;
  return requests;
});
var loanDocumentRequestIDsSelector = createSelector([documentRequestsSelector], function (_ref4) {
  var requestIDs = _ref4.requestIDs;
  return requestIDs;
});
var documentRequestSelector = createSelector([documentRequestIDSelector, documentRequestsByIDSelector], function (documentRequestID, documentRequestsByID) {
  return documentRequestsByID[documentRequestID] || null;
});
var employmentsByIDSelector$1 = byID(employmentsItemsSelector$1);
var incomesByIDSelector$1 = byID(incomeItemsSelector$1);
var assetsByIDSelector$1 = byID(assetsItemsSelector$1);
var giftsByIDSelector = byID(giftsItemsSelector$1);
var ownedRealEstateByIDSelector = byID(ownedRealEstatesItemsSelector$1);
var incomeOrAssetIDSelector = createPropsSelector(function (_ref5) {
  var id = _ref5.id;
  return id;
});
var incomeOrAssetTypeSelector = createPropsSelector(function (_ref6) {
  var type = _ref6.type;
  return type;
});
var VALID_INCOME_OR_ASSET_TYPES = new Set(Object.values(INCOME_OR_ASSET_TYPE_LOOKUP$1));
var incomeOrAssetSelector = createSelector([incomeOrAssetIDSelector, incomeOrAssetTypeSelector, employmentsByIDSelector$1, incomesByIDSelector$1, assetsByIDSelector$1, giftsByIDSelector, ownedRealEstateByIDSelector, liabilitiesItemsSelector$1], function (id, type, employmentByID, incomeByID, assetByID, giftByID, ownedRealEstateByID, liabilitiesByID) {
  var _INCOME_OR_ASSET_TYPE;

  return VALID_INCOME_OR_ASSET_TYPES.has(type) ? (_INCOME_OR_ASSET_TYPE = {}, _INCOME_OR_ASSET_TYPE[INCOME_OR_ASSET_TYPE_LOOKUP$1.EMPLOYMENT] = employmentByID, _INCOME_OR_ASSET_TYPE[INCOME_OR_ASSET_TYPE_LOOKUP$1.INCOME] = incomeByID, _INCOME_OR_ASSET_TYPE[INCOME_OR_ASSET_TYPE_LOOKUP$1.ASSET] = assetByID, _INCOME_OR_ASSET_TYPE[INCOME_OR_ASSET_TYPE_LOOKUP$1.GIFT] = giftByID, _INCOME_OR_ASSET_TYPE[INCOME_OR_ASSET_TYPE_LOOKUP$1.OWNED_REAL_ESTATE] = ownedRealEstateByID, _INCOME_OR_ASSET_TYPE[INCOME_OR_ASSET_TYPE_LOOKUP$1.LIABILITY] = liabilitiesByID, _INCOME_OR_ASSET_TYPE)[type][id] : null;
});
var getIncomeOrAssetSelector = bindSelectorToState(incomeOrAssetSelector); // returns a doc request in the shape required by the conditions API (yes, `*Id` is required)

var documentRequestCreateSelector = createSelector([documentRequestSelector, getIncomeOrAssetSelector], function (docRequest, getIncomeOrAsset) {
  if (docRequest) {
    var tempID = docRequest.tempID,
        title = docRequest.title,
        description = docRequest.description,
        assetId = docRequest.assetId,
        assetType = docRequest.assetType,
        borrowerId = docRequest.borrowerId;
    var incomeOrAsset = getIncomeOrAsset({
      id: assetId,
      type: assetType
    });
    var assetName = incomeOrAsset ? getLabelForIncomeOrAsset(incomeOrAsset, assetType) : '';
    return {
      tempID: tempID,
      title: title,
      description: description,
      borrowerId: borrowerId,
      assetId: assetId,
      assetType: assetType,
      assetName: assetName
    };
  }

  return null;
});
var getDocumentRequestForIdSelector = bindSelectorToState(documentRequestSelector);
var documentRequestsForLoanSelector = createSelector([loanDocumentRequestIDsSelector, getDocumentRequestForIdSelector, getIncomeOrAssetSelector], function (loanDocumentRequestIDs, getDocumentRequestForId, getIncomeOrAsset) {
  return flatMap(loanDocumentRequestIDs, function (documentRequestID) {
    var _task$documents;

    var task = getDocumentRequestForId({
      documentRequestID: documentRequestID
    });

    if (!task) {
      return [];
    }

    var incomeOrAsset = task.assetId && task.assetType ? getIncomeOrAsset({
      id: task.assetId,
      type: task.assetType
    }) : null;
    return Object.assign({}, task, {
      incomeOrAsset: incomeOrAsset && getIncomeOrAssetDisplayData(incomeOrAsset, task.assetType),
      documents: ((_task$documents = task.documents) !== null && _task$documents !== void 0 ? _task$documents : []).filter(function (_ref7) {
        var urls = _ref7.urls;
        return !!(urls !== null && urls !== void 0 && urls.embeddedUrl);
      }).map(function (_ref8) {
        var name = _ref8.name,
            urls = _ref8.urls;
        return {
          filename: name,
          documentURL: urls.embeddedUrl
        };
      })
    });
  });
});
var approvedDocumentRequestsSelector = createSelector([documentRequestsForLoanSelector], function (docRequests) {
  return docRequests.filter(function (_ref9) {
    var status = _ref9.status;
    return status === DOCUMENT_REQUEST_STATUS_LOOKUP.APPROVED;
  });
});
var documentRequestsByBorrowerSelector = createSelector([documentRequestsForLoanSelector], function (docRequests) {
  return groupBy(docRequests, function (_ref10) {
    var borrowerId = _ref10.borrowerId;
    return borrowerId;
  });
});
var unaffiliatedDocumentsSelector = createSelector([documentRequestsSelector], function (_ref11) {
  var unaffiliatedDocuments = _ref11.unaffiliatedDocuments;
  return unaffiliatedDocuments;
});
var unaffiliatedDocumentsTasksSelector = createSelector([unaffiliatedDocumentsSelector], function (unaffiliatedDocuments) {
  return unaffiliatedDocuments && unaffiliatedDocuments.length > 0 ? unaffiliatedDocuments.filter(function (_ref12) {
    var urls = _ref12.urls;
    return !!(urls !== null && urls !== void 0 && urls.embeddedUrl);
  }).map(function (_ref13) {
    var id = _ref13.id,
        name = _ref13.name,
        urls = _ref13.urls;
    return {
      id: id,
      documents: [{
        filename: name,
        documentURL: urls.embeddedUrl
      }],
      status: undefined,
      // `null` is `DOCUMENT_REQUEST_STATUS_LOOKUP.DRAFT`, which we don't want
      title: name,
      // intentionally empty values to mimic a task object
      description: '',
      borrowerId: null,
      assetId: null,
      assetType: null,
      assetName: null
    };
  }) : null;
});
var tasksByBorrowerSelector = createSelector([documentRequestsByBorrowerSelector, borrowerListSelector$1], function (tasksForBorrowerId, borrowersList) {
  return borrowersList.map(function (_ref14) {
    var _attributes$names, _attributes$names$, _tasksForBorrowerId$i;

    var id = _ref14.id,
        attributes = _ref14.attributes;
    return {
      borrower: {
        id: id,
        displayName: ((_attributes$names = attributes.names) === null || _attributes$names === void 0 ? void 0 : (_attributes$names$ = _attributes$names[0]) === null || _attributes$names$ === void 0 ? void 0 : _attributes$names$.displayName) || ''
      },
      tasks: ((_tasksForBorrowerId$i = tasksForBorrowerId[id]) !== null && _tasksForBorrowerId$i !== void 0 ? _tasksForBorrowerId$i : []).filter(function (_ref15) {
        var status = _ref15.status;
        return status !== DOCUMENT_REQUEST_STATUS_LOOKUP.APPROVED;
      })
    };
  });
});
var taskTypesSelector = createSelector([documentRequestsSelector], function (_ref16) {
  var taskTypes = _ref16.taskTypes;
  return Object.values(taskTypes).map(function (_ref17) {
    var id = _ref17.id,
        taskType = _objectWithoutProperties(_ref17, _excluded$h);

    return {
      id: id,
      category: taskType.conditionTypeName,
      label: taskType.conditionName,
      description: taskType.conditionDescription
    };
  });
});
var taskTypesByIDSelector = byID(taskTypesSelector);
var taskTypeCategoriesSelector = createSelector([taskTypesSelector], function (taskTypes) {
  return uniq(taskTypes.map(function (_ref18) {
    var category = _ref18.category;
    return category;
  }));
});
var draftRequestIDsSelector = createSelector([loanDocumentRequestIDsSelector, documentRequestsByIDSelector], function (requestIDs, documentRequestsByID) {
  return requestIDs.filter(function (id) {
    var _documentRequestsByID;

    return ((_documentRequestsByID = documentRequestsByID[id]) === null || _documentRequestsByID === void 0 ? void 0 : _documentRequestsByID.status) === DOCUMENT_REQUEST_STATUS_LOOKUP.DRAFT;
  });
});
var hasDraftsSelector = createSelector([draftRequestIDsSelector], function (draftRequestIDs) {
  return draftRequestIDs.length > 0;
});
var borrowersIncomeAndAssetsSelector = createSelector([borrowersEmploymentsSelector$1, borrowersIncomeSelector$1, borrowersAssetsSelector$1, borrowersGiftsSelector$1, borrowersOwnedRealEstatesItemsSelector$1, borrowerLiabilitiesSelector$1], function (borrowersEmployments, borrowersIncome, borrowersAssets, borrowersGifts, borrowersOwnedRealEstate, borrowersLiabilities) {
  var _ref19;

  return _ref19 = {}, _ref19[INCOME_OR_ASSET_TYPE_LOOKUP$1.EMPLOYMENT] = borrowersEmployments, _ref19[INCOME_OR_ASSET_TYPE_LOOKUP$1.INCOME] = borrowersIncome, _ref19[INCOME_OR_ASSET_TYPE_LOOKUP$1.ASSET] = borrowersAssets, _ref19[INCOME_OR_ASSET_TYPE_LOOKUP$1.GIFT] = borrowersGifts, _ref19[INCOME_OR_ASSET_TYPE_LOOKUP$1.OWNED_REAL_ESTATE] = borrowersOwnedRealEstate, _ref19[INCOME_OR_ASSET_TYPE_LOOKUP$1.LIABILITY] = borrowersLiabilities, _ref19;
});
var borrowersIncomeAndAssetChoicesSelector = createSelector([borrowersIncomeAndAssetsSelector], function (borrowersIncomeAndAssets) {
  return mapValues(borrowersIncomeAndAssets, function (objects, type) {
    return objects.map(function (obj) {
      return getIncomeOrAssetDisplayData(obj, type);
    });
  });
});
var borrowersCombinedIncomeAndAssetsSelector = createSelector([borrowersIncomeAndAssetChoicesSelector], function (borrowersIncomeAndAssetChoices) {
  return flatten(Object.values(borrowersIncomeAndAssetChoices));
});
var borrowerCombinedIncomeAndAssetsSelectorCreator = bindSelectorToState(borrowersCombinedIncomeAndAssetsSelector);
var incomeOrAssetChoicesByBorrowerSelector = createSelector([borrowerIDsSelector$1, borrowerCombinedIncomeAndAssetsSelectorCreator], function (borrowerIDs, getIncomeAndAssetsForBorrower) {
  return borrowerIDs.reduce(function (acc, borrowerID) {
    var _Object$assign;

    return Object.assign({}, acc, (_Object$assign = {}, _Object$assign[borrowerID] = getIncomeAndAssetsForBorrower({
      borrowerId: borrowerID,
      borrowerID: borrowerID
    }), _Object$assign));
  }, {});
});

var conditions = /*#__PURE__*/Object.freeze({
  __proto__: null,
  documentRequestSelector: documentRequestSelector,
  incomeOrAssetSelector: incomeOrAssetSelector,
  documentRequestCreateSelector: documentRequestCreateSelector,
  documentRequestsForLoanSelector: documentRequestsForLoanSelector,
  approvedDocumentRequestsSelector: approvedDocumentRequestsSelector,
  unaffiliatedDocumentsTasksSelector: unaffiliatedDocumentsTasksSelector,
  tasksByBorrowerSelector: tasksByBorrowerSelector,
  taskTypesSelector: taskTypesSelector,
  taskTypesByIDSelector: taskTypesByIDSelector,
  taskTypeCategoriesSelector: taskTypeCategoriesSelector,
  draftRequestIDsSelector: draftRequestIDsSelector,
  hasDraftsSelector: hasDraftsSelector,
  incomeOrAssetChoicesByBorrowerSelector: incomeOrAssetChoicesByBorrowerSelector
});

var FETCH_FUNDING_REQUEST_CONTENT = createActionObject('FETCH_FUNDING_REQUEST_CONTENT');
var SUBMIT_FUNDING_REQUEST_FORM = createActionObject('SUBMIT_FUNDING_REQUEST_FORM');
var clearStatus$1 = function clearStatus() {
  return {
    type: CLEAR_STATUS
  };
};
var fetchFundingRequestContent = function fetchFundingRequestContent() {
  return API['accounting-sync'].GET.thunk({
    action: 'v1/accounting-sync/funding-requests',
    authenticated: true,
    handleLogin: false
  }, FETCH_FUNDING_REQUEST_CONTENT);
};
var submitFundingRequestForm = function submitFundingRequestForm(beginDateInput, endDateInput, isReprocess, isManualRequest) {
  var beginDate = toDateString(beginDateInput);
  var endDate = toDateString(endDateInput);
  return API['accounting-sync'].POST.thunk({
    action: 'v1/accounting-sync/funding-requests',
    authenticated: true,
    handleLogin: false,
    input: {
      beginDate: beginDate,
      endDate: endDate,
      isReprocess: isReprocess,
      isManualRequest: isManualRequest
    }
  }, SUBMIT_FUNDING_REQUEST_FORM);
};
var DEFAULT_STATE$b = {
  status: {},
  requests: []
};
var fundingRequest = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$b;
  }

  var type = _ref.type,
      payload = _ref.payload;

  switch (type) {
    case FETCH_FUNDING_REQUEST_CONTENT.BEGIN:
      return Object.assign({}, state, {
        status: {
          type: STATUS.LOADING
        }
      });

    case FETCH_FUNDING_REQUEST_CONTENT.SUCCESS:
      return Object.assign({}, state, {
        requests: payload,
        status: {
          type: STATUS.SUCCESS,
          action: FETCH_FUNDING_REQUEST_CONTENT.SUCCESS
        }
      });

    case FETCH_FUNDING_REQUEST_CONTENT.FAILURE:
      return Object.assign({}, state, {
        status: {
          type: STATUS.ERROR,
          action: FETCH_FUNDING_REQUEST_CONTENT.FAILURE
        }
      });

    case SUBMIT_FUNDING_REQUEST_FORM.BEGIN:
      return Object.assign({}, state, {
        status: {
          type: STATUS.LOADING
        }
      });

    case SUBMIT_FUNDING_REQUEST_FORM.SUCCESS:
      {
        return Object.assign({}, state, {
          status: {
            type: STATUS.SUCCESS,
            action: SUBMIT_FUNDING_REQUEST_FORM.SUCCESS
          }
        });
      }

    case SUBMIT_FUNDING_REQUEST_FORM.FAILURE:
      return Object.assign({}, state, {
        status: {
          type: STATUS.ERROR,
          action: SUBMIT_FUNDING_REQUEST_FORM.FAILURE
        }
      });

    case CLEAR_STATUS:
      return Object.assign({}, state, {
        status: {}
      });

    default:
      return state;
  }
});

var _fundingRequest = /*#__PURE__*/Object.freeze({
  __proto__: null,
  FETCH_FUNDING_REQUEST_CONTENT: FETCH_FUNDING_REQUEST_CONTENT,
  SUBMIT_FUNDING_REQUEST_FORM: SUBMIT_FUNDING_REQUEST_FORM,
  clearStatus: clearStatus$1,
  fetchFundingRequestContent: fetchFundingRequestContent,
  submitFundingRequestForm: submitFundingRequestForm,
  DEFAULT_STATE: DEFAULT_STATE$b,
  'default': fundingRequest
});

var _FUNDING_REQUEST_ROUT, _FUNDING_REQUEST_STAT;
var ROUTE_MAP$3 = {
  FUNDING_REQUEST: 'FUNDING_REQUEST',
  FUNDING_REQUEST_STATUS: 'FUNDING_REQUEST_STATUS'
};
var FUNDING_REQUEST_ROUTE_PERMISSION = (_FUNDING_REQUEST_ROUT = {}, _FUNDING_REQUEST_ROUT[ROUTE_MAP$3.FUNDING_REQUEST] = ['zhl-accountant'], _FUNDING_REQUEST_ROUT[ROUTE_MAP$3.FUNDING_REQUEST_STATUS] = ['zhl-accountant'], _FUNDING_REQUEST_ROUT);
var FUNDING_REQUEST_STATUS_MESSAGES = (_FUNDING_REQUEST_STAT = {}, _FUNDING_REQUEST_STAT[FETCH_FUNDING_REQUEST_CONTENT.FAILURE] = CONNECTION_ERROR_MESSAGE, _FUNDING_REQUEST_STAT[SUBMIT_FUNDING_REQUEST_FORM.SUCCESS] = 'Successfully created funding request', _FUNDING_REQUEST_STAT[SUBMIT_FUNDING_REQUEST_FORM.FAILURE] = 'Failed to create funding request', _FUNDING_REQUEST_STAT);
var fundingRequestSelector = function fundingRequestSelector(_ref) {
  var accountingPortal = _ref.accountingPortal;
  return accountingPortal || null;
};
var fundingRequestRoutesSelector = getAccessibleRoutesSelector(FUNDING_REQUEST_ROUTE_PERMISSION);
var fundingRequestStatusSelector = createSelector([fundingRequestSelector], function (accountingPortal) {
  var _accountingPortal$fun, _accountingPortal$fun2, _accountingPortal$fun3, _accountingPortal$fun4, _accountingPortal$fun5, _accountingPortal$fun6, _accountingPortal$fun7;

  var message = FUNDING_REQUEST_STATUS_MESSAGES[accountingPortal === null || accountingPortal === void 0 ? void 0 : (_accountingPortal$fun = accountingPortal.fundingRequest) === null || _accountingPortal$fun === void 0 ? void 0 : (_accountingPortal$fun2 = _accountingPortal$fun.status) === null || _accountingPortal$fun2 === void 0 ? void 0 : _accountingPortal$fun2.action];
  return accountingPortal !== null && accountingPortal !== void 0 && (_accountingPortal$fun3 = accountingPortal.fundingRequest) !== null && _accountingPortal$fun3 !== void 0 && _accountingPortal$fun3.status ? {
    type: accountingPortal === null || accountingPortal === void 0 ? void 0 : (_accountingPortal$fun4 = accountingPortal.fundingRequest) === null || _accountingPortal$fun4 === void 0 ? void 0 : (_accountingPortal$fun5 = _accountingPortal$fun4.status) === null || _accountingPortal$fun5 === void 0 ? void 0 : _accountingPortal$fun5.type,
    message: (accountingPortal === null || accountingPortal === void 0 ? void 0 : (_accountingPortal$fun6 = accountingPortal.fundingRequest) === null || _accountingPortal$fun6 === void 0 ? void 0 : (_accountingPortal$fun7 = _accountingPortal$fun6.status) === null || _accountingPortal$fun7 === void 0 ? void 0 : _accountingPortal$fun7.message) || message
  } : {};
});

var formatDateTime = function formatDateTime(field) {
  return field ? new Date(field).toLocaleString() : 'N/A';
};

var formatDate = function formatDate(field) {
  return field ? new Date(field).toLocaleDateString() : 'N/A';
};

var getFundingRequests = function getFundingRequests(fundingRequests) {
  if (fundingRequests && Object.keys(fundingRequests).length > 0) {
    var mappedFundingRequests = mapToArrayWithId(fundingRequests, 'id');
    return mappedFundingRequests.map(function (fundingRequest) {
      return Object.assign({}, fundingRequest, {
        createdDate: formatDateTime(fundingRequest.createdUTC),
        processedDate: formatDateTime(fundingRequest.processedUTC),
        beginDate: formatDate(fundingRequest.beginDate),
        endDate: formatDate(fundingRequest.endDate)
      });
    });
  }

  return [];
};

var fundingRequestStatusesSelector = createSelector([fundingRequestSelector], function (accountingPortal) {
  var _accountingPortal$fun8;

  return getFundingRequests(accountingPortal === null || accountingPortal === void 0 ? void 0 : (_accountingPortal$fun8 = accountingPortal.fundingRequest) === null || _accountingPortal$fun8 === void 0 ? void 0 : _accountingPortal$fun8.requests);
});

var fundingRequest$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  ROUTE_MAP: ROUTE_MAP$3,
  FUNDING_REQUEST_ROUTE_PERMISSION: FUNDING_REQUEST_ROUTE_PERMISSION,
  fundingRequestSelector: fundingRequestSelector,
  fundingRequestRoutesSelector: fundingRequestRoutesSelector,
  fundingRequestStatusSelector: fundingRequestStatusSelector,
  fundingRequestStatusesSelector: fundingRequestStatusesSelector
});

var FETCH_LOAN_REQUEST_CONTENT = createActionObject('FETCH_LOAN_REQUEST_CONTENT');
var SUBMIT_LOAN_REQUEST_FORM = createActionObject('SUBMIT_LOAN_REQUEST_FORM');
var clearStatus$2 = function clearStatus() {
  return {
    type: CLEAR_STATUS
  };
};
var fetchLoanRequestContent = function fetchLoanRequestContent() {
  return API['accounting-sync'].GET.thunk({
    action: 'v1/accounting-sync/loan-imports',
    authenticated: true,
    handleLogin: false
  }, FETCH_LOAN_REQUEST_CONTENT);
};
var submitLoanRequestForm = function submitLoanRequestForm(lineItemTypeId, loanNumber) {
  return API['accounting-sync'].POST.thunk({
    action: 'v1/accounting-sync/loan-imports',
    authenticated: true,
    handleLogin: false,
    input: {
      lineItemTypeId: lineItemTypeId,
      loanNumber: loanNumber
    }
  }, SUBMIT_LOAN_REQUEST_FORM);
};
var DEFAULT_STATE$c = {
  status: {},
  requests: []
};
var loanRequest = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$c;
  }

  var type = _ref.type,
      payload = _ref.payload;

  switch (type) {
    case FETCH_LOAN_REQUEST_CONTENT.BEGIN:
      return Object.assign({}, state, {
        status: {
          type: STATUS.LOADING
        }
      });

    case FETCH_LOAN_REQUEST_CONTENT.SUCCESS:
      return Object.assign({}, state, {
        requests: payload,
        status: {
          type: STATUS.SUCCESS,
          action: FETCH_LOAN_REQUEST_CONTENT.SUCCESS
        }
      });

    case FETCH_LOAN_REQUEST_CONTENT.FAILURE:
      return Object.assign({}, state, {
        status: {
          type: STATUS.ERROR,
          action: FETCH_LOAN_REQUEST_CONTENT.FAILURE
        }
      });

    case SUBMIT_LOAN_REQUEST_FORM.BEGIN:
      return Object.assign({}, state, {
        status: {
          type: STATUS.LOADING
        }
      });

    case SUBMIT_LOAN_REQUEST_FORM.SUCCESS:
      {
        return Object.assign({}, state, {
          status: {
            type: STATUS.SUCCESS,
            action: SUBMIT_LOAN_REQUEST_FORM.SUCCESS
          }
        });
      }

    case SUBMIT_LOAN_REQUEST_FORM.FAILURE:
      return Object.assign({}, state, {
        status: {
          type: STATUS.ERROR,
          action: SUBMIT_LOAN_REQUEST_FORM.FAILURE
        }
      });

    case CLEAR_STATUS:
      return Object.assign({}, state, {
        status: {}
      });

    default:
      return state;
  }
});

var _loanRequest = /*#__PURE__*/Object.freeze({
  __proto__: null,
  FETCH_LOAN_REQUEST_CONTENT: FETCH_LOAN_REQUEST_CONTENT,
  SUBMIT_LOAN_REQUEST_FORM: SUBMIT_LOAN_REQUEST_FORM,
  clearStatus: clearStatus$2,
  fetchLoanRequestContent: fetchLoanRequestContent,
  submitLoanRequestForm: submitLoanRequestForm,
  DEFAULT_STATE: DEFAULT_STATE$c,
  'default': loanRequest
});

var _LOAN_REQUEST_ROUTE_P, _LOAN_REQUEST_STATUS_;
var ROUTE_MAP$4 = {
  LOAN_REQUEST: 'LOAN_REQUEST',
  LOAN_REQUEST_STATUS: 'LOAN_REQUEST_STATUS'
};
var LOAN_REQUEST_ROUTE_PERMISSION = (_LOAN_REQUEST_ROUTE_P = {}, _LOAN_REQUEST_ROUTE_P[ROUTE_MAP$4.LOAN_REQUEST] = ['zhl-accountant'], _LOAN_REQUEST_ROUTE_P[ROUTE_MAP$4.LOAN_REQUEST_STATUS] = ['zhl-accountant'], _LOAN_REQUEST_ROUTE_P);
var LOAN_REQUEST_STATUS_MESSAGES = (_LOAN_REQUEST_STATUS_ = {}, _LOAN_REQUEST_STATUS_[FETCH_LOAN_REQUEST_CONTENT.FAILURE] = CONNECTION_ERROR_MESSAGE, _LOAN_REQUEST_STATUS_[SUBMIT_LOAN_REQUEST_FORM.SUCCESS] = 'Successfully created loan import request', _LOAN_REQUEST_STATUS_[SUBMIT_LOAN_REQUEST_FORM.FAILURE] = 'Failed to create loan import request', _LOAN_REQUEST_STATUS_);
var loanRequestSelector = function loanRequestSelector(_ref) {
  var accountingPortal = _ref.accountingPortal;
  return accountingPortal || null;
};
var loanRequestRoutesSelector = getAccessibleRoutesSelector(LOAN_REQUEST_ROUTE_PERMISSION);
var loanRequestStatusSelector = createSelector([loanRequestSelector], function (accountingPortal) {
  var _accountingPortal$loa, _accountingPortal$loa2, _accountingPortal$loa3, _accountingPortal$loa4, _accountingPortal$loa5, _accountingPortal$loa6, _accountingPortal$loa7;

  var message = LOAN_REQUEST_STATUS_MESSAGES[accountingPortal === null || accountingPortal === void 0 ? void 0 : (_accountingPortal$loa = accountingPortal.loanRequest) === null || _accountingPortal$loa === void 0 ? void 0 : (_accountingPortal$loa2 = _accountingPortal$loa.status) === null || _accountingPortal$loa2 === void 0 ? void 0 : _accountingPortal$loa2.action];
  return accountingPortal !== null && accountingPortal !== void 0 && (_accountingPortal$loa3 = accountingPortal.loanRequest) !== null && _accountingPortal$loa3 !== void 0 && _accountingPortal$loa3.status ? {
    type: accountingPortal === null || accountingPortal === void 0 ? void 0 : (_accountingPortal$loa4 = accountingPortal.loanRequest) === null || _accountingPortal$loa4 === void 0 ? void 0 : (_accountingPortal$loa5 = _accountingPortal$loa4.status) === null || _accountingPortal$loa5 === void 0 ? void 0 : _accountingPortal$loa5.type,
    message: (accountingPortal === null || accountingPortal === void 0 ? void 0 : (_accountingPortal$loa6 = accountingPortal.loanRequest) === null || _accountingPortal$loa6 === void 0 ? void 0 : (_accountingPortal$loa7 = _accountingPortal$loa6.status) === null || _accountingPortal$loa7 === void 0 ? void 0 : _accountingPortal$loa7.message) || message
  } : {};
});

var formatDateTime$1 = function formatDateTime(field) {
  return field ? new Date(field).toLocaleString() : 'N/A';
};

var getLoanRequests = function getLoanRequests(loanRequests) {
  if (loanRequests && Object.keys(loanRequests).length > 0) {
    var mappedLoanRequests = mapToArrayWithId(loanRequests, 'id');
    return mappedLoanRequests.map(function (loanRequest) {
      return Object.assign({}, loanRequest, {
        createdDate: formatDateTime$1(loanRequest.createdUTC),
        importedDate: formatDateTime$1(loanRequest.importedUTC)
      });
    });
  }

  return [];
};

var loanRequestStatusesSelector = createSelector([loanRequestSelector], function (accountingPortal) {
  var _accountingPortal$loa8;

  return getLoanRequests(accountingPortal === null || accountingPortal === void 0 ? void 0 : (_accountingPortal$loa8 = accountingPortal.loanRequest) === null || _accountingPortal$loa8 === void 0 ? void 0 : _accountingPortal$loa8.requests);
});

var loanRequest$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  ROUTE_MAP: ROUTE_MAP$4,
  LOAN_REQUEST_ROUTE_PERMISSION: LOAN_REQUEST_ROUTE_PERMISSION,
  loanRequestSelector: loanRequestSelector,
  loanRequestRoutesSelector: loanRequestRoutesSelector,
  loanRequestStatusSelector: loanRequestStatusSelector,
  loanRequestStatusesSelector: loanRequestStatusesSelector
});

var FETCH_PURCHASE_REQUEST_CONTENT = createActionObject('FETCH_PURCHASE_REQUEST_CONTENT');
var SUBMIT_PURCHASE_REQUEST_FORM = createActionObject('SUBMIT_PURCHASE_REQUEST_FORM');
var clearStatus$3 = function clearStatus() {
  return {
    type: CLEAR_STATUS
  };
};
var fetchPurchaseRequestContent = function fetchPurchaseRequestContent() {
  return API['accounting-sync'].GET.thunk({
    action: 'v1/accounting-sync/purchase-requests',
    authenticated: true,
    handleLogin: false
  }, FETCH_PURCHASE_REQUEST_CONTENT);
};
var submitPurchaseRequestForm = function submitPurchaseRequestForm(beginDateInput, endDateInput, isReprocess, isManualRequest) {
  var beginDate = toDateString(beginDateInput);
  var endDate = toDateString(endDateInput);
  return API['accounting-sync'].POST.thunk({
    action: 'v1/accounting-sync/purchase-requests',
    authenticated: true,
    handleLogin: false,
    input: {
      beginDate: beginDate,
      endDate: endDate,
      isReprocess: isReprocess,
      isManualRequest: isManualRequest
    }
  }, SUBMIT_PURCHASE_REQUEST_FORM);
};
var DEFAULT_STATE$d = {
  status: {},
  requests: []
};
var purchaseRequest = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$d;
  }

  var type = _ref.type,
      payload = _ref.payload;

  switch (type) {
    case FETCH_PURCHASE_REQUEST_CONTENT.BEGIN:
      return Object.assign({}, state, {
        status: {
          type: STATUS.LOADING
        }
      });

    case FETCH_PURCHASE_REQUEST_CONTENT.SUCCESS:
      {
        return Object.assign({}, state, {
          requests: payload,
          status: {
            type: STATUS.SUCCESS,
            action: FETCH_PURCHASE_REQUEST_CONTENT.SUCCESS
          }
        });
      }

    case FETCH_PURCHASE_REQUEST_CONTENT.FAILURE:
      return Object.assign({}, state, {
        status: {
          type: STATUS.ERROR,
          action: FETCH_PURCHASE_REQUEST_CONTENT.FAILURE
        }
      });

    case SUBMIT_PURCHASE_REQUEST_FORM.BEGIN:
      return Object.assign({}, state, {
        status: {
          type: STATUS.LOADING
        }
      });

    case SUBMIT_PURCHASE_REQUEST_FORM.SUCCESS:
      {
        return Object.assign({}, state, {
          status: {
            type: STATUS.SUCCESS,
            action: SUBMIT_PURCHASE_REQUEST_FORM.SUCCESS
          }
        });
      }

    case SUBMIT_PURCHASE_REQUEST_FORM.FAILURE:
      return Object.assign({}, state, {
        status: {
          type: STATUS.ERROR,
          action: SUBMIT_PURCHASE_REQUEST_FORM.FAILURE
        }
      });

    case CLEAR_STATUS:
      return Object.assign({}, state, {
        status: {}
      });

    default:
      return state;
  }
});

var _purchaseRequest = /*#__PURE__*/Object.freeze({
  __proto__: null,
  FETCH_PURCHASE_REQUEST_CONTENT: FETCH_PURCHASE_REQUEST_CONTENT,
  SUBMIT_PURCHASE_REQUEST_FORM: SUBMIT_PURCHASE_REQUEST_FORM,
  clearStatus: clearStatus$3,
  fetchPurchaseRequestContent: fetchPurchaseRequestContent,
  submitPurchaseRequestForm: submitPurchaseRequestForm,
  DEFAULT_STATE: DEFAULT_STATE$d,
  'default': purchaseRequest
});

var _PURCHASE_REQUEST_ROU, _PURCHASE_REQUEST_STA;
var ROUTE_MAP$5 = {
  PURCHASE_REQUEST: 'PURCHASE_REQUEST',
  PURCHASE_REQUEST_STATUS: 'PURCHASE_REQUEST_STATUS'
};
var PURCHASE_REQUEST_ROUTE_PERMISSION = (_PURCHASE_REQUEST_ROU = {}, _PURCHASE_REQUEST_ROU[ROUTE_MAP$5.PURCHASE_REQUEST] = ['zhl-accountant'], _PURCHASE_REQUEST_ROU[ROUTE_MAP$5.PURCHASE_REQUEST_STATUS] = ['zhl-accountant'], _PURCHASE_REQUEST_ROU);
var PURCHASE_REQUEST_STATUS_MESSAGES = (_PURCHASE_REQUEST_STA = {}, _PURCHASE_REQUEST_STA[FETCH_PURCHASE_REQUEST_CONTENT.FAILURE] = CONNECTION_ERROR_MESSAGE, _PURCHASE_REQUEST_STA[SUBMIT_PURCHASE_REQUEST_FORM.SUCCESS] = 'Successfully created purchase request', _PURCHASE_REQUEST_STA[SUBMIT_PURCHASE_REQUEST_FORM.FAILURE] = 'Failed to create purchase request', _PURCHASE_REQUEST_STA);
var purchaseRequestSelector = function purchaseRequestSelector(_ref) {
  var accountingPortal = _ref.accountingPortal;
  return accountingPortal || null;
};
var purchaseRequestRoutesSelector = getAccessibleRoutesSelector(PURCHASE_REQUEST_ROUTE_PERMISSION);
var purchaseRequestStatusSelector = createSelector([purchaseRequestSelector], function (accountingPortal) {
  var _accountingPortal$pur, _accountingPortal$pur2, _accountingPortal$pur3, _accountingPortal$pur4, _accountingPortal$pur5, _accountingPortal$pur6, _accountingPortal$pur7;

  var message = PURCHASE_REQUEST_STATUS_MESSAGES[accountingPortal === null || accountingPortal === void 0 ? void 0 : (_accountingPortal$pur = accountingPortal.purchaseRequest) === null || _accountingPortal$pur === void 0 ? void 0 : (_accountingPortal$pur2 = _accountingPortal$pur.status) === null || _accountingPortal$pur2 === void 0 ? void 0 : _accountingPortal$pur2.action];
  return accountingPortal !== null && accountingPortal !== void 0 && (_accountingPortal$pur3 = accountingPortal.purchaseRequest) !== null && _accountingPortal$pur3 !== void 0 && _accountingPortal$pur3.status ? {
    type: accountingPortal === null || accountingPortal === void 0 ? void 0 : (_accountingPortal$pur4 = accountingPortal.purchaseRequest) === null || _accountingPortal$pur4 === void 0 ? void 0 : (_accountingPortal$pur5 = _accountingPortal$pur4.status) === null || _accountingPortal$pur5 === void 0 ? void 0 : _accountingPortal$pur5.type,
    message: (accountingPortal === null || accountingPortal === void 0 ? void 0 : (_accountingPortal$pur6 = accountingPortal.purchaseRequest) === null || _accountingPortal$pur6 === void 0 ? void 0 : (_accountingPortal$pur7 = _accountingPortal$pur6.status) === null || _accountingPortal$pur7 === void 0 ? void 0 : _accountingPortal$pur7.message) || message
  } : {};
});

var formatDateTime$2 = function formatDateTime(field) {
  return field ? new Date(field).toLocaleString() : 'N/A';
};

var formatDate$1 = function formatDate(field) {
  return field ? new Date(field).toLocaleDateString() : 'N/A';
};

var getPurchaseRequests = function getPurchaseRequests(purchaseRequests) {
  if (purchaseRequests && Object.keys(purchaseRequests).length > 0) {
    var mappedPurchaseRequests = mapToArrayWithId(purchaseRequests, 'id');
    return mappedPurchaseRequests.map(function (purchaseRequest) {
      return Object.assign({}, purchaseRequest, {
        createdDate: formatDateTime$2(purchaseRequest.createdUTC),
        processedDate: formatDateTime$2(purchaseRequest.processedUTC),
        beginDate: formatDate$1(purchaseRequest.beginDate),
        endDate: formatDate$1(purchaseRequest.endDate)
      });
    });
  }

  return [];
};

var purchaseRequestStatusesSelector = createSelector([purchaseRequestSelector], function (accountingPortal) {
  var _accountingPortal$pur8;

  return getPurchaseRequests(accountingPortal === null || accountingPortal === void 0 ? void 0 : (_accountingPortal$pur8 = accountingPortal.purchaseRequest) === null || _accountingPortal$pur8 === void 0 ? void 0 : _accountingPortal$pur8.requests);
});

var purchaseRequest$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  ROUTE_MAP: ROUTE_MAP$5,
  PURCHASE_REQUEST_ROUTE_PERMISSION: PURCHASE_REQUEST_ROUTE_PERMISSION,
  purchaseRequestSelector: purchaseRequestSelector,
  purchaseRequestRoutesSelector: purchaseRequestRoutesSelector,
  purchaseRequestStatusSelector: purchaseRequestStatusSelector,
  purchaseRequestStatusesSelector: purchaseRequestStatusesSelector
});

var _LOAN_OFFICER_PORTAL_;
var ROUTE_MAP$6 = {
  LOANS: 'LOANS'
};
var LOAN_OFFICER_PORTAL_LOANS_ROUTE_PERMISSIONS = (_LOAN_OFFICER_PORTAL_ = {}, _LOAN_OFFICER_PORTAL_[ROUTE_MAP$6.LOANS] = ['zhl-loan-officer'], _LOAN_OFFICER_PORTAL_);
var accessibleRoutesSelector$1 = getAccessibleRoutesSelector(LOAN_OFFICER_PORTAL_LOANS_ROUTE_PERMISSIONS);

var loans = /*#__PURE__*/Object.freeze({
  __proto__: null,
  ROUTE_MAP: ROUTE_MAP$6,
  accessibleRoutesSelector: accessibleRoutesSelector$1
});

var CLEAR_STORE = 'CLEAR_STORE';
var clearStore = function clearStore() {
  return function (dispatch) {
    return dispatch({
      type: CLEAR_STORE
    });
  };
};

var index$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  CLEAR_STORE: CLEAR_STORE,
  clearStore: clearStore
});

var _MODES_RESULT_TYPE_LO;
var MODES = {
  ADDRESS: 'allAddress',
  COUNTY: 'county',
  CITY: 'city',
  REGION: 'allRegion',
  ZIP: 'zipCode'
};
var MODES_RESULT_TYPE_LOOKUP = (_MODES_RESULT_TYPE_LO = {}, _MODES_RESULT_TYPE_LO[MODES.ADDRESS] = 'Address', _MODES_RESULT_TYPE_LO[MODES.COUNTY] = 'Region', _MODES_RESULT_TYPE_LO[MODES.CITY] = 'Region', _MODES_RESULT_TYPE_LO[MODES.REGION] = 'Region', _MODES_RESULT_TYPE_LO[MODES.ZIP] = 'Region', _MODES_RESULT_TYPE_LO);
var createAutocompleteQuery = function createAutocompleteQuery(mode, query, subdivision, code) {
  return {
    q: query,
    resultTypes: Array.isArray(mode) ? mode.join(',') : mode,
    viewState: subdivision,
    viewZip: code
  };
};
var AUTOCOMPLETE = createActionObject('AUTOCOMPLETE');
var autocompleteThunk = function autocompleteThunk(input, key) {
  return API.autocomplete.GET.thunk({
    action: 'autocomplete/v3/suggestions',
    input: input,
    metadata: key
  }, AUTOCOMPLETE);
};
var countyAutocompleteThunk = function countyAutocompleteThunk(query, subdivision, code) {
  return function (dispatch) {
    return dispatch(autocompleteThunk(createAutocompleteQuery(MODES.COUNTY, query, subdivision, code)));
  };
};
var APPLICABLE_PROPERTIES = ['streetNumber', 'streetName', 'unitNumber', 'city', 'zipCode', 'state', 'county', 'countyFIPS', 'regionType'];

var cleanseAutocompleteOption = function cleanseAutocompleteOption(_ref, county, countyFIPS) {
  var metaData = _ref.metaData,
      display = _ref.display,
      resultType = _ref.resultType;
  return omitBy(Object.assign({}, pick(metaData, APPLICABLE_PROPERTIES), {
    resultType: resultType,
    county: county,
    countyFIPS: countyFIPS,
    display: display
  }), function (val) {
    return val === undefined;
  });
};

var getCompleteResult = function getCompleteResult(option) {
  return function (dispatch) {
    return new Promise(function ($return, $error) {
      var _option$metaData, state, zipCode, _option$metaData2, county, countyFIPS, _await$dispatch, results, _await$dispatch2, _results;

      _option$metaData = option.metaData, state = _option$metaData.state, zipCode = _option$metaData.zipCode;
      _option$metaData2 = option.metaData, county = _option$metaData2.county, countyFIPS = _option$metaData2.countyFIPS;

      if (!county && zipCode) {
        var $Try_1_Post = function () {
          try {
            return $If_3.call(this);
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }.bind(this);

        var $Try_1_Catch = function (_unused) {// We are simply attempting to extend the object, if it can't resolve a county, then perhaps
          // the option was invalid

          try {
            return $Try_1_Post();
          } catch ($boundEx) {
            return $error($boundEx);
          }
        };

        try {
          return Promise.resolve(dispatch(autocompleteThunk(createAutocompleteQuery(MODES.ZIP, zipCode, state)))).then(function ($await_5) {
            try {
              _await$dispatch = $await_5, results = _await$dispatch.results;
              county = results[0].metaData.county;
              return $Try_1_Post();
            } catch ($boundEx) {
              return $Try_1_Catch($boundEx);
            }
          }, $Try_1_Catch);
        } catch (_unused) {
          $Try_1_Catch(_unused);
        }
      }

      function $If_3() {
        if (!countyFIPS && county) {
          var $Try_2_Post = function () {
            try {
              return $If_4.call(this);
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }.bind(this);

          var $Try_2_Catch = function (_unused2) {// Same reasoning as above

            try {
              return $Try_2_Post();
            } catch ($boundEx) {
              return $error($boundEx);
            }
          };

          try {
            return Promise.resolve(dispatch(countyAutocompleteThunk(county, state, zipCode))).then(function ($await_6) {
              try {
                _await$dispatch2 = $await_6, _results = _await$dispatch2.results;
                countyFIPS = _results[0].metaData.countyFIPS;
                return $Try_2_Post();
              } catch ($boundEx) {
                return $Try_2_Catch($boundEx);
              }
            }, $Try_2_Catch);
          } catch (_unused2) {
            $Try_2_Catch(_unused2);
          }
        }

        function $If_4() {
          return $return(cleanseAutocompleteOption(option, county, countyFIPS));
        }

        return $If_4.call(this);
      }

      return $If_3.call(this);
    });
  };
};

var autocomplete = /*#__PURE__*/Object.freeze({
  __proto__: null,
  MODES: MODES,
  MODES_RESULT_TYPE_LOOKUP: MODES_RESULT_TYPE_LOOKUP,
  createAutocompleteQuery: createAutocompleteQuery,
  AUTOCOMPLETE: AUTOCOMPLETE,
  autocompleteThunk: autocompleteThunk,
  countyAutocompleteThunk: countyAutocompleteThunk,
  getCompleteResult: getCompleteResult
});

var DIRECT_LEAD_SUBMITTED = createActionObject('SUBMIT_DIRECT_LEAD');
var submitDirectLead = function submitDirectLead(input, authenticated) {
  if (authenticated === void 0) {
    authenticated = false;
  }

  return API['leads'].POST.thunk({
    action: 'v1/leads/direct',
    input: input,
    authenticated: authenticated
  }, DIRECT_LEAD_SUBMITTED);
}; // we do not want to track any personal data / PII

var TRACKABLE_DIRECT_LEAD_ATTRIBUTES = new Set(['isPrequalificationLead', 'gaClientID', 'stateAbbreviation', 'propertyType', 'propertyUse', 'propertyValue', 'creditScore', 'closingTimelineDays', 'transactionTimeframe', 'journeyStage', 'vaEligible', 'annualIncome', 'monthlyDebts', 'selfEmployed', 'sellingHome', 'hasForeclosure', 'hasBankruptcy', 'details', 'source', 'channel', 'offrampReason', 'eSignatureConsent', 'tcpaConsent']);

var getTrackableDirectLead = function getTrackableDirectLead(directLead) {
  var _trackableLead$hasFor;

  var trackableLead = pick(directLead, _toConsumableArray(TRACKABLE_DIRECT_LEAD_ATTRIBUTES)); // ZHL-4509: hasForeclosure and hasBankruptcy information are lumped together in the hasBankruptcy field in pre-qualify
  // hasForeclosure default value implemented to always include the property in GA event label
  // to be removed once the hasForeclosure property is its own individual form field in pre-qualify
  // ensure to remove associated test in reducers leads-test

  trackableLead.hasForeclosure = (_trackableLead$hasFor = trackableLead.hasForeclosure) !== null && _trackableLead$hasFor !== void 0 ? _trackableLead$hasFor : false;
  return trackableLead.details.purchase ? Object.assign({}, trackableLead, {
    downPaymentPercentage: trackableLead.details.purchase.downPayment / trackableLead.propertyValue,
    loanAmount: trackableLead.propertyValue - trackableLead.details.purchase.downPayment
  }) : Object.assign({}, trackableLead, {
    loanAmount: trackableLead.details.refinance.currentBalance
  });
};

var handleLeadEventTracking = function handleLeadEventTracking(directLead, leadID) {
  trackEvent_1({
    category: 'Mortgages',
    action: 'Contact Zillow Lender',
    label: JSON.stringify(getTrackableDirectLead(directLead))
  });
  trackEvent_1({
    category: 'Mortgages',
    action: 'Pre-Approval Submission Success',
    label: leadID
  });
  return directLead;
};

/**
 * @function camelCaseToSentenceCaseLastProperty
 * @param {String} path period-concatenated path, ie address.zip
 * @returns {String} path as Sentence Case
 */
var camelCaseToSentenceCaseLastProperty = function camelCaseToSentenceCaseLastProperty(path) {
  var lastProperty = path.split('.').slice(-1)[0];
  var lowerCaseFirstSentenceCase = lastProperty.replace(/([A-Z])/g, ' $1').toLowerCase();
  return lowerCaseFirstSentenceCase.charAt(0).toUpperCase() + lowerCaseFirstSentenceCase.slice(1);
};

var int = parsers.int;
var trackUserSessionID = function trackUserSessionID() {
  return function (dispatch, getState) {
    var userSessionID = userSessionIDSelector(getState());
    trackEvent_1({
      category: 'Mortgages',
      action: 'Long Flow Landing',
      label: userSessionID !== null && userSessionID !== void 0 ? userSessionID : null
    });
  };
};
var submitLongFlowLead = function submitLongFlowLead(inputs) {
  return function (dispatch) {
    return new Promise(function ($return, $error) {
      var request, response;
      request = Object.assign({}, inputs, {
        gaClientID: getGAClientID(),
        phoneNumber: toPhoneString(inputs.phoneNumber),
        annualIncome: inputs.annualIncome || 0
      });
      return Promise.resolve(dispatch(submitDirectLead(request))).then(function ($await_1) {
        try {
          response = $await_1;
          handleLeadEventTracking(request, response.id);
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  };
};
var SET_QUESTION_RESPONSE = 'SET_QUESTION_RESPONSE';
var setQuestionResponse = function setQuestionResponse(key, value) {
  return {
    type: SET_QUESTION_RESPONSE,
    payload: {
      key: key,
      value: value
    }
  };
};
var setLongFlowLoanType = function setLongFlowLoanType(loanType) {
  return setQuestionResponse('loanType', loanType);
};

var formatServerErrors = function formatServerErrors(_ref) {
  var detail = _ref.detail;
  var errors = (detail !== null && detail !== void 0 ? detail : []).reduce(function (acc, _ref2) {
    var _Object$assign;

    var loc = _ref2.loc,
        msg = _ref2.msg;

    var _loc = _slicedToArray(loc, 2),
        field = _loc[1];

    var titleCaseField = camelCaseToSentenceCaseLastProperty(loc.join('.')); // Relying on translating server messages skeeves me out but since various errors like (555) 555-5555
    // not being a valid phone number can't be caught by Yup and we need to rely on the server errors to determine
    // what went wrong. The only alternative to this is to use a default laconic error message, but that doesn't
    // allow the user to understand why their submission failed.

    return Object.assign({}, acc, (_Object$assign = {}, _Object$assign[field] = msg.replace('value', titleCaseField), _Object$assign));
  }, {});
  return errors;
};

var CLEAR_LONGFLOW_ERROR = 'CLEAR_LONGFLOW_ERROR';
var clearLongFlowError = function clearLongFlowError() {
  return {
    type: CLEAR_LONGFLOW_ERROR
  };
};
var CLEAR_LONGFLOW_ERROR_FIELD = 'CLEAR_LONGFLOW_ERROR_FIELD';
var clearLongFlowErrorField = function clearLongFlowErrorField(field) {
  return {
    type: CLEAR_LONGFLOW_ERROR_FIELD,
    payload: {
      field: field
    }
  };
};
var DEFAULT_STATE$e = {
  responses: {
    downPayment: 0,
    annualIncome: null
  },
  selectedOpening: null,
  longFlowError: null
};
var longFlow$1 = (function (state, _ref3) {
  if (state === void 0) {
    state = DEFAULT_STATE$e;
  }

  var type = _ref3.type,
      payload = _ref3.payload,
      key = _ref3.key;

  if (type === REHYDRATE && key === 'longFlow') {
    // strangely enough, even though longFlowError: null should clear longFlowError, it still persists unless we
    // delete the payload
    payload === null || payload === void 0 ? true : delete payload.longFlowError;
    return Object.assign({}, state, payload, {
      longFlowError: null
    });
  }

  switch (type) {
    case SET_QUESTION_RESPONSE:
      var _Object$assign2;

      var downPayment;

      if (payload.key === 'downPayment') {
        downPayment = payload.value;
      } else if (payload.key === 'budget' && !state.downPaymentDirty) {
        downPayment = int(payload.value * 0.2);
      } else {
        downPayment = state.responses.downPayment;
      }

      return Object.assign({}, state, {
        downPaymentDirty: state.downPaymentDirty || payload.key === 'downPayment',
        responses: Object.assign({}, state.responses, (_Object$assign2 = {}, _Object$assign2[payload.key] = payload.value, _Object$assign2.downPayment = downPayment, _Object$assign2))
      });

    case DIRECT_LEAD_SUBMITTED.BEGIN:
      return Object.assign({}, state, {
        longFlowError: null,
        leadSubmissionInProgress: true
      });

    case DIRECT_LEAD_SUBMITTED.SUCCESS:
      return Object.assign({}, state, {
        leadSubmitted: true,
        applicationURL: payload.applicationURL,
        longFlowError: null,
        leadSubmissionInProgress: false
      });

    case DIRECT_LEAD_SUBMITTED.FAILURE:
      return Object.assign({}, state, {
        longFlowError: toYupSchema(formatServerErrors(payload)),
        leadSubmissionInProgress: false
      });

    case CLEAR_LONGFLOW_ERROR_FIELD:
      var _state = state,
          longFlowError = _state.longFlowError;

      if (longFlowError) {
        delete longFlowError[payload.field];
      }

      return Object.assign({}, state, {
        longFlowError: longFlowError
      });

    case CLEAR_LONGFLOW_ERROR:
      return Object.assign({}, state, {
        longFlowError: null
      });

    default:
      return state;
  }
});

var longFlow$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  trackUserSessionID: trackUserSessionID,
  submitLongFlowLead: submitLongFlowLead,
  setQuestionResponse: setQuestionResponse,
  setLongFlowLoanType: setLongFlowLoanType,
  clearLongFlowError: clearLongFlowError,
  clearLongFlowErrorField: clearLongFlowErrorField,
  DEFAULT_STATE: DEFAULT_STATE$e,
  'default': longFlow$1
});

var getErrorTypeString = function getErrorTypeString(prefix, field) {
  return prefix ? prefix + "_SET_ERROR_" + field : "SET_ERROR_" + field;
};

var getSetTypeString = function getSetTypeString(prefix, field) {
  return prefix ? prefix + "_SET_INPUT_" + field : "SET_INPUT_" + field;
};

var VALIDATE_ALL_FIELDS = 'VALIDATE_ALL_FIELDS';
var validateAllFields = function validateAllFields() {
  return {
    type: VALIDATE_ALL_FIELDS
  };
};
var createFieldActionCreators = function createFieldActionCreators(fields, schema, prefix) {
  return fields.reduce(function (prev, field) {
    var _Object$assign, _Object$assign2, _Object$assign3, _Object$assign4, _Object$assign5;

    return {
      actionCreators: Object.assign({}, prev.actionCreators, (_Object$assign = {}, _Object$assign[field] = function (payload) {
        return function (dispatch) {
          schema && schema[field] && dispatch({
            type: getErrorTypeString(prefix, field),
            payload: schema[field](payload)
          });
          return dispatch({
            type: getSetTypeString(prefix, field),
            payload: payload
          });
        };
      }, _Object$assign)),
      actionTypes: Object.assign({}, prev.actionTypes, (_Object$assign2 = {}, _Object$assign2[field] = getSetTypeString(prefix, field), _Object$assign2)),
      actionToFieldMap: Object.assign({}, prev.actionToFieldMap, (_Object$assign3 = {}, _Object$assign3[getSetTypeString(prefix, field)] = field, _Object$assign3)),
      errorTypes: Object.assign({}, prev.errorTypes, (_Object$assign4 = {}, _Object$assign4[field] = getErrorTypeString(prefix, field), _Object$assign4)),
      errorToFieldMap: Object.assign({}, prev.errorToFieldMap, (_Object$assign5 = {}, _Object$assign5[getErrorTypeString(prefix, field)] = field, _Object$assign5))
    };
  }, {
    actionCreators: {
      validateAllFields: function validateAllFields() {
        return {
          type: prefix ? prefix + "_VALIDATE_FIELDS" : 'VALIDATE_FIELDS',
          schema: schema
        };
      }
    },
    actionTypes: {
      VALIDATE: prefix ? prefix + "_VALIDATE_FIELDS" : 'VALIDATE_FIELDS'
    }
  });
};
var fieldReducerOrPassThrough = function fieldReducerOrPassThrough(actionToFieldMap, errorToFieldMap, actionTypes) {
  if (errorToFieldMap === void 0) {
    errorToFieldMap = {};
  }

  if (actionTypes === void 0) {
    actionTypes = {};
  }

  return function (state, _ref) {
    var type = _ref.type,
        _ref$payload = _ref.payload,
        payload = _ref$payload === void 0 ? {} : _ref$payload,
        schema = _ref.schema;

    if (actionToFieldMap[type]) {
      var _Object$assign6, _Object$assign7;

      return Object.assign({}, state, {
        fields: Object.assign({}, state.fields, (_Object$assign6 = {}, _Object$assign6[actionToFieldMap[type]] = payload, _Object$assign6)),
        dirtyFields: Object.assign({}, state.dirtyFields, (_Object$assign7 = {}, _Object$assign7[actionToFieldMap[type]] = true, _Object$assign7))
      });
    } else if (errorToFieldMap[type] && payload !== null) {
      var _Object$assign8;

      return Object.assign({}, state, {
        errors: Object.assign({}, state.errors, (_Object$assign8 = {}, _Object$assign8[errorToFieldMap[type]] = payload.message, _Object$assign8))
      });
    } else if (actionTypes.VALIDATE === type || VALIDATE_ALL_FIELDS === type) {
      return Object.assign({}, state, {
        errors: Object.keys(schema).reduce(function (errors, field) {
          var _Object$assign9;

          return Object.assign({}, errors, (_Object$assign9 = {}, _Object$assign9[field] = schema[field](state.fields[field]), _Object$assign9));
        }, state.errors)
      });
    } else {
      return state;
    }
  };
};

var fieldReducer = /*#__PURE__*/Object.freeze({
  __proto__: null,
  VALIDATE_ALL_FIELDS: VALIDATE_ALL_FIELDS,
  validateAllFields: validateAllFields,
  createFieldActionCreators: createFieldActionCreators,
  fieldReducerOrPassThrough: fieldReducerOrPassThrough
});

var calculateAffordability = affordability.default,
    affordabilityDefaults$1 = affordability.affordabilityDefaults;

var getCalculatedAffordabiltyValues = function getCalculatedAffordabiltyValues(currentFields) {
  var affordabilityValues = affordabilityDefaults$1(Object.assign({}, currentFields, {
    includeHomeownersInsurance: currentFields.includeTaxesInsurance,
    includeHOA: true,
    includePropertyTax: currentFields.includeTaxesInsurance
  }));
  var affordabilityOutputs = calculateAffordability(affordabilityValues);
  return Object.assign({}, affordabilityOutputs, {
    monthlyPayment: mortgageCalculations(Object.assign({
      homePrice: affordabilityOutputs.affordabilityByDTI
    }, affordabilityValues))['monthlyPayment']
  });
};

var fields$5 = {
  annualIncome: 70000,
  maximumPayment: 3500,
  monthlyDebts: 250,
  downPayment: 20000,
  dti: 36,
  rate: 5,
  term: 360,
  propertyTaxRate: 1.2,
  annualHomeownersInsurance: 225000 * 0.00035 * 12,
  monthlyHOA: 0,
  includePMI: true,
  includeTaxesInsurance: true,
  customPayment: false,
  monthlyPayment: 1850
};
var DEFAULT_STATE$f = {
  fields: fields$5,
  calculatedAffordabilityValues: getCalculatedAffordabiltyValues(fields$5)
};

var _createFieldActionCre = createFieldActionCreators(Object.keys(DEFAULT_STATE$f.fields), null, 'affordability'),
    actionCreators = _createFieldActionCre.actionCreators,
    actionTypes = _createFieldActionCre.actionTypes,
    actionToFieldMap = _createFieldActionCre.actionToFieldMap;

var defaultCaseReducer = fieldReducerOrPassThrough(actionToFieldMap);
var AFFORDABILITY_ACTION_TYPES = actionTypes;
var updateFieldActions = actionCreators;
var affordability$2 = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$f;
  }

  var type = _ref.type,
      payload = _ref.payload;

  if (type in actionToFieldMap) {
    var updatedState = defaultCaseReducer(state, {
      type: type,
      payload: payload
    });
    return Object.assign({}, updatedState, {
      calculatedAffordabilityValues: getCalculatedAffordabiltyValues(updatedState.fields)
    });
  }

  switch (type) {
    default:
      return state;
  }
});

var affordability$3 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  DEFAULT_STATE: DEFAULT_STATE$f,
  AFFORDABILITY_ACTION_TYPES: AFFORDABILITY_ACTION_TYPES,
  updateFieldActions: updateFieldActions,
  'default': affordability$2
});

var LOAN_TERM_IN_MONTHS$1 = constants.LOAN_TERM_IN_MONTHS,
    LOAN_PROGRAMS$1 = constants.LOAN_PROGRAMS;
var fields$6 = {
  loanAmount: 200000,
  rate: 5,
  term: LOAN_TERM_IN_MONTHS$1[LOAN_PROGRAMS$1.fixed30Year],
  originationYear: new Date().getFullYear() - 6,
  newLoanAmount: 195000,
  newTerm: LOAN_TERM_IN_MONTHS$1[LOAN_PROGRAMS$1.fixed30Year],
  newRate: 4.35,
  fees: 1000,
  cashOut: 0,
  rollFees: false
};
var DEFAULT_STATE$g = {
  fields: fields$6,
  calculatedRefinanceValues: refinance$1.default(fields$6)
};

var _createFieldActionCre$1 = createFieldActionCreators(Object.keys(DEFAULT_STATE$g.fields), null, 'refinance'),
    actionCreators$1 = _createFieldActionCre$1.actionCreators,
    actionTypes$1 = _createFieldActionCre$1.actionTypes,
    actionToFieldMap$1 = _createFieldActionCre$1.actionToFieldMap;

var defaultCaseReducer$1 = fieldReducerOrPassThrough(actionToFieldMap$1);
var REFINANCE_ACTION_TYPES = actionTypes$1;
var updateFieldActions$1 = actionCreators$1;
var refinance$2 = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$g;
  }

  var type = _ref.type,
      payload = _ref.payload;

  if (type in actionToFieldMap$1) {
    var updatedState = defaultCaseReducer$1(state, {
      type: type,
      payload: payload
    });
    return Object.assign({}, updatedState, {
      calculatedRefinanceValues: refinance$1.default(updatedState.fields)
    });
  }

  switch (type) {
    default:
      return state;
  }
});

var refinance$3 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  DEFAULT_STATE: DEFAULT_STATE$g,
  REFINANCE_ACTION_TYPES: REFINANCE_ACTION_TYPES,
  updateFieldActions: updateFieldActions$1,
  'default': refinance$2
});

var DEFAULT_STATE$h = {
  fields: {
    homePrice: 225000,
    loanProgram: constants.LOAN_PROGRAMS.fixed30Year,
    interestRate: 5,
    downPayment: 45000,
    downPaymentPercent: 20,
    homeInsurance: 225000 * 0.00035 * 12,
    taxRate: 1.2,
    hoa: 0,
    includeTaxesInsurance: true,
    includePMI: true
  }
};
var UPDATE_MORTGAGE_CALCULATOR_FIELDS = 'UPDATE_MORTGAGE_CALCULATOR_FIELDS';
var updateFields$5 = function updateFields(fields, errors) {
  if (errors === void 0) {
    errors = {};
  }

  return {
    type: 'UPDATE_MORTGAGE_CALCULATOR_FIELDS',
    payload: {
      fields: fields,
      errors: errors
    }
  };
};
var mortgage$1 = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$h;
  }

  var type = _ref.type,
      payload = _ref.payload;

  switch (type) {
    case UPDATE_MORTGAGE_CALCULATOR_FIELDS:
      {
        var fields = payload.fields,
            errors = payload.errors;
        return Object.keys(errors).length > 0 ? state : Object.assign({}, state, {
          fields: fields
        });
      }

    default:
      return state;
  }
});

var mortgage$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  DEFAULT_STATE: DEFAULT_STATE$h,
  UPDATE_MORTGAGE_CALCULATOR_FIELDS: UPDATE_MORTGAGE_CALCULATOR_FIELDS,
  updateFields: updateFields$5,
  'default': mortgage$1
});

var _excluded$i = ["name", "value"];
var fields$7 = {
  name: null,
  friendlyName: null,
  description: null,
  category: null,
  schema: null,
  uiHint: null,
  readRoles: null,
  writeRoles: null,
  value: null
};
var SET_SETTING_EDITING = 'SET_SETTING_EDITING';
var setSettingEditing = function setSettingEditing(setting) {
  return {
    type: SET_SETTING_EDITING,
    payload: setting
  };
};
var CLEAR_SETTING_EDITING = 'CLEAR_SETTING_EDITING';
var clearSettingEditing = function clearSettingEditing() {
  return {
    type: CLEAR_SETTING_EDITING
  };
};
var FETCH_SETTINGS_VALUES = createActionObject('FETCH_SETTINGS_VALUES');
var fetchSettingsValues = function fetchSettingsValues() {
  return API.settings.GET.thunk({
    action: 'v1/values',
    authenticated: true,
    handleLogin: false
  }, FETCH_SETTINGS_VALUES);
};
var FETCH_PUBLIC_SETTINGS_VALUE = createActionObject('FETCH_PUBLIC_SETTING_VALUE');
var fetchPublicSettingsValues = function fetchPublicSettingsValues(key) {
  return API.settings.GET.thunk({
    action: "v1/public/values/" + key,
    metadata: {
      key: key
    }
  }, FETCH_PUBLIC_SETTINGS_VALUE);
};
var REFINANCE_KEYS = {
  UPDATED: 'website.sample_rate.updated',
  THIRTY_YEAR_RATE: 'website.sample_rate.thirty_yr.rate',
  THIRTY_YEAR_APR: 'website.sample_rate.thirty_yr.apr',
  THIRTY_YEAR_PAYMENT: 'website.sample_rate.thirty_yr.payment',
  FIFTEEN_YEAR_RATE: 'website.sample_rate.fifteen_yr.rate',
  FIFTEEN_YEAR_APR: 'website.sample_rate.fifteen_yr.apr',
  FIFTEEN_YEAR_PAYMENT: 'website.sample_rate.fifteen_yr.payment'
};
var fetchRefinanceLoansSettings = function fetchRefinanceLoansSettings() {
  return function () {
    for (var _len = arguments.length, thunkArgs = new Array(_len), _key = 0; _key < _len; _key++) {
      thunkArgs[_key] = arguments[_key];
    }

    Promise.all(Object.values(REFINANCE_KEYS).map(function (key) {
      return fetchPublicSettingsValues(key).apply(void 0, thunkArgs);
    }));
  };
};
var FETCH_SETTINGS_DEFINITIONS = createActionObject('FETCH_SETTINGS_DEFINITIONS');
var fetchSettingsDefinitions = function fetchSettingsDefinitions() {
  return API.settings.GET.thunk({
    action: 'v1/definitions',
    authenticated: true,
    handleLogin: false
  }, FETCH_SETTINGS_DEFINITIONS);
};
var SET_FILTER_VALUE = 'SET_FILTER_VALUE';
var setFilterValue = function setFilterValue(value) {
  return {
    type: SET_FILTER_VALUE,
    payload: value
  };
};
var SET_SETTING_VALUE = createActionObject('SET_SETTING_VALUE');
var setSettingValue = function setSettingValue(key, value) {
  return API.settings.PUT.thunk({
    action: "v1/values/" + key,
    input: {
      value: value
    },
    metadata: {
      key: key,
      value: value
    },
    authenticated: true,
    handleLogin: false
  }, SET_SETTING_VALUE);
};
var CREATE_OR_UPDATE_SETTING = createActionObject('CREATE_OR_UPDATE_SETTING');
var createOrUpdateSetting = function createOrUpdateSetting(name, definition, value) {
  return API.settings.PUT.thunk({
    action: "v1/definitions/" + name,
    input: {
      definition: definition,
      value: value
    },
    authenticated: true,
    metadata: {
      name: name
    },
    handleLogin: false
  }, CREATE_OR_UPDATE_SETTING);
};
var createOrUpdateSettingFromFormBuilder = function createOrUpdateSettingFromFormBuilder(isNew) {
  return function (dispatch, getState) {
    var _settingsRequestSelec = settingsRequestSelector(getState()),
        name = _settingsRequestSelec.name,
        value = _settingsRequestSelec.value,
        definition = _objectWithoutProperties(_settingsRequestSelec, _excluded$i);

    return dispatch(createOrUpdateSetting(name, definition, isNew ? value : null));
  };
};
var DELETE_SETTING = createActionObject('DELETE_SETTING');
var deleteSetting = function deleteSetting(name) {
  return API.settings.DELETE.thunk({
    action: "v1/definitions/" + name,
    authenticated: true,
    handleLogin: false
  }, DELETE_SETTING);
};

var extractCategoriesFromDefinitions = function extractCategoriesFromDefinitions(definitions) {
  if (definitions === void 0) {
    definitions = {};
  }

  return Object.keys(definitions).reduce(function (categories, key) {
    var _Object$assign;

    var category = definitions[key].category;
    return Object.assign({}, categories, (_Object$assign = {}, _Object$assign[category] = categories[category] ? categories[category].concat([key]) : [key], _Object$assign));
  }, {});
};

var DEFAULT_STATE$i = {
  values: null,
  fetchValueErrors: {},
  definitions: null,
  categories: null,
  fields: fields$7
};
var settings$1 = (function (state, _ref) {
  var _Object$assign4, _Object$assign5;

  if (state === void 0) {
    state = DEFAULT_STATE$i;
  }

  var _ref$payload = _ref.payload,
      payload = _ref$payload === void 0 ? {} : _ref$payload,
      type = _ref.type,
      metadata = _ref.metadata;

  switch (type) {
    case CLEAR_SETTING_EDITING:
      return Object.assign({}, state, {
        fields: fields$7
      });

    case SET_SETTING_EDITING:
      return Object.assign({}, state, {
        fields: payload
      });

    case FETCH_SETTINGS_VALUES.SUCCESS:
      return Object.assign({}, state, {
        values: payload.values
      });

    case FETCH_PUBLIC_SETTINGS_VALUE.SUCCESS:
      {
        var _Object$assign2, _Object$assign3;

        return Object.assign({}, state, {
          values: Object.assign({}, state.values, (_Object$assign2 = {}, _Object$assign2[metadata.key] = payload.value, _Object$assign2)),
          fetchValueErrors: Object.assign({}, state.fetchValueErrors, (_Object$assign3 = {}, _Object$assign3[metadata.key] = undefined, _Object$assign3))
        });
      }

    case FETCH_PUBLIC_SETTINGS_VALUE.FAILURE:
      return Object.assign({}, state, {
        fetchValueErrors: Object.assign({}, state.fetchValueErrors, (_Object$assign4 = {}, _Object$assign4[metadata.key] = payload, _Object$assign4))
      });

    case SET_SETTING_VALUE.SUCCESS:
      return Object.assign({}, state, {
        values: Object.assign({}, state.values, (_Object$assign5 = {}, _Object$assign5[metadata.key] = metadata.value, _Object$assign5))
      });

    case FETCH_SETTINGS_DEFINITIONS.SUCCESS:
      return Object.assign({}, state, {
        definitions: payload.definitions,
        categories: extractCategoriesFromDefinitions(payload.definitions)
      });

    case SET_FILTER_VALUE:
      return Object.assign({}, state, {
        filter: payload
      });

    default:
      return state;
  }
});

var settings$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  SET_SETTING_EDITING: SET_SETTING_EDITING,
  setSettingEditing: setSettingEditing,
  CLEAR_SETTING_EDITING: CLEAR_SETTING_EDITING,
  clearSettingEditing: clearSettingEditing,
  FETCH_SETTINGS_VALUES: FETCH_SETTINGS_VALUES,
  fetchSettingsValues: fetchSettingsValues,
  FETCH_PUBLIC_SETTINGS_VALUE: FETCH_PUBLIC_SETTINGS_VALUE,
  fetchPublicSettingsValues: fetchPublicSettingsValues,
  REFINANCE_KEYS: REFINANCE_KEYS,
  fetchRefinanceLoansSettings: fetchRefinanceLoansSettings,
  FETCH_SETTINGS_DEFINITIONS: FETCH_SETTINGS_DEFINITIONS,
  fetchSettingsDefinitions: fetchSettingsDefinitions,
  SET_FILTER_VALUE: SET_FILTER_VALUE,
  setFilterValue: setFilterValue,
  SET_SETTING_VALUE: SET_SETTING_VALUE,
  setSettingValue: setSettingValue,
  CREATE_OR_UPDATE_SETTING: CREATE_OR_UPDATE_SETTING,
  createOrUpdateSetting: createOrUpdateSetting,
  createOrUpdateSettingFromFormBuilder: createOrUpdateSettingFromFormBuilder,
  DELETE_SETTING: DELETE_SETTING,
  deleteSetting: deleteSetting,
  DEFAULT_STATE: DEFAULT_STATE$i,
  'default': settings$1
});

var _constants$loanFile$d = constants.loanFile,
    LOAN_STATUS_LOOKUP$1 = _constants$loanFile$d.LOAN_STATUS_LOOKUP,
    LOAN_EDITOR_LOOKUP$1 = _constants$loanFile$d.LOAN_EDITOR_LOOKUP;
var SUBMIT_APPLICATION = createActionObject('SUBMIT_APPLICATION');

var submitApplicationThunk = function submitApplicationThunk(loanID) {
  return API['loan-file'].POST.thunk({
    action: "v1/loans/" + loanID + "/actions/submit",
    authenticated: true
  }, SUBMIT_APPLICATION);
};

var submitApplication = function submitApplication(props) {
  return function (dispatch) {
    var loanID = loanIDPropSelector(null, props);
    return dispatch(submitApplicationThunk(loanID));
  };
};
var API_REQUEST_DENIAL = createActionObject('API_REQUEST_DENIAL');

var apiRequestDenial = function apiRequestDenial(loanID, request) {
  return API['loan-file'].POST.thunk({
    action: "v1/loans/" + loanID + "/actions/request-denial",
    input: {
      request: request
    },
    authenticated: true
  }, API_REQUEST_DENIAL);
};

var API_REQUEST_WITHDRAWAL = createActionObject('API_REQUEST_WITHDRAWAL');

var apiRequestWithdrawal = function apiRequestWithdrawal(loanID, request) {
  return API['loan-file'].POST.thunk({
    action: "v1/loans/" + loanID + "/actions/request-withdrawal",
    input: {
      request: request
    },
    authenticated: true
  }, API_REQUEST_WITHDRAWAL);
};

var requestDenial = function requestDenial(request, props) {
  return function (dispatch, getState) {
    var loanID = loanIDPropSelector(getState(), props);
    return dispatch(apiRequestDenial(loanID, request));
  };
};
var requestWithdrawal = function requestWithdrawal(request, props) {
  return function (dispatch, getState) {
    var loanID = loanIDPropSelector(getState(), props);
    return dispatch(apiRequestWithdrawal(loanID, request));
  };
};
var takeOverLoan = function takeOverLoan(props) {
  return function (dispatch, getState) {
    var loanID = loanIDPropSelector(getState(), props);
    return dispatch(apiUpdateLoanEditor(loanID, LOAN_EDITOR_LOOKUP$1.LENDER));
  };
};
var DEFAULT_STATE$j = {
  status: null,
  editor: null
};
var status = function status(state, action) {
  if (state === void 0) {
    state = DEFAULT_STATE$j.status;
  }

  switch (action.type) {
    case FETCH_LOAN.SUCCESS:
      return action.payload.loan.status;

    case FETCH_LOAN_STATUS.SUCCESS:
      return action.payload.status;

    case API_REQUEST_DENIAL.SUCCESS:
      return LOAN_STATUS_LOOKUP$1.DENIAL_REQUESTED;

    case API_REQUEST_WITHDRAWAL.SUCCESS:
      return LOAN_STATUS_LOOKUP$1.WITHDRAWAL_REQUESTED;

    default:
      return state;
  }
};
var editor = function editor(state, action) {
  if (state === void 0) {
    state = DEFAULT_STATE$j.editor;
  }

  switch (action.type) {
    case FETCH_LOAN.SUCCESS:
      return action.payload.loan.editor;

    case API_UPDATE_LOAN_EDITOR.SUCCESS:
      return action.request.editor;

    case FETCH_LOAN_STATUS.SUCCESS:
      return action.payload.editor;

    default:
      return state;
  }
};

var loanActions$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  SUBMIT_APPLICATION: SUBMIT_APPLICATION,
  submitApplication: submitApplication,
  API_REQUEST_DENIAL: API_REQUEST_DENIAL,
  API_REQUEST_WITHDRAWAL: API_REQUEST_WITHDRAWAL,
  requestDenial: requestDenial,
  requestWithdrawal: requestWithdrawal,
  takeOverLoan: takeOverLoan,
  DEFAULT_STATE: DEFAULT_STATE$j,
  status: status,
  editor: editor
});

var ERRORS = constants.loanFile.ERRORS;
var API_UPDATE_LOAN_ATTRIBUTES = createActionObject('API_UPDATE_LOAN_ATTRIBUTES');
var apiUpdateLoanAttributes = function apiUpdateLoanAttributes(loanID, attributes) {
  return API['loan-file'].PUT.thunk({
    action: "v1/loans/" + loanID + "/attributes",
    input: {
      attributes: attributes
    },
    authenticated: true
  }, API_UPDATE_LOAN_ATTRIBUTES);
};
var AUTOCOMPLETE_KEY = 'loanAttributes';
var updateLoanAttributes = function updateLoanAttributes(props, loanAttributes) {
  return function (dispatch, getState) {
    return new Promise(function ($return, $error) {
      var loanID, addressCountyFIPS, countyFIPSRequest, attributes;
      loanID = loanIDPropSelector(null, props);
      addressCountyFIPS = null;

      if (loanAttributes !== null && loanAttributes !== void 0 && loanAttributes.addressCounty) {
        countyFIPSRequest = createAutocompleteQuery(MODES.COUNTY, loanAttributes.addressCounty, loanAttributes.addressState, loanAttributes.addressZIP);
        return Promise.resolve(dispatch(autocompleteThunk(countyFIPSRequest, AUTOCOMPLETE_KEY))).then(function ($await_2) {
          try {
            addressCountyFIPS = propertyCountyFIPSSelector(getState());
            return $If_1.call(this);
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }.bind(this), $error);
      }

      function $If_1() {
        attributes = toAPI$1(Object.assign({}, loanAttributes, {
          addressCountyFIPS: addressCountyFIPS
        }));
        return Promise.resolve(dispatch(apiUpdateLoanAttributes(loanID, attributes))).then(function ($await_3) {
          try {
            return $return(dispatch(fetchLoan(loanID)));
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }, $error);
      }

      return $If_1.call(this);
    });
  };
};
var DEFAULT_STATE$k = {
  attributes: {},
  computed: {},
  errors: {}
};
var loanAttributes$1 = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$k;
  }

  var type = _ref.type,
      payload = _ref.payload,
      metadata = _ref.metadata,
      request = _ref.request;

  switch (type) {
    case AUTOCOMPLETE.SUCCESS:
      if (metadata === AUTOCOMPLETE_KEY) {
        var _payload$results$0$me, _payload$results, _payload$results$, _payload$results$$met;

        var countyFIPS = (_payload$results$0$me = payload === null || payload === void 0 ? void 0 : (_payload$results = payload.results) === null || _payload$results === void 0 ? void 0 : (_payload$results$ = _payload$results[0]) === null || _payload$results$ === void 0 ? void 0 : (_payload$results$$met = _payload$results$.metaData) === null || _payload$results$$met === void 0 ? void 0 : _payload$results$$met.countyFIPS) !== null && _payload$results$0$me !== void 0 ? _payload$results$0$me : null;
        return Object.assign({}, state, {
          propertyCountyFIPS: countyFIPS,
          errors: Object.assign({}, state.errors, {
            propertyCountyFIPS: countyFIPS === null ? ERRORS.INVALID_COUNTY : null
          })
        });
      }

      return state;

    case AUTOCOMPLETE.FAILURE:
      if (metadata === AUTOCOMPLETE_KEY) {
        return Object.assign({}, state, {
          errors: Object.assign({}, state.errors, {
            propertyCountyFIPS: ERRORS.INVALID_COUNTY
          })
        });
      }

      return state;

    case FETCH_LOAN.SUCCESS:
      return Object.assign({}, state, {
        computed: payload.loan.computed,
        attributes: fromAPI$1(payload.loan)
      });

    case API_UPDATE_LOAN_ATTRIBUTES.SUCCESS:
      return Object.assign({}, state, {
        attributes: Object.assign({}, state.attributes, fromAPI$1(request))
      });

    default:
      return state;
  }
});

var loanAttributes$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  API_UPDATE_LOAN_ATTRIBUTES: API_UPDATE_LOAN_ATTRIBUTES,
  apiUpdateLoanAttributes: apiUpdateLoanAttributes,
  updateLoanAttributes: updateLoanAttributes,
  DEFAULT_STATE: DEFAULT_STATE$k,
  'default': loanAttributes$1
});

var apiUpdateBorrowerAttributes = function apiUpdateBorrowerAttributes(attributes, borrowerID, action) {
  return API['loan-file'].PUT.thunk({
    action: "v1/borrowers/" + borrowerID + "/attributes",
    input: {
      attributes: attributes
    },
    authenticated: true,
    jsonEncoder: toJSON,
    metadata: {
      borrowerID: borrowerID
    }
  }, action);
};

var _excluded$j = ["creditConsent"];
var CONSENT_TYPES = constants.loanFile.CONSENT_TYPES;
var FETCH_BORROWERS = createActionObject('FETCH_BORROWERS');
var fetchBorrowers = function fetchBorrowers(loanIDs) {
  return API['loan-file'].GET.thunk({
    action: 'v1/borrowers',
    input: {
      loanID: loanIDs
    },
    authenticated: true
  }, FETCH_BORROWERS);
};
var BLEND_EMAIL_ENDPOINT = 'activate';
var SEND_BLEND_EMAIL = createActionObject('SEND_BLEND_EMAIL');
var sendBlendEmail = function sendBlendEmail(_ref) {
  var loanID = _ref.loanID,
      borrowerID = _ref.borrowerID;
  return API[BLEND_EMAIL_ENDPOINT].POST.thunk({
    action: 'resend-email',
    input: {
      loanID: loanID,
      borrowerID: borrowerID
    },
    metadata: {
      borrowerID: borrowerID
    },
    authenticated: true
  }, SEND_BLEND_EMAIL);
};
var createBorrower = function createBorrower(props, borrower) {
  return function (dispatch) {
    var loanID = loanIDPropSelector(null, props);

    var _toAPI = toAPI(borrower !== null && borrower !== void 0 ? borrower : {}),
        attributes = _toAPI.attributes;

    return dispatch(apiCreateBorrower(loanID, attributes));
  };
};
var REVOKE_CREDIT_CONSENT = createActionObject('REVOKE_CREDIT_CONSENT');
var revokeCreditConsentThunk = function revokeCreditConsentThunk(borrowerID) {
  return API['loan-file'].POST.thunk({
    action: "v1/borrowers/" + borrowerID + "/actions/revoke-credit-consent",
    authenticated: true,
    metadata: {
      borrowerID: borrowerID
    }
  }, REVOKE_CREDIT_CONSENT);
};
var GRANT_CREDIT_CONSENT = createActionObject('GRANT_CREDIT_CONSENT');
var grantCreditConsentThunk = function grantCreditConsentThunk(method, borrowerID) {
  return API['loan-file'].POST.thunk({
    action: "v1/borrowers/" + borrowerID + "/actions/grant-credit-consent",
    input: {
      method: method
    },
    authenticated: true,
    metadata: {
      borrowerID: borrowerID
    }
  }, GRANT_CREDIT_CONSENT);
};
var GRANT_SOFT_CREDIT_CONSENT = createActionObject('GRANT_SOFT_CREDIT_CONSENT');
var grantSoftCreditConsentThunk = function grantSoftCreditConsentThunk(method, borrowerID) {
  return API['loan-file'].POST.thunk({
    action: "v1/borrowers/" + borrowerID + "/actions/grant-soft-credit-consent",
    input: {
      method: method
    },
    authenticated: true
  }, GRANT_SOFT_CREDIT_CONSENT);
};
var grantOrRevokeCreditConsent = function grantOrRevokeCreditConsent(creditConsent, borrowerID) {
  return CONSENT_TYPES[creditConsent === null || creditConsent === void 0 ? void 0 : creditConsent.method] ? grantCreditConsentThunk(creditConsent.method, borrowerID) : revokeCreditConsentThunk(borrowerID);
};
var REVOKE_ECONSENT = createActionObject('REVOKE_ECONSENT');
var revokeEConsentThunk = function revokeEConsentThunk(borrowerID) {
  return API['loan-file'].POST.thunk({
    action: "v1/borrowers/" + borrowerID + "/actions/revoke-e-consent",
    authenticated: true
  }, REVOKE_ECONSENT);
};
var revokeEConsent = function revokeEConsent(props) {
  return function (dispatch) {
    var borrowerID = borrowerIDPropSelector(null, props);
    return dispatch(revokeEConsentThunk(borrowerID));
  };
};
var API_UPDATE_BORROWER = createActionObject('API_UPDATE_BORROWER');
var updateBorrower = function updateBorrower(props, borrower) {
  return function (dispatch) {
    var borrowerID = borrowerIDPropSelector(null, props);

    var _toAPI2 = toAPI(borrower),
        attributes = _toAPI2.attributes,
        creditConsent = _toAPI2.creditConsent;

    return Promise.all([dispatch(grantOrRevokeCreditConsent(creditConsent, borrowerID)), dispatch(apiUpdateBorrowerAttributes(attributes, borrowerID, API_UPDATE_BORROWER))]);
  };
};
var DEFAULT_STATE$l = {
  items: {},
  apiErrors: {},
  fetched: false,
  blendEmails: {}
};
var borrowers$1 = (function (state, _ref2) {
  if (state === void 0) {
    state = DEFAULT_STATE$l;
  }

  var type = _ref2.type,
      payload = _ref2.payload,
      metadata = _ref2.metadata,
      request = _ref2.request;

  switch (type) {
    case SEND_BLEND_EMAIL.BEGIN:
      {
        var _Object$assign;

        return Object.assign({}, state, {
          blendEmails: Object.assign({}, state.blendEmails, (_Object$assign = {}, _Object$assign[metadata.borrowerID] = {
            isLoading: true,
            isSuccessful: false
          }, _Object$assign))
        });
      }

    case SEND_BLEND_EMAIL.FAILURE:
      {
        var _Object$assign2;

        return Object.assign({}, state, {
          blendEmails: Object.assign({}, state.blendEmails, (_Object$assign2 = {}, _Object$assign2[metadata.borrowerID] = {
            isLoading: false,
            isSuccessful: false,
            error: payload
          }, _Object$assign2))
        });
      }

    case SEND_BLEND_EMAIL.SUCCESS:
      {
        var _Object$assign3;

        return Object.assign({}, state, {
          blendEmails: Object.assign({}, state.blendEmails, (_Object$assign3 = {}, _Object$assign3[metadata.borrowerID] = {
            isLoading: false,
            isSuccessful: true
          }, _Object$assign3))
        });
      }

    case API_REMOVE_BORROWER.SUCCESS:
      {
        var borrowerIDToRemove = metadata.borrowerID;

        var _state$items = state.items,
            itemToRemove = _state$items[borrowerIDToRemove],
            items = _objectWithoutProperties(_state$items, [borrowerIDToRemove].map(_toPropertyKey));

        var _state$apiErrors = state.apiErrors,
            apiErrorToRemove = _state$apiErrors[borrowerIDToRemove],
            apiErrors = _objectWithoutProperties(_state$apiErrors, [borrowerIDToRemove].map(_toPropertyKey));

        return Object.assign({}, state, {
          items: items,
          apiErrors: apiErrors
        });
      }

    case FETCH_BORROWERS.SUCCESS:
      {
        return Object.assign({}, state, {
          items: payload.borrowers.reduce(function (items, borrower) {
            var _Object$assign4;

            return Object.assign({}, items, (_Object$assign4 = {}, _Object$assign4[borrower.id] = fromAPI(borrower), _Object$assign4));
          }, DEFAULT_STATE$l.items),
          apiErrors: {},
          fetched: true
        });
      }

    case API_CREATE_BORROWER.SUCCESS:
      {
        var _Object$assign5;

        var borrowerID = payload.borrowerID;
        return Object.assign({}, state, {
          items: Object.assign({}, state.items, (_Object$assign5 = {}, _Object$assign5[borrowerID] = fromAPI({
            id: borrowerID,
            attributes: request.attributes
          }), _Object$assign5))
        });
      }

    case API_UPDATE_BORROWER.SUCCESS:
      {
        var _state$items$_borrowe, _Object$assign6;

        var _borrowerID = metadata.borrowerID;

        var _state$apiErrors2 = state.apiErrors,
            _apiErrorToRemove = _state$apiErrors2[_borrowerID],
            _apiErrors = _objectWithoutProperties(_state$apiErrors2, [_borrowerID].map(_toPropertyKey));

        return Object.assign({}, state, {
          items: Object.assign({}, state.items, (_Object$assign6 = {}, _Object$assign6[_borrowerID] = Object.assign({}, state.items[_borrowerID], {
            attributes: Object.assign({}, (_state$items$_borrowe = state.items[_borrowerID]) === null || _state$items$_borrowe === void 0 ? void 0 : _state$items$_borrowe.attributes, fromAPI({
              attributes: request === null || request === void 0 ? void 0 : request.attributes
            }).attributes)
          }), _Object$assign6)),
          apiErrors: _apiErrors
        });
      }

    case GRANT_CREDIT_CONSENT.FAILURE:
    case REVOKE_CREDIT_CONSENT.FAILURE:
    case API_UPDATE_BORROWER.FAILURE:
    case API_REMOVE_BORROWER.FAILURE:
      {
        var _Object$assign7;

        return Object.assign({}, state, {
          apiErrors: Object.assign({}, state.apiErrors, (_Object$assign7 = {}, _Object$assign7[metadata.borrowerID] = {
            type: errorTypeFromError(payload),
            error: payload
          }, _Object$assign7))
        });
      }

    case GRANT_CREDIT_CONSENT.SUCCESS:
      {
        var _state$items$metadata, _Object$assign8;

        return Object.assign({}, state, {
          items: Object.assign({}, state.items, (_Object$assign8 = {}, _Object$assign8[metadata.borrowerID] = Object.assign({}, state.items[metadata.borrowerID], {
            creditConsent: Object.assign({}, (_state$items$metadata = state.items[metadata.borrowerID]) === null || _state$items$metadata === void 0 ? void 0 : _state$items$metadata.creditConsent, {
              method: request.method
            })
          }), _Object$assign8))
        });
      }

    case REVOKE_CREDIT_CONSENT.SUCCESS:
      {
        var _Object$assign9;

        var _state$items$metadata2 = state.items[metadata.borrowerID],
            creditConsent = _state$items$metadata2.creditConsent,
            borrowerWithoutConsent = _objectWithoutProperties(_state$items$metadata2, _excluded$j);

        return Object.assign({}, state, {
          items: Object.assign({}, state.items, (_Object$assign9 = {}, _Object$assign9[metadata.borrowerID] = borrowerWithoutConsent, _Object$assign9))
        });
      }

    default:
      return state;
  }
});

var borrowers$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  apiCreateBorrower: apiCreateBorrower,
  API_CREATE_BORROWER: API_CREATE_BORROWER,
  apiRemoveBorrower: apiRemoveBorrower,
  API_REMOVE_BORROWER: API_REMOVE_BORROWER,
  FETCH_BORROWERS: FETCH_BORROWERS,
  fetchBorrowers: fetchBorrowers,
  BLEND_EMAIL_ENDPOINT: BLEND_EMAIL_ENDPOINT,
  SEND_BLEND_EMAIL: SEND_BLEND_EMAIL,
  sendBlendEmail: sendBlendEmail,
  createBorrower: createBorrower,
  REVOKE_CREDIT_CONSENT: REVOKE_CREDIT_CONSENT,
  revokeCreditConsentThunk: revokeCreditConsentThunk,
  GRANT_CREDIT_CONSENT: GRANT_CREDIT_CONSENT,
  grantCreditConsentThunk: grantCreditConsentThunk,
  GRANT_SOFT_CREDIT_CONSENT: GRANT_SOFT_CREDIT_CONSENT,
  grantSoftCreditConsentThunk: grantSoftCreditConsentThunk,
  grantOrRevokeCreditConsent: grantOrRevokeCreditConsent,
  REVOKE_ECONSENT: REVOKE_ECONSENT,
  revokeEConsentThunk: revokeEConsentThunk,
  revokeEConsent: revokeEConsent,
  API_UPDATE_BORROWER: API_UPDATE_BORROWER,
  updateBorrower: updateBorrower,
  DEFAULT_STATE: DEFAULT_STATE$l,
  'default': borrowers$1
});

var _excluded$k = ["intentToOccupy", "recentOwnershipInterestDerived", "propertyUse", "ownershipType", "undisclosedFunds", "undisclosedFundsAmount", "hasBankruptcy", "bankruptcyChapter7", "bankruptcyChapter11", "bankruptcyChapter12", "bankruptcyChapter13"],
    _excluded2$5 = ["base"],
    _excluded3$2 = ["ethnicityInferred", "sexInferred", "raceInferred"];
var _constants$loanFile$e = constants.loanFile,
    RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE_LOOKUP$1 = _constants$loanFile$e.RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE_LOOKUP,
    ETHNICITIES_LOOKUP$1 = _constants$loanFile$e.ETHNICITIES_LOOKUP,
    SEXES_LOOKUP$1 = _constants$loanFile$e.SEXES_LOOKUP,
    RACES_LOOKUP$1 = _constants$loanFile$e.RACES_LOOKUP,
    STRING_TRUE$1 = constants.STRING_TRUE,
    STRING_FALSE$1 = constants.STRING_FALSE;
var createDeclarationsAPIPayload = function createDeclarationsAPIPayload(declarations) {
  var modifiedDeclarations = Object.entries(declarations).reduce(function (acc, _ref) {
    var _ref2 = _slicedToArray(_ref, 2),
        key = _ref2[0],
        value = _ref2[1];

    if (value === STRING_TRUE$1) {
      acc[key] = true;
    } else if (value === STRING_FALSE$1) {
      acc[key] = false;
    } else if (value !== '') {
      acc[key] = value;
    }

    return acc;
  }, {});

  var intentToOccupy = modifiedDeclarations.intentToOccupy,
      recentOwnershipInterestDerived = modifiedDeclarations.recentOwnershipInterestDerived,
      propertyUse = modifiedDeclarations.propertyUse,
      ownershipType = modifiedDeclarations.ownershipType,
      undisclosedFunds = modifiedDeclarations.undisclosedFunds,
      undisclosedFundsAmount = modifiedDeclarations.undisclosedFundsAmount,
      hasBankruptcy = modifiedDeclarations.hasBankruptcy,
      bankruptcyChapter7 = modifiedDeclarations.bankruptcyChapter7,
      bankruptcyChapter11 = modifiedDeclarations.bankruptcyChapter11,
      bankruptcyChapter12 = modifiedDeclarations.bankruptcyChapter12,
      bankruptcyChapter13 = modifiedDeclarations.bankruptcyChapter13,
      otherPayloadFields = _objectWithoutProperties(modifiedDeclarations, _excluded$k);

  var payload = Object.assign({}, otherPayloadFields);

  if (intentToOccupy) {
    // The following weird logic is due to URLA inconsistencies causing LFS models to be weird.
    // Please refer to LFS documentation for clarifications.
    if (recentOwnershipInterestDerived) {
      var fhaSecondaryResidence = propertyUse === RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE_LOOKUP$1.FHA_SECONDARY_RESIDENCE;
      payload.recentOwnershipInterest = {
        propertyUse: fhaSecondaryResidence ? RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE_LOOKUP$1.PRIMARY_RESIDENCE : propertyUse,
        fhaSecondaryResidence: fhaSecondaryResidence,
        ownershipType: ownershipType
      };
    } else {
      payload.recentOwnershipInterest = {
        na: true
      };
    }

    payload.intentToOccupy = true;
  } else if (intentToOccupy === false) {
    payload.intentToOccupy = false;
    payload.recentOwnershipInterest = null;
  } // Set undisclosedFundsAmount only when user explicitly set/unset undisclosedFunds


  if (undisclosedFunds) {
    payload.undisclosedFundsAmount = undisclosedFundsAmount;
  } else if (undisclosedFunds === false) {
    payload.undisclosedFundsAmount = 0;
  }

  if (hasBankruptcy) {
    payload.bankruptcy = {
      chapter7: bankruptcyChapter7,
      chapter11: bankruptcyChapter11,
      chapter12: bankruptcyChapter12,
      chapter13: bankruptcyChapter13
    };
  } else if (hasBankruptcy === false) {
    payload.bankruptcy = {
      na: true
    };
  }

  return payload;
};
var createDemographicsAPIPayload = function createDemographicsAPIPayload(_ref3) {
  var _Object$assign, _ref4;

  var _ref3$ethnicity = _ref3.ethnicity,
      base = _ref3$ethnicity.base,
      otherEthnicities = _objectWithoutProperties(_ref3$ethnicity, _excluded2$5),
      sex = _ref3.sex,
      _ref3$race = _ref3.race,
      _RACES_LOOKUP$NOT_DIS = RACES_LOOKUP$1.NOT_DISCLOSED,
      raceNotDisclosed = _ref3$race[_RACES_LOOKUP$NOT_DIS],
      races = _objectWithoutProperties(_ref3$race, [_RACES_LOOKUP$NOT_DIS].map(_toPropertyKey));

  return {
    ethnicity: // Send null for ethnicity if "I do not wish to provide" option was chosen
    base === ETHNICITIES_LOOKUP$1.NOT_DISCLOSED ? null : Object.assign((_Object$assign = {}, _Object$assign[ETHNICITIES_LOOKUP$1.HISPANIC_OR_LATINO] = base === ETHNICITIES_LOOKUP$1.HISPANIC_OR_LATINO, _Object$assign[ETHNICITIES_LOOKUP$1.NOT_HISPANIC_OR_LATINO] = base === ETHNICITIES_LOOKUP$1.NOT_HISPANIC_OR_LATINO, _Object$assign), otherEthnicities),
    sex: sex && sex !== SEXES_LOOKUP$1.NOT_DISCLOSED ? (_ref4 = {}, _ref4[sex] = true, _ref4) : null,
    race: Object.values(races).some(Boolean) && !raceNotDisclosed ? races : null
  };
};
var createDemographicsCollectionAPIPayload = function createDemographicsCollectionAPIPayload(_ref5) {
  var ethnicityInferred = _ref5.ethnicityInferred,
      sexInferred = _ref5.sexInferred,
      raceInferred = _ref5.raceInferred,
      otherDemographicsCollections = _objectWithoutProperties(_ref5, _excluded3$2);

  return Object.assign({
    ethnicityInferred: ethnicityInferred === STRING_TRUE$1,
    sexInferred: sexInferred === STRING_TRUE$1,
    raceInferred: raceInferred === STRING_TRUE$1
  }, otherDemographicsCollections);
};

var API_UPDATE_BORROWER_DECLARATIONS = createActionObject('API_UPDATE_BORROWER_DECLARATIONS');
var apiUpdateBorrowerDeclarations = function apiUpdateBorrowerDeclarations(props, declarations) {
  return function (dispatch) {
    var borrowerID = borrowerIDPropSelector(null, props);
    var attributes = createDeclarationsAPIPayload(declarations);
    return dispatch(apiUpdateBorrowerAttributes(attributes, borrowerID, API_UPDATE_BORROWER_DECLARATIONS));
  };
};

var getItemsFromBorrowers = function getItemsFromBorrowers(borrowers) {
  return borrowers.reduce(function (acc, borrower) {
    var _Object$assign;

    return Object.assign({}, acc, (_Object$assign = {}, _Object$assign[borrower.id] = borrower.attributes, _Object$assign));
  }, {});
};

var DEFAULT_STATE$m = {
  items: {}
};
var declarations$1 = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$m;
  }

  var type = _ref.type,
      payload = _ref.payload,
      metadata = _ref.metadata,
      request = _ref.request;

  switch (type) {
    case FETCH_BORROWERS.SUCCESS:
      return Object.assign({}, state, {
        items: getItemsFromBorrowers(payload.borrowers)
      });

    case API_UPDATE_BORROWER_DECLARATIONS.SUCCESS:
      {
        var _Object$assign2;

        var borrowerID = metadata.borrowerID;
        return Object.assign({}, state, {
          items: Object.assign({}, state.items, (_Object$assign2 = {}, _Object$assign2[borrowerID] = Object.assign({}, state.items[borrowerID], request.attributes), _Object$assign2))
        });
      }

    default:
      return state;
  }
});

var declarations$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  API_UPDATE_BORROWER_DECLARATIONS: API_UPDATE_BORROWER_DECLARATIONS,
  apiUpdateBorrowerDeclarations: apiUpdateBorrowerDeclarations,
  DEFAULT_STATE: DEFAULT_STATE$m,
  'default': declarations$1
});

var SET_PURPOSE = 'SET_PURPOSE';
var setPurpose = function setPurpose(purpose) {
  return {
    type: SET_PURPOSE,
    payload: purpose
  };
};
var SET_UI_FLOW = 'SET_UI_FLOW';
var setUIFlow = function setUIFlow(uiFlow) {
  return {
    type: SET_UI_FLOW,
    payload: uiFlow
  };
};
var CREATE_LOAN = createActionObject('CREATE_LOAN');
var createLoan = function createLoan(payload) {
  return API['loan-file'].POST.thunk({
    action: 'v1/loan-start',
    authenticated: true,
    input: payload
  }, CREATE_LOAN);
};
var createLoanFromStart = function createLoanFromStart() {
  return function (dispatch, getState) {
    var state = getState();

    var _detailSelector = detailSelector(state),
        loanPurpose = _detailSelector.purpose,
        uiFlow = _detailSelector.uiFlow;

    var payload = {
      loanPurpose: loanPurpose,
      uiFlow: uiFlow,
      borrowers: borrowerListSelector(state).map(function (borrower) {
        return {
          firstName: borrower.attributes.names[0].first,
          lastName: borrower.attributes.names[0].last,
          email: borrower.attributes.email
        };
      })
    };
    return dispatch(createLoan(payload));
  };
};
var DEFAULT_STATE$n = {
  uiFlow: null,
  purpose: null
};
var detail$1 = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$n;
  }

  var type = _ref.type,
      payload = _ref.payload;

  switch (type) {
    case SET_UI_FLOW:
      return Object.assign({}, state, {
        uiFlow: payload
      });

    case SET_PURPOSE:
      return Object.assign({}, state, {
        purpose: payload
      });

    default:
      return state;
  }
});

var detail$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  SET_PURPOSE: SET_PURPOSE,
  setPurpose: setPurpose,
  SET_UI_FLOW: SET_UI_FLOW,
  setUIFlow: setUIFlow,
  CREATE_LOAN: CREATE_LOAN,
  createLoan: createLoan,
  createLoanFromStart: createLoanFromStart,
  DEFAULT_STATE: DEFAULT_STATE$n,
  'default': detail$1
});

var _excluded$l = ["tempID"],
    _excluded2$6 = ["comments"];
var DOCUMENT_REQUEST_STATUS_LOOKUP$1 = conditions$2.DOCUMENT_REQUEST_STATUS_LOOKUP;
var FETCH_DOCUMENT_REQUESTS = createActionObject('FETCH_DOCUMENT_REQUESTS');
var fetchDocumentRequests = function fetchDocumentRequests(loanID) {
  return API.conditions.GET.thunk({
    action: 'v1/doc-requests',
    input: {
      loanId: loanID
    },
    authenticated: true,
    metadata: {
      loanID: loanID
    },
    headers: {
      'Content-Type': 'text/json'
    }
  }, FETCH_DOCUMENT_REQUESTS);
};
var FETCH_UNAFFILIATED_DOCUMENTS = createActionObject('FETCH_UNAFFILIATED_DOCUMENTS');
var fetchUnaffiliatedDocuments = function fetchUnaffiliatedDocuments(loanID) {
  return API.conditions.GET.thunk({
    action: 'v1/documents',
    input: {
      loanId: loanID,
      noAssociationOnly: true
    },
    authenticated: true,
    metadata: {
      loanID: loanID
    },
    headers: {
      'Content-Type': 'text/json'
    }
  }, FETCH_UNAFFILIATED_DOCUMENTS);
};

var withLoanID = function withLoanID(actionCreator) {
  return function (props) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }

    return function (dispatch, getState) {
      var loanID = loanIDRouteParamSelector(getState(), props);
      return dispatch(actionCreator.apply(void 0, [loanID].concat(args)));
    };
  };
};

var fetchDocumentRequestsForLoan = withLoanID(function (loanID) {
  return function (dispatch) {
    return Promise.all([dispatch(fetchDocumentRequests(loanID)), dispatch(fetchUnaffiliatedDocuments(loanID)), dispatch(fetchIncomeAndAssets(loanID))]);
  };
});
var CREATE_DOCUMENT_REQUEST = createActionObject('CREATE_DOCUMENT_REQUEST');
var createDocumentRequest = function createDocumentRequest(loanId, _ref) {
  var tempID = _ref.tempID,
      docRequest = _objectWithoutProperties(_ref, _excluded$l);

  return API.conditions.POST.thunk({
    action: 'v1/doc-requests',
    input: Object.assign({
      loanId: loanId
    }, docRequest),
    metadata: {
      tempID: tempID
    },
    authenticated: true,
    headers: {
      'Content-Type': 'text/json'
    }
  }, CREATE_DOCUMENT_REQUEST);
};
var DELETE_DOCUMENT_REQUEST = createActionObject('DELETE_DOCUMENT_REQUEST');

var deleteDocumentRequest = function deleteDocumentRequest(id) {
  return API.conditions.DELETE.thunk({
    action: "v1/doc-requests/" + id,
    authenticated: true,
    metadata: {
      id: id
    },
    headers: {
      'Content-Type': 'text/json'
    }
  }, DELETE_DOCUMENT_REQUEST);
};

var FETCH_DOCUMENT_REQUEST_TEMPLATES = createActionObject('FETCH_DOCUMENT_REQUEST_TEMPLATES');
var fetchDocumentRequestTemplates = function fetchDocumentRequestTemplates() {
  return API.conditions.GET.thunk({
    action: 'v1/templates',
    authenticated: true,
    headers: {
      'Content-Type': 'text/json'
    }
  }, FETCH_DOCUMENT_REQUEST_TEMPLATES);
};
var UPDATE_DOCUMENT_REQUEST_STATUS = createActionObject('UPDATE_DOCUMENT_REQUEST_STATUS');

var updateDocumentRequestStatus = function updateDocumentRequestStatus(id, status) {
  return API.conditions.PUT.thunk({
    action: "v1/doc-requests/" + id + "/status",
    queryParams: {
      status: status
    },
    authenticated: true,
    headers: {
      'Content-Type': 'text/json'
    }
  }, UPDATE_DOCUMENT_REQUEST_STATUS);
};

var ADD_COMMENT = createActionObject('ADD_COMMENT');

var addComment = function addComment(id, comment) {
  return API.conditions.POST.thunk({
    action: "v1/doc-requests/" + id + "/comments",
    input: comment,
    authenticated: true,
    headers: {
      'Content-Type': 'text/json'
    }
  }, ADD_COMMENT);
};

var START_NEW_DOCUMENT_REQUEST = 'START_NEW_DOCUMENT_REQUEST';

var startNewDocumentRequest = function startNewDocumentRequest(documentRequest) {
  return {
    type: START_NEW_DOCUMENT_REQUEST,
    payload: documentRequest
  };
};

var DEFAULT_DOCUMENT_REQUEST_SHAPE = {
  title: '',
  description: '',
  assetType: null,
  assetId: null,
  borrowerId: null,
  status: null
};

var makeDocumentRequest = function makeDocumentRequest(overrides) {
  return Object.assign({
    tempID: v4_1()
  }, DEFAULT_DOCUMENT_REQUEST_SHAPE, overrides);
};

var startNewDocumentRequests = withLoanID(function (loanID, borrowerID, types) {
  return function (dispatch, getState) {
    var taskTypesByID = taskTypesByIDSelector(getState());
    var documentRequests = types.map(function (type) {
      var _taskTypesByID$type = taskTypesByID[type],
          category = _taskTypesByID$type.category,
          label = _taskTypesByID$type.label,
          description = _taskTypesByID$type.description;
      return makeDocumentRequest({
        title: category + " - " + label,
        description: description,
        loanId: loanID,
        borrowerId: borrowerID
      });
    });
    documentRequests.forEach(function (documentRequest) {
      dispatch(startNewDocumentRequest(documentRequest));
    });
  };
});
var UPDATE_DOCUMENT_REQUEST = 'UPDATE_DOCUMENT_REQUEST';
var updateDocumentRequest = function updateDocumentRequest(id, data, errors) {
  return {
    type: UPDATE_DOCUMENT_REQUEST,
    payload: {
      id: id,
      data: data,
      errors: errors
    }
  };
};
var sendTask = withLoanID(function (loanID, documentRequestID) {
  return function (dispatch, getState) {
    var docRequest = documentRequestCreateSelector(getState(), {
      documentRequestID: documentRequestID
    });
    return dispatch(createDocumentRequest(loanID, docRequest));
  };
});
var sendAllDrafts = function sendAllDrafts(props) {
  return function (dispatch, getState) {
    var draftIDs = draftRequestIDsSelector(getState());
    return Promise.all(draftIDs.map(function (id) {
      return dispatch(sendTask(props, id));
    }));
  };
};
var DELETE_DRAFT_DOCUMENT_REQUEST = 'DELETE_DRAFT_DOCUMENT_REQUEST'; // draft doc requests don't exist on the server yet

var deleteDraftDocumentRequest = function deleteDraftDocumentRequest(id) {
  return {
    type: DELETE_DRAFT_DOCUMENT_REQUEST,
    metadata: {
      id: id
    }
  };
};

var deleteTask = function deleteTask(documentRequestID) {
  return function (dispatch, getState) {
    var docRequest = documentRequestSelector(getState(), {
      documentRequestID: documentRequestID
    });

    if (docRequest.status === DOCUMENT_REQUEST_STATUS_LOOKUP$1.DRAFT) {
      return Promise.resolve(dispatch(deleteDraftDocumentRequest(documentRequestID)));
    } else {
      return dispatch(deleteDocumentRequest(documentRequestID));
    }
  };
};

var approveOrRejectTask = function approveOrRejectTask(status) {
  return withLoanID(function (loanID, id, comments) {
    return function (dispatch) {
      return new Promise(function ($return, $error) {
        var $Try_1_Finally = function ($Try_1_Exit) {
          return function ($Try_1_Value) {
            try {
              dispatch(fetchDocumentRequests(loanID));
              return $Try_1_Exit && $Try_1_Exit.call(this, $Try_1_Value);
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }.bind(this);
        }.bind(this);

        var $Try_1_Post = function () {
          try {
            return $return();
          } catch ($boundEx) {
            return $error($boundEx);
          }
        };

        var $Try_1_Catch = function ($exception_2) {
          try {
            throw $exception_2;
          } catch ($boundEx) {
            return $Try_1_Finally($error)($boundEx);
          }
        };

        try {
          return Promise.resolve(Promise.all([dispatch(updateDocumentRequestStatus(id, status))].concat(_toConsumableArray(comments.map(function (comment) {
            return dispatch(addComment(id, comment));
          }))))).then(function ($await_3) {
            try {
              return $Try_1_Finally($Try_1_Post)();
            } catch ($boundEx) {
              return $Try_1_Catch($boundEx);
            }
          }, $Try_1_Catch);
        } catch ($exception_2) {
          $Try_1_Catch($exception_2);
        }
      });
    };
  });
};

var approveTask = approveOrRejectTask(DOCUMENT_REQUEST_STATUS_LOOKUP$1.APPROVED);
var rejectTask = approveOrRejectTask(DOCUMENT_REQUEST_STATUS_LOOKUP$1.REJECTED);

var keyBy = function keyBy(arr, getKey) {
  return arr.reduce(function (keyedBy, value) {
    var _Object$assign;

    return Object.assign({}, keyedBy, (_Object$assign = {}, _Object$assign[getKey(value)] = value, _Object$assign));
  }, {});
};

var taskTypes = function taskTypes(state, _ref2) {
  if (state === void 0) {
    state = {};
  }

  var type = _ref2.type,
      payload = _ref2.payload;

  switch (type) {
    case FETCH_DOCUMENT_REQUEST_TEMPLATES.SUCCESS:
      return keyBy(payload, function (_ref3) {
        var id = _ref3.id;
        return id;
      });

    default:
      return state;
  }
};

var replaceCommentsWithIDs = function replaceCommentsWithIDs(_ref4) {
  var comments = _ref4.comments,
      docRequest = _objectWithoutProperties(_ref4, _excluded2$6);

  return Object.assign({
    comments: (comments || []).map(function (_ref5) {
      var id = _ref5.id;
      return id;
    })
  }, docRequest);
};

var mergeNewDocumentRequests = function mergeNewDocumentRequests(state, newDocumentRequests) {
  return Object.assign({}, state, keyBy(newDocumentRequests, function (_ref6) {
    var id = _ref6.id;
    return id;
  }));
};

var filterObject = function filterObject(obj, iteratee) {
  return Object.entries(obj).filter(function (_ref7) {
    var _ref8 = _slicedToArray(_ref7, 2),
        key = _ref8[0],
        value = _ref8[1];

    return iteratee(value, key);
  }).reduce(function (acc, _ref9) {
    var _Object$assign2;

    var _ref10 = _slicedToArray(_ref9, 2),
        key = _ref10[0],
        value = _ref10[1];

    return Object.assign({}, acc, (_Object$assign2 = {}, _Object$assign2[key] = value, _Object$assign2));
  }, {});
};

var requests = function requests(state, _ref11) {
  var _Object$assign3;

  if (state === void 0) {
    state = {};
  }

  var type = _ref11.type,
      payload = _ref11.payload,
      metadata = _ref11.metadata;

  switch (type) {
    case FETCH_DOCUMENT_REQUESTS.BEGIN:
      // Because we rely on `redux-persist` to keep drafts, we can't just throw everything out
      // with each new fetch. However, we can't keep everything either because that means
      // requests for other loans would stick around. Resolve this by filtering state down to
      // just tasks for the current loan on fetch.
      return filterObject(state, function (_ref12) {
        var loanId = _ref12.loanId;
        return metadata.loanID === loanId;
      });

    case FETCH_DOCUMENT_REQUESTS.SUCCESS:
      return mergeNewDocumentRequests(state, payload.map(replaceCommentsWithIDs));

    case START_NEW_DOCUMENT_REQUEST:
      return mergeNewDocumentRequests(state, [makeDocumentRequest(Object.assign({
        id: payload.tempID
      }, payload))]);

    case CREATE_DOCUMENT_REQUEST.SUCCESS:
      var _state = state,
          _metadata$tempID = metadata.tempID,
          _state$_metadata$temp = _state[_metadata$tempID],
          oldDocRequest = _state$_metadata$temp === void 0 ? {} : _state$_metadata$temp,
          newState = _objectWithoutProperties(_state, [_metadata$tempID].map(_toPropertyKey));

      return mergeNewDocumentRequests(newState, [replaceCommentsWithIDs(Object.assign({
        // copy over the borrower ID because the API currently doesn't persist it
        borrowerID: oldDocRequest.borrowerID || oldDocRequest.borrowerId,
        // initialize status because the API currently doesn't return it
        status: DOCUMENT_REQUEST_STATUS_LOOKUP$1.NEW
      }, payload))]);

    case UPDATE_DOCUMENT_REQUEST:
      return Object.assign({}, state, (_Object$assign3 = {}, _Object$assign3[payload.id] = Object.assign({}, state[payload.id], payload.data), _Object$assign3));

    case DELETE_DRAFT_DOCUMENT_REQUEST:
    case DELETE_DOCUMENT_REQUEST.SUCCESS:
      var _state2 = state,
          _metadata$id = metadata.id,
          deleted = _state2[_metadata$id],
          _newState = _objectWithoutProperties(_state2, [_metadata$id].map(_toPropertyKey));

      return _newState;

    default:
      return state;
  }
};

var unaffiliatedDocuments = function unaffiliatedDocuments(state, _ref13) {
  if (state === void 0) {
    state = [];
  }

  var type = _ref13.type,
      payload = _ref13.payload;

  switch (type) {
    case FETCH_UNAFFILIATED_DOCUMENTS.SUCCESS:
      return payload;

    default:
      return state;
  }
};

var appendNewValues = function appendNewValues(existingValues, values) {
  return _toConsumableArray(new Set(existingValues.concat(values)));
};

var requestIDs = function requestIDs(state, _ref14) {
  if (state === void 0) {
    state = [];
  }

  var type = _ref14.type,
      payload = _ref14.payload,
      metadata = _ref14.metadata;

  switch (type) {
    case FETCH_DOCUMENT_REQUESTS.SUCCESS:
      return appendNewValues(state, payload.map(function (_ref15) {
        var id = _ref15.id;
        return id;
      }));

    case START_NEW_DOCUMENT_REQUEST:
      return appendNewValues(state, [payload.tempID]);

    case CREATE_DOCUMENT_REQUEST.SUCCESS:
      // Only append if we can't replace, to maintain display order
      var index = state.findIndex(function (id) {
        return id === metadata.tempID;
      });

      if (index === -1) {
        return appendNewValues(state, [payload.id]);
      }

      var newState = _toConsumableArray(state);

      newState[index] = payload.id;
      return newState;

    case DELETE_DRAFT_DOCUMENT_REQUEST:
    case DELETE_DOCUMENT_REQUEST.SUCCESS:
      return state.filter(function (id) {
        return id !== metadata.id;
      });

    default:
      return state;
  }
};

var loading = function loading(state, _ref16) {
  if (state === void 0) {
    state = false;
  }

  var type = _ref16.type;

  switch (type) {
    case FETCH_DOCUMENT_REQUESTS.BEGIN:
      return true;

    case FETCH_DOCUMENT_REQUESTS.SUCCESS:
    case FETCH_DOCUMENT_REQUESTS.FAILURE:
      return false;

    default:
      return state;
  }
};

var error = function error(state, _ref17) {
  if (state === void 0) {
    state = null;
  }

  var type = _ref17.type,
      payload = _ref17.payload;

  switch (type) {
    case FETCH_DOCUMENT_REQUESTS.BEGIN:
    case FETCH_DOCUMENT_REQUESTS.SUCCESS:
      return null;

    case FETCH_DOCUMENT_REQUESTS.FAILURE:
      return payload;

    default:
      return state;
  }
};

var reducer = persistReducer({
  key: 'conditions',
  storage: storage,
  whitelist: ['requests', 'requestIDs']
}, combineReducers({
  taskTypes: taskTypes,
  requests: requests,
  unaffiliatedDocuments: unaffiliatedDocuments,
  requestIDs: requestIDs,
  loading: loading,
  error: error
}));
var DEFAULT_STATE$o = reducer(undefined, {
  type: 'INIT'
});

var conditions$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  FETCH_DOCUMENT_REQUESTS: FETCH_DOCUMENT_REQUESTS,
  fetchDocumentRequests: fetchDocumentRequests,
  FETCH_UNAFFILIATED_DOCUMENTS: FETCH_UNAFFILIATED_DOCUMENTS,
  fetchUnaffiliatedDocuments: fetchUnaffiliatedDocuments,
  fetchDocumentRequestsForLoan: fetchDocumentRequestsForLoan,
  CREATE_DOCUMENT_REQUEST: CREATE_DOCUMENT_REQUEST,
  createDocumentRequest: createDocumentRequest,
  DELETE_DOCUMENT_REQUEST: DELETE_DOCUMENT_REQUEST,
  FETCH_DOCUMENT_REQUEST_TEMPLATES: FETCH_DOCUMENT_REQUEST_TEMPLATES,
  fetchDocumentRequestTemplates: fetchDocumentRequestTemplates,
  UPDATE_DOCUMENT_REQUEST_STATUS: UPDATE_DOCUMENT_REQUEST_STATUS,
  ADD_COMMENT: ADD_COMMENT,
  START_NEW_DOCUMENT_REQUEST: START_NEW_DOCUMENT_REQUEST,
  startNewDocumentRequests: startNewDocumentRequests,
  UPDATE_DOCUMENT_REQUEST: UPDATE_DOCUMENT_REQUEST,
  updateDocumentRequest: updateDocumentRequest,
  sendTask: sendTask,
  sendAllDrafts: sendAllDrafts,
  DELETE_DRAFT_DOCUMENT_REQUEST: DELETE_DRAFT_DOCUMENT_REQUEST,
  deleteTask: deleteTask,
  approveTask: approveTask,
  rejectTask: rejectTask,
  DEFAULT_STATE: DEFAULT_STATE$o,
  'default': reducer
});

var _constants$loanFile$U = constants.loanFile.UNDERWRITER_LOOKUP,
    DESKTOP_UNDERWRITER = _constants$loanFile$U.DESKTOP_UNDERWRITER,
    DESKTOP_UNDERWRITER_POS = _constants$loanFile$U.DESKTOP_UNDERWRITER_POS,
    LOAN_PRODUCT_ADVISOR = _constants$loanFile$U.LOAN_PRODUCT_ADVISOR;
var SUBMIT_FOR_UNDERWRITING_TIMEOUT = 120000;
var SUBMIT_FOR_UNDERWRITING = createActionObject('SUBMIT_FOR_UNDERWRITING');
var submitForUnderwriting = function submitForUnderwriting(input, queryParams, underwriter) {
  return API['automated-underwriting'].POST.thunk({
    action: 'v1/findings',
    input: input,
    queryParams: queryParams,
    metadata: underwriter,
    timeout: SUBMIT_FOR_UNDERWRITING_TIMEOUT,
    authenticated: true
  }, SUBMIT_FOR_UNDERWRITING);
};
var FETCH_UNDERWRITING_FINDINGS = createActionObject('FETCH_UNDERWRITING_FINDINGS');
var fetchUnderwritingFindings = function fetchUnderwritingFindings(loanID) {
  return API['automated-underwriting'].GET.thunk({
    action: 'v1/findings',
    input: {
      loanID: loanID,
      include: 'documentURL'
    },
    authenticated: true
  }, FETCH_UNDERWRITING_FINDINGS);
};
var runUnderwriting = function runUnderwriting(loanID, underwriter) {
  return function (dispatch) {
    return dispatch(submitForUnderwriting({
      loanID: loanID,
      underwriter: underwriter
    }, {
      include: 'documentURL'
    }, underwriter)).then(function () {
      return dispatch(fetchUnderwritingFindings(loanID));
    });
  };
};
var SET_SELECTED_UNDERWRITER = createActionObject('SET_SELECTED_UNDERWRITER');
var setSelectedUnderwriter = function setSelectedUnderwriter(loanID, selectedUnderwriter) {
  return API['automated-underwriting'].POST.thunk({
    action: 'v1/selected-underwriter',
    input: {
      loanID: loanID,
      selectedUnderwriter: selectedUnderwriter
    },
    authenticated: true
  }, SET_SELECTED_UNDERWRITER);
};
var FETCH_SELECTED_UNDERWRITER = createActionObject('FETCH_SELECTED_UNDERWRITER');
var fetchSelectedUnderwriter = function fetchSelectedUnderwriter(loanID) {
  return API['automated-underwriting'].GET.thunk({
    action: 'v1/selected-underwriter',
    input: {
      loanID: loanID
    },
    authenticated: true
  }, FETCH_SELECTED_UNDERWRITER);
};
var DEFAULT_STATE$p = {
  findings: {},
  findingsStatus: {},
  submissionStatus: {},
  selectedUnderwriter: null,
  selectedUnderwriterSubmissionStatus: {},
  selectedUnderwriterStatus: {}
};
var underwriting$1 = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$p;
  }

  var type = _ref.type,
      payload = _ref.payload,
      metadata = _ref.metadata,
      request = _ref.request;

  switch (type) {
    case SUBMIT_FOR_UNDERWRITING.BEGIN:
      {
        var _Object$assign;

        return Object.assign({}, state, {
          submissionStatus: Object.assign({}, state.submissionStatus, (_Object$assign = {}, _Object$assign[metadata] = {
            type: STATUS.LOADING,
            error: null
          }, _Object$assign))
        });
      }

    case SUBMIT_FOR_UNDERWRITING.FAILURE:
      {
        var _Object$assign2;

        var error = payload || {
          type: constants.AUS_UNKNOWN_ERROR
        };
        return Object.assign({}, state, {
          submissionStatus: Object.assign({}, state.submissionStatus, (_Object$assign2 = {}, _Object$assign2[metadata] = {
            type: STATUS.ERROR,
            error: error
          }, _Object$assign2))
        });
      }

    case SUBMIT_FOR_UNDERWRITING.SUCCESS:
      {
        var _Object$assign3;

        return Object.assign({}, state, {
          submissionStatus: Object.assign({}, state.submissionStatus, (_Object$assign3 = {}, _Object$assign3[metadata] = {
            type: STATUS.SUCCESS,
            error: null
          }, _Object$assign3))
        });
      }

    case FETCH_UNDERWRITING_FINDINGS.BEGIN:
      {
        return Object.assign({}, state, {
          findingsStatus: {
            type: STATUS.LOADING
          }
        });
      }

    case FETCH_UNDERWRITING_FINDINGS.FAILURE:
      {
        return Object.assign({}, state, {
          findingsStatus: {
            type: STATUS.ERROR
          }
        });
      }

    case FETCH_UNDERWRITING_FINDINGS.SUCCESS:
      {
        var _payload$findings$red;

        return Object.assign({}, state, {
          findings: payload.findings.reduce(function (acc, finding) {
            var _ref2;

            var du = acc[DESKTOP_UNDERWRITER];
            var lpa = acc[LOAN_PRODUCT_ADVISOR];
            var underwriter = finding.underwriter;
            return _ref2 = {}, _ref2[DESKTOP_UNDERWRITER] = [DESKTOP_UNDERWRITER, DESKTOP_UNDERWRITER_POS].includes(underwriter) ? [].concat(_toConsumableArray(du), [finding]) : du, _ref2[LOAN_PRODUCT_ADVISOR] = underwriter === LOAN_PRODUCT_ADVISOR ? [].concat(_toConsumableArray(lpa), [finding]) : lpa, _ref2;
          }, (_payload$findings$red = {}, _payload$findings$red[DESKTOP_UNDERWRITER] = [], _payload$findings$red[LOAN_PRODUCT_ADVISOR] = [], _payload$findings$red)),
          findingsStatus: {
            type: STATUS.SUCCESS
          }
        });
      }

    case SET_SELECTED_UNDERWRITER.BEGIN:
      {
        return Object.assign({}, state, {
          selectedUnderwriterSubmissionStatus: {
            type: STATUS.LOADING,
            error: null
          }
        });
      }

    case SET_SELECTED_UNDERWRITER.FAILURE:
      {
        var _error = payload || {
          type: constants.AUS_UNKNOWN_ERROR
        };

        return Object.assign({}, state, {
          selectedUnderwriterSubmissionStatus: {
            type: STATUS.ERROR,
            error: _error
          }
        });
      }

    case SET_SELECTED_UNDERWRITER.SUCCESS:
      {
        return Object.assign({}, state, {
          selectedUnderwriter: request.selectedUnderwriter,
          selectedUnderwriterSubmissionStatus: {
            type: STATUS.SUCCESS,
            error: null
          }
        });
      }

    case FETCH_SELECTED_UNDERWRITER.BEGIN:
      {
        return Object.assign({}, state, {
          selectedUnderwriterStatus: {
            type: STATUS.LOADING,
            error: null
          }
        });
      }

    case FETCH_SELECTED_UNDERWRITER.FAILURE:
      {
        var _error2 = payload || {
          type: constants.AUS_UNKNOWN_ERROR
        };

        return Object.assign({}, state, {
          selectedUnderwriterStatus: {
            type: STATUS.ERROR,
            error: _error2
          }
        });
      }

    case FETCH_SELECTED_UNDERWRITER.SUCCESS:
      {
        return Object.assign({}, state, {
          selectedUnderwriter: payload.selectedUnderwriter,
          selectedUnderwriterStatus: {
            type: STATUS.SUCCESS,
            error: null
          }
        });
      }

    default:
      return state;
  }
});

var underwriting$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  SUBMIT_FOR_UNDERWRITING_TIMEOUT: SUBMIT_FOR_UNDERWRITING_TIMEOUT,
  SUBMIT_FOR_UNDERWRITING: SUBMIT_FOR_UNDERWRITING,
  submitForUnderwriting: submitForUnderwriting,
  FETCH_UNDERWRITING_FINDINGS: FETCH_UNDERWRITING_FINDINGS,
  fetchUnderwritingFindings: fetchUnderwritingFindings,
  runUnderwriting: runUnderwriting,
  SET_SELECTED_UNDERWRITER: SET_SELECTED_UNDERWRITER,
  setSelectedUnderwriter: setSelectedUnderwriter,
  FETCH_SELECTED_UNDERWRITER: FETCH_SELECTED_UNDERWRITER,
  fetchSelectedUnderwriter: fetchSelectedUnderwriter,
  DEFAULT_STATE: DEFAULT_STATE$p,
  'default': underwriting$1
});

var VERIFICATIONS_SUBDOMAIN = 'financials-verification';
var VERIFICATIONS_ENDPOINT = 'v1/verifications';
var VERIFICATIONS_REPORT_ENDPOINT = 'v1/reports';
var EMAIL_CTA = 'EmailCTA';
var SUBMIT_EMAIL_CTA = createActionObject('SUBMIT_FOR_EMAIL_CTA');
var FETCH_BORROWER_REPORT = createActionObject('FETCH_BORROWER_REPORT');
var LONG_POLL_FETCH_BORROWER_REPORT = createActionObject('LONG_POLL_FETCH_BORROWER_REPORT');
var _constants$loanFile$V = constants.loanFile.VERIFICATIONS_REPORT_TYPE_LOOKUP,
    ASSET_REPORT_TYPE = _constants$loanFile$V.ASSET_REPORT_TYPE,
    EMPLOYMENT_REPORT_TYPE = _constants$loanFile$V.EMPLOYMENT_REPORT_TYPE;
var submitVerificationEmailCTA = function submitVerificationEmailCTA(_ref) {
  var loanID = _ref.loanID,
      borrowerIDs = _ref.borrowerIDs;
  return API[VERIFICATIONS_SUBDOMAIN].POST.thunk({
    action: VERIFICATIONS_ENDPOINT,
    input: {
      loanID: loanID,
      BorrowerIds: borrowerIDs,
      type: EMAIL_CTA
    },
    headers: {
      'Content-Type': 'application/json'
    },
    authenticated: true
  }, SUBMIT_EMAIL_CTA);
};

var fetchBorrowerAssetReportsThunk = function fetchBorrowerAssetReportsThunk(_ref2) {
  var loanId = _ref2.loanID;
  return API[VERIFICATIONS_SUBDOMAIN].GET.thunk({
    action: VERIFICATIONS_REPORT_ENDPOINT,
    input: {
      loanId: loanId
    },
    headers: {
      'Content-Type': 'application/json'
    },
    authenticated: true
  }, FETCH_BORROWER_REPORT);
};

var fetchBorrowerAssetReports = function fetchBorrowerAssetReports(args) {
  return function (dispatch) {
    try {
      return dispatch(fetchBorrowerAssetReportsThunk(args));
    } catch (err) {
      var loanID = args.loanID;
      captureException(err, {
        extra: {
          loanID: loanID,
          module: 'verifications'
        }
      });
      return err;
    }
  };
};
var longPollFetchBorrowerAssetReports = function longPollFetchBorrowerAssetReports(_ref3) {
  var loanId = _ref3.loanID;
  return API[VERIFICATIONS_SUBDOMAIN].GET.thunk({
    action: VERIFICATIONS_REPORT_ENDPOINT,
    input: {
      loanId: loanId
    },
    headers: {
      'Content-Type': 'application/json'
    },
    authenticated: true
  }, LONG_POLL_FETCH_BORROWER_REPORT);
};
var REPORTS_ENDPOINT = 'v1/reports';
var REFRESH_REPORTS = createActionObject('REFRESH_REPORTS');
var refreshVerificationReports = function refreshVerificationReports(_ref4) {
  var reportIDs = _ref4.reportIDs,
      borrowerID = _ref4.borrowerID;
  return API[VERIFICATIONS_SUBDOMAIN].POST.thunk({
    action: REPORTS_ENDPOINT,
    input: {
      reportIDs: reportIDs,
      borrowerSystem: 'lfs'
    },
    headers: {
      'Content-Type': 'application/json'
    },
    authenticated: true,
    // Metadata included because this information is required at BEGIN; request information
    // is undefined at this stage of action dispatch
    metadata: {
      reportIDs: reportIDs,
      borrowerID: borrowerID
    }
  }, REFRESH_REPORTS);
};

var getReportIDsByType = function getReportIDsByType(reportsList, type) {
  return reportsList.reduce(function (acc, report) {
    return report.type === type ? [].concat(_toConsumableArray(acc), [report.reportID]) : acc;
  }, []);
};

var submitVerificationRefresh = function submitVerificationRefresh(_ref5) {
  var borrowers = _ref5.borrowers,
      refreshFormData = _ref5.refreshFormData,
      reports = _ref5.reports;
  return function (dispatch) {
    return Promise.all(borrowers.map(function (borrower) {
      var _reports$borrowerID, _refreshFormData$borr, _refreshFormData$borr2;

      var borrowerID = borrower === null || borrower === void 0 ? void 0 : borrower.id;
      var reportsList = (_reports$borrowerID = reports[borrowerID]) !== null && _reports$borrowerID !== void 0 ? _reports$borrowerID : [];
      var reportIDs = [];

      if (refreshFormData !== null && refreshFormData !== void 0 && (_refreshFormData$borr = refreshFormData[borrowerID]) !== null && _refreshFormData$borr !== void 0 && _refreshFormData$borr.assets) {
        var voaReportIDs = getReportIDsByType(reportsList, ASSET_REPORT_TYPE);
        reportIDs.push.apply(reportIDs, _toConsumableArray(voaReportIDs));
      }

      if (refreshFormData !== null && refreshFormData !== void 0 && (_refreshFormData$borr2 = refreshFormData[borrowerID]) !== null && _refreshFormData$borr2 !== void 0 && _refreshFormData$borr2.employment) {
        var voieReportIDs = getReportIDsByType(reportsList, EMPLOYMENT_REPORT_TYPE);
        reportIDs.push.apply(reportIDs, _toConsumableArray(voieReportIDs));
      }

      return dispatch(refreshVerificationReports({
        reportIDs: reportIDs,
        borrowerID: borrowerID
      }));
    }));
  };
};

var transformRefreshByBorrower = function transformRefreshByBorrower(refreshedReports) {
  return (// Refreshing a report replaces the old id with a new one, so the status of the old must be
    // deleted if successful. The presence of a reportID indicates the refresh was successful
    refreshedReports.reduce(function (acc, report) {
      var replacedReportIDs = _toConsumableArray(acc.replacedReportIDs);

      var newStatuses = Object.assign({}, acc.newStatuses);

      if (report.originalReportID && report.reportID) {
        replacedReportIDs.push(report.originalReportID);
        newStatuses[report.reportID] = {
          hasError: null,
          isSuccessful: true
        };
      } else {
        newStatuses[report.originalReportID] = {
          hasError: true,
          isSuccessful: false
        };
      }

      return {
        replacedReportIDs: replacedReportIDs,
        newStatuses: newStatuses
      };
    }, {
      replacedReportIDs: [],
      newStatuses: {}
    })
  );
};

var DEFAULT_STATE$q = {
  borrowerVerifications: {
    verificationID: null,
    verificationLink: null,
    errors: null,
    isLoading: false,
    isSuccessful: false
  },
  borrowerReports: {
    isLoading: true,
    isSuccessful: false,
    reports: {}
  },
  hadNetworkError: false,
  refreshByBorrower: {}
};
var verifications$1 = (function (state, _ref6) {
  if (state === void 0) {
    state = DEFAULT_STATE$q;
  }

  var type = _ref6.type,
      payload = _ref6.payload,
      metadata = _ref6.metadata;

  switch (type) {
    case LONG_POLL_FETCH_BORROWER_REPORT.BEGIN:
      {
        return Object.assign({}, state, {
          hadNetworkError: false
        });
      }

    case FETCH_BORROWER_REPORT.BEGIN:
      {
        return Object.assign({}, state, {
          hadNetworkError: false,
          borrowerReports: {
            isLoading: true,
            isSuccessful: false
          }
        });
      }

    case FETCH_BORROWER_REPORT.SUCCESS:
      {
        var _payload$reports;

        return Object.assign({}, state, {
          borrowerReports: Object.assign({}, state.borrowerReports, {
            isLoading: false,
            isSuccessful: true,
            reports: ((_payload$reports = payload.reports) !== null && _payload$reports !== void 0 ? _payload$reports : []).reduce(function (acc, report) {
              var _acc$report$borrowerI, _Object$assign;

              return Object.assign({}, acc, (_Object$assign = {}, _Object$assign[report.borrowerID] = [].concat(_toConsumableArray((_acc$report$borrowerI = acc[report.borrowerID]) !== null && _acc$report$borrowerI !== void 0 ? _acc$report$borrowerI : []), [report]), _Object$assign));
            }, {})
          })
        });
      }

    case LONG_POLL_FETCH_BORROWER_REPORT.SUCCESS:
      {
        var _payload$reports2;

        var payloadReports = ((_payload$reports2 = payload.reports) !== null && _payload$reports2 !== void 0 ? _payload$reports2 : []).reduce(function (acc, report) {
          var _acc$report$borrowerI2, _Object$assign2;

          return Object.assign({}, acc, (_Object$assign2 = {}, _Object$assign2[report.borrowerID] = [].concat(_toConsumableArray((_acc$report$borrowerI2 = acc[report.borrowerID]) !== null && _acc$report$borrowerI2 !== void 0 ? _acc$report$borrowerI2 : []), [report]), _Object$assign2));
        }, {});
        return isEqual(state.borrowerReports.reports, payloadReports) ? state : Object.assign({}, state, {
          borrowerReports: Object.assign({}, state.borrowerReports, {
            reports: payloadReports
          })
        });
      }

    case FETCH_BORROWER_REPORT.FAILURE:
      {
        return Object.assign({}, state, {
          hadNetworkError: (payload === null || payload === void 0 ? void 0 : payload.type) === NETWORK_ERROR_TYPE || !(payload !== null && payload !== void 0 && payload.type),
          borrowerReports: Object.assign({}, state.borrowerReports, {
            isSuccessful: false,
            isLoading: false
          })
        });
      }

    case SUBMIT_EMAIL_CTA.BEGIN:
      {
        return Object.assign({}, state, {
          hadNetworkError: false,
          borrowerVerifications: Object.assign({}, state.borrowerVerifications, {
            isLoading: true,
            errors: null,
            isSuccessful: false
          })
        });
      }

    case SUBMIT_EMAIL_CTA.SUCCESS:
      {
        return Object.assign({}, state, {
          borrowerVerifications: Object.assign({}, state.borrowerVerifications, payload, {
            isLoading: false,
            isSuccessful: true
          })
        });
      }

    case SUBMIT_EMAIL_CTA.FAILURE:
      {
        var errors = [DEFAULT_LOP_API_FAILURE_ERROR_MESSAGE];

        if (payload !== null && payload !== void 0 && payload.verifications) {
          errors = Array.from(payload.verifications.reduce(function (acc, cur) {
            if (cur.errorMessage) {
              acc.add(cur.errorMessage);
            }

            return acc;
          }, new Set()));
        }

        return Object.assign({}, state, {
          hadNetworkError: payload.type === NETWORK_ERROR_TYPE,
          borrowerVerifications: Object.assign({}, state.borrowerVerifications, {
            errors: errors,
            isLoading: false,
            isSuccessful: false
          })
        });
      }

    case REFRESH_REPORTS.BEGIN:
      {
        var _state$refreshByBorro, _Object$assign4;

        return Object.assign({}, state, {
          hadNetworkError: false,
          refreshByBorrower: Object.assign({}, state.refreshByBorrower, (_Object$assign4 = {}, _Object$assign4[metadata.borrowerID] = Object.assign({}, state.refreshByBorrower[metadata.borrowerID], {
            isRefreshing: true,
            isSuccessful: false,
            hasError: null,
            reportRefreshStatuses: Object.assign({}, (_state$refreshByBorro = state.refreshByBorrower[metadata.borrowerID]) === null || _state$refreshByBorro === void 0 ? void 0 : _state$refreshByBorro.reportRefreshStatuses, metadata.reportIDs.reduce(function (acc, id) {
              var _Object$assign3;

              return Object.assign({}, acc, (_Object$assign3 = {}, _Object$assign3[id] = {
                isSuccessful: false,
                hasError: null
              }, _Object$assign3));
            }, {}))
          }), _Object$assign4))
        });
      }

    case REFRESH_REPORTS.SUCCESS:
      {
        var _state$refreshByBorro2, _Object$assign5;

        var previousStatuses = (_state$refreshByBorro2 = state.refreshByBorrower[metadata.borrowerID]) === null || _state$refreshByBorro2 === void 0 ? void 0 : _state$refreshByBorro2.reportRefreshStatuses;
        var transformedRefreshStatus = transformRefreshByBorrower(payload.reports);
        var filteredStatuses = omit(previousStatuses, transformedRefreshStatus.replacedReportIDs);
        return Object.assign({}, state, {
          refreshByBorrower: Object.assign({}, state.refreshByBorrower, (_Object$assign5 = {}, _Object$assign5[metadata.borrowerID] = Object.assign({}, state.refreshByBorrower[metadata.borrowerID], {
            isRefreshing: false,
            isSuccessful: true,
            hasError: null,
            reportRefreshStatuses: Object.assign({}, filteredStatuses, transformedRefreshStatus.newStatuses)
          }), _Object$assign5))
        });
      }

    case REFRESH_REPORTS.FAILURE:
      {
        var _state$refreshByBorro3, _payload$reports3, _Object$assign6;

        var _previousStatuses = (_state$refreshByBorro3 = state.refreshByBorrower[metadata.borrowerID]) === null || _state$refreshByBorro3 === void 0 ? void 0 : _state$refreshByBorro3.reportRefreshStatuses;

        var _transformedRefreshStatus = transformRefreshByBorrower((_payload$reports3 = payload === null || payload === void 0 ? void 0 : payload.reports) !== null && _payload$reports3 !== void 0 ? _payload$reports3 : []);

        var _filteredStatuses = omit(_previousStatuses, _transformedRefreshStatus.replacedReportIDs);

        return Object.assign({}, state, {
          hadNetworkError: (payload === null || payload === void 0 ? void 0 : payload.type) === NETWORK_ERROR_TYPE || !(payload !== null && payload !== void 0 && payload.type),
          refreshByBorrower: Object.assign({}, state.refreshByBorrower, (_Object$assign6 = {}, _Object$assign6[metadata.borrowerID] = Object.assign({}, state.refreshByBorrower[metadata.borrowerID], {
            isRefreshing: false,
            isSuccessful: false,
            hasError: true,
            reportRefreshStatuses: Object.assign({}, _filteredStatuses, _transformedRefreshStatus.newStatuses)
          }), _Object$assign6))
        });
      }

    default:
      return state;
  }
});

var verifications$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  VERIFICATIONS_SUBDOMAIN: VERIFICATIONS_SUBDOMAIN,
  VERIFICATIONS_ENDPOINT: VERIFICATIONS_ENDPOINT,
  VERIFICATIONS_REPORT_ENDPOINT: VERIFICATIONS_REPORT_ENDPOINT,
  EMAIL_CTA: EMAIL_CTA,
  SUBMIT_EMAIL_CTA: SUBMIT_EMAIL_CTA,
  FETCH_BORROWER_REPORT: FETCH_BORROWER_REPORT,
  LONG_POLL_FETCH_BORROWER_REPORT: LONG_POLL_FETCH_BORROWER_REPORT,
  submitVerificationEmailCTA: submitVerificationEmailCTA,
  fetchBorrowerAssetReports: fetchBorrowerAssetReports,
  longPollFetchBorrowerAssetReports: longPollFetchBorrowerAssetReports,
  REPORTS_ENDPOINT: REPORTS_ENDPOINT,
  REFRESH_REPORTS: REFRESH_REPORTS,
  refreshVerificationReports: refreshVerificationReports,
  submitVerificationRefresh: submitVerificationRefresh,
  DEFAULT_STATE: DEFAULT_STATE$q,
  'default': verifications$1
});

var _excluded$m = ["attributes"],
    _excluded2$7 = ["id", "attributes", "rateQuotes", "targetRateQuoteID"];
var FEES_SERVICE = 'loan-fees';
var FEE_ID_SELECTED = 'FEE_ID_SELECTED';
var feeIDSelected = function feeIDSelected(feeID) {
  return {
    type: FEE_ID_SELECTED,
    payload: feeID
  };
};
var TWENTY_SECONDS = 20000;
var CREATE_FEES = createActionObject('CREATE_FEES');
var createFees = function createFees(request) {
  return API[FEES_SERVICE].POST.thunk({
    action: 'v1/fees',
    input: request,
    authenticated: true,
    headers: {
      'Content-Type': 'application/json'
    },
    timeout: TWENTY_SECONDS
  }, CREATE_FEES);
};
var createFeesForCurrentPricingScenario = function createFeesForCurrentPricingScenario(loanID) {
  return function (dispatch, getState) {
    return new Promise(function ($return, $error) {
      var feesRequest, _await$dispatch, feeID;

      feesRequest = createFeesRequestSelector(getState(), {
        loanID: loanID
      });

      var $Try_1_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_1_Catch = function (e) {
        try {
          captureException(e);
          return $Try_1_Post();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        return Promise.resolve(dispatch(createFees(feesRequest))).then(function ($await_10) {
          try {
            _await$dispatch = $await_10, feeID = _await$dispatch.feeID;
            dispatch(feeIDSelected(feeID));
            return $Try_1_Post();
          } catch ($boundEx) {
            return $Try_1_Catch($boundEx);
          }
        }, $Try_1_Catch);
      } catch (e) {
        $Try_1_Catch(e);
      }
    });
  };
};
var FETCH_FEES = createActionObject('FETCH_FEES');
var fetchFees = function fetchFees(feeID) {
  return API[FEES_SERVICE].GET.thunk({
    action: "v1/fees/" + feeID,
    authenticated: true,
    headers: {
      'Content-Type': 'application/json'
    }
  }, FETCH_FEES);
};
var fetchFeesForScenarios = function fetchFeesForScenarios() {
  return function (dispatch, getState) {
    return new Promise(function ($return, $error) {
      var scenarioFeeIDs;
      scenarioFeeIDs = feeIDsForScenariosSelector(getState());

      if (scenarioFeeIDs) {
        return Promise.resolve(Promise.all(scenarioFeeIDs.map(function (feeID) {
          return dispatch(fetchFees(feeID));
        }))).then(function ($await_11) {
          try {
            return $If_3.call(this);
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }.bind(this), $error);
      }

      function $If_3() {
        return $return();
      }

      return $If_3.call(this);
    });
  };
};
var UPDATE_PRICING_FIELDS = 'UPDATE_PRICING_FIELDS';
var updateFields$6 = function updateFields(fields, errors) {
  return {
    type: UPDATE_PRICING_FIELDS,
    payload: {
      fields: fields,
      errors: errors || {}
    }
  };
};
var resetEditableFields = function resetEditableFields() {
  return function (dispatch, getState) {
    var _primaryScenarioLoanP;

    var loanPurpose = (_primaryScenarioLoanP = primaryScenarioLoanPurposeSelector(getState())) !== null && _primaryScenarioLoanP !== void 0 ? _primaryScenarioLoanP : DEFAULT_FIELDS.loanPurpose;
    return dispatch(updateFields$6(Object.assign({}, DEFAULT_FIELDS, {
      loanPurpose: loanPurpose
    })));
  };
};
var FETCH_RATE_LOCK_URL = createActionObject('FETCH_RATE_LOCK_URL');
var fetchRateLockURL = function fetchRateLockURL(props) {
  return function (dispatch) {
    var loanID = loanIDPropSelector(null, props);
    return dispatch(API.pricing.GET.thunk({
      action: "v1/loan-rate-quotes/" + loanID + "/mortech-lock-url",
      authenticated: true
    }, FETCH_RATE_LOCK_URL));
  };
};
var FETCH_PRICING_RESULTS = createActionObject('FETCH_PRICING_RESULTS');

var fetchScenariosThunk = function fetchScenariosThunk(loanID, defaultLockTerm) {
  return API.pricing.GET.thunk({
    action: 'v1/scenarios',
    input: {
      loanID: loanID,
      include: ['loanRateQuote', 'rateQuotes'],
      targetLockTerm: defaultLockTerm,
      targetDiscountPoints: 0 // Default rate quote is that which is closest to PAR

    },
    authenticated: true
  }, FETCH_PRICING_RESULTS);
};

var FETCH_SCENARIO_RATE_QUOTES = createActionObject('FETCH_SCENARIO_RATE_QUOTES');
var fetchScenarioRateQuotes = function fetchScenarioRateQuotes(props) {
  return function (dispatch) {
    var scenarioID = scenarioIDPropSelector(null, props);
    var scenarioLabel = scenarioLabelPropSelector(null, props);
    return dispatch(API.pricing.GET.thunk({
      action: "v1/scenarios/" + scenarioID + "/rate-quotes",
      authenticated: true,
      metadata: {
        scenarioLabel: scenarioLabel
      }
    }, FETCH_SCENARIO_RATE_QUOTES));
  };
};
var DEFAULT_LOCK_TERM_SETTING = 'default_lock_term';
var defaultLockTermResponse;

var fetchDefaultLockTermThunk = function fetchDefaultLockTermThunk() {
  return function (dispatch) {
    return new Promise(function ($return, $error) {
      var $Try_2_Post = function () {
        try {
          return $return(defaultLockTermResponse);
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_2_Catch = function (e) {
        try {
          // It's fine, we are ok using a fallback if need be
          defaultLockTermResponse = null;
          return $Try_2_Post();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      try {
        if (defaultLockTermResponse === undefined) {
          return Promise.resolve(dispatch(fetchPublicSettingsValues(DEFAULT_LOCK_TERM_SETTING))).then(function ($await_12) {
            try {
              defaultLockTermResponse = $await_12;
              return $If_4.call(this);
            } catch ($boundEx) {
              return $Try_2_Catch($boundEx);
            }
          }.bind(this), $Try_2_Catch);
        }

        function $If_4() {
          return $Try_2_Post();
        }

        return $If_4.call(this);
      } catch (e) {
        $Try_2_Catch();
      }
    });
  };
};

var defaultLockTermSelector = createSettingsValueSelector(DEFAULT_LOCK_TERM_SETTING);
var fetchScenarios = function fetchScenarios(loanID) {
  return function (dispatch, getState) {
    return new Promise(function ($return, $error) {
      var defaultLockTerm;
      return Promise.resolve(dispatch(fetchDefaultLockTermThunk())).then(function ($await_13) {
        try {
          defaultLockTerm = defaultLockTermSelector(getState());
          return Promise.resolve(dispatch(fetchScenariosThunk(loanID, defaultLockTerm))).then(function ($await_14) {
            try {
              return Promise.resolve(dispatch(fetchFeesForScenarios())).then(function ($await_15) {
                try {
                  return $return();
                } catch ($boundEx) {
                  return $error($boundEx);
                }
              }, $error);
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }, $error);
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  };
};
var updatePricingFieldsFromLoanFile = function updatePricingFieldsFromLoanFile(props) {
  return function (dispatch, getState) {
    return dispatch(updateFields$6(loanFilePricingFieldsSelector(getState(), props)));
  };
};
var INITIALIZE_SCENARIO_BUILDER = createActionObject('INITIALIZED_SCENARIO_BUILDER');
var initializeScenarioBuilder = function initializeScenarioBuilder(loanID) {
  return function (dispatch) {
    return new Promise(function ($return, $error) {
      dispatch({
        type: INITIALIZE_SCENARIO_BUILDER.BEGIN
      });
      return Promise.resolve(Promise.all([dispatch(fetchScenarios(loanID)), dispatch(fetchEmploymentsAndIncome({
        loanID: loanID
      })), dispatch(fetchReports(loanID)), dispatch(fetchBorrowers([loanID]))])).then(function ($await_16) {
        try {
          dispatch(updatePricingFieldsFromLoanFile({
            loanID: loanID
          }));
          return $return(dispatch({
            type: INITIALIZE_SCENARIO_BUILDER.SUCCESS
          }));
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  };
};
var CREATE_SCENARIO = createActionObject('CREATE_SCENARIO');

var createScenarioThunk = function createScenarioThunk(attributes, loanID, defaultLockTerm) {
  return API.pricing.POST.thunk({
    action: 'v1/scenarios',
    input: {
      loanID: loanID,
      attributes: attributes,
      targetLockTerm: defaultLockTerm
    },
    authenticated: true
  }, CREATE_SCENARIO);
};

var SET_RATE_QUOTE_FOR_LOAN = createActionObject('SET_RATE_QUOTE_FOR_LOAN');

var setRateQuoteForLoanThunk = function setRateQuoteForLoanThunk(loanID, rateQuoteID) {
  return API.pricing.PUT.thunk({
    action: "v1/loan-rate-quotes/" + loanID,
    input: {
      rateQuoteID: rateQuoteID
    },
    authenticated: true
  }, SET_RATE_QUOTE_FOR_LOAN);
};

var FORCE_RATE_QUOTE_SELECTION = 'FORCE_RATE_QUOTE_SELECTION';

var forceRateQuoteSelection = function forceRateQuoteSelection(rateQuote, scenarioLabel) {
  return {
    type: FORCE_RATE_QUOTE_SELECTION,
    payload: {
      rateQuote: rateQuote,
      scenarioLabel: scenarioLabel
    }
  };
};

var scenarioHasValidPricing = function scenarioHasValidPricing(scenario) {
  return scenario.rateQuotes.length > 0;
};

var createScenario = function createScenario(props, scenarioLabel) {
  if (scenarioLabel === void 0) {
    scenarioLabel = v4_1();
  }

  return function (dispatch, getState) {
    return new Promise(function ($return, $error) {
      var isAnUpdateToPrimary, selectedRateQuote, defaultLockTerm, request, loanAttributesUpdateRequest, loanID, createdScenario, isNewlyCreatedPrimary, rateQuoteID;
      isAnUpdateToPrimary = scenarioLabel === primaryScenarioLabelSelector(getState());
      selectedRateQuote = primaryRateQuoteSelector(getState(), props);
      return Promise.resolve(dispatch(fetchDefaultLockTermThunk())).then(function ($await_17) {
        try {
          defaultLockTerm = defaultLockTermSelector(getState());
          request = pricingRequestSelector(getState());
          loanAttributesUpdateRequest = loanAttributesUpdateRequestSelector(getState());
          loanID = loanIDPropSelector(null, props);
          return Promise.resolve(dispatch(createScenarioThunk(Object.assign({}, request, {
            scenarioLabel: scenarioLabel
          }), loanID, defaultLockTerm))).then(function ($await_18) {
            try {
              createdScenario = $await_18;

              if (scenarioHasValidPricing(createdScenario.scenario)) {
                isNewlyCreatedPrimary = !isAnUpdateToPrimary && scenarioLabel === primaryScenarioLabelSelector(getState());

                if (isNewlyCreatedPrimary) {
                  rateQuoteID = createdScenario.scenario.targetRateQuoteID;
                  return Promise.resolve(dispatch(setRateQuoteForLoanThunk(loanID, rateQuoteID))).then(function ($await_19) {
                    try {
                      return $If_6.call(this);
                    } catch ($boundEx) {
                      return $error($boundEx);
                    }
                  }.bind(this), $error);
                } else {
                  if (isAnUpdateToPrimary) {
                    // When we update a primary scenario, we need the LO to select a rate quote from the
                    // rate quote modal. We want to show the list of rate quotes for the newly created
                    // pricing run, but also the previously selected rate quote as context for their decision
                    dispatch(forceRateQuoteSelection(selectedRateQuote, scenarioLabel));
                  }

                  return $If_6.call(this);
                }

                function $If_6() {
                  if (isNewlyCreatedPrimary || isAnUpdateToPrimary) {
                    return Promise.resolve(dispatch(apiUpdateLoanAttributes(loanID, loanAttributesUpdateRequest))).then(function ($await_20) {
                      try {
                        return Promise.resolve(dispatch(fetchLoan(loanID))).then(function ($await_21) {
                          try {
                            return $If_7.call(this);
                          } catch ($boundEx) {
                            return $error($boundEx);
                          }
                        }.bind(this), $error);
                      } catch ($boundEx) {
                        return $error($boundEx);
                      }
                    }.bind(this), $error);
                  }

                  function $If_7() {
                    return $If_5.call(this);
                  }

                  return $If_7.call(this);
                }
              }

              function $If_5() {
                return $return();
              }

              return $If_5.call(this);
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }.bind(this), $error);
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }.bind(this), $error);
    });
  };
};
var updateScenario = function updateScenario(props, scenario) {
  return function (dispatch) {
    return dispatch(createScenario(props, scenario.scenarioLabel));
  };
};
var updateScenarioFromOutdatedAlert = function updateScenarioFromOutdatedAlert(loanID) {
  return function (dispatch, getState) {
    return new Promise(function ($return, $error) {
      var primaryScenario;
      primaryScenario = primaryScenarioSelector(getState(), {
        loanID: loanID
      });
      dispatch(updatePricingFieldsFromLoanFile());

      if (primaryScenario.scenario.feeID) {
        return Promise.resolve(dispatch(createFeesForCurrentPricingScenario(loanID))).then(function ($await_22) {
          try {
            return $If_8.call(this);
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }.bind(this), $error);
      }

      function $If_8() {
        return $return(dispatch(updateScenario({
          loanID: loanID
        }, primaryScenario.scenario)));
      }

      return $If_8.call(this);
    });
  };
};
var SET_PRIMARY_SCENARIO = 'SET_PRIMARY_SCENARIO';
var setPrimaryScenario = function setPrimaryScenario(loanID, scenario) {
  return function (dispatch) {
    return new Promise(function ($return, $error) {
      return Promise.resolve(dispatch(setRateQuoteForLoanThunk(loanID, scenario.selectedRateQuoteID))).then(function ($await_23) {
        try {
          return $return(dispatch({
            type: SET_PRIMARY_SCENARIO,
            payload: scenario.scenario.scenarioLabel
          }));
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  };
};
var UPDATE_SELECTED_RATE_QUOTE = 'UPDATE_SELECTED_RATE_QUOTE';
var updateSelectedRateQuote = function updateSelectedRateQuote(loanID, scenarioLabel, selectedRateQuoteID) {
  return function (dispatch, getState) {
    return new Promise(function ($return, $error) {
      if (scenarioLabel === primaryScenarioLabelSelector(getState()) && loanID) {
        return Promise.resolve(dispatch(setRateQuoteForLoanThunk(loanID, selectedRateQuoteID))).then(function ($await_24) {
          try {
            return $If_9.call(this);
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }.bind(this), $error);
      }

      function $If_9() {
        return $return(dispatch({
          type: UPDATE_SELECTED_RATE_QUOTE,
          payload: {
            scenarioLabel: scenarioLabel,
            selectedRateQuoteID: selectedRateQuoteID
          }
        }));
      }

      return $If_9.call(this);
    });
  };
};
var GET_PRE_QUALIFICATION_SCENARIOS = createActionObject('GET_PRE_QUALIFICATION_SCENARIOS');
var getPreQualificationScenariosThunk = function getPreQualificationScenariosThunk(loanID) {
  return API.pricing.POST.thunk({
    action: 'v1/prequalification-scenarios',
    input: {
      loanID: loanID
    },
    authenticated: true
  }, GET_PRE_QUALIFICATION_SCENARIOS);
};

var normalizeRateQuotes = function normalizeRateQuotes(rateQuoteIDs, includedRateQuotes) {
  return rateQuoteIDs.reduce(function (accum, rateQuoteID) {
    var _Object$assign, _accum$rateQuoteIDsBy, _Object$assign2;

    var rateQuote = includedRateQuotes[rateQuoteID];

    if (!rateQuote) {
      return accum;
    }

    var attributes = rateQuote.attributes,
        rest = _objectWithoutProperties(rateQuote, _excluded$m);

    var discountPoints = attributes.discountPoints,
        loanAmount = attributes.loanAmount,
        adjustments = attributes.adjustments;
    return {
      rateQuotes: Object.assign({}, accum.rateQuotes, (_Object$assign = {}, _Object$assign[rateQuoteID] = Object.assign({}, attributes, rest, {
        costOfPrice: discountPoints / 100 * loanAmount,
        totalPriceAdjustment: (adjustments || []).reduce(function (sum, _ref) {
          var discountPointAdjustment = _ref.discountPointAdjustment;
          return sum + discountPointAdjustment;
        }, 0)
      }), _Object$assign)),
      rateQuoteIDsByLockTerm: Object.assign({}, accum.rateQuoteIDsByLockTerm, (_Object$assign2 = {}, _Object$assign2[attributes.lockTerm] = [].concat(_toConsumableArray((_accum$rateQuoteIDsBy = accum.rateQuoteIDsByLockTerm[attributes.lockTerm]) !== null && _accum$rateQuoteIDsBy !== void 0 ? _accum$rateQuoteIDsBy : []), [rateQuoteID]), _Object$assign2))
    };
  }, {
    rateQuotes: {},
    rateQuoteIDsByLockTerm: {}
  });
};

var produceNormalizedScenario = function produceNormalizedScenario(scenarioID, scenario, rateQuoteIDs, includedRateQuotes, selectedRateQuoteID) {
  return Object.assign({
    id: scenarioID,
    scenario: scenario
  }, normalizeRateQuotes(rateQuoteIDs, includedRateQuotes), {
    selectedRateQuoteID: selectedRateQuoteID
  });
};

var normalizeFeeStateAndResponse = function normalizeFeeStateAndResponse(existingFeeMap, _ref2) {
  var _Object$assign3;

  var feeID = _ref2.feeID,
      taxItems = _ref2.taxItems,
      feeItems = _ref2.feeItems;
  return Object.assign({}, existingFeeMap, (_Object$assign3 = {}, _Object$assign3[feeID] = {
    id: feeID,
    taxItems: taxItems !== null && taxItems !== void 0 ? taxItems : [],
    feeItems: feeItems !== null && feeItems !== void 0 ? feeItems : []
  }, _Object$assign3));
};

var DEFAULT_STATE$r = {
  isLoading: false,
  areFeesLoading: false,
  scenarios: {},
  feesByID: null,
  scenarioOrder: [],
  hasLoadedScenarios: false,
  hasInitializedScenarioBuilder: false,
  fields: DEFAULT_FIELDS,
  forceRateQuoteSelection: false,
  previouslySelectedRateQuote: null,
  errors: {}
};

var getStateForNewScenario = function getStateForNewScenario(state, payload) {
  var _Object$assign4, _Object$assign5;

  var scenario = payload.scenario,
      rateQuotes = payload.included.rateQuotes;
  var id = scenario.id,
      attributes = scenario.attributes,
      targetRateQuoteID = scenario.targetRateQuoteID,
      rateQuoteIDs = scenario.rateQuotes;
  var createdScenarioHasValidPricing = scenarioHasValidPricing(scenario);
  var common = Object.assign({}, state, {
    isLoading: false,
    errors: Object.assign({}, state.errors, (_Object$assign4 = {}, _Object$assign4[PRICING_ERRORS.INELIGIBLE] = createdScenarioHasValidPricing ? null : {
      type: PRICING_ERRORS.INELIGIBLE
    }, _Object$assign4))
  });
  var scenarioOrder = common.scenarioOrder,
      scenarios = common.scenarios,
      primaryScenarioLabel = common.primaryScenarioLabel;
  return createdScenarioHasValidPricing ? Object.assign({}, common, {
    scenarios: Object.assign({}, scenarios, (_Object$assign5 = {}, _Object$assign5[attributes.scenarioLabel] = Object.assign({}, scenario, produceNormalizedScenario(id, attributes, rateQuoteIDs, rateQuotes, targetRateQuoteID)), _Object$assign5)),
    scenarioOrder: scenarioOrder.includes(attributes.scenarioLabel) ? scenarioOrder : [].concat(_toConsumableArray(scenarioOrder), [attributes.scenarioLabel]),
    primaryScenarioLabel: scenarioOrder.length === 0 ? attributes.scenarioLabel : primaryScenarioLabel,
    fields: DEFAULT_STATE$r.fields
  }) : common;
};

var pricing$1 = (function (state, _ref3) {
  var _Object$assign8, _Object$assign15, _Object$assign16;

  if (state === void 0) {
    state = DEFAULT_STATE$r;
  }

  var type = _ref3.type,
      payload = _ref3.payload,
      _ref3$metadata = _ref3.metadata,
      metadata = _ref3$metadata === void 0 ? {} : _ref3$metadata;

  switch (type) {
    case FORCE_RATE_QUOTE_SELECTION:
      return Object.assign({}, state, {
        scenarioLabelToSelectFor: payload.scenarioLabel,
        forceRateQuoteSelection: true,
        previouslySelectedRateQuote: payload.rateQuote
      });

    case FETCH_PRICING_RESULTS.FAILURE:
      var _Object$assign6;

      return Object.assign({}, state, {
        errors: Object.assign({}, state.errors, (_Object$assign6 = {}, _Object$assign6[PRICING_ERRORS.FETCH_SCENARIO_FAILURE] = {
          type: errorTypeFromError(payload),
          error: payload
        }, _Object$assign6))
      });

    case INITIALIZE_SCENARIO_BUILDER.BEGIN:
      return DEFAULT_STATE$r;

    case INITIALIZE_SCENARIO_BUILDER.SUCCESS:
      return Object.assign({}, state, {
        hasInitializedScenarioBuilder: true
      });

    case FETCH_PRICING_RESULTS.SUCCESS:
      var apiScenarios = payload.scenarios,
          _payload$included = payload.included;
      _payload$included = _payload$included === void 0 ? {} : _payload$included;
      var _payload$included$rat = _payload$included.rateQuotes,
          includedRateQuotes = _payload$included$rat === void 0 ? {} : _payload$included$rat,
          loanRateQuote = _payload$included.loanRateQuote;

      var _ref4 = loanRateQuote !== null && loanRateQuote !== void 0 ? loanRateQuote : {},
          loanRateQuoteID = _ref4.rateQuoteID,
          loanScenarioID = _ref4.scenarioID;

      return (apiScenarios !== null && apiScenarios !== void 0 ? apiScenarios : []).filter(function (scenario) {
        return scenarioHasValidPricing(scenario) && !!scenario.attributes.scenarioLabel;
      }).sort(function (_ref5, _ref6) {
        var a = _ref5.createdUTC;
        var b = _ref6.createdUTC;
        return new Date(b).getTime() - new Date(a).getTime();
      }).reduce(function (acc, _ref7) {
        var id = _ref7.id,
            attributes = _ref7.attributes,
            rateQuoteIDs = _ref7.rateQuotes,
            targetRateQuoteID = _ref7.targetRateQuoteID,
            otherScenarioProps = _objectWithoutProperties(_ref7, _excluded2$7);

        var scenarios = acc.scenarios;
        var scenarioOrder = acc.scenarioOrder;
        var primaryScenarioLabel = acc.primaryScenarioLabel;
        var isLoanScenario = loanScenarioID && loanScenarioID === id; // must be false when loanScenarioID and id are `null`.
        // Select target rate quote if not the primary rate quote on the loan

        var selectedRateQuoteID = isLoanScenario ? loanRateQuoteID : targetRateQuoteID; // Ignore previous runs of scenarios we already have in the store, as they are
        // sorted by created date above.

        if (!acc.scenarios[attributes.scenarioLabel]) {
          var _Object$assign7;

          scenarios = Object.assign({}, scenarios, (_Object$assign7 = {}, _Object$assign7[attributes.scenarioLabel] = Object.assign({}, otherScenarioProps, produceNormalizedScenario(id, attributes, rateQuoteIDs, includedRateQuotes, selectedRateQuoteID)), _Object$assign7));

          if (isLoanScenario) {
            primaryScenarioLabel = attributes.scenarioLabel;
            scenarioOrder = [attributes.scenarioLabel].concat(_toConsumableArray(scenarioOrder));
          } else {
            scenarioOrder = scenarioOrder.concat(attributes.scenarioLabel);
          }
        }

        return Object.assign({}, acc, {
          primaryScenarioLabel: primaryScenarioLabel,
          scenarios: scenarios,
          scenarioOrder: scenarioOrder
        });
      }, Object.assign({}, state, {
        hasLoadedScenarios: true,
        loanRateQuote: loanRateQuote
      }));

    case CREATE_SCENARIO.BEGIN:
      return Object.assign({}, state, {
        isLoading: true,
        errors: Object.assign({}, state.errors, (_Object$assign8 = {}, _Object$assign8[PRICING_ERRORS.CREATE_SCENARIO_FAILURE] = null, _Object$assign8))
      });

    case CREATE_SCENARIO.SUCCESS:
      return getStateForNewScenario(state, payload);

    case CREATE_SCENARIO.FAILURE:
      var _Object$assign9;

      return Object.assign({}, state, {
        isLoading: false,
        errors: Object.assign({}, state.errors, (_Object$assign9 = {}, _Object$assign9[PRICING_ERRORS.CREATE_SCENARIO_FAILURE] = {
          type: errorTypeFromError(payload),
          error: payload
        }, _Object$assign9))
      });

    case UPDATE_SELECTED_RATE_QUOTE:
      var _Object$assign10;

      return Object.assign({}, state, {
        scenarios: Object.assign({}, state.scenarios, (_Object$assign10 = {}, _Object$assign10[payload.scenarioLabel] = Object.assign({}, state.scenarios[payload.scenarioLabel], {
          selectedRateQuoteID: payload.selectedRateQuoteID
        }), _Object$assign10)),
        scenarioLabelToSelectFor: DEFAULT_STATE$r.scenarioLabelToSelectFor,
        forceRateQuoteSelection: false,
        previouslySelectedRateQuote: DEFAULT_STATE$r.previouslySelectedRateQuote,
        fields: DEFAULT_STATE$r.fields
      });

    case UPDATE_PRICING_FIELDS:
      return Object.assign({}, state, payload);

    case SET_PRIMARY_SCENARIO:
      return Object.assign({}, state, {
        scenarioOrder: [payload].concat(_toConsumableArray(state.scenarioOrder.filter(function (id) {
          return id !== payload;
        }))),
        primaryScenarioLabel: payload
      });

    case FETCH_SCENARIO_RATE_QUOTES.BEGIN:
      var _Object$assign11;

      return Object.assign({}, state, {
        errors: Object.assign({}, state.errors, (_Object$assign11 = {}, _Object$assign11[PRICING_ERRORS.FETCH_SCENARIO_RATE_QUOTES_ERROR] = null, _Object$assign11))
      });

    case FETCH_SCENARIO_RATE_QUOTES.SUCCESS:
      var _Object$assign13;

      var rateQuotes = payload.rateQuotes;

      var _rateQuotes$reduce = rateQuotes.reduce(function (acc, rateQuote) {
        var _Object$assign12;

        return {
          rateQuoteIDs: [].concat(_toConsumableArray(acc.rateQuoteIDs), [rateQuote.id]),
          rateQuoteMap: Object.assign({}, acc.rateQuoteMap, (_Object$assign12 = {}, _Object$assign12[rateQuote.id] = rateQuote, _Object$assign12))
        };
      }, {
        rateQuoteIDs: [],
        rateQuoteMap: {}
      }),
          rateQuoteIDs = _rateQuotes$reduce.rateQuoteIDs,
          rateQuoteMap = _rateQuotes$reduce.rateQuoteMap;

      return Object.assign({}, state, {
        scenarios: Object.assign({}, state.scenarios, (_Object$assign13 = {}, _Object$assign13[metadata.scenarioLabel] = Object.assign({}, state.scenarios[metadata.scenarioLabel], normalizeRateQuotes(rateQuoteIDs, rateQuoteMap)), _Object$assign13))
      });

    case FETCH_SCENARIO_RATE_QUOTES.FAILURE:
      var _Object$assign14;

      return Object.assign({}, state, {
        errors: Object.assign({}, state.errors, (_Object$assign14 = {}, _Object$assign14[PRICING_ERRORS.FETCH_SCENARIO_RATE_QUOTES_ERROR] = {
          error: payload,
          type: errorTypeFromError(payload)
        }, _Object$assign14))
      });

    case FEE_ID_SELECTED:
      return Object.assign({}, state, {
        currentFeeID: payload
      });

    case FETCH_FEES.BEGIN:
    case CREATE_FEES.BEGIN:
      return Object.assign({}, state, {
        areFeesLoading: true,
        errors: Object.assign({}, state.errors, (_Object$assign15 = {}, _Object$assign15[PRICING_ERRORS.FEES_ERROR] = null, _Object$assign15))
      });

    case FETCH_FEES.SUCCESS:
    case CREATE_FEES.SUCCESS:
      var feesByID = normalizeFeeStateAndResponse(state.feesByID, payload);
      return Object.assign({}, state, {
        feesByID: feesByID,
        areFeesLoading: false
      });

    case FETCH_FEES.FAILURE:
    case CREATE_FEES.FAILURE:
      return Object.assign({}, state, {
        errors: Object.assign({}, state.errors, (_Object$assign16 = {}, _Object$assign16[PRICING_ERRORS.FEES_ERROR] = {
          error: payload,
          type: payload === null || payload === void 0 ? void 0 : payload.type
        }, _Object$assign16)),
        areFeesLoading: false
      });

    default:
      return state;
  }
});

var pricing$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  FEE_ID_SELECTED: FEE_ID_SELECTED,
  feeIDSelected: feeIDSelected,
  CREATE_FEES: CREATE_FEES,
  createFees: createFees,
  createFeesForCurrentPricingScenario: createFeesForCurrentPricingScenario,
  FETCH_FEES: FETCH_FEES,
  fetchFees: fetchFees,
  fetchFeesForScenarios: fetchFeesForScenarios,
  UPDATE_PRICING_FIELDS: UPDATE_PRICING_FIELDS,
  updateFields: updateFields$6,
  resetEditableFields: resetEditableFields,
  FETCH_RATE_LOCK_URL: FETCH_RATE_LOCK_URL,
  fetchRateLockURL: fetchRateLockURL,
  FETCH_PRICING_RESULTS: FETCH_PRICING_RESULTS,
  FETCH_SCENARIO_RATE_QUOTES: FETCH_SCENARIO_RATE_QUOTES,
  fetchScenarioRateQuotes: fetchScenarioRateQuotes,
  fetchScenarios: fetchScenarios,
  updatePricingFieldsFromLoanFile: updatePricingFieldsFromLoanFile,
  INITIALIZE_SCENARIO_BUILDER: INITIALIZE_SCENARIO_BUILDER,
  initializeScenarioBuilder: initializeScenarioBuilder,
  CREATE_SCENARIO: CREATE_SCENARIO,
  SET_RATE_QUOTE_FOR_LOAN: SET_RATE_QUOTE_FOR_LOAN,
  FORCE_RATE_QUOTE_SELECTION: FORCE_RATE_QUOTE_SELECTION,
  createScenario: createScenario,
  updateScenario: updateScenario,
  updateScenarioFromOutdatedAlert: updateScenarioFromOutdatedAlert,
  SET_PRIMARY_SCENARIO: SET_PRIMARY_SCENARIO,
  setPrimaryScenario: setPrimaryScenario,
  UPDATE_SELECTED_RATE_QUOTE: UPDATE_SELECTED_RATE_QUOTE,
  updateSelectedRateQuote: updateSelectedRateQuote,
  GET_PRE_QUALIFICATION_SCENARIOS: GET_PRE_QUALIFICATION_SCENARIOS,
  getPreQualificationScenariosThunk: getPreQualificationScenariosThunk,
  DEFAULT_STATE: DEFAULT_STATE$r,
  'default': pricing$1
});

var addAddressItem = function addAddressItem(props, fields) {
  return function (dispatch, getState) {
    var borrowerID = borrowerIDPropSelector(null, props);
    var items = borrowersAddressesSelector(getState(), props).concat(fields);

    var _addressItemsToAPI = addressItemsToAPI(items),
        attributes = _addressItemsToAPI.attributes;

    return dispatch(apiUpdateBorrowerAttributes(attributes, borrowerID, API_UPDATE_BORROWER));
  };
};
var updateAddressItem = function updateAddressItem(props, fields) {
  return function (dispatch, getState) {
    var borrowerID = borrowerIDPropSelector(null, props);
    var items = borrowersAddressesSelector(getState(), props).map(function (item) {
      return item.id === fields.id ? fields : item;
    });

    var _addressItemsToAPI2 = addressItemsToAPI(items),
        attributes = _addressItemsToAPI2.attributes;

    return dispatch(apiUpdateBorrowerAttributes(attributes, borrowerID, API_UPDATE_BORROWER));
  };
};
var deleteAddressItem = function deleteAddressItem(props, addressID) {
  return function (dispatch, getState) {
    var borrowerID = borrowerIDPropSelector(null, props);
    var items = borrowersAddressesSelector(getState(), props).filter(function (_ref) {
      var id = _ref.id;
      return id !== addressID;
    });

    var _addressItemsToAPI3 = addressItemsToAPI(items),
        attributes = _addressItemsToAPI3.attributes;

    return dispatch(apiUpdateBorrowerAttributes(attributes, borrowerID, API_UPDATE_BORROWER));
  };
};

var addresses$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  addAddressItem: addAddressItem,
  updateAddressItem: updateAddressItem,
  deleteAddressItem: deleteAddressItem
});

var API_GET_OTHER_NEW_MORTGAGES = createActionObject('API_GET_OTHER_NEW_MORTGAGES');
var apiGetOtherNewMortgages = function apiGetOtherNewMortgages(loanID) {
  return API['loan-file'].GET.thunk({
    action: 'v1/other-new-mortgages',
    input: {
      loanID: loanID
    },
    authenticated: true
  }, API_GET_OTHER_NEW_MORTGAGES);
};
var API_CREATE_OTHER_NEW_MORTGAGE = createActionObject('API_CREATE_OTHER_NEW_MORTGAGE');
var apiCreateOtherNewMortgage = function apiCreateOtherNewMortgage(attributes, loanID) {
  return API['loan-file'].POST.thunk({
    action: 'v1/other-new-mortgages',
    authenticated: true,
    input: {
      loanID: loanID,
      attributes: attributes
    }
  }, API_CREATE_OTHER_NEW_MORTGAGE);
};
var API_UPDATE_OTHER_NEW_MORTGAGE_ATTRIBUTES = createActionObject('API_UPDATE_OTHER_NEW_MORTGAGE_ATTRIBUTES');
var apiUpdateOtherNewMortgageAttributes = function apiUpdateOtherNewMortgageAttributes(otherNewMortgageID, attributes) {
  return API['loan-file'].PUT.thunk({
    action: "v1/other-new-mortgages/" + otherNewMortgageID + "/attributes",
    authenticated: true,
    input: {
      attributes: attributes
    },
    metadata: otherNewMortgageID
  }, API_UPDATE_OTHER_NEW_MORTGAGE_ATTRIBUTES);
};
var API_DELETE_OTHER_NEW_MORTGAGE = createActionObject('API_DELETE_OTHER_NEW_MORTGAGE');
var apiDeleteOtherNewMortgage = function apiDeleteOtherNewMortgage(otherNewMortgageID) {
  return API['loan-file'].DELETE.thunk({
    action: "v1/other-new-mortgages/" + otherNewMortgageID,
    authenticated: true,
    metadata: otherNewMortgageID
  }, API_DELETE_OTHER_NEW_MORTGAGE);
};
var createOtherNewMortgage = function createOtherNewMortgage(loanID) {
  return function (dispatch, getState) {
    var _otherNewMortgagesFor = otherNewMortgagesFormFieldsSelector(getState()),
        attributes = _otherNewMortgagesFor.attributes;

    return dispatch(apiCreateOtherNewMortgage(attributes, loanID));
  };
};
var updateOtherNewMortgage = function updateOtherNewMortgage() {
  return function (dispatch, getState) {
    var _otherNewMortgagesFor2 = otherNewMortgagesFormFieldsSelector(getState()),
        id = _otherNewMortgagesFor2.id,
        attributes = _otherNewMortgagesFor2.attributes;

    return dispatch(apiUpdateOtherNewMortgageAttributes(id, attributes));
  };
};
var deleteOtherNewMortgage = function deleteOtherNewMortgage() {
  return function (dispatch, getState) {
    var _otherNewMortgagesFor3 = otherNewMortgagesFormFieldsSelector(getState()),
        id = _otherNewMortgagesFor3.id;

    return dispatch(apiDeleteOtherNewMortgage(id));
  };
};
var SET_OTHER_NEW_MORTGAGE_FORM_VALUES = 'SET_OTHER_NEW_MORTGAGE_FORM_VALUES';
var setOtherNewMortgageFormValues = function setOtherNewMortgageFormValues(item) {
  return {
    type: SET_OTHER_NEW_MORTGAGE_FORM_VALUES,
    payload: item
  };
};
var RESET_OTHER_NEW_MORTGAGE_FIELDS = 'RESET_OTHER_NEW_MORTGAGE_FIELDS';
var resetOtherNewMortgageFields = function resetOtherNewMortgageFields() {
  return {
    type: RESET_OTHER_NEW_MORTGAGE_FIELDS
  };
};
var UPDATE_FIELDS$5 = 'otherNewMortgages/UPDATE_FIELDS';
var updateFields$7 = function updateFields(fields, errors) {
  return {
    type: UPDATE_FIELDS$5,
    payload: {
      fields: fields,
      errors: errors
    }
  };
};
var fields$8 = {
  attributes: {}
};
var DEFAULT_STATE$s = {
  fields: fields$8,
  errors: {},
  items: []
};
var otherNewMortgages$1 = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$s;
  }

  var type = _ref.type,
      payload = _ref.payload,
      metadata = _ref.metadata,
      request = _ref.request;

  switch (type) {
    case API_GET_OTHER_NEW_MORTGAGES.SUCCESS:
      return Object.assign({}, state, {
        items: payload.otherNewMortgages
      });

    case API_CREATE_OTHER_NEW_MORTGAGE.SUCCESS:
      return Object.assign({}, state, {
        items: state.items.concat(Object.assign({}, request, {
          id: payload.otherNewMortgageID
        }))
      });

    case API_UPDATE_OTHER_NEW_MORTGAGE_ATTRIBUTES.SUCCESS:
      return Object.assign({}, state, {
        items: state.items.map(function (item) {
          return item.id === metadata ? Object.assign({}, item, request) : item;
        })
      });

    case API_DELETE_OTHER_NEW_MORTGAGE.SUCCESS:
      return Object.assign({}, state, {
        items: state.items.filter(function (_ref2) {
          var id = _ref2.id;
          return id !== metadata;
        })
      });

    case UPDATE_FIELDS$5:
      return isDeeplyEmpty(payload.errors) ? Object.assign({}, state, {
        fields: payload.fields,
        errors: {}
      }) : Object.assign({}, state, {
        errors: payload.errors
      });

    case SET_OTHER_NEW_MORTGAGE_FORM_VALUES:
      return Object.assign({}, state, {
        fields: payload,
        errors: {}
      });

    case RESET_OTHER_NEW_MORTGAGE_FIELDS:
      return Object.assign({}, state, {
        fields: fields$8,
        errors: {}
      });

    default:
      return state;
  }
});

var otherNewMortgages$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  API_GET_OTHER_NEW_MORTGAGES: API_GET_OTHER_NEW_MORTGAGES,
  apiGetOtherNewMortgages: apiGetOtherNewMortgages,
  API_CREATE_OTHER_NEW_MORTGAGE: API_CREATE_OTHER_NEW_MORTGAGE,
  apiCreateOtherNewMortgage: apiCreateOtherNewMortgage,
  API_UPDATE_OTHER_NEW_MORTGAGE_ATTRIBUTES: API_UPDATE_OTHER_NEW_MORTGAGE_ATTRIBUTES,
  apiUpdateOtherNewMortgageAttributes: apiUpdateOtherNewMortgageAttributes,
  API_DELETE_OTHER_NEW_MORTGAGE: API_DELETE_OTHER_NEW_MORTGAGE,
  apiDeleteOtherNewMortgage: apiDeleteOtherNewMortgage,
  createOtherNewMortgage: createOtherNewMortgage,
  updateOtherNewMortgage: updateOtherNewMortgage,
  deleteOtherNewMortgage: deleteOtherNewMortgage,
  SET_OTHER_NEW_MORTGAGE_FORM_VALUES: SET_OTHER_NEW_MORTGAGE_FORM_VALUES,
  setOtherNewMortgageFormValues: setOtherNewMortgageFormValues,
  RESET_OTHER_NEW_MORTGAGE_FIELDS: RESET_OTHER_NEW_MORTGAGE_FIELDS,
  resetOtherNewMortgageFields: resetOtherNewMortgageFields,
  UPDATE_FIELDS: UPDATE_FIELDS$5,
  updateFields: updateFields$7,
  DEFAULT_STATE: DEFAULT_STATE$s,
  'default': otherNewMortgages$1
});

var DOCUMENT_GENERATION_API = API['document-generation'];
var API_GET_PRE_APPROVALS = createActionObject('API_GET_PRE_APPROVALS');
var apiGetPreApprovals = function apiGetPreApprovals(loanID) {
  return DOCUMENT_GENERATION_API.GET.thunk({
    action: 'v1/pre-approvals/borrower',
    input: {
      loanID: loanID,
      compute: ['documentURL', 'downloadURL']
    },
    authenticated: true
  }, API_GET_PRE_APPROVALS);
};
var API_CREATE_PRE_APPROVAL = createActionObject('API_CREATE_PRE_APPROVAL');
var apiCreatePreApproval = function apiCreatePreApproval(loanID) {
  return DOCUMENT_GENERATION_API.POST.thunk({
    action: 'v1/pre-approvals/borrower',
    input: {
      loanID: loanID
    },
    authenticated: true
  }, API_CREATE_PRE_APPROVAL);
};
var fetchPreApprovals = function fetchPreApprovals(props) {
  return function (dispatch, getState) {
    var loanID = loanIDRouteParamSelector(getState(), props);
    return dispatch(apiGetPreApprovals(loanID));
  };
};
var newPreApproval = function newPreApproval(props) {
  return function (dispatch, getState) {
    return new Promise(function ($return, $error) {
      var $Try_1_Finally = function ($Try_1_Exit) {
        return function ($Try_1_Value) {
          try {
            return Promise.resolve(dispatch(apiGetPreApprovals(loanID))).then(function ($await_3) {
              try {
                return $Try_1_Exit && $Try_1_Exit.call(this, $Try_1_Value);
              } catch ($boundEx) {
                return $error($boundEx);
              }
            }.bind(this), $error);
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }.bind(this);
      }.bind(this);

      var loanID;
      loanID = loanIDRouteParamSelector(getState(), props);

      var $Try_1_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_1_Catch = function ($exception_2) {
        try {
          throw $exception_2;
        } catch ($boundEx) {
          return $Try_1_Finally($error)($boundEx);
        }
      };

      try {
        return Promise.resolve(dispatch(apiCreatePreApproval(loanID))).then(function ($await_4) {
          try {
            return $Try_1_Finally($Try_1_Post)();
          } catch ($boundEx) {
            return $Try_1_Catch($boundEx);
          }
        }, $Try_1_Catch);
      } catch ($exception_2) {
        $Try_1_Catch($exception_2);
      }
    });
  };
};

var loading$1 = function loading(_ref) {
  var BEGIN = _ref.BEGIN,
      SUCCESS = _ref.SUCCESS,
      FAILURE = _ref.FAILURE;
  return function (state, _ref2) {
    if (state === void 0) {
      state = false;
    }

    var type = _ref2.type;

    switch (type) {
      case BEGIN:
        return true;

      case SUCCESS:
      case FAILURE:
        return false;

      default:
        return state;
    }
  };
};

var error$1 = function error(_ref3) {
  var BEGIN = _ref3.BEGIN,
      SUCCESS = _ref3.SUCCESS,
      FAILURE = _ref3.FAILURE;
  return function (state, _ref4) {
    var _ref5, _ref6, _payload$detail;

    if (state === void 0) {
      state = null;
    }

    var type = _ref4.type,
        payload = _ref4.payload;

    switch (type) {
      case FAILURE:
        return (_ref5 = (_ref6 = (_payload$detail = payload === null || payload === void 0 ? void 0 : payload.detail) !== null && _payload$detail !== void 0 ? _payload$detail : payload === null || payload === void 0 ? void 0 : payload.message) !== null && _ref6 !== void 0 ? _ref6 : payload === null || payload === void 0 ? void 0 : payload.msg) !== null && _ref5 !== void 0 ? _ref5 : typeof payload === 'string' ? payload : DEFAULT_LOP_API_FAILURE_ERROR_MESSAGE;

      case BEGIN:
      case SUCCESS:
        return null;

      default:
        return state;
    }
  };
};

var letters = function letters(state, _ref7) {
  if (state === void 0) {
    state = [];
  }

  var type = _ref7.type,
      payload = _ref7.payload;

  switch (type) {
    case API_GET_PRE_APPROVALS.SUCCESS:
      return payload.documents;

    default:
      return state;
  }
};

var reducer$1 = combineReducers({
  fetchLoading: loading$1(API_GET_PRE_APPROVALS),
  createLoading: loading$1(API_CREATE_PRE_APPROVAL),
  fetchError: error$1(API_GET_PRE_APPROVALS),
  createError: error$1(API_CREATE_PRE_APPROVAL),
  letters: letters
});
var DEFAULT_STATE$t = reducer$1(undefined, {
  type: 'INIT'
});

var preApprovals$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  API_GET_PRE_APPROVALS: API_GET_PRE_APPROVALS,
  apiGetPreApprovals: apiGetPreApprovals,
  API_CREATE_PRE_APPROVAL: API_CREATE_PRE_APPROVAL,
  apiCreatePreApproval: apiCreatePreApproval,
  fetchPreApprovals: fetchPreApprovals,
  newPreApproval: newPreApproval,
  DEFAULT_STATE: DEFAULT_STATE$t,
  'default': reducer$1
});

var UPDATE_NOTES = createActionObject('UPDATE_NOTES');
var updateNotesThunk = function updateNotesThunk(notes, loanID) {
  return API['loan-file'].PUT.thunk({
    action: "v1/loans/" + loanID + "/notes",
    authenticated: true,
    input: {
      notes: notes
    }
  }, UPDATE_NOTES);
};
var updateNotes = function updateNotes(props) {
  return function (dispatch, getState) {
    var loanID = loanIDPropSelector(null, props);

    var _notesFieldsSelector = notesFieldsSelector(getState()),
        notes = _notesFieldsSelector.notes;

    return dispatch(updateNotesThunk(notes, loanID));
  };
};
var GET_NOTES = createActionObject('GET_NOTES');
var getNotesThunk = function getNotesThunk(loanID) {
  return API['loan-file'].GET.thunk({
    action: "v1/loans/" + loanID + "/notes",
    authenticated: true
  }, GET_NOTES);
};
var getNotes = function getNotes(props) {
  return function (dispatch) {
    var loanID = loanIDPropSelector(null, props);
    return dispatch(getNotesThunk(loanID));
  };
};
var UPDATE_NOTES_FIELDS = 'UPDATE_NOTES_FIELDS';
var updateFields$8 = function updateFields(notes) {
  return {
    type: UPDATE_NOTES_FIELDS,
    payload: notes
  };
};
var DEFAULT_STATE$u = {
  notes: null,
  fields: {
    notes: null
  }
};
var notes$1 = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$u;
  }

  var type = _ref.type,
      payload = _ref.payload;

  switch (type) {
    case GET_NOTES.SUCCESS:
      {
        return Object.assign({}, state, {
          notes: payload.notes
        });
      }

    case UPDATE_NOTES.SUCCESS:
      {
        return Object.assign({}, state, {
          notes: state.fields.notes
        });
      }

    case UPDATE_NOTES_FIELDS:
      {
        return Object.assign({}, state, {
          fields: payload
        });
      }

    default:
      return state;
  }
});

var notes$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  UPDATE_NOTES: UPDATE_NOTES,
  updateNotesThunk: updateNotesThunk,
  updateNotes: updateNotes,
  GET_NOTES: GET_NOTES,
  getNotesThunk: getNotesThunk,
  getNotes: getNotes,
  UPDATE_NOTES_FIELDS: UPDATE_NOTES_FIELDS,
  updateFields: updateFields$8,
  DEFAULT_STATE: DEFAULT_STATE$u,
  'default': notes$1
});

var API_UPDATE_BORROWER_DEMOGRAPHICS = createActionObject('API_UPDATE_BORROWER_DEMOGRAPHICS');
var apiUpdateBorrowerDemographics = function apiUpdateBorrowerDemographics(borrowerID, demographics) {
  return API['loan-file'].PUT.thunk({
    action: "v1/borrowers/" + borrowerID + "/demographics",
    input: {
      demographics: demographics
    },
    authenticated: true,
    metadata: borrowerID
  }, API_UPDATE_BORROWER_DEMOGRAPHICS);
};
var API_UPDATE_BORROWER_DEMOGRAPHICS_COLLECTION = createActionObject('API_UPDATE_BORROWER_DEMOGRAPHICS_COLLECTION');
var apiUpdateBorrowerDemographicsCollection = function apiUpdateBorrowerDemographicsCollection(borrowerID, demographicsCollection) {
  return API['loan-file'].PUT.thunk({
    action: "v1/borrowers/" + borrowerID + "/demographics-collection",
    input: {
      demographicsCollection: demographicsCollection
    },
    authenticated: true,
    metadata: borrowerID
  }, API_UPDATE_BORROWER_DEMOGRAPHICS_COLLECTION);
};
var updateBorrowerDemographics = function updateBorrowerDemographics(props, _ref) {
  var demographics = _ref.demographics,
      demographicsCollection = _ref.demographicsCollection;
  return function (dispatch) {
    var borrowerID = borrowerIDPropSelector(null, props);
    var demographicsPayload = createDemographicsAPIPayload(demographics);
    var demographicsCollectionPayload = createDemographicsCollectionAPIPayload(demographicsCollection);
    return Promise.all([apiUpdateBorrowerDemographics(borrowerID, demographicsPayload), apiUpdateBorrowerDemographicsCollection(borrowerID, demographicsCollectionPayload)].map(dispatch));
  };
};

var getItemsFromBorrowers$1 = function getItemsFromBorrowers(borrowers) {
  return borrowers.reduce(function (acc, _ref2) {
    var _Object$assign;

    var id = _ref2.id,
        demographics = _ref2.demographics,
        demographicsCollection = _ref2.demographicsCollection;
    return Object.assign({}, acc, (_Object$assign = {}, _Object$assign[id] = {
      demographics: demographics,
      demographicsCollection: demographicsCollection
    }, _Object$assign));
  }, {});
};

var DEFAULT_STATE$v = {
  items: {}
};
var borrowersDemographics$1 = (function (state, _ref3) {
  var _state, _state2, _state2$items, _Object$assign2, _state3, _state4, _state4$items, _Object$assign3;

  if (state === void 0) {
    state = DEFAULT_STATE$v;
  }

  var type = _ref3.type,
      payload = _ref3.payload,
      metadata = _ref3.metadata,
      request = _ref3.request;

  switch (type) {
    case FETCH_BORROWERS.SUCCESS:
      return Object.assign({}, state, {
        items: getItemsFromBorrowers$1(payload.borrowers)
      });

    case API_UPDATE_BORROWER_DEMOGRAPHICS.SUCCESS:
      return Object.assign({}, state, {
        items: Object.assign({}, (_state = state) === null || _state === void 0 ? void 0 : _state.items, (_Object$assign2 = {}, _Object$assign2[metadata] = Object.assign({}, (_state2 = state) === null || _state2 === void 0 ? void 0 : (_state2$items = _state2.items) === null || _state2$items === void 0 ? void 0 : _state2$items[metadata], {
          demographics: request === null || request === void 0 ? void 0 : request.demographics
        }), _Object$assign2))
      });

    case API_UPDATE_BORROWER_DEMOGRAPHICS_COLLECTION.SUCCESS:
      return Object.assign({}, state, {
        items: Object.assign({}, (_state3 = state) === null || _state3 === void 0 ? void 0 : _state3.items, (_Object$assign3 = {}, _Object$assign3[metadata] = Object.assign({}, (_state4 = state) === null || _state4 === void 0 ? void 0 : (_state4$items = _state4.items) === null || _state4$items === void 0 ? void 0 : _state4$items[metadata], {
          demographicsCollection: request === null || request === void 0 ? void 0 : request.demographicsCollection
        }), _Object$assign3))
      });

    default:
      return state;
  }
});

var borrowersDemographics$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  API_UPDATE_BORROWER_DEMOGRAPHICS: API_UPDATE_BORROWER_DEMOGRAPHICS,
  apiUpdateBorrowerDemographics: apiUpdateBorrowerDemographics,
  API_UPDATE_BORROWER_DEMOGRAPHICS_COLLECTION: API_UPDATE_BORROWER_DEMOGRAPHICS_COLLECTION,
  apiUpdateBorrowerDemographicsCollection: apiUpdateBorrowerDemographicsCollection,
  updateBorrowerDemographics: updateBorrowerDemographics,
  DEFAULT_STATE: DEFAULT_STATE$v,
  'default': borrowersDemographics$1
});

var FETCH_BORROWER_PAIRS = createActionObject('FETCH_BORROWER_PAIRS');
var fetchBorrowerPairs = function fetchBorrowerPairs(loanID) {
  return API['loan-file'].GET.thunk({
    action: "v1/borrower-groups/" + loanID,
    authenticated: true
  }, FETCH_BORROWER_PAIRS);
};
var UPDATE_BORROWER_PAIRS = createActionObject('UPDATE_BORROWER_PAIRS');
var updateBorrowerPairs = function updateBorrowerPairs(loanID, borrowerPairs) {
  return API['loan-file'].PUT.thunk({
    action: 'v1/borrower-groups',
    input: {
      borrowerGroup: {
        loanID: loanID,
        borrowerPairs: borrowerPairs
      }
    },
    authenticated: true
  }, UPDATE_BORROWER_PAIRS);
};
var DEFAULT_STATE$w = {
  pairs: []
};
var borrowerPairs$1 = (function (state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$w;
  }

  var type = _ref.type,
      payload = _ref.payload,
      request = _ref.request;

  switch (type) {
    case FETCH_LOAN.SUCCESS:
      return Object.assign({}, state, {
        pairs: payload.loan.borrowerGroup
      });

    case FETCH_BORROWER_PAIRS.SUCCESS:
      return Object.assign({}, state, {
        pairs: payload.borrowerGroup.borrowerPairs
      });

    case UPDATE_BORROWER_PAIRS.SUCCESS:
      return Object.assign({}, state, {
        pairs: request.borrowerGroup.borrowerPairs
      });

    default:
      return state;
  }
});

var borrowerPairs$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  FETCH_BORROWER_PAIRS: FETCH_BORROWER_PAIRS,
  fetchBorrowerPairs: fetchBorrowerPairs,
  UPDATE_BORROWER_PAIRS: UPDATE_BORROWER_PAIRS,
  updateBorrowerPairs: updateBorrowerPairs,
  DEFAULT_STATE: DEFAULT_STATE$w,
  'default': borrowerPairs$1
});

var HOMESTORY_PROXY_SERVICE = 'homestory-proxy';
var FETCH_HOMESTORY_ELIGIBILITY = createActionObject('FETCH_HOMESTORY_ELIGIBILITY');
var fetchHomestoryEligibility = function fetchHomestoryEligibility(props) {
  return function (dispatch, getState) {
    var loanID = loanIDPropSelector(getState(), props);
    return dispatch(API[HOMESTORY_PROXY_SERVICE].GET.thunk({
      action: 'v1/leads/-/eligibility',
      authenticated: true,
      input: {
        loanID: loanID
      }
    }, FETCH_HOMESTORY_ELIGIBILITY));
  };
};
var SUBMIT_HOMESTORY_LEAD = createActionObject('SUBMIT_HOMESTORY_LEAD');
var submitHomestoryLead = function submitHomestoryLead(props) {
  return function (dispatch, getState) {
    var loanID = loanIDPropSelector(getState(), props);
    return dispatch(API[HOMESTORY_PROXY_SERVICE].POST.thunk({
      action: 'v1/leads',
      authenticated: true,
      input: {
        loanID: loanID
      }
    }, SUBMIT_HOMESTORY_LEAD));
  };
};
var CLEAR_HOMESTORY_ERRORS = 'CLEAR_HOMESTORY_ERRORS';
var clearHomestoryErrors = function clearHomestoryErrors() {
  return {
    type: CLEAR_HOMESTORY_ERRORS
  };
};
var DEFAULT_STATE$x = {
  homestory: {
    isError: false,
    isEligible: false,
    shouldEvaluateEligibility: true
  }
};

var agent$1 = function agent(state, _ref) {
  if (state === void 0) {
    state = DEFAULT_STATE$x;
  }

  var type = _ref.type,
      payload = _ref.payload;

  switch (type) {
    case FETCH_HOMESTORY_ELIGIBILITY.BEGIN:
      {
        return Object.assign({}, state, {
          homestory: Object.assign({}, state.homestory, {
            shouldEvaluateEligibility: false
          })
        });
      }

    case FETCH_HOMESTORY_ELIGIBILITY.SUCCESS:
      return Object.assign({}, state, {
        homestory: payload
      });

    case FETCH_HOMESTORY_ELIGIBILITY.FAILURE:
      return Object.assign({}, state, {
        homestory: Object.assign({}, state.homestory, {
          error: payload,
          isEligible: false
        })
      });

    case SUBMIT_HOMESTORY_LEAD.SUCCESS:
      return Object.assign({}, state, {
        homestory: Object.assign({}, payload, {
          isSuccessful: true,
          error: null
        })
      });

    case SUBMIT_HOMESTORY_LEAD.FAILURE:
      return Object.assign({}, state, {
        homestory: Object.assign({}, state.homestory, {
          isSuccessful: false,
          error: payload
        })
      });

    case CLEAR_HOMESTORY_ERRORS:
      return Object.assign({}, state, {
        homestory: Object.assign({}, state.homestory, {
          error: null
        })
      });

    case API_UPDATE_LOAN_ATTRIBUTES.SUCCESS:
      {
        return Object.assign({}, state, {
          homestory: Object.assign({}, state.homestory, {
            shouldEvaluateEligibility: true
          })
        });
      }

    default:
      return state;
  }
};

var agent$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  HOMESTORY_PROXY_SERVICE: HOMESTORY_PROXY_SERVICE,
  FETCH_HOMESTORY_ELIGIBILITY: FETCH_HOMESTORY_ELIGIBILITY,
  fetchHomestoryEligibility: fetchHomestoryEligibility,
  SUBMIT_HOMESTORY_LEAD: SUBMIT_HOMESTORY_LEAD,
  submitHomestoryLead: submitHomestoryLead,
  CLEAR_HOMESTORY_ERRORS: CLEAR_HOMESTORY_ERRORS,
  clearHomestoryErrors: clearHomestoryErrors,
  DEFAULT_STATE: DEFAULT_STATE$x,
  'default': agent$1
});

var combinedReducer = combineReducers({
  computed: computed,
  loanNumber: loanNumber,
  tridTriggeredUTC: tridTriggeredUTC,
  status: status,
  editor: editor,
  assets: assets$1,
  gifts: gifts$1,
  loanAttributes: loanAttributes$1,
  borrowers: borrowers$1,
  credit: credit,
  liabilities: liabilities,
  declarations: declarations$1,
  detail: detail$1,
  documentRequests: reducer,
  income: income$1,
  employments: employments$1,
  underwriting: underwriting$1,
  pricing: pricing$1,
  otherNewMortgages: otherNewMortgages$1,
  preApprovals: reducer$1,
  ownedRealEstates: ownedRealEstates,
  notes: notes$1,
  borrowersDemographics: borrowersDemographics$1,
  borrowerPairs: borrowerPairs$1,
  agent: agent$1,
  verifications: verifications$1
});
var loanFile$1 = (function (state, action) {
  return combinedReducer(action.type === RESET_LOAN ? undefined : state, action);
});

var loanFile$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  notes: notes$2,
  assets: assets$2,
  gifts: gifts$2,
  loanActions: loanActions$1,
  loanAttributes: loanAttributes$2,
  borrowers: borrowers$2,
  credit: credit$1,
  income: income$2,
  detail: detail$2,
  underwriting: underwriting$2,
  pricing: pricing$2,
  addresses: addresses$1,
  employments: employments$2,
  liabilities: liabilities$1,
  declarations: declarations$2,
  otherNewMortgages: otherNewMortgages$2,
  ownedRealEstates: ownedRealEstates$1,
  documentRequests: conditions$1,
  preApprovals: preApprovals$1,
  verifications: verifications$2,
  borrowersDemographics: borrowersDemographics$2,
  borrowerPairs: borrowerPairs$2,
  agent: agent$2,
  combinedReducer: combinedReducer,
  'default': loanFile$1,
  RESET_LOAN: RESET_LOAN,
  resetLoan: resetLoan,
  FETCH_LOAN: FETCH_LOAN,
  fetchLoan: fetchLoan,
  FETCH_LOAN_STATUS: FETCH_LOAN_STATUS,
  fetchLoanStatusThunk: fetchLoanStatusThunk,
  fetchLoanStatus: fetchLoanStatus,
  SEARCH_LOANS: SEARCH_LOANS,
  searchLoans: searchLoans,
  API_UPDATE_LOAN_EDITOR: API_UPDATE_LOAN_EDITOR,
  apiUpdateLoanEditor: apiUpdateLoanEditor,
  FETCH_CURRENT_REPORTS: FETCH_CURRENT_REPORTS,
  apiFetchReports: apiFetchReports,
  API_REMOVE_BORROWER: API_REMOVE_BORROWER,
  apiRemoveBorrower: apiRemoveBorrower,
  API_CREATE_BORROWER: API_CREATE_BORROWER,
  apiCreateBorrower: apiCreateBorrower,
  fetchLoanIDFromLead: fetchLoanIDFromLead,
  fetchAssetsAndGifts: fetchAssetsAndGifts,
  createAssetOrGift: createAssetOrGift,
  updateGift: updateGift,
  updateAsset: updateAsset,
  updateAssetOrGift: updateAssetOrGift,
  deleteAssetOrGift: deleteAssetOrGift,
  resetCombinedAssetAndGiftFormFields: resetCombinedAssetAndGiftFormFields,
  setCombinedAssetAndGiftFormFields: setCombinedAssetAndGiftFormFields,
  changeFromGiftToAsset: changeFromGiftToAsset,
  changeFromAssetToGift: changeFromAssetToGift,
  fetchEmploymentsAndIncome: fetchEmploymentsAndIncome,
  resetCombinedEmploymentAndIncomeFields: resetCombinedEmploymentAndIncomeFields,
  changeFromEmploymentToIncome: changeFromEmploymentToIncome,
  changeFromIncomeToEmployment: changeFromIncomeToEmployment,
  setCombinedEmploymentAndIncomeFormValues: setCombinedEmploymentAndIncomeFormValues,
  createEmploymentOrIncome: createEmploymentOrIncome,
  updateEmployment: updateEmployment,
  updateIncome: updateIncome,
  updateEmploymentOrIncome: updateEmploymentOrIncome,
  deleteEmploymentOrIncome: deleteEmploymentOrIncome,
  fetchIncomeAndAssets: fetchIncomeAndAssets,
  DEFAULT_STATE: DEFAULT_STATE$6,
  loanNumber: loanNumber,
  tridTriggeredUTC: tridTriggeredUTC,
  computed: computed,
  errorTypeFromError: errorTypeFromError
});

var _constants$loanFile$f = constants.loanFile,
    INCOME_FREQUENCY_LOOKUP$1 = _constants$loanFile$f.INCOME_FREQUENCY_LOOKUP,
    INCOME_TYPE_LOOKUP$1 = _constants$loanFile$f.INCOME_TYPE_LOOKUP,
    OWNERSHIP_SHARE_LOOKUP = _constants$loanFile$f.OWNERSHIP_SHARE_LOOKUP;
var INCOME_TOGGLE_FIELDS = preQualification.INCOME_TOGGLE_FIELDS,
    INCOME_TOGGLE_FIELD_LOOKUP = preQualification.INCOME_TOGGLE_FIELD_LOOKUP;

var annualIncome = function annualIncome(amount) {
  return {
    frequency: INCOME_FREQUENCY_LOOKUP$1.ANNUAL,
    amount: amount
  };
};

var CREATED_EMPLOYMENT_OR_INCOME = 'CREATED_EMPLOYMENT_OR_INCOME';
var createdEmploymentOrIncome = function createdEmploymentOrIncome(type, id) {
  return {
    type: CREATED_EMPLOYMENT_OR_INCOME,
    payload: {
      type: type,
      id: id
    }
  };
};

var createEmploymentOrIncome$1 = function createEmploymentOrIncome(incomeType) {
  return function (dispatch, getState) {
    return new Promise(function ($return, $error) {
      var amount, borrowerID, attributes, _await$dispatch, employmentID, _INCOME_TOGGLE_FIELD_, source, _attributes, _await$dispatch2, otherIncomeID;

      amount = preQualifyIncomesSelector(getState())[incomeType];
      borrowerID = preQualifyBorrowerIDSelector(getState());

      switch (incomeType) {
        case INCOME_TOGGLE_FIELD_LOOKUP.EMPLOYMENT.type:
        case INCOME_TOGGLE_FIELD_LOOKUP.SELF_EMPLOYMENT.type:
          attributes = incomeType === INCOME_TOGGLE_FIELD_LOOKUP.EMPLOYMENT.type ? {
            selfEmployed: false,
            income: {
              base: annualIncome(amount)
            }
          } : {
            selfEmployed: true,
            ownershipShare: OWNERSHIP_SHARE_LOOKUP.AT_LEAST_25_PERCENT,
            income: annualIncome(amount)
          };
          return Promise.resolve(dispatch(apiCreateEmployment(attributes, borrowerID))).then(function ($await_2) {
            try {
              _await$dispatch = $await_2, employmentID = _await$dispatch.employmentID;
              return $return(dispatch(createdEmploymentOrIncome(incomeType, employmentID)));
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }, $error);

        case INCOME_TOGGLE_FIELD_LOOKUP.SOCIAL_SECURITY.type:
        case INCOME_TOGGLE_FIELD_LOOKUP.PENSION.type:
        case INCOME_TOGGLE_FIELD_LOOKUP.OTHER.type:
          source = (_INCOME_TOGGLE_FIELD_ = {}, _INCOME_TOGGLE_FIELD_[INCOME_TOGGLE_FIELD_LOOKUP.SOCIAL_SECURITY.type] = INCOME_TYPE_LOOKUP$1.SOCIAL_SECURITY, _INCOME_TOGGLE_FIELD_[INCOME_TOGGLE_FIELD_LOOKUP.PENSION.type] = INCOME_TYPE_LOOKUP$1.RETIREMENT, _INCOME_TOGGLE_FIELD_[INCOME_TOGGLE_FIELD_LOOKUP.OTHER.type] = INCOME_TYPE_LOOKUP$1.OTHER, _INCOME_TOGGLE_FIELD_)[incomeType];
          _attributes = incomeType === INCOME_TOGGLE_FIELD_LOOKUP.OTHER.type ? {
            source: source,
            otherSourceSpecified: 'All other income',
            income: annualIncome(amount)
          } : {
            source: source,
            income: annualIncome(amount)
          };
          return Promise.resolve(dispatch(apiCreateOtherIncome(_attributes, borrowerID))).then(function ($await_3) {
            try {
              _await$dispatch2 = $await_3, otherIncomeID = _await$dispatch2.otherIncomeID;
              return $return(dispatch(createdEmploymentOrIncome(incomeType, otherIncomeID)));
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }, $error);
      }

      return $return();
    });
  };
};

var immutablySet = function immutablySet(obj, path, value) {
  return setWith(clone(obj), path, value, clone);
}; // Unfortunately, these endpoints do not support selectively updating a subset of fields,
// so we need to clone whatever the full object is that we got back from LFS, update the
// income amount, and send the whole thing right back.
// Thankfully, we authored the object and know nothing else has edited it, so this is
// fairly safe to do, even though the user hasn't seen the rest of the fields we're editing.


var updateEmploymentOrIncome$1 = function updateEmploymentOrIncome(incomeType) {
  return function (dispatch, getState) {
    var employmentOrIncome = employmentsAndIncomesSelector(getState())[incomeType];
    var amount = preQualifyIncomesSelector(getState())[incomeType];
    var id = employmentOrIncome.id,
        attributes = employmentOrIncome.attributes; // if we're going to set the income to the same value again, skip the request

    switch (incomeType) {
      case INCOME_TOGGLE_FIELD_LOOKUP.EMPLOYMENT.type:
        if (attributes.income.base.amount === amount) {
          return;
        }

        break;

      case INCOME_TOGGLE_FIELD_LOOKUP.SELF_EMPLOYMENT.type:
      case INCOME_TOGGLE_FIELD_LOOKUP.SOCIAL_SECURITY.type:
      case INCOME_TOGGLE_FIELD_LOOKUP.PENSION.type:
      case INCOME_TOGGLE_FIELD_LOOKUP.OTHER.type:
        if (attributes.income.amount === amount) {
          return;
        }

        break;
    }

    switch (incomeType) {
      case INCOME_TOGGLE_FIELD_LOOKUP.EMPLOYMENT.type:
      case INCOME_TOGGLE_FIELD_LOOKUP.SELF_EMPLOYMENT.type:
        var _INCOME_TOGGLE_FIELD_2;

        var incomeAmountPath = (_INCOME_TOGGLE_FIELD_2 = {}, _INCOME_TOGGLE_FIELD_2[INCOME_TOGGLE_FIELD_LOOKUP.EMPLOYMENT.type] = 'income.base.amount', _INCOME_TOGGLE_FIELD_2[INCOME_TOGGLE_FIELD_LOOKUP.SELF_EMPLOYMENT.type] = 'income.amount', _INCOME_TOGGLE_FIELD_2)[incomeType];
        var updatedAttributes = getEmploymentAttributesForRequest(immutablySet(attributes, incomeAmountPath, amount));
        return dispatch(apiUpdateEmploymentAttributes(id, updatedAttributes));

      case INCOME_TOGGLE_FIELD_LOOKUP.SOCIAL_SECURITY.type:
      case INCOME_TOGGLE_FIELD_LOOKUP.PENSION.type:
      case INCOME_TOGGLE_FIELD_LOOKUP.OTHER.type:
        var _updatedAttributes = immutablySet(attributes, 'income.amount', amount);

        return dispatch(apiUpdateOtherIncomeAttributes(id, _updatedAttributes));
    }
  };
};

var deleteEmploymentOrIncome$1 = function deleteEmploymentOrIncome(type) {
  return function (dispatch, getState) {
    var id = employmentsAndIncomesSelector(getState())[type].id;

    switch (type) {
      case INCOME_TOGGLE_FIELD_LOOKUP.EMPLOYMENT.type:
      case INCOME_TOGGLE_FIELD_LOOKUP.SELF_EMPLOYMENT.type:
        return dispatch(apiDeleteEmployment(id));

      case INCOME_TOGGLE_FIELD_LOOKUP.SOCIAL_SECURITY.type:
      case INCOME_TOGGLE_FIELD_LOOKUP.PENSION.type:
      case INCOME_TOGGLE_FIELD_LOOKUP.OTHER.type:
        return dispatch(apiDeleteOtherIncome(id));
    }
  };
};

var submitIncome = function submitIncome() {
  return function (dispatch, getState) {
    var preQualifyIncomes = preQualifyIncomesSelector(getState());
    var employmentsAndIncomes = employmentsAndIncomesSelector(getState());
    return Promise.all(INCOME_TOGGLE_FIELDS.map(function (_ref) {
      var toggle = _ref.toggle,
          type = _ref.type;
      var shouldExist = preQualifyIncomes[toggle];
      var doesExist = !!employmentsAndIncomes[type];

      var action = function action() {
        return function () {};
      };

      if (shouldExist && !doesExist) {
        action = createEmploymentOrIncome$1;
      }

      if (shouldExist && doesExist) {
        action = updateEmploymentOrIncome$1;
      }

      if (!shouldExist && doesExist) {
        action = deleteEmploymentOrIncome$1;
      }

      return dispatch(action(type));
    }));
  };
};
var createdEmploymentsAndIncomes = (function (state, _ref2) {
  var _Object$assign;

  if (state === void 0) {
    var _state;

    state = (_state = {}, _state[INCOME_TOGGLE_FIELD_LOOKUP.EMPLOYMENT.type] = null, _state[INCOME_TOGGLE_FIELD_LOOKUP.SELF_EMPLOYMENT.type] = null, _state[INCOME_TOGGLE_FIELD_LOOKUP.SOCIAL_SECURITY.type] = null, _state[INCOME_TOGGLE_FIELD_LOOKUP.PENSION.type] = null, _state[INCOME_TOGGLE_FIELD_LOOKUP.OTHER.type] = null, _state);
  }

  var type = _ref2.type,
      payload = _ref2.payload,
      metadata = _ref2.metadata;

  switch (type) {
    case CREATED_EMPLOYMENT_OR_INCOME:
      return Object.assign({}, state, (_Object$assign = {}, _Object$assign[payload.type] = payload.id, _Object$assign));

    case API_DELETE_EMPLOYMENT.SUCCESS:
    case API_DELETE_OTHER_INCOME.SUCCESS:
      return mapValues(state, function (id) {
        return id === metadata ? null : id;
      });

    default:
      return state;
  }
});

var ASSET_TYPES_LOOKUP$2 = constants.loanFile.ASSET_TYPES_LOOKUP;
var ASSET_TOGGLE_FIELDS = preQualification.ASSET_TOGGLE_FIELDS,
    ASSET_TOGGLE_FIELD_LOOKUP = preQualification.ASSET_TOGGLE_FIELD_LOOKUP;
var CREATED_ASSET = 'CREATED_ASSET';
var createdAsset = function createdAsset(type, id) {
  return {
    type: CREATED_ASSET,
    payload: {
      type: type,
      id: id
    }
  };
};

var createAsset = function createAsset(type) {
  return function (dispatch, getState) {
    return new Promise(function ($return, $error) {
      var _ASSET_TOGGLE_FIELD_L, borrowerID, assets, assetType, attributes, _await$dispatch, assetID;

      borrowerID = preQualifyBorrowerIDSelector(getState());
      assets = preQualifyAssetsSelector(getState());
      assetType = (_ASSET_TOGGLE_FIELD_L = {}, _ASSET_TOGGLE_FIELD_L[ASSET_TOGGLE_FIELD_LOOKUP.CASH.type] = ASSET_TYPES_LOOKUP$2.CHECKING_ACCOUNT, _ASSET_TOGGLE_FIELD_L[ASSET_TOGGLE_FIELD_LOOKUP.BROKERAGE.type] = ASSET_TYPES_LOOKUP$2.STOCKS, _ASSET_TOGGLE_FIELD_L[ASSET_TOGGLE_FIELD_LOOKUP.RETIREMENT.type] = ASSET_TYPES_LOOKUP$2.RETIREMENT_ACCOUNT, _ASSET_TOGGLE_FIELD_L[ASSET_TOGGLE_FIELD_LOOKUP.OTHER.type] = ASSET_TYPES_LOOKUP$2.OTHER, _ASSET_TOGGLE_FIELD_L)[type];
      attributes = {
        type: assetType,
        marketValue: assets[type],
        otherTypeSpecified: assetType === ASSET_TYPES_LOOKUP$2.OTHER ? 'Unspecified' : undefined,
        otherTypeLiquid: assetType === ASSET_TYPES_LOOKUP$2.OTHER ? false : undefined
      };
      return Promise.resolve(dispatch(apiCreateAsset(attributes, [borrowerID]))).then(function ($await_1) {
        try {
          _await$dispatch = $await_1, assetID = _await$dispatch.assetID;
          return $return(dispatch(createdAsset(type, assetID)));
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  };
}; // Unfortunately, this endpoint does not support selectively updating a subset of fields,
// so we need to clone whatever the full asset is that we got back from LFS, update the
// marketValue, and send the whole thing right back.
// Thankfully, we authored the asset and know nothing else has edited it, so this is
// fairly safe to do, even though the user hasn't seen the rest of the fields we're editing.


var updateAsset$1 = function updateAsset(type) {
  return function (dispatch, getState) {
    var borrowerID = preQualifyBorrowerIDSelector(getState());
    var marketValue = preQualifyAssetsSelector(getState())[type];
    var _assetsSelector$type = assetsSelector$1(getState())[type],
        id = _assetsSelector$type.id,
        attributes = _assetsSelector$type.attributes;

    if (attributes.marketValue === marketValue) {
      return;
    }

    var updatedAttributes = Object.assign({}, attributes, {
      marketValue: marketValue
    });
    return dispatch(apiUpdateAssetAttributes(id, updatedAttributes));
  };
};

var deleteAsset = function deleteAsset(type) {
  return function (dispatch, getState) {
    var id = assetsSelector$1(getState())[type].id;
    return dispatch(apiDeleteAsset(id));
  };
};

var submitAssets = function submitAssets() {
  return function (dispatch, getState) {
    var preQualifyAssets = preQualifyAssetsSelector(getState());
    var existingAssets = assetsSelector$1(getState());
    return Promise.all(ASSET_TOGGLE_FIELDS.map(function (_ref) {
      var toggle = _ref.toggle,
          type = _ref.type;
      var shouldExist = preQualifyAssets[toggle];
      var doesExist = !!existingAssets[type];

      var action = function action() {
        return function () {};
      };

      if (shouldExist && !doesExist) {
        action = createAsset;
      }

      if (shouldExist && doesExist) {
        action = updateAsset$1;
      }

      if (!shouldExist && doesExist) {
        action = deleteAsset;
      }

      return dispatch(action(type));
    }));
  };
};
var createdAssets = (function (state, _ref2) {
  var _Object$assign;

  if (state === void 0) {
    var _state;

    state = (_state = {}, _state[ASSET_TOGGLE_FIELD_LOOKUP.CASH.type] = null, _state[ASSET_TOGGLE_FIELD_LOOKUP.BROKERAGE.type] = null, _state[ASSET_TOGGLE_FIELD_LOOKUP.RETIREMENT.type] = null, _state[ASSET_TOGGLE_FIELD_LOOKUP.OTHER.type] = null, _state);
  }

  var type = _ref2.type,
      payload = _ref2.payload,
      metadata = _ref2.metadata;

  switch (type) {
    case CREATED_ASSET:
      return Object.assign({}, state, (_Object$assign = {}, _Object$assign[payload.type] = payload.id, _Object$assign));

    case API_DELETE_ASSET.SUCCESS:
      return mapValues(state, function (id) {
        return id === metadata ? null : id;
      });

    default:
      return state;
  }
});

var _excluded$n = ["cityOrZip"];
var _constants$loanFile$g = constants.loanFile,
    CONSENT_TYPES_LOOKUP = _constants$loanFile$g.CONSENT_TYPES_LOOKUP,
    UNDERWRITER_LOOKUP = _constants$loanFile$g.UNDERWRITER_LOOKUP,
    CREDIT_PULL_TYPE_LOOKUP$5 = _constants$loanFile$g.CREDIT_PULL_TYPE_LOOKUP;
var fetchESignatureConsentDocumentURL = function fetchESignatureConsentDocumentURL() {
  return function (dispatch, getState) {
    var state = getState();
    var eSignatureConsentDocumentURL = eSignatureConsentDocumentURLSelector(state);

    if (eSignatureConsentDocumentURL) {
      return;
    }

    return dispatch(fetchPublicSettingsValues(ESIGNATURE_DOCUMENT_URL_SETTINGS_KEY));
  };
};
var submitLead = function submitLead() {
  return function (dispatch, getState) {
    return new Promise(function ($return, $error) {
      var _preQualifyDirectLead, cityOrZip, directLead, response, leadID;

      _preQualifyDirectLead = preQualifyDirectLeadSelector(getState()), cityOrZip = _preQualifyDirectLead.cityOrZip, directLead = _objectWithoutProperties(_preQualifyDirectLead, _excluded$n);
      return Promise.resolve(dispatch(submitDirectLead(directLead, true))).then(function ($await_1) {
        try {
          response = $await_1;
          leadID = preQualifyLeadIDSelector(getState());
          handleLeadEventTracking(directLead, leadID);
          return $return(response);
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  };
};
var updateLoanAttributesFromPreQualify = function updateLoanAttributesFromPreQualify() {
  return function (dispatch, getState) {
    var loanID = preQualifyLoanIDSelector(getState());
    var loanAttributes = preQualifyLoanAttributesSelector(getState());
    return dispatch(apiUpdateLoanAttributes(loanID, loanAttributes));
  };
};
var UPDATE_PRE_QUALIFY_BORROWER_ATTRIBUTES = createActionObject('UPDATE_PRE_QUALIFY_BORROWER_ATTRIBUTES');
var updateBorrowerAddressFromPreQualify = function updateBorrowerAddressFromPreQualify() {
  return function (dispatch, getState) {
    var borrowerID = preQualifyBorrowerIDSelector(getState());
    var borrowerAttributes = pick(preQualifyBorrowerAttributesSelector(getState()), ['residences']);
    return dispatch(apiUpdateBorrowerAttributes(borrowerAttributes, borrowerID, UPDATE_PRE_QUALIFY_BORROWER_ATTRIBUTES));
  };
};
var grantSoftCreditConsentFromPreQualify = function grantSoftCreditConsentFromPreQualify() {
  return function (dispatch, getState) {
    var borrowerID = preQualifyBorrowerIDSelector(getState());
    return dispatch(grantSoftCreditConsentThunk(CONSENT_TYPES_LOOKUP.WEBSITE, borrowerID));
  };
};
var updateBorrowerTaxpayerIDFromPreQualify = function updateBorrowerTaxpayerIDFromPreQualify() {
  return function (dispatch, getState) {
    var borrowerID = preQualifyBorrowerIDSelector(getState());
    var borrowerAttributes = pick(preQualifyBorrowerAttributesSelector(getState()), ['taxpayerID']);
    return dispatch(apiUpdateBorrowerAttributes(borrowerAttributes, borrowerID, UPDATE_PRE_QUALIFY_BORROWER_ATTRIBUTES));
  };
};

var handleSubmissionEventTracking = function handleSubmissionEventTracking(incomesAndAssets, leadID) {
  trackEvent_1({
    category: 'Mortgages',
    action: 'Prequal Complete|Lead Info',
    label: JSON.stringify(incomesAndAssets)
  });
  trackEvent_1({
    category: 'Mortgages',
    action: 'Prequal Complete|LeadID',
    label: leadID
  });
};

var handlePreQualifySubmission = function handlePreQualifySubmission() {
  return function (dispatch, getState) {
    return new Promise(function ($return, $error) {
      var preQualifyIncomesAndAssets, leadID, loanID, borrowerID, _await$dispatch, loanRateQuoteID, isPricingScenarioEligibleForUnderwriting;

      preQualifyIncomesAndAssets = preQualifyIncomesAndAssetsSelector(getState());
      leadID = preQualifyLeadIDSelector(getState());
      handleSubmissionEventTracking(preQualifyIncomesAndAssets, leadID);
      loanID = preQualifyLoanIDSelector(getState());
      borrowerID = preQualifyBorrowerIDSelector(getState());
      return Promise.resolve(dispatch(pullCreditReport({
        loanID: loanID,
        borrowerID: borrowerID,
        creditPullType: CREDIT_PULL_TYPE_LOOKUP$5.SOFT
      }))).then(function ($await_2) {
        try {
          return Promise.resolve(dispatch(getPreQualificationScenariosThunk(loanID))).then(function ($await_3) {
            try {
              _await$dispatch = $await_3, loanRateQuoteID = _await$dispatch.loanRateQuoteID;
              isPricingScenarioEligibleForUnderwriting = !!loanRateQuoteID;

              if (isPricingScenarioEligibleForUnderwriting) {
                return $return(dispatch(submitForUnderwriting({
                  loanID: loanID,
                  underwriter: UNDERWRITER_LOOKUP.DESKTOP_UNDERWRITER_POS
                })));
              }

              return $return();
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }, $error);
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    });
  };
}; // one page/form in the prequal flow has been completed

var PRE_QUALIFY_FORM_SUBMITTED = 'PRE_QUALIFY_FORM_SUBMITTED';
var submitPreQualifyForm = function submitPreQualifyForm(payload) {
  return {
    type: PRE_QUALIFY_FORM_SUBMITTED,
    payload: payload
  };
};
var CONSENT_TO_ESIGNATURES = 'CONSENT_TO_ESIGNATURES';
var consentToESignatures = function consentToESignatures() {
  return {
    type: CONSENT_TO_ESIGNATURES
  };
};
var CONSENT_TO_TCPA = 'CONSENT_TO_TCPA';
var consentToTCPA = function consentToTCPA() {
  return {
    type: CONSENT_TO_TCPA
  };
};

var fields$9 = function fields(state, _ref) {
  if (state === void 0) {
    state = {};
  }

  var type = _ref.type,
      payload = _ref.payload;

  switch (type) {
    case PRE_QUALIFY_FORM_SUBMITTED:
      return Object.assign({}, state, payload);

    case CONSENT_TO_ESIGNATURES:
      return Object.assign({}, state, {
        eSignatureConsent: true
      });

    case CONSENT_TO_TCPA:
      return Object.assign({}, state, {
        tcpaConsent: true
      });

    default:
      return state;
  }
};

var lead = function lead(state, _ref2) {
  if (state === void 0) {
    state = {
      id: null,
      applicationURL: null,
      borrowerID: null,
      loanID: null
    };
  }

  var type = _ref2.type,
      payload = _ref2.payload;

  switch (type) {
    case DIRECT_LEAD_SUBMITTED.SUCCESS:
      var borrowerID = payload.borrowerID,
          id = payload.id,
          applicationURL = payload.applicationURL,
          loanID = payload.loanID;
      return {
        id: id,
        borrowerID: borrowerID,
        applicationURL: applicationURL,
        loanID: loanID
      };

    default:
      return state;
  }
};

var underwriting$3 = function underwriting(state, _ref3) {
  if (state === void 0) {
    state = {
      recommendation: null
    };
  }

  var type = _ref3.type,
      payload = _ref3.payload;

  switch (type) {
    case SUBMIT_FOR_UNDERWRITING.SUCCESS:
      var recommendation = payload.finding.recommendation;
      return {
        recommendation: recommendation
      };

    default:
      return state;
  }
};

var hasSubmitted = function hasSubmitted(state, _ref4) {
  if (state === void 0) {
    state = false;
  }

  var type = _ref4.type;

  switch (type) {
    case PULL_CREDIT_REPORT.BEGIN:
      return true;

    default:
      return state;
  }
};

var hasAPIErrors = function hasAPIErrors(state, _ref5) {
  if (state === void 0) {
    state = false;
  }

  var type = _ref5.type;

  switch (type) {
    case PULL_CREDIT_REPORT.FAILURE:
    case GET_PRE_QUALIFICATION_SCENARIOS.FAILURE:
    case SUBMIT_FOR_UNDERWRITING.FAILURE:
      return true;

    default:
      return state;
  }
};

var reducer$2 = combineReducers({
  fields: fields$9,
  lead: lead,
  underwriting: underwriting$3,
  hasAPIErrors: hasAPIErrors,
  hasSubmitted: hasSubmitted,
  createdEmploymentsAndIncomes: createdEmploymentsAndIncomes,
  createdAssets: createdAssets,
  employments: employments$1,
  income: income$1,
  assets: assets$1
});
var RESET_PRE_QUALIFY = 'RESET_PRE_QUALIFY';
var reset = function reset() {
  return {
    type: RESET_PRE_QUALIFY
  };
};
var index$2 = (function (state, action) {
  return reducer$2(action.type === RESET_PRE_QUALIFY ? undefined : state, action);
});

var index$3 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  fetchESignatureConsentDocumentURL: fetchESignatureConsentDocumentURL,
  submitLead: submitLead,
  updateLoanAttributesFromPreQualify: updateLoanAttributesFromPreQualify,
  UPDATE_PRE_QUALIFY_BORROWER_ATTRIBUTES: UPDATE_PRE_QUALIFY_BORROWER_ATTRIBUTES,
  updateBorrowerAddressFromPreQualify: updateBorrowerAddressFromPreQualify,
  grantSoftCreditConsentFromPreQualify: grantSoftCreditConsentFromPreQualify,
  updateBorrowerTaxpayerIDFromPreQualify: updateBorrowerTaxpayerIDFromPreQualify,
  handlePreQualifySubmission: handlePreQualifySubmission,
  PRE_QUALIFY_FORM_SUBMITTED: PRE_QUALIFY_FORM_SUBMITTED,
  submitPreQualifyForm: submitPreQualifyForm,
  CONSENT_TO_ESIGNATURES: CONSENT_TO_ESIGNATURES,
  consentToESignatures: consentToESignatures,
  CONSENT_TO_TCPA: CONSENT_TO_TCPA,
  consentToTCPA: consentToTCPA,
  RESET_PRE_QUALIFY: RESET_PRE_QUALIFY,
  reset: reset,
  'default': index$2,
  submitIncome: submitIncome,
  submitAssets: submitAssets
});

var bulkUpdate$2 = _bulkUpdate;
var processorPipeline$2 = _processorPipeline;
var processorPortal = combineReducers({
  bulkUpdate: bulkUpdate,
  processorPipeline: processorPipeline
});

var processorPortal$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  bulkUpdate: bulkUpdate$2,
  processorPipeline: processorPipeline$2,
  'default': processorPortal
});

var fundingRequest$2 = _fundingRequest;
var loanRequest$2 = _loanRequest;
var purchaseRequest$2 = _purchaseRequest;
var accountingPortal = combineReducers({
  fundingRequest: fundingRequest,
  loanRequest: loanRequest,
  purchaseRequest: purchaseRequest
});

var accountingPortal$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  fundingRequest: fundingRequest$2,
  loanRequest: loanRequest$2,
  purchaseRequest: purchaseRequest$2,
  'default': accountingPortal
});

export { API, DEFAULT_LOP_API_FAILURE_CTA, DEFAULT_LOP_API_FAILURE_ERROR_MESSAGE, NETWORK_ERROR_TYPE, STATUS, accountingPortal$1 as accountingPortalReducers, admin as adminSelectors, affordability$3 as affordabilityCalculatorReducers, affordability$1 as affordabilitySelectors, auth$2 as authReducers, auth as authSelectors, autocomplete, bulkUpdate$1 as bulkUpdateSelectors, calculators as calculatorSelectors, cms$1 as cmsReducers, cms$2 as cmsSelectors, index$1 as commonReducers, conditions as conditionsSelectors, createActionObject, createPropsSelector, creditBase as creditSelectors, fieldReducer, fundingRequest$1 as fundingRequestSelectors, liabilitiesBase as liabilitiesSelectors, loanFile$2 as loanFileReducers, index as loanFileSelectors, loans as loanOfficerPortalLoansSelectors, loanRequest$1 as loanRequestSelectors, longFlow$2 as longFlowReducers, longFlow as longFlowSelectors, mortgage$2 as mortgageCalculatorReducers, mortgage as mortgageCalculatorSelectors, mortgage as mortgageSelectors, otherNewMortgages as otherNewMortgageSelectors, standardReduxPersistStore as persist, index$3 as preQualifyReducers, preQualify as preQualifySelectors, pricingBase as pricingSelectors, processorPipeline$1 as processorPipelineSelectors, processorPortal$1 as processorPortalReducers, purchaseRequest$1 as purchaseRequestSelectors, queryParams$2 as queryParamsReducers, queryParams as queryParamsSelectors, refinance$3 as refinanceCalculatorReducers, settings$2 as settingsReducers, settings as settingsSelectors, underwriting as underwritingSelectors, getConfig as zhlConfig };
